import { AccordionDisclosure } from "theme";
import { Drawer } from "theme";
import { EffectCarousel } from "theme";

class AccordionDisclosureGroup extends HTMLElement {
  constructor() {
    super(); // always call super() first in the constructor. This also calls the extended class' constructor.
    this.accordions = this.querySelectorAll('details');
  }
  connectedCallback() {
    this.accordions.forEach((details) => {
      details.addEventListener("click", this.closeOthers.bind(this), true);
    });
  }
  disconnectedCallback() {
    if (this.accordions) {
      this.accordions.forEach((details) => {
        details.removeEventListener("click", this.closeOthers.bind(this), true);
      });
    }
  }
  closeOthers(e) {
    const clickedDetails = e.target.closest('details');
    this.accordions.forEach((details) => {
      if (details !== clickedDetails) {
        details.removeAttribute('open');
        details.setAttribute('aria-expanded', 'false');
      }
    });
  }
};
customElements.define("accordion-group", AccordionDisclosureGroup);


class SurfaceSelector extends HTMLElement {
  constructor() {
    super();
    this._onChange = this._onChange.bind(this);
    const script = this.querySelector("script[data-variant-recommendation]");
    let variants = [];
    if (script && script.textContent) {
      try {
        const parsed = JSON.parse(script.textContent);
        variants = Array.isArray(parsed) ? parsed : [];
      } catch (e) {
        console.error("Invalid JSON in data-variant-recommendation script:", e);
        variants = [];
      }
    }
    this._variantAvailable = Array.isArray(variants) ? variants : [];
  }

  connectedCallback() {
    // Attach event listeners to all select elements inside this component
    this._attachListeners();
  }

  disconnectedCallback() {
    this._detachListeners();
  }

  _attachListeners() {
    this._selects = this.querySelectorAll('select');
    this._selects.forEach(select => {
      select.addEventListener('change', this._onChange);
    });
    this.addEventListener('variant:change', this._onVariantChange);
  }

  _detachListeners() {
    if (this._selects) {
      this._selects.forEach(select => {
        select.removeEventListener('change', this._onChange);
      });
    }
  }
  _onVariantChange(event) {
    // Reset all selects to default value and enable all options
    this._selects.forEach(select => {
      select.value = '';
      select.removeAttribute('disabled');
      Array.from(select.options).forEach(option => {
        option.disabled = false;
      });
    });
  }
  _onChange(event) {
    // Loop through this._variantAvailable and collect available sheen parameters
    const selectedSurface = event.target.value;
    // Collect all unique sheens for variants that include the selected surface
    const availableSheens = Array.from(
      new Set(
        this._variantAvailable
          .filter(variant => Array.isArray(variant.surface) && variant.surface.includes(selectedSurface))
          .flatMap(variant => Array.isArray(variant.sheen) ? variant.sheen : [])
          .filter(sheen => sheen !== undefined && sheen !== null && sheen !== '')
      )
    );
    // Custom event for parent listeners, e.g. for form or product updates
    this._selects.forEach(select => {
      if (select !== event.target) {
        select.removeAttribute('disabled');
        Array.from(select.options).forEach(option => {
          if (option.value === '') {
            // Always enable the default empty option
            option.disabled = false;
          } else if (select.name.toLowerCase().includes('sheen')) {
            // Enable or disable sheen options based on availableSheens
            option.disabled = !availableSheens.includes(option.value);
          } else {
            // For other selects, you can implement similar logic if needed
            option.disabled = false; // Or some other condition
          }
          // If the currently selected option is now disabled, reset to default
          if (option.selected && option.disabled) {
            select.value = '';
            select.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });
      }
    });


    // If both selectors have non-default values, find recommended variant(s)
    const selectedValues = Array.from(this._selects)
      .map(select => select.value)
      .filter(value => value !== '');

    if (selectedValues.length === this._selects.length) {
      // Both selectors have non-default values
      const recommendedVariants = this._variantAvailable.filter(variant => {
        // Check if variant matches all selected values
        return selectedValues.every(value =>
          (Array.isArray(variant.surface) && variant.surface.includes(value)) ||
          (Array.isArray(variant.sheen) && variant.sheen.includes(value))
        );
      });
      // You can use recommendedVariants as needed, e.g. display, select, etc.
      // If there is an input element with the same value as a recommended variant, select it
      for (const variant of recommendedVariants) {
        const valueToMatch = String(variant.id);
        const labels = this.closest('quick-order-list')?.querySelectorAll('label[data-variant-id]');
        if (!labels) continue;

        for (const label of labels) {
          const variantIds = label.dataset.variantId.split(',').map(id => id.trim());

          if (variantIds.includes(valueToMatch)) {
            label.classList.add('selected');

            const input = label.parentElement.querySelector('input[type="radio"], input[type="checkbox"]');

            if (input) {
              input.dataset.triggeredBy = 'surface-selector';
              setTimeout(() => {
                input.click();
                input.checked = true;
              }, 200);
            }

            // Stop both loops after first match
            return;
          } else {
            label.classList.remove('selected');
          }
        }
      }
    } else {
      // If not all selectors have values, remove 'selected' class from all labels
      const labels = this.closest('quick-order-list')?.querySelectorAll('label[data-variant-id]');
      if (labels) {
        labels.forEach(label => {
          const input = label.parentElement.querySelector('input[type="radio"], input[type="checkbox"]');
          label.classList.remove('selected');
          if (input) {
            input.checked = false;
            const sizeAccordion = document.querySelector('.accordion--size');
            if (sizeAccordion) {
              sizeAccordion.removeAttribute('open');
              sizeAccordion.setAttribute('disabled', '');
            }

            const selectedOption = document.querySelector('selected-option');
            if (selectedOption) {
              const span = selectedOption.querySelector('span');
              const input = selectedOption.querySelector('input');
              
              if (span) span.innerHTML = 'Select finish';
              if (input) input.checked = false;
            }
          }
        });
      }
    }
    this.dispatchEvent(new CustomEvent('surface-selector:change', {
      detail: {
        name: event.target.name,
        value: event.target.value
      },
      bubbles: true
    }));
  }
}

customElements.define('surface-selector', SurfaceSelector);
// reset the paint calculate when user select non recommended variant options


/* -------------------------------------------
 * <quick-order-list-submit>
 * ----------------------------------------- */

class QuickOrderListSubmit extends HTMLElement {
  static get observedAttributes() {
    return ['include-zero']; // optional flag to send zeroes too
  }

  connectedCallback() {
    this.addEventListener('click', this.onClick);
    this.setAttribute('role', this.getAttribute('role') || 'button');
    this.tabIndex ||= 0;
    this.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        this.click();
      }
    });

    // Listen for changes to quantity inputs to update total price
    this._bindQuantityChange();
    this._updateTotalPrice();

    // Listen for variant:change to reinitialize
    document.addEventListener('variant:change', this._onVariantChange);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    this._unbindQuantityChange();
    document.removeEventListener('variant:change', this._onVariantChange);
  }

  _bindQuantityChange() {
    const root = this.closest('quick-order-list') || document.querySelector('quick-order-list') || document;
    this._quantityInputs = Array.from(
      root.querySelectorAll('quick-order-list-quantity-selector[variant-id] input[type="number"], quick-order-list-quantity-selector[variant-id] input[inputmode="numeric"]')
    );
    this._quantityInputs.forEach(input => {
      input.addEventListener('input', this._updateTotalPrice.bind(this));
      input.addEventListener('change', this._updateTotalPrice.bind(this));
    });
  }

  _unbindQuantityChange() {
    if (this._quantityInputs) {
      this._quantityInputs.forEach(input => {
        input.removeEventListener('input', this._updateTotalPrice);
        input.removeEventListener('change', this._updateTotalPrice);
      });
    }
  }

  _updateTotalPrice = () => {
    const root = document;
    const selectors = Array.from(
      root.querySelectorAll('quick-order-list-quantity-selector[variant-id]')
    );
    let total = 0;
    selectors.forEach(el => {
      const input =
        el.querySelector('.quantity-selector__input-wrapper input') ||
        el.querySelector('input[type="number"], input[inputmode="numeric"]');
      const qty = Number(input?.value ?? 0);
      const price = Number(input.getAttribute('data-price') || 0); // expects price in cents
      if (Number.isFinite(qty) && Number.isFinite(price)) {
        total += qty * price;
      }
    });

    // Display total price (expects an element with class .quick-order-list-total)
    const totalEls = root.querySelectorAll('.quick-order-list-total');
    totalEls.forEach(totalEl => {
      // Format price as Shopify money (assumes cents)
      totalEl.textContent = new Intl.NumberFormat(Shopify.locale, { style: "currency", currency: Shopify.currency.active }).format(total / 100);
      if (total == 0) {
        this.classList.add('hidden');
        if (totalEl.closest('price-list')) {
          totalEl.closest('price-list').classList.add('hidden');
        }
        if (totalEl.closest('.product-info__block-item')) {
          totalEl.closest('.product-info__block-item').querySelector('quick-order-list-submit').classList.add('hidden');
        }
      } else {
        this.classList.remove('hidden');
        if (totalEl.closest('price-list')) {
          totalEl.closest('price-list').classList.remove('hidden');
        }
        document.querySelectorAll('quick-order-list-submit').forEach(btn => btn.classList.remove('hidden'));
      }
    });
  };

  onClick = () => {
    const root = this.closest('quick-order-list') || document.querySelector('quick-order-list') || document;
    const selectors = Array.from(
      root.querySelectorAll('quick-order-list-quantity-selector[variant-id]')
    );
    const includeZero = this.hasAttribute('include-zero');
    const updates = {};
    for (const el of selectors) {
      const variantId = el.getAttribute('variant-id');
      const input =
        el.querySelector('.quantity-selector__input-wrapper input') ||
        el.querySelector('input[type="number"], input[inputmode="numeric"]');
      const qty = Number(input?.value ?? 0);
      if (!Number.isFinite(qty) || !variantId) continue;

      if (includeZero || qty > 0) {
        updates[variantId] = qty;
      }
    }

    if (!Object.keys(updates).length) {
      this.setAttribute('aria-disabled', 'true');
      setTimeout(() => this.removeAttribute('aria-disabled'), 150);
      return;
    }

    this.setAttribute('aria-busy', 'true');

    // Try to find the quick-order-list element and dispatch the event on it
    // This ensures the event reaches the right listeners even when DOM position changes
    const quickOrderList = root.querySelector('quick-order-list') || root.closest('quick-order-list');
    const eventTarget = quickOrderList || root || document;
    eventTarget.dispatchEvent(
      new CustomEvent('quick-order-list:update', {
        bubbles: true,
        composed: true,
        detail: { updates },
      })
    );

    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) {
      cartDrawer.show();
      cartDrawer.classList.add('open');
    }

    setTimeout(() => this.removeAttribute('aria-busy'), 800);
    // Reset all quantities to zero after the drawer is opened
    this._quantityInputs = Array.from(
      root.querySelectorAll('quick-order-list-quantity-selector[variant-id] input[type="number"], quick-order-list-quantity-selector[variant-id] input[inputmode="numeric"]')
    );
    if (cartDrawer) {
      setTimeout(() => {
        this._quantityInputs.forEach(input => {
          input.value = 0;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }, 2000);
    }
  };

  _onVariantChange = () => {
    // Unbind previous listeners and re-bind everything
    this._unbindQuantityChange();
    this._bindQuantityChange();
    this._updateTotalPrice();
  };
}

if (!customElements.get('quick-order-list-submit')) {
  customElements.define('quick-order-list-submit', QuickOrderListSubmit);
}



var AccordionDisclosureDrawer = class extends AccordionDisclosure {
  static get observedAttributes() {
    return ['disabled'];
  }

  constructor() {
    super();
    this._onSummaryClick = this._onSummaryClick.bind(this);
    this._onSummaryKeydown = this._onSummaryKeydown.bind(this);
    this._onContentChange = this._onContentChange.bind(this);
    this._onScrollDown = this._onScrollDown.bind(this);
  }

  connectedCallback() {
    super.connectedCallback && super.connectedCallback();

    // Initial update
    this._updateSelectedOption();

    // Listen for changes on radio inputs inside accordion__content
    const content = this.querySelector('.accordion__content');
    if (content) {
      content.addEventListener('change', this._onContentChange);
    }

    // Add click listener to selected-option to open accordion
    const selectedOptionEl = this.querySelector('selected-option');
    if (selectedOptionEl) {
      selectedOptionEl.addEventListener('click', () => {
        if (!this.hasAttribute('disabled')) {
          this.open && this.open();
        }
      });
    }

    // Intercept clicks/keys on the summary to prevent native toggle when disabled
    this._summary = this.querySelector('summary, .accordion__summary');
    if (this._summary) {
      this._summary.addEventListener('click', this._onSummaryClick, true);
      this._summary.addEventListener('keydown', this._onSummaryKeydown, true);
    }

    // Apply initial disabled state if present
    if (this.hasAttribute('disabled')) this._applyDisabledState();
    if (this.classList.contains('accordion--finish')) {
      document.addEventListener('dialog:body-scroll', this._onScrollDown);
    }
  }

  disconnectedCallback() {
    const content = this.querySelector('.accordion__content');
    if (content) {
      content.removeEventListener('change', this._onContentChange);
    }

    const selectedOptionEl = this.querySelector('selected-option');
    if (selectedOptionEl) {
      selectedOptionEl.removeEventListener('click', () => { });
    }

    if (this._summary) {
      this._summary.removeEventListener('click', this._onSummaryClick, true);
      this._summary.removeEventListener('keydown', this._onSummaryKeydown, true);
    }

    super.disconnectedCallback && super.disconnectedCallback();
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'disabled') {
      if (this.hasAttribute('disabled')) {
        this._applyDisabledState();
      } else {
        this._removeDisabledState();
      }
    }
  }
  _onScrollDown(e) {
    const detail = e?.detail ?? {};
    const scrollY = Number(detail.scrollY ?? detail.scrollTop ?? detail.y ?? window.pageYOffset ?? document.documentElement?.scrollTop ?? 0);
    const threshold = 5; // pixels to ignore tiny movements

    if (typeof this._lastScrollY === 'undefined') this._lastScrollY = scrollY;

    const isScrollDown = scrollY - this._lastScrollY > threshold;
    this._lastScrollY = scrollY;
    if (isScrollDown && !this.hasAttribute('disabled')) {
      super.close && super.close();
    }
  }
  _onContentChange(e) {
    if (e.target.matches('input[type="radio"]')) {
      this._updateSelectedOption();
    }
  }

  _onSummaryClick(e) {
    if (this.hasAttribute('disabled')) {
      // Prevent native details toggle
      e.preventDefault();
      e.stopImmediatePropagation();
      // Ensure it is closed and marked as not expanded
      this.removeAttribute('open');
      this.setAttribute('aria-expanded', 'false');
    }
  }

  _onSummaryKeydown(e) {
    if (!this.hasAttribute('disabled')) return;
    // Prevent Enter/Space from toggling when disabled
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      e.stopImmediatePropagation();
    }
  }

  _applyDisabledState() {
    this.setAttribute('aria-disabled', 'true');
    // ensure closed
    this.removeAttribute('open');
    this.setAttribute('aria-expanded', 'false');
    if (this._summary) {
      // Make summary unfocusable
      this._previousTabIndex = this._summary.getAttribute('tabindex');
      this._summary.setAttribute('tabindex', '-1');
      this._summary.classList.add('is-disabled');
    }
  }

  _removeDisabledState() {
    this.removeAttribute('aria-disabled');
    if (this._summary) {
      if (this._previousTabIndex !== null && this._previousTabIndex !== undefined) {
        this._summary.setAttribute('tabindex', this._previousTabIndex);
      } else {
        this._summary.removeAttribute('tabindex');
      }
      this._summary.classList.remove('is-disabled');
    }
  }

  // Prevent programmatic open/close when disabled
  open() {
    if (this.hasAttribute('disabled')) return;
    return super.open && super.open();
  }

  close() {
    if (this.hasAttribute('disabled')) return;
    return super.close && super.close();
  }

  _updateSelectedOption() {
    const content = this.querySelector('.accordion__content');
    const selectedOptionEl = this.querySelector('selected-option span');
    if (!content || !selectedOptionEl) return;

    // Find checked radio input inside accordion__content
    const checkedInput = content.querySelector('input[type="radio"]:checked');
    if (checkedInput) {
      // Find the label associated with the checked input
      const label = content.querySelector(`label[for="${checkedInput.id}"] span.heading`);
      if (label) {
        // Clone the label and replace selected-option's content
        selectedOptionEl.innerHTML = '';
        selectedOptionEl.appendChild(label.cloneNode(true));
      } else {
        // If no label, just show the value
        selectedOptionEl.textContent = checkedInput.value;
      }
      selectedOptionEl.setAttribute('aria-hidden', 'false');
      selectedOptionEl.closest('.checkbox-control').querySelector('input[type="checkbox"]').checked = true;
    } else {
      // No selection
      selectedOptionEl.textContent = 'Select finish';
      selectedOptionEl.setAttribute('aria-hidden', 'true');
    }
  }
};

if (!window.customElements.get("accordion-disclosure-drawer")) {
  window.customElements.define("accordion-disclosure-drawer", AccordionDisclosureDrawer);
}


document.addEventListener('DOMContentLoaded', () => {
  const finishSelector = document.querySelector('#finish-box');
  const finishDrawer = document.querySelector('#finish-drawer');
  const finishField = document.querySelector('#selected-finish');
  const finishFieldI = document.querySelector('#finish-boxI');


  const finishTypesSelector = document.querySelector('.different-finish-types');
  const finishTypesDrawer = document.querySelector('#finishtypes-drawer');

  if (finishTypesSelector && finishTypesDrawer) { 
    finishTypesSelector.addEventListener('click', () => {
      finishTypesDrawer.show();
      finishTypesDrawer.classList.add('open');
    });
  }

  if (finishSelector && finishDrawer) { 
    finishSelector.addEventListener('click', () => {
      if (finishField.value) { 
           finishItems.forEach(i => i.classList.remove('active'));
      document.querySelector('.fi-'+finishField.value).checked = true;
      document.querySelector('.fc-'+finishField.value).classList.add('active');
      }
      finishDrawer.show();
      finishDrawer.classList.add('open');
    });
  }

  const finishItems = document.querySelectorAll('.finish-item');

  finishItems.forEach(item => {
    item.addEventListener('click', () => {
      finishItems.forEach(i => i.classList.remove('active'));
      item.classList.add('active');
      const radio = item.querySelector('input[type="radio"]');
      if (radio) radio.checked = true;
    });
  });
const finishSubmitButtons = document.querySelectorAll('.submit-finish');

finishSubmitButtons.forEach(button => {
  button.addEventListener('click', () => {
    const selectedValue = document.querySelector('input[name="finish-selection"]:checked');

    if (selectedValue) {
      finishField.value = selectedValue.value;
      finishFieldI.textContent = selectedValue.getAttribute('data-option');

      document.querySelectorAll('.switcher-variants__group').forEach(el => {
        el.style.display = 'none';
      });

      const targetSize = document.querySelector('#size_' + selectedValue.value);
      if (targetSize) {
        targetSize.style.display = 'block';

        // Update "Attributes & quality" to match the newly selected finish
        const firstVariantRow = targetSize.querySelector('.switcher-variants__row[data-variant-id]');
        if (firstVariantRow) {
          const variantId = firstVariantRow.dataset.variantId;
          document.querySelectorAll('[data-variant-info-id]').forEach(infoEl => {
            infoEl.classList.toggle('hidden', infoEl.dataset.variantInfoId !== variantId);
          });
        }
      }

      finishDrawer.hide();
      finishDrawer.classList.remove('open');
    }
  });
});



  const urlParams = new URLSearchParams(window.location.search);
  if (!urlParams.has('variant')) {
    document.querySelectorAll('.variant-picker__option-values input:checked').forEach(d => {
      d.checked = false;
    });
  }
});


class VariantInformation extends HTMLElement {
  constructor() {
    super();
    this._onVariantChange = this._onVariantChange.bind(this);
  }

  connectedCallback() {
    document.addEventListener('variant:change', this._onVariantChange);
  }

  disconnectedCallback() {
    document.removeEventListener('variant:change', this._onVariantChange);
  }

  _onVariantChange(e) {
    // Support multiple event shapes: { detail: { variant: { id } } }, { detail: { id } }, { detail: id }
    const id = e.detail.variant.id
    if (!id) return;
    this._updateVisibility(id);
  }

  _updateVisibility(currentId) {
    document.querySelectorAll('[data-variant-info-id]').forEach((el) => {
      if (Number(el.dataset.variantInfoId) === currentId) {
        el.classList.remove('hidden');
      } else {
        el.classList.add('hidden');
      }
    });
  }
}

if (!customElements.get('variant-information')) {
  customElements.define('variant-information', VariantInformation);
}

// js/sections/header-bar.js
import { animate as animate11 } from "vendor";
var HeaderBarCarousel = class extends EffectCarousel {
  createOnChangeAnimationControls(fromSlide, toSlide) { 
    return {
      leaveControls: () => animate11(fromSlide, { opacity: [1, 0], transform: ["translateY(0)", "translateY(-10px)"] }, { duration: 0.25, easing: [0.55, 0.055, 0.675, 0.19] }),
      enterControls: () => animate11(toSlide, { opacity: [0, 1], transform: ["translateY(10px)", "translateY(0px)"] }, { duration: 0.4, easing: [0.215, 0.61, 0.355, 1] })
    };
  }
};
if (!window.customElements.get("header-bar-carousel")) {
  window.customElements.define("header-bar-carousel", HeaderBarCarousel);
}

//create drawer-color-catalog web component that loads more color entrys when scrolling to the bottom from pagination url
class DrawerColorCatalog extends HTMLElement {
  constructor() {
    super();
    this._onScroll = this._onScroll.bind(this);
    this._isLoading = false;
    this._loadedPages = new Set();
  }
  connectedCallback() {
    document.addEventListener('dialog:body-scroll', this._onScroll);
  }
  disconnectedCallback() {
    document.removeEventListener('dialog:body-scroll', this._onScroll);
  }
  _onScroll() {
    if (this._isLoading) return;
    this._loadMoreColors();
  }
  async _loadMoreColors() {
    const nextPageLink = this.querySelector('nav a');
    const nextPageUrl = nextPageLink?.href;
    if (!nextPageUrl || this._loadedPages.has(nextPageUrl)) return;

    this._isLoading = true;
    try {
      const response = await fetch(nextPageUrl);
      const data = await response.text();
      this._appendColors(data);
      this._loadedPages.add(nextPageUrl);
      // Mark the nav link as loaded
      nextPageLink.setAttribute('data-loaded', 'true');
    } catch (error) {
      console.error('Error loading more colors:', error);
    } finally {
      this._isLoading = false;
    }
  }
  _appendColors(data) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(data, 'text/html');
    const newColors = doc.querySelectorAll('.color_entry.color_entry--drawer');
    const colorContainer = this.querySelector('.color-entries');
    const lastEntry = colorContainer.lastElementChild;
    newColors.forEach(color => {
      color.setAttribute('data-loaded', 'true');
      if (lastEntry) {
      colorContainer.insertBefore(color, lastEntry.nextSibling);
      } else {
      colorContainer.appendChild(color);
      }
    });
  }
}

customElements.define('drawer-color-catalog', DrawerColorCatalog);





// js/components/ux-drawer.js
// DS drawer shell (milestone 2, GitHub #42): the theme's Drawer (focus trap, Escape, scrim, focus return), sliding
// in from the right on desktop and rising from the bottom on mobile (DS: "a bottom-to-top full-height drawer on
// mobile"). No slide when the visitor prefers reduced motion. Markup: snippets/ux-drawer.liquid.
// Focus return across a chain of drawers (Add to basket → Finish → Size; Add primer → Added to basket): the theme's
// focus trap returns focus to whatever was focused when the drawer opened, and the second drawer of a chain opens
// while focus is still inside the first, now hidden. Each drawer remembers the page control that started the chain and
// puts focus back on it when the last drawer closes with focus lost.
var uxDrawerOpener = null;
var UxDrawer = class extends Drawer {
  connectedCallback() {
    super.connectedCallback();
    const signal = this.abortController.signal; // re-wired each time the drawer moves to <body> and back
    this.addEventListener("dialog:before-show", () => {
      const active = document.activeElement;
      const lost = !active || active === document.body || Boolean(active.closest?.("ux-drawer"));
      this._uxOpener = lost ? uxDrawerOpener : active;
      uxDrawerOpener = this._uxOpener;
    }, { signal });
    this.addEventListener("dialog:after-hide", () => {
      const active = document.activeElement;
      const lost = !active || active === document.body || Boolean(active.closest?.("ux-drawer:not([open])"));
      if (lost && !document.querySelector("ux-drawer[open]") && this._uxOpener?.isConnected) this._uxOpener.focus({ preventScroll: true });
    }, { signal });
  }
  get shadowDomTemplate() {
    return this.getAttribute("template") || "ux-drawer-template";
  }
  /* phones: up from the bottom, unless the drawer is .ux-drawer--right (Matt, 22 Oct: the palette builder's filter,
     the product page's and Buy's Finish and Size) */
  get uxFromBottom() {
    return window.matchMedia("(max-width: 699px)").matches && !this.classList.contains("ux-drawer--right");
  }
  uxAnimate(entering) {
    const overlay = this.getShadowPartByName("overlay");
    const content = this.getShadowPartByName("content");
    content.style.marginInlineStart = "auto";
    const still = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const away = this.uxFromBottom ? "translateY(100%)" : "translateX(calc(var(--transform-logical-flip, 1) * 100%))";
    const home = this.uxFromBottom ? "translateY(0)" : "translateX(0)";
    const options = { duration: still ? 0 : 300, easing: "cubic-bezier(0.645, 0.045, 0.355, 1)", fill: "forwards" };
    const animations = [
      overlay.animate({ opacity: entering ? [0, 1] : [1, 0] }, options),
      content.animate({ transform: entering ? [away, home] : [home, away] }, options)
    ];
    return {
      finished: Promise.all(animations.map((animation) => animation.finished)),
      finish: () => animations.forEach((animation) => animation.finish())
    };
  }
  createEnterAnimationControls() {
    return this.uxAnimate(true);
  }
  createLeaveAnimationControls() {
    return this.uxAnimate(false);
  }
};
if (!window.customElements.get("ux-drawer")) {
  window.customElements.define("ux-drawer", UxDrawer);
}

// js/components/ux-qty-stepper.js
// DS Qty stepper (milestone 2, GitHub #43): minus / count / plus. A button at its limit is aria-disabled (dimmed, still
// focusable) rather than disabled, so focus stays put when the count reaches 0. The count is announced through its
// aria-live wrapper. Fires "ux-qty:change" { variantId, quantity } and marks the parent .ux-size-row selected.
// Markup: snippets/ux-qty-stepper.liquid.
var UxQtyStepper = class extends HTMLElement {
  constructor() {
    super();
    this._onClick = this._onClick.bind(this);
  }
  connectedCallback() {
    this.addEventListener("click", this._onClick);
  }
  disconnectedCallback() {
    this.removeEventListener("click", this._onClick);
  }
  get max() {
    return this.hasAttribute("data-max") ? Number(this.dataset.max) : Infinity;
  }
  get value() {
    return Number(this.querySelector("[data-count]").textContent) || 0;
  }
  set value(quantity) {
    const next = Math.min(Math.max(0, Math.round(Number(quantity) || 0)), this.max);
    if (next === this.value) return;
    this.sync(next);
    this.dispatchEvent(new CustomEvent("ux-qty:change", {
      bubbles: true,
      detail: { variantId: Number(this.dataset.variantId), quantity: next }
    }));
  }
  // Show a quantity without firing ux-qty:change (e.g. the basket re-syncing from the server).
  sync(quantity) {
    this.querySelector("[data-count]").textContent = String(quantity);
    this.querySelectorAll("[data-step]").forEach((button) => {
      const atLimit = Number(button.dataset.step) < 0 ? quantity <= 0 : quantity >= this.max;
      atLimit ? button.setAttribute("aria-disabled", "true") : button.removeAttribute("aria-disabled");
    });
    this.closest(".ux-size-row")?.classList.toggle("is-selected", quantity > 0);
  }
  _onClick(event) {
    const button = event.target.closest("[data-step]");
    if (!button || button.getAttribute("aria-disabled") === "true") return;
    this.value = this.value + Number(button.dataset.step);
  }
};
if (!window.customElements.get("ux-qty-stepper")) {
  window.customElements.define("ux-qty-stepper", UxQtyStepper);
}

// js/components/ux-basket-line.js
// DS Basket line (milestone 2, GitHub #45). The stepper (debounced, so quick taps send one request) and Remove go
// through /cart/change.js the way the theme's own line items do: bundle the sections that asked for it
// (cart:prepare-bundled-sections), then dispatch cart:change so the header count, basket bar and cart drawer follow.
// Every line re-syncs from any cart:change: new quantity, line price and "each", or it leaves the page (focus moves
// to the next line). Markup: snippets/ux-basket-line.liquid.
var uxFormatMoney = (cents) => {
  const pattern = window.themeVariables?.settings?.moneyFormat || "£{{amount}}";
  const value = (cents / 100).toFixed(2);
  const withCommas = value.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return pattern
    .replace(/\{\{\s*amount_no_decimals\s*\}\}/, withCommas.split(".")[0])
    .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, value.replace(".", ","))
    .replace(/\{\{\s*amount\s*\}\}/, withCommas);
};
var UxBasketLine = class extends HTMLElement {
  constructor() {
    super();
    this._onQtyChange = this._onQtyChange.bind(this);
    this._onClick = this._onClick.bind(this);
    this._onCartChange = this._onCartChange.bind(this);
  }
  connectedCallback() {
    this.addEventListener("ux-qty:change", this._onQtyChange);
    this.addEventListener("click", this._onClick);
    document.addEventListener("cart:change", this._onCartChange);
  }
  disconnectedCallback() {
    clearTimeout(this._timer);
    this.removeEventListener("ux-qty:change", this._onQtyChange);
    this.removeEventListener("click", this._onClick);
    document.removeEventListener("cart:change", this._onCartChange);
  }
  _onQtyChange(event) {
    event.stopPropagation(); // a basket line's quantity is not a size choice
    clearTimeout(this._timer);
    this._timer = setTimeout(() => this.change(event.detail.quantity), 350);
  }
  _onClick(event) {
    if (event.target.closest("[data-ux-basket-remove]")) {
      clearTimeout(this._timer);
      this.change(0);
    }
  }
  async change(quantity) {
    const sections = [];
    document.documentElement.dispatchEvent(new CustomEvent("cart:prepare-bundled-sections", { bubbles: true, detail: { sections } }));
    document.documentElement.dispatchEvent(new CustomEvent("theme:loading:start", { bubbles: true }));
    this.setAttribute("aria-busy", "true");
    try {
      const response = await fetch(`${Shopify.routes.root}cart/change.js`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: this.dataset.lineKey, quantity, sections: sections.join(",") })
      });
      const cart = await response.json();
      if (!response.ok) throw new Error(cart.description || cart.message || response.statusText);
      this._showError("");
      document.documentElement.dispatchEvent(new CustomEvent("cart:change", {
        bubbles: true,
        detail: { baseEvent: "line-item:change", cart }
      }));
    } catch (error) {
      this._showError(error.message);
      const cart = await (await fetch(`${Shopify.routes.root}cart.js`)).json();
      this._sync(cart);
    } finally {
      this.removeAttribute("aria-busy");
      document.documentElement.dispatchEvent(new CustomEvent("theme:loading:end", { bubbles: true }));
    }
  }
  _onCartChange(event) {
    const cart = event.detail?.cart;
    if (cart && Array.isArray(cart.items)) this._sync(cart);
  }
  _sync(cart) {
    const item = cart.items.find((lineItem) => lineItem.key === this.dataset.lineKey);
    if (!item) {
      this._leave();
      return;
    }
    this.querySelector("ux-qty-stepper")?.sync(item.quantity);
    /* a consultancy code limited to products (CC-…, #334), and a Professional's trade discount (automatic, #308), are
       their own rows in the summary: the line shows its price before them */
    const trade = this.hasAttribute("data-trade-back");
    const back = (item.line_level_discount_allocations || []).filter((allocation) => {
      const application = allocation.discount_application || {};
      return /^CC-/i.test(application.title || "") || (trade && application.type === "automatic");
    }).reduce((sum, allocation) => sum + (Number(allocation.amount) || 0), 0);
    const total = item.final_line_price + back;
    this.querySelector("[data-ux-line-total]").textContent = uxFormatMoney(total);
    const each = this.querySelector("[data-ux-line-each]");
    each.textContent = each.dataset.template.replace("[price]", uxFormatMoney(back ? Math.round(total / item.quantity) : item.final_price));
    each.hidden = item.quantity <= 1;
  }
  _leave() {
    if (this.contains(document.activeElement)) {
      const siblings = [...this.parentElement.querySelectorAll(":scope > ux-basket-line")];
      const index = siblings.indexOf(this);
      const target = siblings[index + 1] || siblings[index - 1];
      target?.querySelector(".ux-basket-line__name")?.focus();
    }
    this.remove();
  }
  _showError(message) {
    const error = this.querySelector(".ux-basket-line__error");
    if (!error) return;
    error.textContent = message;
    error.hidden = !message;
  }
};
if (!window.customElements.get("ux-basket-line")) {
  window.customElements.define("ux-basket-line", UxBasketLine);
}

// js/components/ux-basket.js
// DS basket page (milestone 4, GitHub #90). Bundles its own section into every cart change and, on cart:change,
// redraws the count, the free-delivery line, the group heads and the order summary from it. The lines keep themselves
// in step and leave when removed (#45); a group with nothing left goes with them. A line the page doesn't have yet (an
// add from the upsell) brings in the server's line list. An empty basket reloads into the empty state. Discount codes
// go through the live /discount/CODE permalink and come back here.
var UxBasket = class extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.dataset.sectionId;
    this._onPrepare = (event) => event.detail.sections.push(this.sectionId);
    this._onCartChange = (event) => this._refresh(event.detail?.cart);
    this._onClick = (event) => {
      if (event.target.closest("[data-ux-discount-apply]")) this._applyDiscount();
    };
    this._onKeydown = (event) => {
      if (event.key === "Enter" && event.target.matches("[data-ux-discount-input]")) {
        event.preventDefault();
        this._applyDiscount();
      }
    };
    document.addEventListener("cart:prepare-bundled-sections", this._onPrepare);
    document.addEventListener("cart:change", this._onCartChange);
    this.addEventListener("click", this._onClick);
    this.addEventListener("keydown", this._onKeydown);
  }
  disconnectedCallback() {
    document.removeEventListener("cart:prepare-bundled-sections", this._onPrepare);
    document.removeEventListener("cart:change", this._onCartChange);
    this.removeEventListener("click", this._onClick);
    this.removeEventListener("keydown", this._onKeydown);
  }
  async _refresh(cart) {
    if (cart && cart.item_count === 0) {
      window.location.reload();
      return;
    }
    let html = cart?.sections?.[this.sectionId];
    if (!html) {
      try {
        html = await (await fetch(`${window.Shopify.routes.root}cart?section_id=${this.sectionId}`)).text();
      } catch (error) {
        return;
      }
    }
    const fresh = new DOMParser().parseFromString(html, "text/html").querySelector("ux-basket");
    if (!fresh) return;
    const keys = (root) => [...root.querySelectorAll("ux-basket-line")].map((line) => line.dataset.lineKey);
    const mine = new Set(keys(this));
    if (keys(fresh).some((key) => !mine.has(key))) {
      this.querySelector("[data-ux-basket-lines]")?.replaceWith(fresh.querySelector("[data-ux-basket-lines]"));
    } else {
      this.querySelectorAll("[data-ux-basket-group]").forEach((group) => {
        const next = fresh.querySelector(`[data-ux-basket-group="${group.dataset.uxBasketGroup}"]`);
        if (next) {
          group.querySelector(".ux-basket__group-head").replaceWith(next.querySelector(".ux-basket__group-head"));
          return;
        }
        const hadFocus = group.contains(document.activeElement);
        group.remove();
        if (hadFocus) this.querySelector(".ux-basket-line__name, .ux-basket-summary__title")?.focus();
      });
    }
    ["[data-ux-basket-page-count]", "[data-ux-basket-head-free]", "[data-ux-basket-consult]", "[data-ux-basket-upsell]"].forEach((selector) => {
      const next = fresh.querySelector(selector);
      if (next) this.querySelector(selector)?.replaceWith(next);
    });
    const summary = this.querySelector("[data-ux-basket-summary]");
    const nextSummary = fresh.querySelector("[data-ux-basket-summary]");
    if (summary && nextSummary) {
      const wasOpen = summary.querySelector(".ux-basket-discount")?.open;
      summary.replaceChildren(...nextSummary.childNodes);
      const discount = summary.querySelector(".ux-basket-discount");
      if (discount && wasOpen) discount.open = true;
    }
  }
  _applyDiscount() {
    const input = this.querySelector("[data-ux-discount-input]");
    const message = this.querySelector("[data-ux-discount-message]");
    const button = this.querySelector("[data-ux-discount-apply]");
    const code = input?.value.trim();
    if (!code) {
      if (message) message.textContent = message.dataset.empty;
      input?.focus();
      return;
    }
    button.textContent = button.dataset.labelBusy;
    button.disabled = true;
    const back = window.location.pathname + window.location.search;
    window.location.href = `${window.Shopify.routes.root}discount/${encodeURIComponent(code)}?redirect=${encodeURIComponent(back)}`;
  }
};
if (!window.customElements.get("ux-basket")) {
  window.customElements.define("ux-basket", UxBasket);
}

// js/components/ux-basket-drawer.js
// Basket drawer (milestone 4, GitHub #91): the theme's <cart-drawer> wearing the DS shell (sections/cart-drawer.liquid).
// It moves like the other DS drawers (a sheet rising from the bottom on mobile, no slide with reduced motion). It
// re-renders everything inside it on each cart change, so focus goes back to the same control of the same line, or
// to the next line's name when that line went.
window.customElements.whenDefined("cart-drawer").then(() => {
  const CartDrawerClass = window.customElements.get("cart-drawer");
  ["uxAnimate", "createEnterAnimationControls", "createLeaveAnimationControls"].forEach((name) => {
    CartDrawerClass.prototype[name] = UxDrawer.prototype[name];
  });
  Object.defineProperty(CartDrawerClass.prototype, "uxFromBottom", Object.getOwnPropertyDescriptor(UxDrawer.prototype, "uxFromBottom"));
});
(() => {
  let memo = null;
  window.addEventListener("cart:change", () => {
    const drawer = document.getElementById("cart-drawer");
    const active = document.activeElement;
    memo = null;
    if (!drawer?.open || !drawer.contains(active)) return;
    const line = active.closest("ux-basket-line");
    if (!line) return;
    memo = {
      key: line.dataset.lineKey,
      step: active.dataset.step,
      edit: active.hasAttribute("data-ux-basket-edit"),
      index: [...drawer.querySelectorAll("ux-basket-line")].indexOf(line)
    };
  }, true); // capture on window: before <cart-drawer> swaps its content
  document.addEventListener("cart-drawer:refreshed", (event) => {
    if (!memo) return;
    const drawer = event.target;
    const line = [...drawer.querySelectorAll("ux-basket-line")].find((candidate) => candidate.dataset.lineKey === memo.key);
    let target = null;
    if (line && memo.step) target = line.querySelector(`[data-step="${memo.step}"]`);
    if (line && memo.edit) target = line.querySelector("[data-ux-basket-edit]");
    if (!target) {
      const lines = [...drawer.querySelectorAll("ux-basket-line")];
      target = (lines[memo.index] || lines[memo.index - 1])?.querySelector(".ux-basket-line__name") || drawer;
    }
    target.focus({ preventScroll: true });
    memo = null;
  });
})();

// js/components/ux-basket-editor.js
// Basket Edit drawer (milestone 4, GitHub #92). A Basket line's Edit opens it: it fetches the edit panel for that
// line's product (sections/ux-basket-edit.liquid, rendered in the product's own context with ?variant=<the line's>)
// and shows the line's finish with its size at its quantity. Change finish swaps to the finish step (the PDP finish
// cards and filters); choosing one carries the sizes over where the new finish has them, as the primer's finish
// toggles do. Update basket replaces the line: other variants are added with the line's properties, the line itself
// is set to its new quantity or removed, then cart:change carries the bundled sections. From the basket drawer it
// opens in the drawer's place, with Back to basket in the header.
var UxBasketEditor = class extends HTMLElement {
  connectedCallback() {
    if (this._wired) return; // the drawer moves itself to <body> and back: wire once
    this._wired = true;
    this.drawer = this.closest("ux-drawer");
    this.footerSlot = this.drawer.querySelector("[data-ux-edit-footer-slot]");
    this.titleEl = this.drawer.querySelector(".ux-drawer__title");
    this.labelledBy = this.drawer.getAttribute("aria-labelledby");
    this.defaultTitle = this.titleEl.textContent;
    this.addEventListener("click", (event) => this._onClick(event));
    this.addEventListener("change", (event) => this._onChange(event));
    this.addEventListener("ux-qty:change", (event) => {
      event.stopPropagation();
      this._render();
    });
    this.footerSlot.addEventListener("click", (event) => this._onFooterClick(event));
    this.titleEl.addEventListener("click", (event) => {
      if (event.target.closest("[data-ux-edit-back]")) this._backToBasket();
    });
    document.addEventListener("click", (event) => {
      const edit = event.target.closest("[data-ux-basket-edit]");
      const line = edit?.closest("ux-basket-line");
      if (line) this.open(line);
    });
  }
  get group() {
    return [...this.querySelectorAll(".ux-basket-edit__group")].find((group) => group.dataset.finish === this.finish);
  }
  // The chosen rows of the finish on show: [{ id, quantity, size, price }]
  get picks() {
    return [...(this.group?.querySelectorAll("ux-qty-stepper") || [])]
      .filter((stepper) => stepper.value > 0)
      .map((stepper) => {
        const row = stepper.closest(".ux-size-row");
        return { id: Number(stepper.dataset.variantId), quantity: stepper.value, size: row.dataset.size, price: Number(row.dataset.price) };
      });
  }
  open(lineElement) {
    let properties = {};
    try {
      properties = JSON.parse(lineElement.dataset.properties || "{}") || {};
    } catch (error) {
      properties = {};
    }
    this._start({
      key: lineElement.dataset.lineKey,
      variantId: Number(lineElement.dataset.variantId),
      quantity: Number(lineElement.dataset.quantity) || 1,
      handle: lineElement.dataset.handle,
      price: Number(lineElement.dataset.linePrice) || 0,
      properties
    }, Boolean(lineElement.closest("cart-drawer")), false);
  }
  // Add mode (#93): a suggested product with more than one variant opens here to choose its size or pack.
  openAdd(button) {
    this._start({
      key: null,
      variantId: Number(button.dataset.variantId),
      quantity: 1,
      handle: button.dataset.handle,
      price: 0,
      properties: {}
    }, Boolean(button.closest("cart-drawer")), true);
  }
  async _start(line, fromDrawer, addMode) {
    this.line = line;
    this.fromDrawer = fromDrawer;
    this.addMode = addMode;
    this.panel = null;
    this._message(this.dataset.loading);
    this._header();
    const cartDrawer = document.getElementById("cart-drawer");
    if (this.fromDrawer && cartDrawer?.open) {
      cartDrawer.addEventListener("dialog:after-hide", () => this.drawer.show(), { once: true });
      cartDrawer.hide();
    } else {
      this.drawer.show();
    }
    try {
      const root = window.Shopify.routes.root;
      const [panelResponse, cart] = await Promise.all([
        fetch(`${root}products/${encodeURIComponent(this.line.handle)}?variant=${this.line.variantId}&sections=ux-basket-edit`),
        fetch(`${root}cart.js`).then((response) => response.json())
      ]);
      const html = (await panelResponse.json())["ux-basket-edit"];
      const panel = new DOMParser().parseFromString(html || "", "text/html").querySelector(".ux-basket-edit");
      if (!panelResponse.ok || !panel) throw new Error(this.dataset.error);
      this.basketTotal = cart.total_price;
      this._mount(panel);
    } catch (error) {
      this._message(this.dataset.error);
    }
  }
  _message(text) {
    const message = document.createElement("p");
    message.className = "ux-basket-editor__message";
    message.textContent = text;
    this.replaceChildren(message);
    this.footerSlot.replaceChildren();
  }
  _header(panel) {
    if (this.fromDrawer) {
      const back = document.createElement("button");
      back.type = "button";
      back.className = "ux-basket-editor__back";
      back.dataset.uxEditBack = "";
      back.innerHTML = '<svg aria-hidden="true" focusable="false" width="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-linecap="square" stroke-linejoin="bevel"><path d="M15 5l-7 7 7 7"/></svg>';
      back.append(document.createTextNode(this.dataset.back));
      this.titleEl.replaceChildren(back);
    } else {
      this.titleEl.textContent = (this.addMode ? panel?.dataset.titleAdd : panel?.dataset.title) || this.defaultTitle;
    }
  }
  _mount(panel) {
    const template = panel.querySelector("template[data-ux-edit-footer]");
    const footer = template ? template.content.cloneNode(true) : null;
    template?.remove();
    this.replaceChildren(panel);
    this.footerSlot.replaceChildren(...(footer ? [footer] : []));
    this.panel = panel;
    this.kind = panel.dataset.kind;
    this.finish = panel.dataset.finish;
    this._header(panel);
    const heading = panel.querySelector("[data-ux-edit-heading]");
    if (heading) {
      heading.hidden = !this.fromDrawer;
      heading.id = "ux-basket-edit-heading";
    }
    if (this.fromDrawer && heading) this.drawer.setAttribute("aria-labelledby", heading.id);
    else this.drawer.setAttribute("aria-labelledby", this.labelledBy);
    const stepper = panel.querySelector(`ux-qty-stepper[data-variant-id="${this.line.variantId}"]`);
    if (stepper) stepper.sync(Math.min(this.line.quantity, stepper.max));
    this._step("sizes");
    this._render();
  }
  _step(name) {
    this.querySelectorAll("[data-ux-edit-step]").forEach((step) => (step.hidden = step.dataset.uxEditStep !== name));
    this.footerSlot.querySelectorAll("[data-ux-edit-footer-step]").forEach((step) => (step.hidden = step.dataset.uxEditFooterStep !== name));
    if (name === "calc") {
      const calc = this.querySelector("ux-paint-calc");
      calc?._describe();
      calc?.calculate();
      calc?.querySelector("input")?.focus();
    }
    if (name === "finish") {
      const current = [...this.querySelectorAll("[name=ux-edit-finish]")].find((input) => input.value === this.finish);
      if (current) current.checked = true;
      this._updateChoose();
      (current || this.querySelector("[name=ux-edit-finish]:not(:disabled)"))?.focus();
    }
  }
  _render() {
    if (!this.panel) return;
    const picks = this.picks;
    const total = picks.reduce((sum, pick) => sum + pick.price * pick.quantity, 0);
    const items = this.footerSlot.querySelector("[data-ux-edit-items]");
    if (items) {
      const summary = picks.map((pick) => `${pick.quantity} × ${pick.size}`).join(", ");
      items.textContent = summary ? (this.kind === "single" ? summary : `${summary} · ${this.finish}`) : items.dataset.empty;
    }
    const totalElement = this.footerSlot.querySelector("[data-ux-edit-total]");
    if (totalElement) totalElement.textContent = uxFormatMoney(total);
    const note = this.footerSlot.querySelector("[data-ux-edit-note]");
    if (note) {
      const threshold = Number(this.dataset.threshold) || 0;
      const after = (this.basketTotal ?? 0) - this.line.price + total;
      const stillFree = threshold > 0 && (this.basketTotal ?? 0) >= threshold && after >= threshold;
      note.textContent = this.addMode ? (stillFree ? note.dataset.stillFree : "") : stillFree ? `${note.dataset.updates} ${note.dataset.stillFree}` : note.dataset.updates;
    }
    const update = this.footerSlot.querySelector("[data-ux-edit-update]");
    if (update) {
      update.disabled = picks.length === 0;
      update.textContent = picks.length ? (this.addMode ? update.dataset.labelAdd : update.dataset.labelSome) : update.dataset.labelNone;
    }
  }
  _onClick(event) {
    if (event.target.closest("[data-ux-edit-change-finish]")) this._step("finish");
    else if (event.target.closest("[data-ux-open-calc]")) this._step("calc");
  }
  // Set the finish's rows to the calculator's recommendation ([{ id, quantity }]) and redraw (#149)
  applyPicks(picks) {
    const steppers = [...(this.group?.querySelectorAll("ux-qty-stepper") || [])];
    steppers.forEach((stepper) => stepper.sync(0));
    picks.forEach((pick) => steppers.find((stepper) => Number(stepper.dataset.variantId) === pick.id)?.sync(pick.quantity));
    this._render();
  }
  _onChange(event) {
    const target = event.target;
    if (target.name === "ux-edit-primer-finish") this._switchFinish(target.value);
    else if (target.name === "ux-edit-finish") this._updateChoose();
    else if (target.matches("[data-ux-finish-filter]")) this._filter();
  }
  _onFooterClick(event) {
    if (event.target.closest("[data-ux-calc-apply]")) this.querySelector("ux-paint-calc")?.choose();
    else if (event.target.closest("[data-ux-edit-back-sizes]")) {
      this._step("sizes");
      this.querySelector("[data-ux-open-calc]")?.focus();
    } else if (event.target.closest("[data-ux-edit-update]")) this._update();
    else if (event.target.closest("[data-ux-edit-choose]")) {
      const picked = this.querySelector("[name=ux-edit-finish]:checked")?.value;
      if (!picked) return;
      this._switchFinish(picked);
      this._step("sizes");
      this.querySelector("[data-ux-edit-change-finish]")?.focus();
    }
  }
  // Show another finish's sizes, carrying the chosen sizes (and quantities) over where it has them.
  _carry(finish) {
    const target = [...this.querySelectorAll(".ux-basket-edit__group")].find((group) => group.dataset.finish === finish);
    if (!target) return [];
    const rows = [...target.querySelectorAll(".ux-size-row")];
    return this.picks
      .map((pick) => ({ pick, row: rows.find((row) => row.dataset.size === pick.size) }))
      .filter(({ row }) => row?.querySelector("ux-qty-stepper"))
      .map(({ pick, row }) => ({ row, quantity: pick.quantity, price: Number(row.dataset.price) }));
  }
  _switchFinish(finish) {
    if (finish === this.finish) return;
    const carried = this._carry(finish);
    const groups = [...this.querySelectorAll(".ux-basket-edit__group")];
    const target = groups.find((group) => group.dataset.finish === finish);
    if (!target) return;
    target.querySelectorAll("ux-qty-stepper").forEach((stepper) => stepper.sync(0));
    carried.forEach(({ row, quantity }) => {
      const stepper = row.querySelector("ux-qty-stepper");
      stepper.sync(Math.min(quantity, stepper.max));
    });
    groups.forEach((group) => (group.hidden = group !== target));
    this.finish = finish;
    const name = this.querySelector("[data-ux-edit-finish-name]");
    if (name) name.textContent = finish;
    this._render();
  }
  _updateChoose() {
    const button = this.footerSlot.querySelector("[data-ux-edit-choose]");
    if (!button) return;
    const picked = this.querySelector("[name=ux-edit-finish]:checked")?.value;
    button.disabled = !picked;
    button.textContent = picked ? button.dataset.labelSome.replace("[finish]", picked) : button.dataset.labelNone;
    const carry = this.footerSlot.querySelector("[data-ux-edit-carry]");
    if (!carry) return;
    const picks = this.picks;
    const carried = picked && picked !== this.finish ? this._carry(picked) : [];
    if (!carried.length) {
      carry.textContent = "";
      return;
    }
    const items = carried.map(({ row, quantity }) => `${quantity} × ${row.dataset.size}`).join(", ");
    const price = carried.reduce((sum, { quantity, price: each }) => sum + quantity * each, 0);
    const was = picks.reduce((sum, pick) => sum + pick.quantity * pick.price, 0);
    carry.textContent = carry.dataset.template.replace("[items]", items).replace("[price]", uxFormatMoney(price)).replace("[was]", uxFormatMoney(was));
  }
  _filter() {
    const wanted = {};
    this.querySelectorAll("[data-ux-finish-filter]").forEach((select) => (wanted[select.dataset.uxFinishFilter] = select.value));
    let shown = 0;
    this.querySelectorAll(".ux-finish-option").forEach((card) => {
      const match = Object.entries(wanted).every(([key, value]) => !value || card.dataset[key].includes(`|${value}|`));
      card.hidden = !match;
      if (match) shown += 1;
    });
    const empty = this.querySelector("[data-ux-finish-empty]");
    if (empty) empty.hidden = shown > 0;
    const status = this.querySelector("[data-ux-finish-count]");
    if (status) status.textContent = (window.themeVariables?.strings?.uxFinishesShown || "[count] finishes shown").replace("[count]", shown);
  }
  async _update() {
    const button = this.footerSlot.querySelector("[data-ux-edit-update]");
    const picks = this.picks;
    if (!picks.length || button.getAttribute("aria-busy") === "true") return;
    const error = this.footerSlot.querySelector("[data-ux-edit-error]");
    error.hidden = true;
    button.setAttribute("aria-busy", "true");
    const root = window.Shopify.routes.root;
    const properties = Object.keys(this.line.properties).length ? this.line.properties : undefined;
    try {
      if (this.addMode) {
        const sections = [];
        document.documentElement.dispatchEvent(new CustomEvent("cart:prepare-bundled-sections", { bubbles: true, detail: { sections } }));
        const response = await fetch(`${root}cart/add.js`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ items: picks.map(({ id, quantity }) => ({ id, quantity })), sections: sections.join(",") })
        });
        const added = await response.json();
        if (!response.ok) throw new Error(added.description || added.message || response.statusText);
        const cart = await (await fetch(`${root}cart.js`)).json();
        cart.sections = added.sections;
        document.documentElement.dispatchEvent(new CustomEvent("cart:change", { bubbles: true, detail: { baseEvent: "ux-basket:upsell", cart } }));
        this._close(picks[0].id);
        return;
      }
      const same = picks.find((pick) => pick.id === this.line.variantId);
      const others = picks.filter((pick) => pick.id !== this.line.variantId);
      if (others.length) {
        const added = await fetch(`${root}cart/add.js`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ items: others.map(({ id, quantity }) => ({ id, quantity, properties })) })
        });
        const addedJson = await added.json();
        if (!added.ok) throw new Error(addedJson.description || addedJson.message || added.statusText);
      }
      const sections = [];
      document.documentElement.dispatchEvent(new CustomEvent("cart:prepare-bundled-sections", { bubbles: true, detail: { sections } }));
      const response = await fetch(`${root}cart/change.js`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: this.line.key, quantity: same ? same.quantity : 0, sections: sections.join(",") })
      });
      const cart = await response.json();
      if (!response.ok) throw new Error(cart.description || cart.message || response.statusText);
      const focusVariant = (same || others[0]).id;
      document.documentElement.dispatchEvent(new CustomEvent("cart:change", { bubbles: true, detail: { baseEvent: "ux-basket:edit", cart } }));
      this._close(focusVariant);
    } catch (failure) {
      error.textContent = failure.message || this.dataset.error;
      error.hidden = false;
    } finally {
      button.removeAttribute("aria-busy");
    }
  }
  _close(focusVariant) {
    if (this.fromDrawer) {
      this._backToBasket();
      return;
    }
    this.drawer.addEventListener("dialog:after-hide", () => {
      // the page redraws its lines; land on the edited line's Edit once it's there
      setTimeout(() => document.querySelector(`ux-basket[data-section-id] ux-basket-line[data-variant-id="${focusVariant}"] [data-ux-basket-edit]`)?.focus(), 800);
    }, { once: true });
    this.drawer.hide();
  }
  _backToBasket() {
    const cartDrawer = document.getElementById("cart-drawer");
    if (!cartDrawer) {
      this.drawer.hide();
      return;
    }
    this.drawer.addEventListener("dialog:after-hide", () => cartDrawer.show(), { once: true });
    this.drawer.hide();
  }
};
if (!window.customElements.get("ux-basket-editor")) {
  window.customElements.define("ux-basket-editor", UxBasketEditor);
}

// js/components/ux-basket-upsell.js
// "For the perfect finish" (milestone 4, GitHub #93): a one-variant suggestion adds in one tap (the page and the
// basket drawer redraw from the bundled sections, and the card goes because it's now in the basket); one with sizes or
// packs opens the Edit drawer in add mode to choose.
document.addEventListener("click", async (event) => {
  const button = event.target.closest("[data-ux-upsell-add]");
  if (!button) return;
  if (button.dataset.handle) {
    document.querySelector("ux-basket-editor")?.openAdd(button);
    return;
  }
  if (button.getAttribute("aria-busy") === "true") return;
  button.setAttribute("aria-busy", "true");
  const root = window.Shopify.routes.root;
  try {
    const sections = [];
    document.documentElement.dispatchEvent(new CustomEvent("cart:prepare-bundled-sections", { bubbles: true, detail: { sections } }));
    const response = await fetch(`${root}cart/add.js`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ items: [{ id: Number(button.dataset.variantId), quantity: 1 }], sections: sections.join(",") })
    });
    const added = await response.json();
    if (!response.ok) throw new Error(added.description || added.message || response.statusText);
    const cart = await (await fetch(`${root}cart.js`)).json();
    cart.sections = added.sections;
    document.documentElement.dispatchEvent(new CustomEvent("cart:change", { bubbles: true, detail: { baseEvent: "ux-basket:upsell", cart } }));
  } catch (error) {
    button.removeAttribute("aria-busy");
  }
});

// js/components/ux-offer-progress.js
// DS Offer progress (milestone 2, GitHub #46): recounts the sample pots (SKU ending /25) from the basket on every
// cart:change and redraws the track, count, message, check and link with the wording rendered from Theme settings ›
// Offers. Instances with a fixed number of pots (data-fixed, the specimen states) ignore the basket.
// Markup: snippets/ux-offer-progress.liquid.
var UxOfferProgress = class extends HTMLElement {
  constructor() {
    super();
    this._onCartChange = this._onCartChange.bind(this);
  }
  connectedCallback() {
    if (this.hasAttribute("data-fixed")) return;
    document.addEventListener("cart:change", this._onCartChange);
  }
  disconnectedCallback() {
    document.removeEventListener("cart:change", this._onCartChange);
  }
  static countPots(items) {
    return items.reduce((sum, item) => (/\/25$/.test(item.sku || "") ? sum + item.quantity : sum), 0);
  }
  get strings() {
    this._strings ??= JSON.parse(this.querySelector("[data-ux-offer-strings]").textContent);
    return this._strings;
  }
  _onCartChange(event) {
    const cart = event.detail?.cart;
    if (cart && Array.isArray(cart.items)) this.render(UxOfferProgress.countPots(cart.items));
  }
  render(total) {
    const set = Number(this.dataset.set) || 4;
    const pos = total > 0 ? ((total - 1) % set) + 1 : 0;
    const free = Math.floor(total / set);
    const remaining = set - 1 - pos;
    const fill = (template) => template.replace("[pos]", pos).replace("[set]", set).replace("[free]", free).replace("[remaining]", remaining);
    const s = this.strings;
    let message = s.more;
    if (pos === 0) message = s.empty;
    else if (pos === set) message = s.complete;
    else if (pos === set - 1) message = s.freeNext;
    else if (remaining === 1) message = s.oneMore;
    let count = s.countNext;
    if (total <= set) count = s.count;
    else if (pos === set) count = s.countFree;
    else if (free === 1) count = s.countNextOne;
    this.querySelectorAll(".ux-offer-progress__step").forEach((step, index) => step.classList.toggle("is-filled", index < pos));
    this.querySelector("[data-ux-offer-count]").textContent = fill(count);
    this.querySelector("[data-ux-offer-message]").textContent = fill(message);
    this.querySelector("[data-ux-offer-check]").hidden = pos !== set;
    const link = this.querySelector("[data-ux-offer-link]");
    if (link) link.hidden = pos === set;
    this.dataset.pots = total;
  }
};
if (!window.customElements.get("ux-offer-progress")) {
  window.customElements.define("ux-offer-progress", UxOfferProgress);
}

// js/components/ux-quick-add.js
// One-click add (milestone 3, GitHub #56: the PDP's Try it first sample pot). Adds one of data-variant-id with the
// sections that asked to be bundled, re-reads /cart.js and dispatches cart:change, so the header count, basket bar,
// cart drawer and offer progress follow. Its baseEvent is not "variant:add", so the cart drawer refreshes without
// opening: the design keeps the visitor on the page. "Added ✓" shows while the variant is in the basket. With
// data-toggle (Try it first, #148) a click while it's in takes it out again: every line of that variant, with the
// button's aria-label swapping between data-aria-add and data-aria-remove.
var UxQuickAdd = class extends HTMLElement {
  constructor() {
    super();
    this._onClick = this._onClick.bind(this);
    this._onCartChange = this._onCartChange.bind(this);
  }
  connectedCallback() {
    this.querySelector("button")?.addEventListener("click", this._onClick);
    document.addEventListener("cart:change", this._onCartChange);
    /* just clicked: the button keeps its resting look (no hover fill, Added ✓ not yet Remove) until the pointer or
       focus moves away, so the new state shows under the pointer */
    const settle = () => this.removeAttribute("data-fresh");
    this.addEventListener("pointerleave", settle);
    this.addEventListener("focusout", settle);
  }
  disconnectedCallback() {
    this.querySelector("button")?.removeEventListener("click", this._onClick);
    document.removeEventListener("cart:change", this._onCartChange);
  }
  set added(isAdded) {
    this.toggleAttribute("data-added", isAdded);
    this.querySelector("[data-label-add]").hidden = isAdded;
    this.querySelector("[data-label-added]").hidden = !isAdded;
    const button = this.querySelector("button");
    const label = isAdded ? button.dataset.ariaRemove : button.dataset.ariaAdd;
    if (label) button.setAttribute("aria-label", label);
  }
  _onCartChange(event) {
    const items = event.detail?.cart?.items;
    if (Array.isArray(items)) this.added = items.some((item) => String(item.variant_id) === this.dataset.variantId);
  }
  async _onClick() {
    const button = this.querySelector("button");
    if (button.getAttribute("aria-busy") === "true") return;
    const sections = [];
    document.documentElement.dispatchEvent(new CustomEvent("cart:prepare-bundled-sections", { bubbles: true, detail: { sections } }));
    button.setAttribute("aria-busy", "true");
    if (this.hasAttribute("data-toggle") && this.hasAttribute("data-added")) {
      try {
        await this._remove(sections);
      } catch (error) {
        this.querySelector("[data-status]").textContent = error.message;
      } finally {
        button.removeAttribute("aria-busy");
      }
      return;
    }
    try {
      const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          items: [{ id: Number(this.dataset.variantId), quantity: 1 }],
          sections: sections.join(","),
          sections_url: `${Shopify.routes.root}variants/${this.dataset.variantId}` // as the theme's product form does
        })
      });
      const added = await response.json();
      if (!response.ok) throw new Error(added.description || added.message || response.statusText);
      const cart = await (await fetch(`${Shopify.routes.root}cart.js`)).json();
      cart.sections = added.sections;
      this.added = true;
      this.setAttribute("data-fresh", "");
      this.querySelector("[data-status]").textContent = window.themeVariables?.strings?.uxAddedToBasket || "Added to your basket";
      document.documentElement.dispatchEvent(new CustomEvent("cart:change", {
        bubbles: true,
        detail: { baseEvent: "ux-quick-add", cart }
      }));
    } catch (error) {
      this.querySelector("[data-status]").textContent = error.message;
    } finally {
      button.removeAttribute("aria-busy");
    }
  }
};
/* every line of the variant (a palette's line is its own line) set to 0 by its key, with the bundled sections */
UxQuickAdd.prototype._remove = async function(sections) {
  const current = await (await fetch(`${Shopify.routes.root}cart.js`)).json();
  const updates = Object.fromEntries(current.items.filter((item) => String(item.variant_id) === this.dataset.variantId).map((item) => [item.key, 0]));
  let cart = current;
  if (Object.keys(updates).length) {
    const response = await fetch(`${Shopify.routes.root}cart/update.js`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ updates, sections: sections.join(","), sections_url: `${Shopify.routes.root}variants/${this.dataset.variantId}` })
    });
    cart = await response.json();
    if (!response.ok) throw new Error(cart.description || cart.message || response.statusText);
  }
  this.added = false;
  this.setAttribute("data-fresh", "");
  this.querySelector("[data-status]").textContent = window.themeVariables?.strings?.uxRemovedFromBasket || "Removed from your basket";
  document.documentElement.dispatchEvent(new CustomEvent("cart:change", { bubbles: true, detail: { baseEvent: "ux-quick-add", cart } }));
};
if (!window.customElements.get("ux-quick-add")) {
  window.customElements.define("ux-quick-add", UxQuickAdd);
}

// js/components/ux-palette-plan-links.js
// Links to the palette page open on Plan for someone who has a palette (Matt, 25 Oct: "if someone has one or more
// palettes, the buttons that take the user to the palette builder should go to the plan tab"). Palettes live in the
// browser, so the link is pointed at ?tab=palette as it's used (pointer down, or focus for the keyboard), never on a
// visitor without one. A link that already names a tab or a place on the page (#ux-palette-try, Order samples) keeps
// it; ?palette=<id> stays alongside.
(() => {
  const toPlan = (link) => {
    if (!window.UxPalette?.palettes?.().length) return;
    let url;
    try {
      url = new URL(link.getAttribute("href"), window.location.href);
    } catch {
      return;
    }
    const page = UxPalette.pageUrl();
    if (url.origin !== page.origin || url.pathname !== page.pathname) return;
    if (page.searchParams.has("view") && url.searchParams.get("view") !== page.searchParams.get("view")) return;
    if (url.searchParams.has("tab") || url.hash) return;
    url.searchParams.set("tab", "palette");
    link.setAttribute("href", url.pathname + url.search);
  };
  const onUse = (event) => {
    const link = event.target.closest?.("a[href]");
    if (link) toPlan(link);
  };
  document.addEventListener("pointerdown", onUse, true);
  document.addEventListener("focusin", onUse, true);
})();

// js/components/ux-colour-cursor.js
// A photo's colour name, following the pointer (#271: Matt, 25 Oct, "can it just follow the mouse?"): over the photo
// of a [data-ux-colour-name] (the Paint landing's Our paints photos), with a mouse or pen, one label sits just below
// and right of the pointer, flipping left near the window's edge. Touch has no hover, so it never shows; the name is in
// the photo's alt text for screen readers either way.
(() => {
  let label = null;
  let frame = 0;
  const hide = () => {
    if (label) label.hidden = true;
  };
  document.addEventListener("pointermove", (event) => {
    if (event.pointerType === "touch") return;
    /* over the photo's own box (an overlay can sit on top of the image, so not by the element hit) */
    const owner = event.target.closest?.("[data-ux-colour-name]");
    const photo = owner?.querySelector("img");
    const box = photo?.getBoundingClientRect();
    if (!box || event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) return hide();
    if (!label) {
      label = Object.assign(document.createElement("span"), { className: "ux-colour-cursor", hidden: true });
      label.setAttribute("aria-hidden", "true");
      document.body.append(label);
    }
    label.textContent = owner.dataset.uxColourName;
    label.hidden = false;
    const { clientX: x, clientY: y } = event;
    cancelAnimationFrame(frame);
    frame = requestAnimationFrame(() => {
      const flip = x + 16 + label.offsetWidth > window.innerWidth - 8;
      label.style.transform = `translate(${flip ? x - 12 - label.offsetWidth : x + 16}px, ${y + 20}px)`;
    });
  }, { passive: true });
  document.addEventListener("pointerleave", hide);
  document.addEventListener("scroll", hide, { passive: true });
})();

// js/components/ux-klaviyo-signup.js
// The Colour Swatches launch list (#208: Matt, 25 Oct, the list moves to Klaviyo). Shows the sign-up's plain form
// (hidden until now; Shopify's customer form is in <noscript> for visitors without JavaScript, since Shopify's bot
// protection sends its customer forms itself) and posts the email to Klaviyo's client subscriptions API, into
// data-list for the site data-company, then says thanks (data-done) in place of the field, or data-error under it.
// Markup: sections/ux-swatches-hero.liquid.
var UxKlaviyoSignup = class extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector("form:not([action])");
    if (!this.form) return;
    this.form.hidden = false;
    this.form.addEventListener("submit", (event) => this.submit(event));
  }
  async submit(event) {
    event.preventDefault();
    const input = this.form.querySelector('input[type="email"]');
    const button = this.form.querySelector('[type="submit"]');
    if (!input.reportValidity() || button.getAttribute("aria-busy") === "true") return;
    button.setAttribute("aria-busy", "true");
    this.form.querySelector(".ux-swatches-signup__error")?.remove();
    input.removeAttribute("aria-invalid");
    try {
      const response = await fetch(`https://a.klaviyo.com/client/subscriptions?company_id=${encodeURIComponent(this.dataset.company)}`, {
        method: "POST",
        headers: { "Content-Type": "application/vnd.api+json", revision: "2024-10-15" },
        body: JSON.stringify({
          data: {
            type: "subscription",
            attributes: {
              custom_source: this.dataset.source,
              /* the subscription is the consent: Klaviyo's client endpoint refuses a profile "subscriptions" field */
              profile: { data: { type: "profile", attributes: { email: input.value.trim() } } }
            },
            relationships: { list: { data: { type: "list", id: this.dataset.list } } }
          }
        })
      });
      if (!response.ok) {
        const detail = (await response.json().catch(() => null))?.errors?.map((error) => error.detail).join("; ");
        throw new Error(`Klaviyo ${response.status}${detail ? `: ${detail}` : ""}`);
      }
      const done = Object.assign(document.createElement("p"), { className: "ux-swatches-signup__done", textContent: this.dataset.done });
      done.setAttribute("role", "status");
      this.form.replaceChildren(done);
    } catch (failure) {
      console.warn("Colour Swatches sign-up:", failure.message);
      const error = Object.assign(document.createElement("p"), { className: "ux-swatches-signup__error", id: `${this.form.id}-error`, textContent: this.dataset.error });
      error.setAttribute("role", "alert");
      input.setAttribute("aria-invalid", "true");
      input.setAttribute("aria-describedby", error.id);
      this.form.append(error);
    } finally {
      button.removeAttribute("aria-busy");
    }
  }
};
if (!window.customElements.get("ux-klaviyo-signup")) {
  window.customElements.define("ux-klaviyo-signup", UxKlaviyoSignup);
}

// js/components/ux-pdp-buy.js
// DS product page buy state (milestone 3, GitHub #57, #58, #59, #60). Holds the finish and the size quantities the
// visitor has chosen; there is no variant id on the page until both are set. Nothing is preselected: Shopify always
// resolves a first available variant, so the page ignores it and starts empty unless the visit carries a finish
// (?variant=, rendered into data-finish; else ?finish=; else arriving from a finish collection).
//   Finish row → Finish drawer. Size row → Size drawer (through Finish first when there is none).
//   Add to basket → Finish drawer, then Size drawer, then adds: /cart/add.js with the bundled sections, a fresh
//   /cart.js on cart:change, and the theme's Added-to-basket pop-up (primer logic) until #67 replaces it.
// The Finish drawer confirms with its footer Primary ("Choose <finish>"); closing it without confirming puts the
// radios back. Markup: snippets/ux-pdp-buy, ux-pdp-finish-drawer, ux-pdp-size-drawer.
var uxHandle = (text) => String(text || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
var UxPdpBuy = class extends HTMLElement {
  constructor() {
    super();
    this._onFinishPick = this._onFinishPick.bind(this);
    this._onFinishClick = this._onFinishClick.bind(this);
    this._onFilter = this._onFilter.bind(this);
    this._syncRadios = this._syncRadios.bind(this);
    this._onClick = this._onClick.bind(this);
    this._onSizeClick = this._onSizeClick.bind(this);
    this._renderSizes = this._renderSizes.bind(this);
    this._next = null;
  }
  connectedCallback() {
    this.finishDrawer = document.getElementById("ux-pdp-finish-drawer");
    this.sizeDrawer = document.getElementById("ux-pdp-size-drawer");
    if (this.finishDrawer) {
      this.finishDrawer.addEventListener("change", this._onFinishPick);
      this.finishDrawer.addEventListener("click", this._onFinishClick);
      this.finishDrawer.addEventListener("dialog:after-hide", this._syncRadios);
      this.finishDrawer.querySelectorAll("[data-ux-finish-filter]").forEach((select) => select.addEventListener("change", this._onFilter));
    }
    if (this.sizeDrawer) {
      this.sizeDrawer.addEventListener("click", this._onSizeClick);
      this.sizeDrawer.addEventListener("ux-qty:change", this._renderSizes);
    }
    // The selector rows open their drawers in script, so the theme doesn't track them: say when each is open.
    [[this.finishDrawer, "[data-ux-finish-row]"], [this.sizeDrawer, "[data-ux-size-row]"]].forEach(([drawer, selector]) => {
      drawer?.addEventListener("toggle", () => this.querySelector(selector)?.setAttribute("aria-expanded", String(drawer.open)));
    });
    // The free-delivery line counts what is already in the basket, which changes under it (cart drawer, other adds).
    this._onCartChange = (event) => {
      const total = event.detail?.cart?.total_price;
      const delivery = this.sizeDrawer?.querySelector("[data-ux-size-delivery]");
      if (delivery && typeof total === "number") {
        delivery.dataset.basket = total;
        this._renderSizes();
      }
    };
    document.addEventListener("cart:change", this._onCartChange);
    this.addEventListener("click", this._onClick);
    this.finish = this.dataset.finish || this._finishFromVisit() || "";
    this._render();
  }
  disconnectedCallback() {
    document.removeEventListener("cart:change", this._onCartChange);
  }
  get finishes() {
    return [...(this.finishDrawer?.querySelectorAll("[name=ux-pdp-finish]") || [])].filter((input) => !input.disabled).map((input) => input.value);
  }
  get sizeGroup() {
    return [...(this.sizeDrawer?.querySelectorAll(".ux-size-drawer__group") || [])].find((group) => group.dataset.finish === this.finish);
  }
  // The chosen sizes of the chosen finish: [{ id, quantity, size, price }]
  get picks() {
    return [...(this.sizeGroup?.querySelectorAll("ux-qty-stepper") || [])]
      .map((stepper) => ({ stepper, row: stepper.closest(".ux-size-row") }))
      .filter(({ stepper }) => stepper.value > 0)
      .map(({ stepper, row }) => ({ id: Number(stepper.dataset.variantId), quantity: stepper.value, size: row.dataset.size, price: Number(row.dataset.price) }));
  }
  // A finish the visit points at: ?finish=dead-matt (milestone 1 links), or the finish collection we came from.
  _finishFromVisit() {
    const hints = [];
    const own = new URLSearchParams(window.location.search).get("finish");
    if (own) hints.push(own);
    try {
      const from = new URL(document.referrer);
      const collection = from.origin === window.location.origin && from.pathname.match(/\/collections\/([^/?#]+)/);
      if (collection) {
        const byCollection = { "marble-matt-emulsion": "marble-matt", "floor-paint": "floor", "masonry-paint": "masonry" };
        const finishParam = from.searchParams.get("finish");
        if (collection[1] === "multi-surface-paint" && finishParam) hints.push(`multi-surface-${finishParam}`);
        else if (byCollection[collection[1]]) hints.push(byCollection[collection[1]]);
      }
    } catch (error) {
      // no referrer, or not a URL
    }
    for (const hint of hints) {
      const match = this.finishes.find((finish) => uxHandle(finish).includes(uxHandle(hint)));
      if (match) return match;
    }
    return "";
  }
  setFinish(finish) {
    this.finish = finish;
    this._render();
    this.dispatchEvent(new CustomEvent("ux-pdp:finish", { bubbles: true, detail: { finish } }));
  }
  _render() {
    const value = this.querySelector("[data-ux-finish-value]");
    value.textContent = this.finish || value.dataset.empty;
    this.querySelector("[data-ux-finish-row]").classList.toggle("is-chosen", Boolean(this.finish));
    this.sizeDrawer?.querySelectorAll(".ux-size-drawer__group").forEach((group) => (group.hidden = group.dataset.finish !== this.finish));
    const finishLabel = this.sizeDrawer?.querySelector("[data-ux-size-finish]");
    if (finishLabel) finishLabel.textContent = this.finish;
    this._syncRadios();
    this._renderSizes();
  }
  _renderSizes() {
    if (!this.sizeDrawer) return;
    const picks = this.picks;
    const summary = picks.map((pick) => `${pick.quantity} × ${pick.size}`).join(", ");
    const total = picks.reduce((sum, pick) => sum + pick.price * pick.quantity, 0);
    const items = this.sizeDrawer.querySelector("[data-ux-size-items]");
    items.textContent = summary || items.dataset.empty;
    this.sizeDrawer.querySelector("[data-ux-size-total]").textContent = uxFormatMoney(total);
    const delivery = this.sizeDrawer.querySelector("[data-ux-size-delivery]");
    const qualifies = picks.length > 0 && Number(delivery.dataset.basket) + total >= Number(delivery.dataset.threshold);
    delivery.textContent = qualifies ? delivery.dataset.qualifies : delivery.dataset.base;
    const add = this.sizeDrawer.querySelector("[data-ux-size-add]");
    add.disabled = picks.length === 0;
    add.textContent = picks.length ? add.dataset.labelSome : add.dataset.labelNone;
    // "1 × 2.5 Litre · £68.00" once chosen; before that the chosen finish's own "from" price (else any finish's)
    const value = this.querySelector("[data-ux-size-value]");
    value.textContent = summary ? `${summary} · ${uxFormatMoney(total)}` : this.sizeGroup?.dataset.prompt || value.dataset.empty;
    this.querySelector("[data-ux-size-row]").classList.toggle("is-chosen", picks.length > 0);
  }
  // Open one drawer once the other has finished closing, so two never stack.
  _swap(from, to) {
    if (from?.open) {
      from.addEventListener("dialog:after-hide", () => to.show(), { once: true });
      from.hide();
    } else {
      to.show();
    }
  }
  _openSizes() {
    if (!this.finish) {
      this._next = "size";
      this.finishDrawer.show();
    } else {
      this.sizeDrawer.show();
    }
  }
  _onClick(event) {
    if (event.target.closest("[data-ux-finish-row]")) {
      this._next = null;
      this.finishDrawer.show();
    } else if (event.target.closest("[data-ux-size-row]")) {
      this._openSizes();
    } else if (event.target.closest("[data-ux-pdp-add]")) {
      if (!this.finish || this.picks.length === 0) this._openSizes();
      else this.add(event.target.closest("[data-ux-pdp-add]"));
    }
  }
  // Set the chosen finish's steppers to a recommendation ([{ id, quantity }]) and redraw (#79).
  applyPicks(picks) {
    const steppers = [...(this.sizeGroup?.querySelectorAll("ux-qty-stepper") || [])];
    steppers.forEach((stepper) => stepper.sync(0));
    picks.forEach((pick) => {
      const stepper = steppers.find((candidate) => Number(candidate.dataset.variantId) === pick.id);
      if (stepper) stepper.value = pick.quantity;
    });
    this._renderSizes();
  }
  _onSizeClick(event) {
    if (event.target.closest("[data-ux-open-calc]")) {
      const calc = document.getElementById("ux-pdp-calc-drawer");
      if (calc) this._swap(this.sizeDrawer, calc);
      return;
    }
    if (event.target.closest("[data-ux-change-finish]")) {
      this._next = "size";
      this._swap(this.sizeDrawer, this.finishDrawer);
    } else if (event.target.closest("[data-ux-size-add]")) {
      this.add(event.target.closest("[data-ux-size-add]"));
    }
  }
  async add(button) {
    const picks = this.picks;
    if (!picks.length || button.getAttribute("aria-busy") === "true") return;
    const items = picks.map(({ id, quantity }) => ({ id, quantity }));
    const sections = [];
    document.documentElement.dispatchEvent(new CustomEvent("cart:prepare-bundled-sections", { bubbles: true, detail: { sections } }));
    document.documentElement.dispatchEvent(new CustomEvent("theme:loading:start", { bubbles: true }));
    button.setAttribute("aria-busy", "true");
    this._showError("");
    try {
      const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items, sections: sections.join(","), sections_url: `${Shopify.routes.root}variants/${items[0].id}` })
      });
      const added = await response.json();
      if (!response.ok) throw new Error(added.description || added.message || response.statusText);
      const cart = await (await fetch(`${Shopify.routes.root}cart.js`)).json();
      cart.sections = added.sections;
      document.documentElement.dispatchEvent(new CustomEvent("cart:change", { bubbles: true, detail: { baseEvent: "ux-pdp:add", cart } }));
      this.sizeGroup?.querySelectorAll("ux-qty-stepper").forEach((stepper) => stepper.sync(0));
      this._renderSizes();
      const delivery = this.sizeDrawer.querySelector("[data-ux-size-delivery]");
      delivery.dataset.basket = cart.total_price;
      if (this.sizeDrawer.open) this.sizeDrawer.hide();
      // The Added-to-basket moment: the DS drawer (#67) where the page has it, else the live pop-up.
      const atb = document.querySelector("ux-atb");
      if (atb) atb.show(added.items, items, cart);
      else if (typeof window.showAddedToCartPopup === "function") window.showAddedToCartPopup(added, { requestedItems: items });
    } catch (error) {
      this._showError(error.message || window.themeVariables?.strings?.uxAddError);
    } finally {
      button.removeAttribute("aria-busy");
      document.documentElement.dispatchEvent(new CustomEvent("theme:loading:end", { bubbles: true }));
    }
  }
  _showError(message) {
    [this, this.sizeDrawer].forEach((root) => {
      const error = root?.querySelector("[data-ux-add-error]");
      if (!error) return;
      error.textContent = message;
      error.hidden = !message;
    });
  }
  _syncRadios() {
    if (!this.finishDrawer) return;
    this.finishDrawer.querySelectorAll("[name=ux-pdp-finish]").forEach((input) => (input.checked = input.value === this.finish));
    this._updateConfirm();
  }
  _picked() {
    return this.finishDrawer.querySelector("[name=ux-pdp-finish]:checked")?.value || "";
  }
  _updateConfirm() {
    const button = this.finishDrawer.querySelector("[data-ux-finish-confirm]");
    const picked = this._picked();
    button.disabled = !picked;
    button.textContent = picked ? button.dataset.labelSome.replace("[finish]", picked) : button.dataset.labelNone;
  }
  _onFinishPick(event) {
    if (event.target.name === "ux-pdp-finish") this._updateConfirm();
  }
  _onFinishClick(event) {
    if (!event.target.closest("[data-ux-finish-confirm]")) return;
    const picked = this._picked();
    if (!picked) return;
    this.setFinish(picked);
    if (this._next === "calc") {
      // The calculator asked for a finish first: recommend for this one, then show the sizes it set.
      this._next = null;
      const calc = document.querySelector("ux-paint-calc");
      calc?.calculate();
      if (calc?.picks?.length) this.applyPicks(calc.picks);
      this._swap(this.finishDrawer, this.sizeDrawer);
    } else if (this._next === "size") {
      this._next = null;
      this._swap(this.finishDrawer, this.sizeDrawer);
    } else {
      this.finishDrawer.hide();
    }
  }
  _onFilter() {
    const wanted = {};
    this.finishDrawer.querySelectorAll("[data-ux-finish-filter]").forEach((select) => (wanted[select.dataset.uxFinishFilter] = select.value));
    let shown = 0;
    this.finishDrawer.querySelectorAll(".ux-finish-option").forEach((card) => {
      const match = Object.entries(wanted).every(([key, value]) => !value || card.dataset[key].includes(`|${value}|`));
      card.hidden = !match;
      if (match) shown += 1;
    });
    this.finishDrawer.querySelector("[data-ux-finish-empty]").hidden = shown > 0;
    const status = this.finishDrawer.querySelector("[data-ux-finish-count]");
    status.textContent = (window.themeVariables?.strings?.uxFinishesShown || "[count] finishes shown").replace("[count]", shown);
  }
};
if (!window.customElements.get("ux-pdp-buy")) {
  window.customElements.define("ux-pdp-buy", UxPdpBuy);
}

// js/components/ux-atb.js
// Added to basket drawer (milestone 3, GitHub #67). Same moment and logic as theme.js showAddedToCartPopup, in the DS
// drawer: primer = half the litres of paint just added (samples, SKU /25, don't count); Marble Matt → the primer's
// Wall finish, Multi-Surface → its Multi-Surface finish, matched on the primer's own Finish option ("Wall Primer",
// "MS Primer & Undercoat") rather than its title text, which missed Dark Red's "MS"; the smallest size that covers it,
// else multiples of the largest. One Primary: "Add primer · £45.00"; once added, "Primer added" with Undo and Checkout
// becomes the Primary. Samples-only adds get the samples block instead. Footer totals come from a fresh /cart.js
// (discount allocations are only there). Markup: snippets/ux-atb-drawer.liquid.
var uxLitres = (text) => {
  const value = String(text || "").toLowerCase();
  const litre = value.match(/(\d+(?:\.\d+)?)\s*(?:litres?|liters?|ltr|l)\b/);
  if (litre) return parseFloat(litre[1]);
  const ml = value.match(/(\d+(?:\.\d+)?)\s*ml\b/);
  return ml ? parseFloat(ml[1]) / 1000 : 0;
};
var uxIsSample = (item) => /\/(25|CS)$/.test(item.sku || ""); /* pots and Colour Swatches (#206) */
var UxAtb = class extends HTMLElement {
  connectedCallback() {
    // The theme's Drawer moves itself to <body> on open and back on close, reconnecting what's inside: wire once.
    if (this._wired) return;
    this._wired = true;
    this.drawer = this.closest("ux-drawer");
    this.footer = this.drawer.querySelector(".ux-drawer__footer");
    this.strings = JSON.parse(this.querySelector("[data-ux-atb-strings]").textContent);
    const variants = this.querySelector("[data-ux-atb-primer-variants]");
    this.primerVariants = variants ? JSON.parse(variants.textContent) : [];
    this.addEventListener("click", (event) => {
      if (event.target.closest("[data-ux-atb-add-primer]")) this._addPrimer();
      if (event.target.closest("[data-ux-atb-undo]")) this._undoPrimer();
    });
    this.footer.querySelector("[data-ux-atb-continue]").addEventListener("click", () => this.drawer.hide());
    document.addEventListener("cart:change", (event) => {
      if (event.detail?.cart?.items && this.drawer.open) this._renderFooter(event.detail.cart);
    });
  }
  // added: the /cart/add.js items; requested: [{ id, quantity }] as asked for (add.js returns merged line totals);
  // options.noPrimer when the add was the primer itself (the primer drawer, #66)
  show(added, requested, cart, options = {}) {
    const asked = Object.fromEntries((requested || []).map((item) => [String(item.id), item.quantity]));
    this.added = (added || []).map((item) => ({ ...item, quantity: asked[String(item.id)] ?? item.quantity }));
    this.samplesOnly = this.added.length > 0 && this.added.every(uxIsSample);
    this._renderAdded();
    this.primerPick = this.samplesOnly || options.noPrimer ? null : this._pickPrimer();
    this._renderPrimer();
    const samples = this.querySelector("[data-ux-atb-samples]");
    samples.hidden = !this.samplesOnly;
    this._renderTools(cart);
    this._renderFooter(cart);
    this.drawer.show();
  }
  _renderAdded() {
    const box = this.querySelector("[data-ux-atb-added]");
    box.replaceChildren(...this.added.map((item) => {
      const row = document.createElement("div");
      row.className = "ux-atb__line";
      const thumb = document.createElement("span");
      thumb.className = "ux-atb__thumb";
      if (item.image) {
        const img = document.createElement("img");
        img.src = item.image.replace(/(\.[a-z]+)(\?|$)/i, "_160x$1$2");
        img.alt = "";
        img.loading = "lazy";
        thumb.append(img);
      }
      const text = document.createElement("span");
      text.className = "ux-atb__line-text";
      const name = document.createElement("span");
      name.className = "ux-atb__line-name";
      name.textContent = item.product_title;
      const detail = document.createElement("span");
      detail.className = "ux-atb__line-detail";
      const options = (item.options_with_values || []).map((option) => option.value).filter((value) => value !== "Default Title");
      const variantTitle = item.variant_title === "Default Title" ? "" : item.variant_title || "";
      detail.textContent = `${options.join(" · ") || variantTitle}${item.quantity > 1 ? ` × ${item.quantity}` : ""}`;
      text.append(name, detail);
      const price = document.createElement("span");
      price.className = "ux-atb__line-price";
      price.textContent = uxFormatMoney((item.final_price ?? item.price) * item.quantity);
      row.append(thumb, text, price);
      return row;
    }));
  }
  _pickPrimer() {
    if (!this.primerVariants.length) return null;
    const paintLitres = this.added.filter((item) => !uxIsSample(item)).reduce((sum, item) => sum + uxLitres(item.variant_title) * item.quantity, 0);
    const needed = paintLitres / 2;
    if (needed <= 0) return null;
    const paint = this.added.find((item) => !uxIsSample(item));
    const finish = (paint.options_with_values?.find((option) => /finish/i.test(option.name))?.value || paint.variant_options?.[0] || "").toLowerCase();
    let kind = null;
    if (finish.includes("marble matt")) kind = /wall/i;
    else if (/multi[\s-]?surface/.test(finish)) kind = /multi|^ms\b/i;
    const usable = this.primerVariants.filter((variant) => variant.available);
    const matching = kind ? usable.filter((variant) => kind.test(variant.finish)) : usable;
    const sized = (matching.length ? matching : usable).map((variant) => ({ variant, litres: uxLitres(variant.size) })).filter((x) => x.litres > 0).sort((a, b) => a.litres - b.litres);
    if (!sized.length) return null;
    const cover = sized.find((x) => x.litres >= needed);
    const largest = sized[sized.length - 1];
    const pick = cover ? { ...cover, quantity: 1 } : { ...largest, quantity: Math.max(1, Math.ceil(needed / largest.litres)) };
    return { ...pick, paintLitres, needed };
  }
  _renderPrimer() {
    const section = this.querySelector("[data-ux-atb-primer]");
    if (!section) return;
    section.hidden = !this.primerPick;
    if (!this.primerPick) return;
    const { variant, quantity, paintLitres, needed, litres } = this.primerPick;
    const s = this.strings;
    const litresText = (value) => s.litres.replace("[litres]", Math.round(value * 100) / 100);
    section.querySelector("[data-ux-atb-primer-variant]").textContent = `${variant.finish} · ${variant.size}${quantity > 1 ? ` × ${quantity}` : ""}`;
    section.querySelector("[data-ux-atb-primer-price]").textContent = uxFormatMoney(variant.price * quantity);
    section.querySelector("[data-ux-atb-recommendation]").textContent = (quantity > 1 ? s.recommendMany : s.recommendOne)
      .replace("[paint]", litresText(paintLitres)).replace("[primer]", litresText(needed))
      .replace("[size]", litresText(litres)).replace("[count]", quantity);
    section.querySelector("[data-ux-atb-add-primer]").textContent = s.addPrimer.replace("[price]", uxFormatMoney(variant.price * quantity));
    this._primerAdded(false);
  }
  _primerAdded(isAdded) {
    const section = this.querySelector("[data-ux-atb-primer]");
    if (section) {
      section.querySelector("[data-ux-atb-primer-offer]").hidden = isAdded;
      section.querySelector("[data-ux-atb-primer-added]").hidden = !isAdded;
    }
    this.primerIsAdded = isAdded;
    this._renderActions();
  }
  _renderActions() {
    // One Primary per view: Checkout only when nothing else here is asking for the click.
    const offering = this.primerPick && !this.primerIsAdded;
    const checkout = !offering && !this.samplesOnly;
    this.footer.querySelector("[data-ux-atb-checkout]").hidden = !checkout;
    this.footer.querySelector("[data-ux-atb-continue]").hidden = checkout;
    this.footer.querySelector(".ux-atb__actions").classList.toggle("ux-atb__actions--stacked", checkout);
  }
  _renderTools(cart) {
    const inBasket = new Set((cart?.items || []).map((item) => String(item.variant_id)));
    let shown = 0;
    this.querySelectorAll(".ux-atb__tool").forEach((tool) => {
      const show = !this.samplesOnly && shown < 3 && !inBasket.has(tool.dataset.variantId);
      tool.hidden = !show;
      if (show) shown += 1;
    });
    this.querySelector("[data-ux-atb-tools]").hidden = shown === 0;
  }
  _renderFooter(cart) {
    if (!cart) return;
    const s = this.strings;
    this.footer.querySelector("[data-ux-atb-count]").textContent = cart.item_count === 1 ? s.item : s.items.replace("[count]", cart.item_count);
    this.footer.querySelector("[data-ux-atb-total]").textContent = uxFormatMoney(cart.total_price);
    const allSamples = cart.items.length > 0 && cart.items.every(uxIsSample);
    let delivery = cart.total_price >= Number(this.dataset.threshold) ? s.qualifies : s.base;
    if (allSamples) {
      const pots = cart.items.reduce((sum, item) => sum + item.quantity, 0);
      const more = Number(this.dataset.freeDeliveryPots) - pots;
      delivery = more <= 0 ? s.qualifies : more === 1 ? s.morePot : s.morePots.replace("[count]", more);
    }
    this.footer.querySelector("[data-ux-atb-delivery]").textContent = delivery;
    this._renderActions();
  }
  async _change(url, body) {
    const sections = [];
    document.documentElement.dispatchEvent(new CustomEvent("cart:prepare-bundled-sections", { bubbles: true, detail: { sections } }));
    const response = await fetch(`${Shopify.routes.root}${url}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ...body, sections: sections.join(",") })
    });
    const json = await response.json();
    if (!response.ok) throw new Error(json.description || json.message || response.statusText);
    const cart = await (await fetch(`${Shopify.routes.root}cart.js`)).json();
    cart.sections = json.sections;
    document.documentElement.dispatchEvent(new CustomEvent("cart:change", { bubbles: true, detail: { baseEvent: "ux-atb:change", cart } }));
    return json;
  }
  async _addPrimer() {
    const { variant, quantity } = this.primerPick || {};
    const button = this.querySelector("[data-ux-atb-add-primer]");
    if (!variant || button.getAttribute("aria-busy") === "true") return;
    button.setAttribute("aria-busy", "true");
    this._showError("");
    try {
      // the colour it was tinted for rides along as a hidden line property, so the basket can say so (#90)
      const properties = this.dataset.tintedFor ? { _tinted_for: this.dataset.tintedFor } : undefined;
      const added = await this._change("cart/add.js", { items: [{ id: variant.id, quantity, properties }], sections_url: `${Shopify.routes.root}variants/${variant.id}` });
      this.primerLine = added.items?.[0];
      this._primerAdded(true);
      this.querySelector("[data-ux-atb-undo]").focus();
    } catch (error) {
      this._showError(error.message || this.strings.addError);
    } finally {
      button.removeAttribute("aria-busy");
    }
  }
  async _undoPrimer() {
    if (!this.primerLine) return;
    const { variant, quantity } = this.primerPick;
    try {
      const cart = await (await fetch(`${Shopify.routes.root}cart.js`)).json();
      const line = cart.items.find((item) => item.key === this.primerLine.key || item.variant_id === variant.id);
      if (line) await this._change("cart/change.js", { id: line.key, quantity: Math.max(0, line.quantity - quantity) });
      this.primerLine = null;
      this._primerAdded(false);
      this.querySelector("[data-ux-atb-add-primer]").focus();
    } catch (error) {
      this._showError(error.message || this.strings.addError);
    }
  }
  _showError(message) {
    const error = this.querySelector("[data-ux-atb-error]");
    if (!error) return;
    error.textContent = message;
    error.hidden = !message;
  }
};
if (!window.customElements.get("ux-atb")) {
  window.customElements.define("ux-atb", UxAtb);
}

// js/components/ux-primer-quick.js
// Primer quick-add drawer (milestone 3, GitHub #66): the Size drawer pattern for the recommended primer, replacing the
// live #primer-popup. On opening it follows the page: the paint's finish picks the primer's (Marble Matt → Wall,
// Multi-Surface → Multi-Surface) and, if paint sizes are chosen and nothing is set here yet, the half-the-litres rule
// sets the steppers. Adding goes through /cart/add.js, cart:change, then the Added to basket drawer (no primer
// suggestion there: this was it). Markup: snippets/ux-primer-drawer.liquid.
var UxPrimerQuick = class extends HTMLElement {
  connectedCallback() {
    if (this._wired) return; // the drawer moves itself to <body> and back: wire once
    this._wired = true;
    this.drawer = this.closest("ux-drawer");
    this.footer = this.drawer.querySelector(".ux-drawer__footer");
    this.addEventListener("change", (event) => {
      if (event.target.name === "ux-primer-finish") this._showFinish(event.target.value);
    });
    this.addEventListener("ux-qty:change", () => this._render());
    this.footer.querySelector("[data-ux-primer-add]").addEventListener("click", () => this.add());
    // "How much do I need?" (141:4098): the paint calculator; the guide under it halves the answer for primer
    this.querySelector("[data-ux-primer-calc]")?.addEventListener("click", () => {
      const calc = document.getElementById("ux-pdp-calc-drawer");
      if (!calc) return;
      this.drawer.addEventListener("dialog:after-hide", () => calc.show(), { once: true });
      this.drawer.hide();
    });
    this.drawer.addEventListener("dialog:before-show", () => this._prefill());
    document.addEventListener("cart:change", (event) => {
      const total = event.detail?.cart?.total_price;
      if (typeof total !== "number") return;
      this.footer.querySelector("[data-ux-size-delivery]").dataset.basket = total;
      this._render();
    });
    this._render();
  }
  get finish() {
    return this.querySelector("[name=ux-primer-finish]:checked")?.value || this.querySelector(".ux-size-drawer__group")?.dataset.finish;
  }
  get group() {
    return [...this.querySelectorAll(".ux-size-drawer__group")].find((group) => group.dataset.finish === this.finish);
  }
  get picks() {
    return [...(this.group?.querySelectorAll("ux-qty-stepper") || [])]
      .filter((stepper) => stepper.value > 0)
      .map((stepper) => { const row = stepper.closest(".ux-size-row"); return { id: Number(stepper.dataset.variantId), quantity: stepper.value, size: row.dataset.size, price: Number(row.dataset.price) }; });
  }
  _showFinish(finish) {
    this.querySelectorAll(".ux-size-drawer__group").forEach((group) => (group.hidden = group.dataset.finish !== finish));
    const radio = [...this.querySelectorAll("[name=ux-primer-finish]")].find((input) => input.value === finish);
    if (radio) radio.checked = true;
    this._render();
  }
  _prefill() {
    if (this.picks.length) return; // keep what the visitor has already set
    const buy = document.querySelector("ux-pdp-buy");
    const paintFinish = (buy?.finish || "").toLowerCase();
    let kind = null;
    if (paintFinish.includes("marble matt")) kind = /wall/i;
    else if (/multi[\s-]?surface/.test(paintFinish)) kind = /multi|^ms\b/i;
    const finishes = [...this.querySelectorAll(".ux-size-drawer__group")].map((group) => group.dataset.finish);
    const finish = (kind && finishes.find((value) => kind.test(value))) || this.finish;
    this._showFinish(finish);
    const paintLitres = (buy?.picks || []).reduce((sum, pick) => sum + uxLitres(pick.size) * pick.quantity, 0);
    const needed = paintLitres / 2;
    if (needed <= 0) return;
    const sized = [...this.group.querySelectorAll(".ux-size-row")]
      .map((row) => ({ row, litres: uxLitres(row.dataset.size) }))
      .filter((x) => x.litres > 0 && x.row.querySelector("ux-qty-stepper"))
      .sort((a, b) => a.litres - b.litres);
    if (!sized.length) return;
    const cover = sized.find((x) => x.litres >= needed);
    const largest = sized[sized.length - 1];
    const pick = cover ? { ...cover, quantity: 1 } : { ...largest, quantity: Math.ceil(needed / largest.litres) };
    pick.row.querySelector("ux-qty-stepper").value = pick.quantity;
    this._render();
  }
  _render() {
    const picks = this.picks;
    const finish = this.finish;
    const total = picks.reduce((sum, pick) => sum + pick.price * pick.quantity, 0);
    const items = this.footer.querySelector("[data-ux-size-items]");
    items.textContent = picks.length ? `${picks.map((pick) => `${pick.quantity} × ${pick.size}`).join(", ")} · ${finish}` : items.dataset.empty;
    this.footer.querySelector("[data-ux-size-total]").textContent = uxFormatMoney(total);
    const delivery = this.footer.querySelector("[data-ux-size-delivery]");
    delivery.textContent = picks.length && Number(delivery.dataset.basket) + total >= Number(delivery.dataset.threshold) ? delivery.dataset.qualifies : delivery.dataset.base;
    const add = this.footer.querySelector("[data-ux-primer-add]");
    add.disabled = !picks.length;
    add.textContent = picks.length ? add.dataset.labelSome.replace("[price]", uxFormatMoney(total)) : add.dataset.labelNone;
  }
  async add() {
    const button = this.footer.querySelector("[data-ux-primer-add]");
    const picks = this.picks;
    if (!picks.length || button.getAttribute("aria-busy") === "true") return;
    const properties = this.dataset.tintedFor ? { _tinted_for: this.dataset.tintedFor } : undefined;
    const items = picks.map(({ id, quantity }) => ({ id, quantity, properties }));
    const sections = [];
    document.documentElement.dispatchEvent(new CustomEvent("cart:prepare-bundled-sections", { bubbles: true, detail: { sections } }));
    button.setAttribute("aria-busy", "true");
    const error = this.footer.querySelector("[data-ux-add-error]");
    error.hidden = true;
    try {
      const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items, sections: sections.join(","), sections_url: `${Shopify.routes.root}variants/${items[0].id}` })
      });
      const added = await response.json();
      if (!response.ok) throw new Error(added.description || added.message || response.statusText);
      const cart = await (await fetch(`${Shopify.routes.root}cart.js`)).json();
      cart.sections = added.sections;
      document.documentElement.dispatchEvent(new CustomEvent("cart:change", { bubbles: true, detail: { baseEvent: "ux-primer:add", cart } }));
      this.group.querySelectorAll("ux-qty-stepper").forEach((stepper) => stepper.sync(0));
      this._render();
      const atb = document.querySelector("ux-atb");
      if (atb) {
        this.drawer.addEventListener("dialog:after-hide", () => atb.show(added.items, items, cart, { noPrimer: true }), { once: true });
      }
      this.drawer.hide();
    } catch (failure) {
      error.textContent = failure.message || window.themeVariables?.strings?.uxAddError;
      error.hidden = false;
    } finally {
      button.removeAttribute("aria-busy");
    }
  }
};
if (!window.customElements.get("ux-primer-quick")) {
  window.customElements.define("ux-primer-quick", UxPrimerQuick);
}

// js/components/ux-hero-photos.js
// DS product page hero photos, the slice gallery (GitHub #374; design canvas "PDP Hero — Breakpoints", 15 Oct 2026).
// 768 and up: the main photo whole, the others greyscale slices to its left. A click or ‹ › makes another the main
// photo where it stands (a click on the main photo shows the next); CSS does the widths and the motion, this only
// moves the state. From 1000 the hero fills the first screen below the header, which this measures (--ux-hero-top),
// and on a tablet it measures what sits between the gallery and the bottom of Add to basket (--ux-hero-below), so
// that stays in the first screen. A gallery too narrow for the main photo and every slice at 32 (1000–1150 wide with
// six photos, say): the main photo stays whole and the photos after it fill the room there is; the rest wait off the
// row, still reached by ‹ ›. Under 768: one square photo at a time in a scroll-snap strip; the count and the progress
// line follow the swipe. Markup: snippets/ux-pdp-hero-photos.liquid.
var uxHeroStrip = () => !window.matchMedia("(min-width: 768px)").matches;
var UxHeroPhotos = class extends HTMLElement {
  connectedCallback() {
    if (this._wired) return;
    this._wired = true;
    this.track = this.querySelector("[data-ux-track]");
    this.photos = [...this.track.children];
    this.ticks = [...this.querySelectorAll(".ux-hero-photos__progress > span")];
    this.at = 0;

    this.addEventListener("click", (event) => {
      const go = event.target.closest("[data-ux-go]");
      if (go) return this.show(this.at + Number(go.dataset.uxGo));
      const photo = event.target.closest("[data-ux-photo]");
      if (!photo) return;
      const i = this.photos.indexOf(photo);
      this.show(i === this.at ? i + 1 : i);
    });

    // Under 768 the strip scrolls: the photo most in view is the one shown.
    let ticking = false;
    this.track.addEventListener(
      "scroll",
      () => {
        if (ticking || !uxHeroStrip()) return;
        ticking = true;
        requestAnimationFrame(() => {
          ticking = false;
          const i = Math.round(Math.abs(this.track.scrollLeft) / this.track.clientWidth);
          if (i !== this.at && this.photos[i]) this._mark(i);
        });
      },
      { passive: true }
    );

    // The hero's top is the header's height, whatever the announcement and trust bars hold (235 at 1920 today).
    this.hero = this.closest(".ux-pdp__hero");
    if (this.hero) {
      const measure = () => {
        this._measure();
        this._fit();
      };
      window.addEventListener("resize", measure);
      if ("ResizeObserver" in window) {
        const observer = new ResizeObserver(measure);
        document.querySelectorAll(".shopify-section-group-header-group").forEach((section) => observer.observe(section));
      }
      measure();
    }
  }
  // Show photo `i` (from 0; wraps round). In the strip that's a scroll, which marks it as it lands.
  show(i) {
    const n = this.photos.length;
    if (n < 2) return;
    i = ((i % n) + n) % n;
    if (uxHeroStrip()) {
      const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      this.track.scrollTo({ left: i * this.track.clientWidth, behavior: reduced ? "auto" : "smooth" });
      return;
    }
    this._mark(i);
  }
  _mark(i) {
    this.at = i;
    this.photos.forEach((photo, k) => {
      photo.classList.toggle("is-active", k === i);
      if (k === i) photo.setAttribute("aria-current", "true");
      else photo.removeAttribute("aria-current");
    });
    this.ticks.forEach((tick, k) => tick.classList.toggle("is-active", k === i));
    this.style.setProperty("--ux-active-ratio", this.photos[i].style.getPropertyValue("--ux-ratio"));
    const label = this.querySelector("[data-ux-at]");
    if (label) label.textContent = i + 1;
    this._fit();
  }
  // How many slices fit beside the main photo at 32 or wider (one gap after the main photo, one before each slice,
  // #426). The shown ones are a run of photos in their order, the main one in it: the run stays put while the main
  // photo moves inside it and slides only when the main photo would leave it, so the photo before stays a slice to
  // its right (as when every photo fits) instead of vanishing (Matt, 23 Oct, with 12 photos).
  _fit() {
    const n = this.photos.length;
    let room = n - 1;
    if (!uxHeroStrip() && n > 1) {
      const cell = this.parentElement.clientWidth;
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      const ratio = Math.min(parseFloat(this.photos[this.at].style.getPropertyValue("--ux-ratio")) || 14 / 17, 1);
      room = Math.max(0, Math.min(n - 1, Math.floor((cell - this.clientHeight * ratio - gap) / (32 + gap))));
    }
    let start = this._start || 0;
    if (this.at < start) start = this.at;
    else if (this.at > start + room) start = this.at - room;
    start = Math.max(0, Math.min(start, n - 1 - room));
    this._start = start;
    this.photos.forEach((photo, k) => (photo.hidden = k < start || k > start + room));
    this.style.setProperty("--ux-n", room + 1);
  }
  // The hero's distance from the top of the page, in the page's own pixels (from 2880 the page is zoomed 1.5× and
  // the rect is in screen pixels); and on a tablet, what sits between the gallery and the bottom of Add to basket.
  _measure() {
    const zoom = parseFloat(getComputedStyle(document.documentElement).zoom) || 1;
    const top = Math.round((this.hero.getBoundingClientRect().top + window.scrollY) / zoom);
    if (top !== this._top) {
      this._top = top;
      this.hero.style.setProperty("--ux-hero-top", `${top}px`);
    }
    const add = this.hero.querySelector(".ux-pdp-buy__add");
    if (add && window.matchMedia("(min-width: 768px) and (max-width: 999px)").matches) {
      const below = Math.round((add.getBoundingClientRect().bottom - this.getBoundingClientRect().bottom) / zoom);
      if (below !== this._below) {
        this._below = below;
        this.hero.style.setProperty("--ux-hero-below", `${below}px`);
      }
    }
  }
};
if (!window.customElements.get("ux-hero-photos")) {
  window.customElements.define("ux-hero-photos", UxHeroPhotos);
}

// js/components/ux-paint-calc.js
// Paint calculator (milestone 3, GitHub #79): the live calculator's sums (width × height, less 1.8 m² a door and
// 1.5 m² a window, × coats ÷ coverage per litre) and its cheapest stock-capped mix of sizes (a bounded knapsack in
// centilitres, ported from product-paint.liquid), run over the chosen finish's size rows. "Choose these sizes" sets the
// Size drawer's steppers and opens it, choosing a finish first when there isn't one.
// Markup: snippets/ux-pdp-calc-drawer.liquid.
var uxCheapestCover = (litres, packs) => {
  const SCALE = 100;
  const target = Math.ceil(litres * SCALE - 1e-6);
  if (target <= 0) return null;
  const usable = packs
    .filter((pack) => pack.litres > 0 && pack.max > 0)
    .map((pack) => {
      const scaled = Math.round(pack.litres * SCALE);
      return { ...pack, scaled, cap: Math.min(pack.max, Math.ceil(target / scaled) + 1) };
    });
  if (!usable.length) return null;
  // Split each size into binary chunks up to its cap, so the 0/1 knapsack can never use more than is in stock.
  const items = [];
  usable.forEach((pack, index) => {
    let remaining = pack.cap;
    let chunk = 1;
    while (remaining > 0) {
      const take = Math.min(chunk, remaining);
      items.push({ index, take, scaled: pack.scaled * take, price: pack.price * take });
      remaining -= take;
      chunk *= 2;
    }
  });
  const size = target + Math.max(...usable.map((pack) => pack.scaled)) + 1;
  const cost = new Array(size).fill(Infinity);
  cost[0] = 0;
  const took = items.map(() => new Uint8Array(size));
  items.forEach((item, i) => {
    for (let amount = size - 1; amount >= item.scaled; amount--) {
      const candidate = cost[amount - item.scaled] + item.price;
      if (candidate < cost[amount] - 1e-9) {
        cost[amount] = candidate;
        took[i][amount] = 1;
      }
    }
  });
  let best = -1;
  for (let amount = target; amount < size; amount++) {
    if (cost[amount] < (best === -1 ? Infinity : cost[best]) - 1e-9) best = amount;
  }
  if (best === -1) return null;
  const counts = new Map();
  for (let i = items.length - 1, amount = best; i >= 0; i--) {
    if (took[i][amount]) {
      counts.set(items[i].index, (counts.get(items[i].index) || 0) + items[i].take);
      amount -= items[i].scaled;
    }
  }
  return [...counts.entries()]
    .map(([index, quantity]) => ({ ...usable[index], quantity }))
    .sort((a, b) => b.litres - a.litres);
};
/* On the product page it fills the Size drawer (ux-pdp-buy); inside the basket's Edit drawer (#149) the editor
   (ux-basket-editor) holds the finish's rows, shows it as a step and passes "Choose these sizes" on from its footer.
   On a finish page (data-standalone, #150) it carries its finish's sizes itself, with a Finish choice where there are
   several, and "Choose your colour" closes the drawer for the colours (data-next); on Choosing a finish (#504) it goes
   to the chosen finish's page (its group's data-url). */
var UxPaintCalc = class extends HTMLElement {
  connectedCallback() {
    if (this._wired) return; // the drawer moves itself to <body> and back: wire once
    this._wired = true;
    this.drawer = this.closest("ux-drawer");
    this.editor = this.closest("ux-basket-editor");
    this.form = this.querySelector("form");
    this.form.addEventListener("submit", (event) => {
      event.preventDefault();
      this.calculate(true);
    });
    if (this.editor) return;
    this.standalone = this.hasAttribute("data-standalone");
    this.querySelector("[data-ux-calc-finish]")?.addEventListener("change", (event) => {
      this.querySelectorAll("[data-ux-calc-group]").forEach((group) => (group.hidden = group.dataset.finish !== event.target.value));
      this._describe();
      const error = this.querySelector("[data-ux-calc-error]");
      if (!this.querySelector("[data-ux-calc-result]").hidden || !error.hidden) this.calculate(true);
    });
    this.apply.addEventListener("click", () => this.choose());
    this.drawer.addEventListener("dialog:before-show", () => this._describe());
  }
  /* the editor's footer is mounted after the panel, so look it up each time */
  get apply() {
    return this.drawer?.querySelector("[data-ux-calc-apply]") || { disabled: true };
  }
  get buy() {
    return document.querySelector("ux-pdp-buy");
  }
  get group() {
    if (this.editor) return this.editor.group;
    if (this.hasAttribute("data-standalone")) return this.querySelector("[data-ux-calc-group]:not([hidden])");
    return this.buy?.sizeGroup || document.querySelector("#ux-pdp-size-drawer .ux-size-drawer__group");
  }
  _describe() {
    const group = this.group;
    const intro = this.querySelector("[data-ux-calc-intro]");
    if (intro) intro.hidden = !group; /* a finish page with several finishes: nothing to say until one is chosen */
    if (!group || !intro) return;
    intro.textContent = intro.dataset.template.replace("[finish]", group.dataset.finish).replace("[coverage]", group.dataset.perLitre);
  }
  calculate(show = false) {
    const value = (name) => parseFloat(this.form.elements[name].value) || 0;
    const width = value("width");
    const height = value("height");
    const error = this.querySelector("[data-ux-calc-error]");
    const result = this.querySelector("[data-ux-calc-result]");
    const noFinish = this.hasAttribute("data-standalone") && !this.group;
    if (width <= 0 || height <= 0 || noFinish) {
      if (show) {
        if (error.dataset.wall) error.textContent = noFinish ? error.dataset.finish : error.dataset.wall;
        error.hidden = false;
        result.hidden = true;
      }
      this.picks = [];
      this.apply.disabled = true;
      return;
    }
    error.hidden = true;
    const group = this.group;
    const perLitre = Number(group?.dataset.perLitre) || 12;
    const area = Math.max(0, width * height - value("doors") * 1.8 - value("windows") * 1.5);
    const litres = (area * (value("coats") || 1)) / perLitre;
    const packs = [...(group?.querySelectorAll(".ux-size-row") || [])]
      .filter((row) => (row.querySelector("ux-qty-stepper") || row.hasAttribute("data-ux-calc-pack")) && !row.hasAttribute("data-ux-sample")) /* never a sample pot (#405) */
      .map((row) => {
        const stepper = row.querySelector("ux-qty-stepper");
        return { id: Number(row.dataset.variantId), size: row.dataset.size, litres: uxLitres(row.dataset.size), price: Number(row.dataset.price), max: stepper?.hasAttribute("data-max") ? Number(stepper.dataset.max) : Infinity };
      });
    const picks = uxCheapestCover(litres, packs) || [];
    this.picks = picks.map((pick) => ({ id: pick.id, quantity: pick.quantity }));
    this._describe();
    const litresText = this.querySelector("[data-ux-calc-litres]");
    litresText.textContent = litresText.dataset.template.replace("[litres]", Math.round(litres * 10) / 10);
    const recommend = this.querySelector("[data-ux-calc-recommend]");
    recommend.textContent = picks.length ? recommend.dataset.template.replace("[sizes]", picks.map((pick) => `${pick.quantity} × ${pick.size}`).join(" + ")) : recommend.dataset.none;
    result.hidden = false;
    this.apply.disabled = !picks.length;
  }
  choose() {
    if (this.standalone) {
      /* Choosing a finish (#504): the chosen finish's own page */
      if (this.group?.dataset.url) {
        window.location.href = this.group.dataset.url;
        return;
      }
      const next = this.dataset.next && document.querySelector(this.dataset.next);
      this.drawer.addEventListener("dialog:after-hide", () => {
        if (!next) return;
        next.scrollIntoView({ behavior: matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth", block: "start" });
        next.setAttribute("tabindex", "-1");
        /* after the drawer has handed focus back to its opener */
        setTimeout(() => next.focus({ preventScroll: true }), 50);
      }, { once: true });
      this.drawer.hide();
      return;
    }
    if (this.editor) {
      this.calculate();
      if (!this.picks?.length) return;
      this.editor.applyPicks(this.picks);
      this.editor._step("sizes");
      this.editor.footerSlot.querySelector("[data-ux-edit-update]")?.focus();
      return;
    }
    const buy = this.buy;
    if (!buy || !this.picks?.length) return;
    if (!buy.finish) {
      buy._next = "calc";
      buy._swap(this.drawer, buy.finishDrawer);
      return;
    }
    this.calculate();
    buy.applyPicks(this.picks);
    buy._swap(this.drawer, buy.sizeDrawer);
  }
};
if (!window.customElements.get("ux-paint-calc")) {
  window.customElements.define("ux-paint-calc", UxPaintCalc);
}
/* ---------------------------------------------------------------------------
   ux-load-more (milestone 5, GitHub #104): "Load more colours" appends the next page of a paginated grid in place.
   Fetches the link's ?page= URL through the Section Rendering API, appends the new <li>s to the grid named by
   aria-controls, swaps in the fresh status line and link, and moves focus to the first new card. Without JS the
   link is a plain ?page= link. Interim list params (?project=, ?finish=, ?type=) ride along.
   --------------------------------------------------------------------------- */
var UX_KEEP_PARAMS = ["project", "finish", "type"];
var UxLoadMore = class extends HTMLElement {
  connectedCallback() {
    if (this._wired) return;
    this._wired = true;
    this.addEventListener("click", (event) => {
      const link = event.target.closest("a[data-ux-load-more]");
      if (!link) return;
      event.preventDefault();
      this.load(link);
    });
  }
  async load(link) {
    if (this.hasAttribute("aria-busy")) return;
    const grid = document.getElementById(this.getAttribute("aria-controls"));
    if (!grid) return;
    const url = new URL(link.href, window.location.href);
    const current = new URLSearchParams(window.location.search);
    UX_KEEP_PARAMS.forEach((key) => current.has(key) && url.searchParams.set(key, current.get(key)));
    url.searchParams.set("section_id", this.dataset.sectionId);
    this.setAttribute("aria-busy", "true");
    try {
      const response = await fetch(url.toString());
      if (!response.ok) throw new Error(response.status);
      const doc = new DOMParser().parseFromString(await response.text(), "text/html");
      const items = [...doc.querySelectorAll(`#${CSS.escape(grid.id)} > li`)].map((item) => document.importNode(item, true));
      const fresh = doc.querySelector(`ux-load-more[aria-controls="${grid.id}"]`);
      if (!items.length || !fresh) throw new Error("no items");
      grid.append(...items);
      this.replaceChildren(...[...fresh.childNodes].map((node) => document.importNode(node, true)));
      items[0]?.querySelector("a[href]")?.focus();
    } catch (error) {
      window.location.href = link.href;
    } finally {
      this.removeAttribute("aria-busy");
    }
  }
};
if (!window.customElements.get("ux-load-more")) {
  window.customElements.define("ux-load-more", UxLoadMore);
}

/* Colour family strip (milestone 5, #104): on mobile the strip scrolls sideways, so bring the current family into view */
document.querySelectorAll(".ux-finish-colours__families").forEach((strip) => {
  const current = strip.querySelector("[aria-current]");
  if (current && strip.scrollWidth > strip.clientWidth) strip.scrollLeft = current.closest("li").offsetLeft - 20;
});

/* Interim list params (milestone 5, #110 / #111): ?project= and ?finish= ride along on the finish grid's own links
   (colour families, Load more) and on Filter / Sort re-renders; links marked data-ux-drop-params clear them */
var uxKeptParams = () => {
  const current = new URLSearchParams(window.location.search);
  return UX_KEEP_PARAMS.filter((key) => current.has(key)).map((key) => [key, current.get(key)]);
};
document.addEventListener("click", (event) => {
  const link = event.target.closest("[data-ux-keep-params] a[href]:not([data-ux-drop-params])");
  if (!link || link.origin !== window.location.origin) return;
  const kept = uxKeptParams();
  if (!kept.length) return;
  const url = new URL(link.href);
  kept.forEach(([key, value]) => url.searchParams.set(key, value));
  link.href = url.toString();
}, true);
window.addEventListener("facet:update", (event) => {
  const url = event.detail?.url;
  if (url instanceof URL) uxKeptParams().forEach(([key, value]) => url.searchParams.set(key, value));
}, true);

/* Page zoom (#381): pages that set `zoom` on <html> (the paint product page at 2880 and up) measure the window and the
   header in screen pixels, but their CSS lengths are multiplied by the zoom; divide measurements by this before setting
   them as CSS lengths (the CSS divides 100dvh by --ux-page-zoom the same way). 1 everywhere else. */
var uxPageZoom = () => parseFloat(getComputedStyle(document.documentElement).zoom) || 1;

/* ---------------------------------------------------------------------------
   Header search (milestone 5, GitHub #112 / #113; Figma: Website, Search; notes 138:1316)
   Focus opens it: desktop, a panel under the header over a 40% ink scrim; mobile, a full-screen sheet with Cancel.
   Empty field: the no-query panel (#ux-search-idle); typing: the theme's predictive results (#header-predictive-search),
   restyled in sections/predictive-search. Esc closes and returns focus to the field; the scrim, Cancel or a click
   elsewhere closes it. Without JS the field is a plain search form.
   --------------------------------------------------------------------------- */
(() => {
  const input = document.querySelector(".search-box .header-search__input");
  const idle = document.getElementById("ux-search-idle");
  const results = document.getElementById("header-predictive-search");
  if (!input || !idle || !results) return;
  const root = document.documentElement;
  const box = input.closest(".search-box");
  const header = document.querySelector(".shopify-section--header");
  const scrim = document.querySelector(".search-overlay");
  const cancel = box.querySelector("[data-ux-search-cancel]");
  let quiet = false;
  root.classList.add("ux-search-js");
  idle.hidden = false;

  const isOpen = () => root.classList.contains("ux-search-open");
  const mobile = () => window.matchMedia("(max-width: 999px)").matches;
  const place = () => {
    const zoom = uxPageZoom();
    const top = Math.max(0, (mobile() ? box : header).getBoundingClientRect().bottom / zoom);
    root.style.setProperty("--ux-search-top", `${top}px`);
    [idle, results].forEach((panel) => {
      panel.style.top = `${top}px`;
      panel.style.maxHeight = `${window.innerHeight / zoom - top}px`;
    });
  };
  const sync = () => {
    root.dataset.uxSearch = input.value.trim() === "" ? "idle" : "results";
  };
  const open = () => {
    if (quiet) return;
    if (!isOpen()) {
      root.classList.add("ux-search-open");
      input.setAttribute("aria-expanded", "true");
    }
    sync();
    place();
    requestAnimationFrame(place);
    setTimeout(place, 200); /* the mobile sheet's bar settles after the header's own focus handling */
  };
  const close = (refocus) => {
    if (!isOpen()) return;
    root.classList.remove("ux-search-open");
    delete root.dataset.uxSearch;
    input.setAttribute("aria-expanded", "false");
    if (refocus) {
      quiet = true;
      input.focus();
      quiet = false;
    }
  };

  input.setAttribute("aria-expanded", "false");
  input.setAttribute("aria-controls", "ux-search-idle header-predictive-search");
  input.addEventListener("focus", open);
  input.addEventListener("input", () => {
    if (isOpen()) sync();
    else if (document.activeElement === input) open(); /* typing after Esc reopens; an input event off-focus does not */
  });
  document.addEventListener("keydown", (event) => {
    if (event.key === "Escape" && isOpen()) {
      event.preventDefault();
      close(true);
    }
  });
  document.addEventListener("pointerdown", (event) => {
    if (!isOpen() || event.target.closest(".search-box, #ux-search-idle, #header-predictive-search")) return;
    close(false);
  });
  /* focus moving anywhere else closes it too (Tab or a nav item, #378): left open, the next Escape (in a menu, say)
     was taken as the search's own and sent focus back to the field */
  document.addEventListener("focusin", (event) => {
    if (!isOpen() || event.target.closest(".search-box, #ux-search-idle, #header-predictive-search")) return;
    close(false);
  });
  scrim?.addEventListener("click", () => close(false));
  cancel?.addEventListener("click", () => {
    input.value = "";
    results.querySelector('[slot="results"]')?.replaceChildren();
    close(false);
    input.blur();
  });
  window.addEventListener("resize", () => isOpen() && place());

  /* Keyboard (#113): the field is a combobox over whichever panel is showing. Arrows move a highlight through its
     [data-ux-search-option] links (aria-activedescendant), Enter opens the highlighted one, typing resets it. */
  const status = document.getElementById("ux-search-status");
  const slot = results.querySelector('[slot="results"]');
  let active = -1;
  input.setAttribute("role", "combobox");
  input.setAttribute("aria-autocomplete", "list");
  const options = () => {
    const panel = root.dataset.uxSearch === "results" ? results : idle;
    return [...panel.querySelectorAll("[data-ux-search-option]")].filter((option) => option.offsetParent !== null);
  };
  const highlight = (index) => {
    const list = options();
    list.forEach((option) => option.removeAttribute("data-ux-active"));
    active = index === null || !list.length ? -1 : (index + list.length) % list.length;
    const option = list[active];
    if (!option) {
      input.removeAttribute("aria-activedescendant");
      return;
    }
    if (!option.id) option.id = `ux-search-option-${Math.random().toString(36).slice(2, 8)}`;
    option.setAttribute("data-ux-active", "");
    input.setAttribute("aria-activedescendant", option.id);
    option.scrollIntoView({ block: "nearest" });
  };
  input.addEventListener("keydown", (event) => {
    if (!isOpen()) return;
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      highlight(active + (event.key === "ArrowDown" ? 1 : -1));
    } else if (event.key === "Enter" && active > -1) {
      const option = options()[active];
      if (option) {
        event.preventDefault();
        window.location.href = option.href;
      }
    }
  });
  input.addEventListener("input", () => highlight(null));
  input.addEventListener("blur", () => highlight(null));
  if (slot && status) {
    new MutationObserver(() => {
      active = -1;
      input.removeAttribute("aria-activedescendant");
      const count = Number(slot.querySelector(".ux-predictive")?.dataset.count);
      if (Number.isNaN(count)) return;
      status.textContent = count === 1 ? status.dataset.one : status.dataset.other.replace("[count]", count);
    }).observe(slot, { childList: true });
  }
})();

/* ---------------------------------------------------------------------------
   Search results (milestone 5, GitHub #114): the type tabs show one group at a time (All shows every group), and
   "Show N more" reveals a group's next hidden items, moving focus to the first of them.
   --------------------------------------------------------------------------- */
var UxSearchTabs = class extends HTMLElement {
  connectedCallback() {
    if (this._wired) return;
    this._wired = true;
    this.addEventListener("click", (event) => {
      const tab = event.target.closest("[data-ux-tab]");
      if (!tab) return;
      this.querySelectorAll("[data-ux-tab]").forEach((other) => other.setAttribute("aria-pressed", String(other === tab)));
      const results = this.closest("[data-ux-search-results]");
      if (tab.dataset.uxTab === "all") results.removeAttribute("data-ux-tab");
      else results.setAttribute("data-ux-tab", tab.dataset.uxTab);
    });
  }
};
if (!window.customElements.get("ux-search-tabs")) {
  window.customElements.define("ux-search-tabs", UxSearchTabs);
}

var UxSearchMore = class extends HTMLElement {
  connectedCallback() {
    if (this._wired) return;
    this._wired = true;
    this.addEventListener("click", (event) => {
      if (!event.target.closest("button")) return;
      const list = document.getElementById(this.getAttribute("aria-controls"));
      if (!list) return;
      const hidden = [...list.children].filter((item) => item.hidden);
      const next = hidden.slice(0, Number(this.dataset.step) || 8);
      next.forEach((item) => (item.hidden = false));
      next[0]?.querySelector("a[href]:not([tabindex='-1'])")?.focus();
      const left = hidden.length - next.length;
      if (left <= 0) this.remove();
      else this.querySelector("button").textContent = this.dataset.label.replace("[count]", Math.min(left, Number(this.dataset.step) || 8));
    });
  }
};
if (!window.customElements.get("ux-search-more")) {
  window.customElements.define("ux-search-more", UxSearchMore);
}

/* ---------------------------------------------------------------------------
   Palette store v2 (milestone 6, GitHub #175; spec docs/palette-accounts.md §3, branch spec-palette-accounts)
   The visitor's projects and palettes in this browser: a palette book of projects holding palettes of up to 12
   colours, plus loose palettes. A colour is { h, n, c, r }: handle, then the name and colour value when added (so one
   that stops being sold can still be shown), then an optional role (walls, ceiling…). Limits: 12 colours, 30 palettes,
   30 projects; a call that would go over does nothing and says so in what it returns (0, null or false).
   The + buttons, tray and palette page work on the active palette ("Saving to Kitchen"), kept on this device only;
   with none set it's the palette changed last, and the first + makes "My palette". The v1 calls (list, has, toggle,
   addMany, remove, clear, count, shareUrl) all act on it.
   Every change stamps `updated` and records the id as pending, with tombstones for deletes once the store belongs to
   a customer. v1 ("mylands-palette": one list) moves into "My palette" (split at 12).
   Logged in (window.uxCustomer, layout/theme.liquid), the store is the customer's own ("mylands-palettes:<id>"),
   seeded from the book Odoo copies to the customer metafield, and UxPaletteSync (below) keeps it in step with the
   account; UxPalette.account is its door into the store. A guest's palettes merge into the account once, on login.
   Changes fire "ux-palette:change" ({ colours, book, active, local }) on document, other tabs too (local: false).
   Without storage it keeps the book for this page view and tries storage again on every save; "ux-palette:storage"
   ({ failing }) says when that starts and stops (#469). colour(handle) fetches /products/<handle>?view=palette-data;
   null = no longer sold. colour(handle, { card: false }) is the same without the product card (?view=palette-samples,
   for samples, #472), or the whole one if that's already here.
   --------------------------------------------------------------------------- */
var UxPalette = (() => {
  const GUEST_KEY = "mylands-palettes";
  const V1_KEY = "mylands-palette";
  const CUSTOMER = window.uxCustomer?.id ? String(window.uxCustomer.id) : null;
  const KEY = CUSTOMER ? `${GUEST_KEY}:${CUSTOMER}` : GUEST_KEY;
  const STALE_MS = 10000; /* a logged-in page asks the account for the book on load and when it comes back into view, at most every 10 seconds (#322) */
  const MAX_ENTITIES = 100; /* per sync request (spec 4.5) */
  const LIMITS = Object.freeze({ colours: 12, palettes: 30, projects: 30, name: 60, colourName: 80 });
  /* Palette projects are kept in the book but not shown (Matt, 19 Oct: customers don't see "projects" outside colour
     consultancies, whose projects are the consultation's own). Off, projects() is empty and project() null, so every
     surface draws its no-projects state. Interior designers keep them (Matt, 23 Oct: a designer's projects are their
     clients' jobs): layout/theme.liquid sets uxPaletteProjects for a tier with projects (snippets/ux-pro-tier: Interior
     Designer; Trade only if its setting is on, #514) and on the dev-only page.dev-professionals. */
  const PROJECTS = window.uxPaletteProjects === true;
  const HANDLE = /^[a-z0-9][a-z0-9-]{0,99}$/;
  const SLUG = /^[a-z][a-z-]{0,19}$/;
  const TYPE = /^[a-z0-9][a-z0-9-]{0,59}$/;
  const defaultName = () => window.uxPaletteDefaultName || "My palette";
  const now = () => new Date().toISOString();
  const uuid = () => {
    if (window.crypto?.randomUUID) return window.crypto.randomUUID();
    const bytes = window.crypto.getRandomValues(new Uint8Array(16));
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex = [...bytes].map((byte) => byte.toString(16).padStart(2, "0")).join("");
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
  };
  const copy = (value) => JSON.parse(JSON.stringify(value));
  const nameKey = (name) => String(name || "").trim().replace(/\s+/g, " ").toLowerCase();
  const cleanName = (name, fallback) => String(name ?? "").trim().replace(/\s+/g, " ").slice(0, LIMITS.name) || fallback;
  const cleanHex = (value) => {
    const hex = String(value || "").trim();
    if (/^#[0-9a-f]{6}$/i.test(hex)) return hex.toUpperCase();
    if (/^#[0-9a-f]{3}$/i.test(hex)) return `#${[...hex.slice(1)].map((digit) => digit + digit).join("")}`.toUpperCase();
    return "";
  };
  /* the server refuses a palette with a colour it can't read, so every colour is made safe on the way in */
  const cleanColour = (colour) => {
    const h = String(colour?.h || "").trim().toLowerCase();
    if (!HANDLE.test(h)) return null;
    const r = colour.r && SLUG.test(String(colour.r)) ? String(colour.r) : null;
    return { h, n: String(colour.n || h).trim().slice(0, LIMITS.colourName) || h, c: cleanHex(colour.c), r };
  };
  const emptyStore = () => ({ v: 2, owner: CUSTOMER, book: { v: 2, rev: 0, projects: [], palettes: [] }, active: null, pending: { projects: [], palettes: [] }, deleted: [], held: [], checked: null });
  /* Held palettes (#476): new palettes the account refused because it already holds 30. They stay on this device,
     in the book but not sent, until the account has room again; then they go back to being sent, oldest first.
     Stores from before this have no list. */
  const heldOf = (store) => (Array.isArray(store.held) ? store.held : []);
  const validBook = (book) => book && book.v === 2 && Number.isInteger(book.rev) && Array.isArray(book.projects) && Array.isArray(book.palettes);
  const validStore = (data) => data && data.v === 2 && validBook(data.book) && data.pending && Array.isArray(data.pending.projects) && Array.isArray(data.pending.palettes) && Array.isArray(data.deleted);

  /* --- storage ----------------------------------------------------------------------------------------------- */
  /* A write that fails (storage full, or switched off) leaves the book in memory for this page view, and every save
     tries storage again (#469); "ux-palette:storage" ({ failing }) says when that starts and stops. While it's
     failing, memory is the book: storage may hold an older one, and other tabs' changes aren't read over this page's. */
  let storageFailing = false;
  let memory = null;
  let cache = { raw: undefined, store: null };
  const setStorageFailing = (value) => {
    if (storageFailing === value) return;
    storageFailing = value;
    document.dispatchEvent(new CustomEvent("ux-palette:storage", { detail: { failing: value } }));
  };
  const storage = {
    /* undefined: storage can't be read at all */
    get(key) {
      try {
        return window.localStorage.getItem(key);
      } catch (error) {
        return undefined;
      }
    },
    set(key, value) {
      try {
        window.localStorage.setItem(key, value);
        return true;
      } catch (error) {
        return false;
      }
    },
    remove(key) {
      try {
        window.localStorage.removeItem(key);
      } catch (error) {
        /* nothing to do */
      }
    }
  };
  /* a stored store, or null; one that can't be read is copied aside to "<key>:corrupt" first, so the empty store
     that replaces it on the next save doesn't take the only copy with it (#469) */
  const readStore = (key, raw) => {
    if (raw === null || raw === undefined) return null;
    try {
      const data = JSON.parse(raw);
      if (validStore(data)) return data;
    } catch (error) {
      /* unreadable: copied aside below */
    }
    if (storage.get(`${key}:corrupt`) !== raw) storage.set(`${key}:corrupt`, raw);
    return null;
  };
  const migrateV1 = (legacy) => {
    const store = emptyStore();
    let colours = [];
    try {
      const data = JSON.parse(legacy || "null");
      if (data && Array.isArray(data.colours)) colours = data.colours.map(cleanColour).filter(Boolean);
    } catch (error) {
      /* unreadable: start empty */
    }
    colours = colours.filter((colour, index) => colours.findIndex((other) => other.h === colour.h) === index);
    for (let index = 0; index < colours.length; index += LIMITS.colours) {
      const number = index / LIMITS.colours + 1;
      const palette = newPalette(store, number === 1 ? defaultName() : `${defaultName()} ${number}`, null, colours.slice(index, index + LIMITS.colours));
      store.book.palettes.push(palette);
    }
    store.active = store.book.palettes[0]?.id || null;
    return store;
  };
  const save = (store) => {
    memory = store;
    const raw = JSON.stringify(store);
    if (storage.set(KEY, raw)) {
      cache = { raw, store };
      setStorageFailing(false);
    } else setStorageFailing(true);
    return store;
  };
  /* the current store, read-only: callers that change it go through change() */
  const current = () => {
    if (storageFailing) return memory || (memory = emptyStore());
    const raw = storage.get(KEY);
    if (raw === undefined) return memory || (memory = emptyStore());
    if (raw === cache.raw && cache.store) return cache.store;
    let store = readStore(KEY, raw);
    if (!store) {
      /* nothing written until there's something to keep, except a v1 list moving across (a logged-in customer's
         v1 list merges into the account instead: UxPaletteSync) */
      const legacy = CUSTOMER ? null : storage.get(V1_KEY);
      if (legacy) {
        store = save(migrateV1(legacy));
        if (!storageFailing) storage.remove(V1_KEY);
        return store;
      }
      store = emptyStore();
    }
    cache = { raw, store };
    memory = store;
    return store;
  };

  /* --- the book ---------------------------------------------------------------------------------------------- */
  const projectRank = (store) => {
    const ordered = [...store.book.projects].sort((a, b) => a.pos - b.pos || a.id.localeCompare(b.id));
    return new Map(ordered.map((project, index) => [project.id, index]));
  };
  const sortedPalettes = (store) => {
    const rank = projectRank(store);
    const place = (palette) => (palette.project && rank.has(palette.project) ? rank.get(palette.project) : rank.size);
    return [...store.book.palettes].sort((a, b) => place(a) - place(b) || a.pos - b.pos || a.id.localeCompare(b.id));
  };
  const findPalette = (store, id) => store.book.palettes.find((palette) => palette.id === id) || null;
  const findProject = (store, id) => store.book.projects.find((project) => project.id === id) || null;
  const inProject = (store, projectId) => store.book.palettes.filter((palette) => (palette.project || null) === (projectId || null));
  const nextPos = (list) => (list.length ? Math.max(...list.map((item) => item.pos)) + 1 : 0);
  const uniqueName = (name, taken) => {
    const keys = new Set(taken.map(nameKey));
    if (!keys.has(nameKey(name))) return name;
    for (let number = 2; ; number += 1) {
      const suffix = ` ${number}`;
      const candidate = `${name.slice(0, LIMITS.name - suffix.length).trim()}${suffix}`;
      if (!keys.has(nameKey(candidate))) return candidate;
    }
  };
  const touch = (store, kind, entity) => {
    entity.updated = now();
    /* a held palette's changes wait with it: sent now, they'd only be refused again (#476) */
    if (kind === "palettes" && heldOf(store).includes(entity.id)) return;
    if (!store.pending[kind].includes(entity.id)) store.pending[kind].push(entity.id);
    store.deleted = store.deleted.filter((tombstone) => tombstone.id !== entity.id);
  };
  const bury = (store, kind, id) => {
    store.pending[kind] = store.pending[kind].filter((pending) => pending !== id);
    store.deleted = store.deleted.filter((tombstone) => tombstone.id !== id);
    /* a held palette was never in the account: deleting it here is the end of it, nothing to tell the account */
    if (kind === "palettes" && heldOf(store).includes(id)) {
      store.held = heldOf(store).filter((other) => other !== id);
      return;
    }
    /* tombstones only matter once the store syncs to an account: a guest's merge sends what exists */
    if (store.owner) store.deleted.push({ kind: kind.slice(0, -1), id, updated: now() });
  };
  const newPalette = (store, name, projectId, colours) => {
    const palette = { id: uuid(), name, project: projectId || null, pos: nextPos(inProject(store, projectId)), colours: colours || [], updated: now() };
    if (!store.pending.palettes.includes(palette.id)) store.pending.palettes.push(palette.id);
    return palette;
  };
  /* the active palette: the one set on this device, else the one changed last */
  const activePalette = (store) => findPalette(store, store.active) || [...store.book.palettes].sort((a, b) => String(b.updated || "").localeCompare(String(a.updated || "")))[0] || null;
  /* the active palette, made ("My palette") if there are none; null if the 30-palette cap is reached */
  const ensureActive = (store) => {
    let palette = activePalette(store);
    if (!palette) {
      if (store.book.palettes.length >= LIMITS.palettes) return null;
      palette = newPalette(store, uniqueName(defaultName(), inProject(store, null).map((other) => other.name)), null, []);
      store.book.palettes.push(palette);
    }
    store.active = palette.id;
    return palette;
  };

  /* --- changes ----------------------------------------------------------------------------------------------- */
  const UNCHANGED = Symbol("unchanged");
  /* local: a change made on this page (UxPaletteSync sends those); false for other tabs and server answers */
  const emit = (local = true) => {
    const store = current();
    const active = activePalette(store);
    document.dispatchEvent(new CustomEvent("ux-palette:change", { detail: { colours: active ? copy(active.colours) : [], book: copy(store.book), active: active?.id || null, local } }));
  };
  /* runs fn on a copy of the store; saves and announces unless fn returns UNCHANGED */
  const change = (fn) => {
    const store = copy(current());
    const result = fn(store);
    if (result === UNCHANGED) return undefined;
    save(store);
    emit();
    return result;
  };
  const addColours = (store, palette, colours) => {
    let added = 0;
    colours.map(cleanColour).filter(Boolean).forEach((colour) => {
      if (palette.colours.some((saved) => saved.h === colour.h) || palette.colours.length >= LIMITS.colours) return;
      palette.colours.push(colour);
      added += 1;
    });
    if (added) touch(store, "palettes", palette);
    return added;
  };
  const snapshot = (store, palettes, projects) => ({ palettes: copy(palettes), projects: copy(projects || []), active: store.active });

  /* --- the account (spec section 5, from the client's side) -------------------------------------------------- */
  /* The server's book with this device's unsent work on top: an entity that's pending and wasn't sent as it is now
     (changed since, or never sent) stays ours and pending; one sent unchanged takes the server's copy (which may be
     newer still, or absent if the server refused it). Tombstones work the same way. */
  /* the account's book made safe to work on (#469): an entity without an id is dropped; one without `updated` (or a
     position, or a palette without a colour list) gets the earliest date (any change of ours is newer), 0, [] */
  const EPOCH = new Date(0).toISOString();
  const cleanBook = (book) => {
    const clean = (entity) => ({ ...entity, updated: typeof entity.updated === "string" && entity.updated ? entity.updated : EPOCH, pos: Number.isFinite(entity.pos) ? entity.pos : 0 });
    const usable = (entity) => entity && typeof entity === "object" && typeof entity.id === "string" && entity.id;
    return {
      ...copy(book),
      projects: book.projects.filter(usable).map((project) => clean(copy(project))),
      palettes: book.palettes.filter(usable).map((palette) => ({ ...clean(copy(palette)), colours: Array.isArray(palette.colours) ? copy(palette.colours) : [] }))
    };
  };
  const overlay = (store, book, sent) => {
    const next = { ...copy(store), book: cleanBook(book), pending: { projects: [], palettes: [] }, deleted: [] };
    ["projects", "palettes"].forEach((kind) => {
      store.pending[kind].forEach((id) => {
        const local = store.book[kind].find((entity) => entity.id === id);
        if (!local || sent.get(id) === local.updated) return;
        next.book[kind] = next.book[kind].filter((entity) => entity.id !== id).concat(copy(local));
        next.pending[kind].push(id);
      });
    });
    store.deleted.forEach((tombstone) => {
      if (sent.get(tombstone.id) === tombstone.updated) return;
      const kind = `${tombstone.kind}s`;
      next.book[kind] = next.book[kind].filter((entity) => entity.id !== tombstone.id);
      next.deleted.push(copy(tombstone));
    });
    /* held palettes (#476) aren't in the account's book: they're carried over from ours. Once the account has room
       (its palettes plus new ones on their way, under 30), the oldest go back to being sent; "releasing" remembers
       them, so the customer hears when they've been saved */
    const onAccount = new Set(next.book.palettes.map((palette) => palette.id));
    next.held = [];
    heldOf(store).forEach((id) => {
      const local = findPalette(store, id);
      if (!local || onAccount.has(id)) return;
      next.book.palettes.push(copy(local));
      next.held.push(id);
    });
    const onTheirWay = next.pending.palettes.filter((id) => !onAccount.has(id)).length;
    const room = LIMITS.palettes - onAccount.size - onTheirWay;
    const released = next.held.splice(0, Math.max(0, room));
    next.pending.palettes.push(...released);
    next.releasing = [...(Array.isArray(store.releasing) ? store.releasing : []), ...released];
    if (!findPalette(next, next.active)) next.active = activePalette(next)?.id || null;
    return next;
  };
  const guestStore = () => {
    const data = readStore(GUEST_KEY, storage.get(GUEST_KEY));
    if (data) return data;
    const legacy = storage.get(V1_KEY);
    return legacy ? migrateV1(legacy) : null;
  };
  const account = {
    customer: CUSTOMER,
    /* a guest's palettes (or a v1 list) still waiting to join the account */
    needsMerge() {
      const guest = CUSTOMER ? guestStore() : null;
      return Boolean(guest && (guest.book.palettes.length || guest.book.projects.length));
    },
    guestContent() {
      const guest = guestStore();
      return guest ? { projects: copy(guest.book.projects), palettes: copy(guest.book.palettes), active: activePalette(guest)?.id || null } : { projects: [], palettes: [], active: null };
    },
    /* the merge answered: take the account's book, carry on with the palette the guest was using, drop the guest store */
    finishMerge(response, guestActive) {
      const store = current();
      const next = overlay(store, response.book, new Map());
      const mapped = guestActive ? response.merged?.[guestActive] || guestActive : null;
      if (mapped && findPalette(next, mapped)) next.active = mapped;
      next.checked = now();
      save(next);
      storage.remove(GUEST_KEY);
      storage.remove(V1_KEY);
      emit(false);
    },
    /* what to send: deletes and projects first (a palette can name a project sent alongside), up to 100 */
    outbox() {
      const store = current();
      const entries = [
        ...store.deleted.filter((tombstone) => tombstone.kind === "project").map((tombstone) => ["deletes", "projects", { id: tombstone.id, updated: tombstone.updated }]),
        ...store.pending.projects.map((id) => findProject(store, id)).filter(Boolean).map((project) => ["upserts", "projects", copy(project)]),
        ...store.deleted.filter((tombstone) => tombstone.kind === "palette").map((tombstone) => ["deletes", "palettes", { id: tombstone.id, updated: tombstone.updated }]),
        ...store.pending.palettes.map((id) => findPalette(store, id)).filter(Boolean).map((palette) => ["upserts", "palettes", copy(palette)])
      ].slice(0, MAX_ENTITIES);
      if (!entries.length) return null;
      const body = { v: 2, merge: false, upserts: { projects: [], palettes: [] }, deletes: { projects: [], palettes: [] } };
      const sent = new Map();
      entries.forEach(([section, kind, entity]) => {
        body[section][kind].push(entity);
        sent.set(entity.id, entity.updated);
      });
      return { body, sent };
    },
    /* takes the account's answer to a send; returns what it refused, each with the copy this device sent (entity)
       when taking the account's book drops it (a new palette over the cap) or reverts it (an edit refused), so the
       customer can be offered it back (#460); null when this device has changed it since, so it's kept anyway */
    settle(response, sent) {
      const store = current();
      const rejected = (Array.isArray(response.rejected) ? response.rejected : []).filter((entry) => entry && entry.id).map((entry) => {
        const local = store.book[entry.kind === "project" ? "projects" : "palettes"].find((entity) => entity.id === entry.id);
        return { ...entry, entity: local && sent.get(local.id) === local.updated ? copy(local) : null };
      });
      const next = overlay(store, response.book, sent);
      /* a new palette refused because the account has 30 (#476): taking the account's book dropped it, so it's put
         back and held here (not sent) until there's room, instead of vanishing with an Undo that usually can't fit */
      rejected.forEach((entry) => {
        if (entry.kind === "project" || entry.code !== "too_many_palettes" || !entry.entity || findPalette(next, entry.id)) return;
        next.book.palettes.push(copy(entry.entity));
        next.held = [...heldOf(next), entry.id];
        if (store.active === entry.id) next.active = entry.id;
        entry.held = true;
      });
      /* held palettes that went this time and weren't refused are in the account now */
      const refusedIds = new Set(rejected.map((entry) => entry.id));
      const releasing = Array.isArray(next.releasing) ? next.releasing : [];
      const saved = releasing.filter((id) => sent.has(id) && !refusedIds.has(id) && findPalette(next, id));
      next.releasing = releasing.filter((id) => !sent.has(id) && findPalette(next, id) && !heldOf(next).includes(id));
      next.checked = now();
      save(next);
      emit(false);
      if (saved.length) document.dispatchEvent(new CustomEvent("ux-palette:saved", { detail: { names: saved.map((id) => findPalette(next, id).name) } }));
      return rejected;
    },
    /* palettes held on this device (#476): logging out keeps the device copy while there are any */
    holding() {
      return heldOf(current()).length;
    },
    /* a book from the account (the metafield copy, or GET /book): taken if it's newer than ours */
    adopt(book, { server = true } = {}) {
      /* server: the account's own answer (GET /book). The copy printed from the metafield (server: false) may lag
         behind Odoo, so taking it doesn't count as having asked the account (#322) */
      if (!CUSTOMER || !validBook(book)) return false;
      const store = current();
      if (book.rev <= store.book.rev) {
        if (server) save({ ...copy(store), checked: now() });
        return false;
      }
      const next = overlay(store, book, new Map());
      if (server) next.checked = now();
      save(next);
      emit(false);
      return true;
    },
    stale() {
      const checked = Date.parse(current().checked || "");
      return !checked || Date.now() - checked > STALE_MS;
    },
    /* logging out: this device stops holding the customer's palettes */
    forget() {
      storage.remove(KEY);
      cache = { raw: undefined, store: null };
      memory = null;
    }
  };

  const api = {
    LIMITS,
    account,

    /* --- the active palette (the v1 calls) --- */
    list: () => copy(activePalette(current())?.colours || []),
    count: () => activePalette(current())?.colours.length || 0,
    has: (handle) => Boolean(activePalette(current())?.colours.some((colour) => colour.h === handle)),
    add(colour) {
      return api.addMany([colour]) > 0;
    },
    addMany(colours) {
      return change((store) => {
        const palette = ensureActive(store);
        if (!palette) return UNCHANGED;
        const added = addColours(store, palette, colours || []);
        return added || store.book.palettes.length !== current().book.palettes.length ? added : UNCHANGED;
      }) || 0;
    },
    remove(handle) {
      change((store) => {
        const palette = activePalette(store);
        if (!palette || !palette.colours.some((colour) => colour.h === handle)) return UNCHANGED;
        palette.colours = palette.colours.filter((colour) => colour.h !== handle);
        touch(store, "palettes", palette);
        return true;
      });
    },
    toggle(colour) {
      if (api.has(colour.h)) {
        api.remove(colour.h);
        return false;
      }
      return api.add(colour);
    },
    clear() {
      change((store) => {
        const palette = activePalette(store);
        if (!palette || !palette.colours.length) return UNCHANGED;
        palette.colours = [];
        touch(store, "palettes", palette);
        return true;
      });
    },

    /* --- the whole book --- */
    store: () => copy(current()),
    book: () => copy(current().book),
    palettes: () => copy(sortedPalettes(current())),
    /* a palette the account refused for having 30, kept here until there's room (#476) */
    isHeld: (id) => heldOf(current()).includes(id),
    held: () => heldOf(current()).map((id) => findPalette(current(), id)).filter(Boolean).map(copy),
    projectsShown: PROJECTS,
    /* true while this device's storage won't take the book (#469; ux-palette:storage) */
    get storageFailing() {
      return storageFailing;
    },
    projects: () => (PROJECTS ? copy([...current().book.projects].sort((a, b) => a.pos - b.pos || a.id.localeCompare(b.id))) : []),
    palette: (id) => copy(findPalette(current(), id)),
    project: (id) => (PROJECTS ? copy(findProject(current(), id)) : null),
    active: () => copy(activePalette(current())),
    setActive(id) {
      return change((store) => {
        if (!findPalette(store, id) || store.active === id) return UNCHANGED;
        store.active = id;
        return true;
      }) || false;
    },
    hasIn: (id, handle) => Boolean(findPalette(current(), id)?.colours.some((colour) => colour.h === handle)),
    palettesWith: (handle) => sortedPalettes(current()).filter((palette) => palette.colours.some((colour) => colour.h === handle)).map((palette) => palette.id),

    /* --- palettes --- */
    createPalette({ name, project = null, colours = [], activate = true } = {}) {
      return change((store) => {
        if (store.book.palettes.length >= LIMITS.palettes) return UNCHANGED;
        const projectId = project && findProject(store, project) ? project : null;
        const cleaned = colours.map(cleanColour).filter(Boolean).filter((colour, index, list) => list.findIndex((other) => other.h === colour.h) === index);
        const palette = newPalette(store, uniqueName(cleanName(name, defaultName()), inProject(store, projectId).map((other) => other.name)), projectId, cleaned.slice(0, LIMITS.colours));
        store.book.palettes.push(palette);
        if (activate) store.active = palette.id;
        return palette.id;
      }) || null;
    },
    renamePalette(id, name) {
      return change((store) => {
        const palette = findPalette(store, id);
        const next = palette && cleanName(name, palette.name);
        if (!palette || next === palette.name) return UNCHANGED;
        palette.name = next;
        touch(store, "palettes", palette);
        return true;
      }) || false;
    },
    duplicatePalette(id) {
      return change((store) => {
        const source = findPalette(store, id);
        if (!source) return UNCHANGED;
        if (store.book.palettes.length >= LIMITS.palettes) return UNCHANGED;
        const twin = newPalette(store, uniqueName(source.name, inProject(store, source.project).map((other) => other.name)), source.project, copy(source.colours));
        store.book.palettes.push(twin);
        store.active = twin.id;
        return twin.id;
      }) || null;
    },
    /* returns an undo snapshot for restore() */
    deletePalette(id) {
      return change((store) => {
        const palette = findPalette(store, id);
        if (!palette) return UNCHANGED;
        const undo = snapshot(store, [palette]);
        store.book.palettes = store.book.palettes.filter((other) => other.id !== id);
        bury(store, "palettes", id);
        if (store.active === id) store.active = activePalette(store)?.id || null;
        return undo;
      }) || null;
    },
    movePalette(id, project = null, pos = null) {
      return change((store) => {
        const palette = findPalette(store, id);
        const projectId = project && findProject(store, project) ? project : null;
        if (!palette || (palette.project === projectId && pos === null)) return UNCHANGED;
        if (palette.project !== projectId) {
          palette.name = uniqueName(palette.name, inProject(store, projectId).map((other) => other.name));
          palette.project = projectId;
          palette.pos = nextPos(inProject(store, projectId).filter((other) => other.id !== id));
        }
        if (pos !== null) palette.pos = Math.max(0, Math.floor(pos));
        touch(store, "palettes", palette);
        return true;
      }) || false;
    },

    /* --- colours in a palette --- */
    addTo(id, colour) {
      return change((store) => {
        const palette = findPalette(store, id);
        if (!palette) return UNCHANGED;
        return addColours(store, palette, [colour]) > 0 || UNCHANGED;
      }) || false;
    },
    /* returns an undo snapshot for restore() */
    removeFrom(id, handle) {
      return change((store) => {
        const palette = findPalette(store, id);
        if (!palette || !palette.colours.some((colour) => colour.h === handle)) return UNCHANGED;
        const undo = snapshot(store, [palette]);
        palette.colours = palette.colours.filter((colour) => colour.h !== handle);
        touch(store, "palettes", palette);
        return undo;
      }) || null;
    },
    /* Plan's drag between palettes (Matt, 21 Oct): the colour leaves one palette for a place in another, its surface
       kept; refused if the other is full or holds it already. An undo snapshot */
    moveColour(fromId, toId, handle, at) {
      return change((store) => {
        const from = findPalette(store, fromId);
        const to = findPalette(store, toId);
        const index = from ? from.colours.findIndex((saved) => saved.h === handle) : -1;
        if (!from || !to || from === to || index < 0 || to.colours.some((saved) => saved.h === handle) || to.colours.length >= LIMITS.colours) return UNCHANGED;
        const undo = snapshot(store, [from, to]);
        const [colour] = from.colours.splice(index, 1);
        to.colours.splice(Math.max(0, Math.min(Number(at) || 0, to.colours.length)), 0, colour);
        touch(store, "palettes", from);
        touch(store, "palettes", to);
        return undo;
      }) || null;
    },
    /* Plan's pairings (Swap with…, #481): the colour takes the old one's place and surface; an undo snapshot */
    swapColour(id, handle, colour) {
      return change((store) => {
        const palette = findPalette(store, id);
        const at = palette ? palette.colours.findIndex((saved) => saved.h === handle) : -1;
        const next = cleanColour(colour);
        if (at < 0 || !next || palette.colours.some((saved) => saved.h === next.h)) return UNCHANGED;
        const undo = snapshot(store, [palette]);
        palette.colours.splice(at, 1, { ...next, r: palette.colours[at].r });
        touch(store, "palettes", palette);
        return undo;
      }) || null;
    },
    reorderColours(id, handles) {
      return change((store) => {
        const palette = findPalette(store, id);
        if (!palette) return UNCHANGED;
        const order = new Map(handles.map((handle, index) => [handle, index]));
        const next = [...palette.colours].sort((a, b) => (order.get(a.h) ?? handles.length) - (order.get(b.h) ?? handles.length));
        if (next.every((colour, index) => colour.h === palette.colours[index].h)) return UNCHANGED;
        palette.colours = next;
        touch(store, "palettes", palette);
        return true;
      }) || false;
    },
    setRole(id, handle, role) {
      return change((store) => {
        const colour = findPalette(store, id)?.colours.find((saved) => saved.h === handle);
        const next = role && SLUG.test(String(role)) ? String(role) : null;
        if (!colour || colour.r === next) return UNCHANGED;
        colour.r = next;
        touch(store, "palettes", findPalette(store, id));
        return true;
      }) || false;
    },

    /* --- projects --- */
    createProject({ name, type = null } = {}) {
      return change((store) => {
        if (!cleanName(name, "")) return UNCHANGED;
        if (store.book.projects.length >= LIMITS.projects) return UNCHANGED;
        const project = { id: uuid(), name: cleanName(name, ""), type: type && TYPE.test(type) ? type : null, pos: nextPos(store.book.projects), updated: now() };
        store.book.projects.push(project);
        store.pending.projects.push(project.id);
        return project.id;
      }) || null;
    },
    renameProject(id, name) {
      return change((store) => {
        const project = findProject(store, id);
        const next = project && cleanName(name, project.name);
        if (!project || next === project.name) return UNCHANGED;
        project.name = next;
        touch(store, "projects", project);
        return true;
      }) || false;
    },
    setProjectType(id, type) {
      return change((store) => {
        const project = findProject(store, id);
        const next = type && TYPE.test(type) ? type : null;
        if (!project || project.type === next) return UNCHANGED;
        project.type = next;
        touch(store, "projects", project);
        return true;
      }) || false;
    },
    /* the client a project is for (#303, Interior Designers): optional, up to 60 characters; blank clears it. Only
       offered once Odoo stores it (Theme settings › Mylands Professionals › Client names on projects) */
    setProjectClient(id, client) {
      return change((store) => {
        const project = findProject(store, id);
        const next = String(client || "").replace(/\s+/g, " ").trim().slice(0, LIMITS.name) || null;
        if (!project || (project.client || null) === next) return UNCHANGED;
        project.client = next;
        touch(store, "projects", project);
        return true;
      }) || false;
    },
    /* palettes: "keep" makes them loose, "delete" removes them too. Returns an undo snapshot for restore() */
    deleteProject(id, { palettes = "keep" } = {}) {
      return change((store) => {
        const project = findProject(store, id);
        if (!project) return UNCHANGED;
        const inside = inProject(store, id);
        const undo = snapshot(store, inside, [project]);
        store.book.projects = store.book.projects.filter((other) => other.id !== id);
        bury(store, "projects", id);
        if (palettes === "delete") {
          store.book.palettes = store.book.palettes.filter((palette) => palette.project !== id);
          inside.forEach((palette) => bury(store, "palettes", palette.id));
          if (!findPalette(store, store.active)) store.active = activePalette(store)?.id || null;
        } else {
          inside.forEach((palette) => {
            palette.name = uniqueName(palette.name, inProject(store, null).map((other) => other.name));
            palette.project = null;
            palette.pos = nextPos(inProject(store, null));
            touch(store, "palettes", palette);
          });
        }
        return undo;
      }) || null;
    },

    /* puts back what an undo snapshot holds, as a new change; false if the caps no longer allow it */
    restore(undo) {
      if (!undo) return false;
      return change((store) => {
        const newPalettes = undo.palettes.filter((palette) => !findPalette(store, palette.id)).length;
        const newProjects = undo.projects.filter((project) => !findProject(store, project.id)).length;
        if (store.book.palettes.length + newPalettes > LIMITS.palettes || store.book.projects.length + newProjects > LIMITS.projects) return UNCHANGED;
        undo.projects.forEach((saved) => {
          const project = copy(saved);
          store.book.projects = store.book.projects.filter((other) => other.id !== project.id).concat(project);
          touch(store, "projects", project);
        });
        undo.palettes.forEach((saved) => {
          const palette = copy(saved);
          store.book.palettes = store.book.palettes.filter((other) => other.id !== palette.id).concat(palette);
          touch(store, "palettes", palette);
        });
        if (undo.active && findPalette(store, undo.active)) store.active = undo.active;
        return true;
      }) || false;
    },

    /* --- links and colour data --- */
    pageUrl() {
      return new URL(window.uxPaletteUrl || "/pages/contact?view=palette", window.location.origin);
    },
    shareUrl(handles) {
      const url = api.pageUrl();
      url.searchParams.delete("c");
      const list = (handles || api.list().map((colour) => colour.h)).map(encodeURIComponent).join(",");
      return `${url.toString()}${url.search ? "&" : "?"}c=${list}`; /* readable commas: ?c=huguenot,soho-pink */
    },
    /* ?c= from a shared link: product handles only, once each, at most a palette's worth (#468), so a long or
       tampered link can't fetch hundreds of colours at once */
    sharedHandles(search = window.location.search) {
      const value = new URLSearchParams(search).get("c");
      return value ? [...new Set(value.split(",").map((handle) => handle.trim().toLowerCase()).filter((handle) => HANDLE.test(handle)))].slice(0, LIMITS.colours) : [];
    },
    async colour(handle, { card = true } = {}) {
      if (!card && colourCache.has(handle)) return colourCache.get(handle);
      const cache = card ? colourCache : leanCache;
      if (!cache.has(handle)) {
        /* a server error or no connection isn't an answer: it's asked again next time (#389) */
        const retry = () => {
          cache.delete(handle);
          return null;
        };
        cache.set(handle, fetch(`${window.Shopify?.routes?.root || "/"}products/${encodeURIComponent(handle)}?view=${card ? "palette-data" : "palette-samples"}`) /* no Accept: application/json, or Shopify answers with its own product JSON */
          .then((response) => (response.ok ? response.text() : response.status >= 500 || response.status === 429 ? retry() : null))
          .then((text) => {
            if (!text) return null;
            try {
              return JSON.parse(text);
            } catch (error) {
              return null;
            }
          })
          .catch(retry));
      }
      return cache.get(handle);
    },
    /* drops one colour's cached product data, so the next colour(handle) asks again (not account.forget) */
    forget(handle) {
      colourCache.delete(handle);
      leanCache.delete(handle);
    }
  };
  const colourCache = new Map();
  const leanCache = new Map();
  window.addEventListener("storage", (event) => {
    if (event.key === KEY) emit(false);
  });
  return api;
})();
window.UxPalette = UxPalette;
/* no palette projects on show: their pickers, filters and buttons go (audit-fixes.css, .ux-no-projects) */
if (!UxPalette.projectsShown) document.documentElement.classList.add("ux-no-projects");

/* The Mylands app's proxy prefix (#517; odoo-73, 25 Oct: one prefix, a path per feature: palette/, consultancy/,
   professionals/). Theme setting App proxy path (Mylands app); anything that isn't /apps/<slug> means /apps/mylands. */
function uxAppProxy() {
  return /^\/apps\/[a-z0-9][a-z0-9-]{0,40}$/.test(window.uxAppProxy || "") ? window.uxAppProxy : "/apps/mylands";
}

/* ---------------------------------------------------------------------------
   Palette sync (milestone 6, GitHub #177; spec docs/palette-accounts.md sections 4, 5 and 9 T2)
   For a logged-in customer only. Shopify's app proxy forwards {app proxy}/palette/* to Odoo with the customer's ID
   and a signature (always root-relative: no /nl prefix), so the browser never sends who it is. The prefix is the
   theme setting App proxy path (Mylands app) (#517): /apps/mylands live, /apps/mylands-staging on the dev theme
   (Odoo staging through the staging app); anything that isn't /apps/<slug> means /apps/mylands.
   - On load: take the book printed from the customer metafield (#ux-palette-book) if it's newer; merge a guest's
     palettes into the account once; send anything left unsent; then ask the account for the book (at most every 10
     seconds), and again whenever the tab comes back into view (#322).
   - After a change made on this page: send it 1.5s later. Failures back off 2s to 60s; a 409 retries after 1s, and
     another straight after backs off too (#470); 429 waits retry_after; 401, 403, 4xx and 503 stop syncing for this
     page view (the work stays pending for the next).
   - Leaving the page: unsent work goes with fetch keepalive (answered next time). Logging out: send it all, then
     forget; if it can't go, keep it for the customer's next sign-in here (#459).
   --------------------------------------------------------------------------- */
var UxPaletteSync = (() => {
  const account = UxPalette.account;
  if (!account.customer) return null;
  const ROOT = `${uxAppProxy()}/palette`;
  let timer = null;
  let running = false;
  let backoff = 0;
  let stopped = false;
  /* "Not saved yet" (#297): true from a failed save until the next one goes through; ux-palette:sync says when */
  let failing = false;
  const setFailing = (value) => {
    if (failing === value) return;
    failing = value;
    document.dispatchEvent(new CustomEvent("ux-palette:sync", { detail: { failing } }));
  };

  const request = async (method, path, body, keepalive = false) => {
    const response = await fetch(`${ROOT}${path}`, {
      method,
      credentials: "same-origin",
      keepalive,
      headers: body ? { "Content-Type": "application/json", Accept: "application/json" } : { Accept: "application/json" },
      body: body ? JSON.stringify(body) : undefined
    });
    let data = null;
    try {
      data = await response.json();
    } catch (error) {
      /* not JSON: a Shopify error page or a proxy timeout, so treated as passing trouble */
    }
    return { status: response.status, data: data && typeof data === "object" ? data : null };
  };
  const schedule = (delay) => {
    if (stopped) return;
    clearTimeout(timer);
    timer = setTimeout(run, delay);
  };
  /* after a refusal: what to do next */
  const retry = (status, data) => {
    /* a conflict (another device saved first) usually clears on the next go, so the first is retried after 1s
       quietly; one after another back off like any failure, up to a minute (#470) */
    if (status === 409 && !backoff) {
      backoff = 1000;
      return schedule(backoff);
    }
    setFailing(true);
    if (status === 429) return schedule(Math.max(1, Number(data?.retry_after) || 30) * 1000);
    if (status && data && status < 500 && status !== 408 && status !== 409) {
      stopped = true; /* the account won't take it as it is; leave it pending for another page view */
      return undefined;
    }
    if (status === 503 && data) {
      stopped = true; /* palettes switched off in Odoo */
      return undefined;
    }
    backoff = Math.min(backoff ? backoff * 2 : 2000, 60000);
    return schedule(backoff);
  };
  const merge = async () => {
    const guest = account.guestContent();
    const { status, data } = await request("POST", "/sync", { v: 2, merge: true, upserts: { projects: guest.projects, palettes: guest.palettes }, deletes: { projects: [], palettes: [] } });
    if (status === 200 && data?.ok) {
      account.finishMerge(data, guest.active);
      document.dispatchEvent(new CustomEvent("ux-palette:merged", { detail: { merged: data.merged || {}, rejected: data.rejected || [], overLimit: Boolean(data.over_limit) } }));
      return true;
    }
    retry(status, data);
    return false;
  };
  const send = async () => {
    const outbox = account.outbox();
    if (!outbox) return true;
    const { status, data } = await request("POST", "/sync", outbox.body);
    if (status === 200 && data?.ok) {
      const rejected = account.settle(data, outbox.sent);
      if (rejected.length) document.dispatchEvent(new CustomEvent("ux-palette:rejected", { detail: { rejected } }));
      return true;
    }
    retry(status, data);
    return false;
  };
  const refresh = async () => {
    const { status, data } = await request("GET", "/book");
    if (status === 200 && data?.ok) {
      account.adopt(data.book);
      return true;
    }
    retry(status, data);
    return false;
  };
  async function run() {
    if (stopped || running) return;
    running = true;
    clearTimeout(timer);
    try {
      let ok = true;
      if (account.needsMerge()) ok = await merge();
      if (ok) ok = await send();
      if (ok && account.stale()) ok = await refresh();
      if (ok) {
        backoff = 0;
        setFailing(false);
        if (account.outbox()) schedule(1500); /* changed while the request was out */
      }
    } catch (error) {
      retry(0, null); /* offline or blocked */
    } finally {
      running = false;
    }
  }

  /* the book Odoo last copied to the customer metafield, printed by layout/theme.liquid */
  try {
    account.adopt(JSON.parse(document.getElementById("ux-palette-book")?.textContent || "null"), { server: false });
  } catch (error) {
    /* unreadable: the account answers instead */
  }
  document.addEventListener("ux-palette:change", (event) => {
    if (event.detail?.local) schedule(1500);
  });
  window.addEventListener("pagehide", () => {
    const outbox = !running && account.outbox();
    if (outbox) request("POST", "/sync", outbox.body, true).catch(() => {});
  });
  /* Logging out (#459): everything unsent goes first (several requests if it's over 100), at most 4 seconds, then the
     account copy is forgotten. If it can't all go (offline, refused), the copy stays on this device: it's kept under
     the customer's key, so only they see it, and it's sent when they next sign in here. A cmd/ctrl-click (a new tab)
     or a click someone else cancelled isn't logging out of this page. */
  const flush = async () => {
    const until = Date.now() + 4000;
    const timeout = () => new Promise((resolve) => setTimeout(resolve, Math.max(0, until - Date.now()), { status: 0 }));
    while (Date.now() < until) {
      if (running) {
        await new Promise((resolve) => setTimeout(resolve, 100));
        continue;
      }
      const outbox = account.outbox();
      if (!outbox) return true;
      try {
        const { status, data } = await Promise.race([request("POST", "/sync", outbox.body, true), timeout()]);
        if (status !== 200 || !data?.ok) return false;
        account.settle(data, outbox.sent);
      } catch (error) {
        return false;
      }
    }
    return false;
  };
  document.addEventListener("click", async (event) => {
    const link = event.target.closest?.('a[href*="/account/logout"]');
    if (!link || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (link.target && link.target !== "_self") return;
    clearTimeout(timer);
    stopped = true;
    /* a held palette (#476) isn't in the account yet: the device copy stays, as for anything that couldn't go */
    if (!account.outbox() && !running && !account.holding()) {
      account.forget();
      return;
    }
    event.preventDefault();
    link.setAttribute("aria-busy", "true");
    if ((await flush()) && !account.holding()) account.forget();
    window.location.assign(link.href);
  });
  /* Back in view (another tab, a woken phone, the back button): ask the account again if it's been 10 seconds, so a
     change made on another device shows without a reload (#322) */
  const recheck = () => {
    if (document.visibilityState === "visible" && account.stale()) run();
  };
  document.addEventListener("visibilitychange", recheck);
  window.addEventListener("focus", recheck);
  window.addEventListener("pageshow", (event) => {
    if (event.persisted) recheck();
  });
  run();
  /* Try again: a save that was put off or refused is sent now */
  const retryNow = () => {
    stopped = false;
    backoff = 0;
    run();
  };
  return { run, retryNow, get stopped() { return stopped; }, get failing() { return failing; } };
})();
window.UxPaletteSync = UxPaletteSync;

/* ---------------------------------------------------------------------------
   Palette analytics (milestone 6, GitHub #179; the events on the options page)
   UxPaletteTrack(name, data) sends mylands:<name> to Shopify's customer events (a custom pixel in Settings ›
   Customer events forwards them to GA4; Shopify runs pixels only with the visitor's consent) and, once the visitor
   allows marketing (Shopify's consent API, loaded here if the privacy banner hasn't), to Klaviyo. Each event carries
   the active palette's id. Anyone can fire these from a console: read them for trends, never to unlock an offer.
   --------------------------------------------------------------------------- */
var UxPaletteTrack = (() => {
  const KLAVIYO = {
    palette_colour_added: "Added to Palette",
    palette_colour_removed: "Removed from Palette",
    palette_viewed: "Viewed Palette",
    shared_palette_opened: "Opened Shared Palette",
    shared_palette_saved: "Saved Shared Palette",
    palette_link_copied: "Shared Palette",
    palette_shared: "Shared Palette",
    palette_samples_added: "Added Palette Samples",
    palette_consultation_clicked: "Clicked Palette Consultation"
  };
  let privacy = null;
  const consent = () => {
    if (window.Shopify?.customerPrivacy) return Promise.resolve(window.Shopify.customerPrivacy);
    privacy ??= new Promise((resolve) => {
      if (!window.Shopify?.loadFeatures) {
        resolve(null);
        return;
      }
      window.Shopify.loadFeatures([{ name: "consent-tracking-api", version: "0.1", onLoad: (error) => resolve(error ? null : window.Shopify.customerPrivacy || null) }]);
    });
    return privacy;
  };
  return (name, data = {}) => {
    const payload = { palette: UxPalette.active()?.id || null, ...data };
    try {
      window.Shopify?.analytics?.publish?.(`mylands:${name}`, payload);
    } catch (error) {
      /* analytics never gets in the way */
    }
    consent().then((api) => {
      try {
        if (KLAVIYO[name] && api?.marketingAllowed?.() && window.klaviyo?.track) window.klaviyo.track(KLAVIYO[name], payload);
      } catch (error) {
        /* analytics never gets in the way */
      }
    });
  };
})();
window.UxPaletteTrack = UxPaletteTrack;

/* Palette sheets, notice and tray (Palettes v2, GitHub #187; snippets/ux-palette-sheets.liquid, ux-palette-tray.liquid).
   - added(colour): after a +, "Added to Kitchen" with Change (Save to…) and Undo, 5 s (paused while hovered or
     focused), in a polite live region.
   - saveTo(colour): every palette as a checkbox, grouped by project, then New palette; a tick adds, an untick
     removes, straight away; Done closes. A full palette refuses the tick and says so; one deleted elsewhere says it
     no longer exists. While open it follows the store (refresh, #467; the colour panel's Add to list too).
   - newPalette({ colour }): name and project (default: the project of the palette in use); the new palette holds the
     colour, if any, and becomes the one in use.
   - switcher(): the palette in use as radios, by project, then New palette.
   The sheets are <dialog>s (bottom sheets on mobile, 480 dialogs on desktop); focus goes back where it came from. */
/* the roles built before surfaces (21 Oct): still valid, shown as the surface they became */
var LEGACY_ROLES = ["walls", "ceiling", "woodwork", "accent"];
var UxPaletteUi = (() => {
  const root = () => document.querySelector("[data-ux-palette-ui]");
  const text = (key, values = {}) => Object.entries(values).reduce((string, [name, value]) => string.split(`[${name}]`).join(value), root()?.dataset[key] || "");
  const shortName = (name) => String(name || "").split(" No.")[0].replace(/™/g, "").trim();
  const sheet = (kind) => document.querySelector(`[data-ux-palette-sheet="${kind}"]`);
  const checkIcon = () => root()?.querySelector("[data-ux-icon-check]")?.content.cloneNode(true) || document.createTextNode("");

  /* --- dialogs --- */
  let opener = null;
  const open = (dialog, from) => {
    if (!dialog) return;
    opener = from || document.activeElement;
    document.querySelectorAll("dialog.ux-sheet[open]:not([data-ux-sheet-stays])").forEach((other) => other !== dialog && other.close());
    if (!dialog.open) dialog.showModal();
    document.documentElement.classList.add("ux-sheet-open");
  };
  const wire = (dialog) => {
    if (!dialog || dialog.dataset.wired) return;
    dialog.dataset.wired = "true";
    dialog.querySelectorAll("[data-ux-sheet-close]").forEach((button) => button.addEventListener("click", () => dialog.close()));
    /* the backdrop closes it, but only a press that started on it (a press and hold that opened the sheet doesn't) */
    let downOnBackdrop = false;
    dialog.addEventListener("pointerdown", (event) => {
      downOnBackdrop = event.target === dialog;
    });
    dialog.addEventListener("click", (event) => {
      if (event.target === dialog && downOnBackdrop) dialog.close();
      downOnBackdrop = false;
    });
    dialog.addEventListener("close", () => {
      if (!document.querySelector("dialog.ux-sheet[open]")) document.documentElement.classList.remove("ux-sheet-open");
      if (opener?.isConnected && !document.querySelector("dialog.ux-sheet[open]")) opener.focus({ preventScroll: true });
    });
  };
  const error = (dialog, message) => {
    const box = dialog.querySelector("[data-ux-sheet-error]");
    if (!box) return;
    box.textContent = message || "";
    box.hidden = !message;
  };

  /* palettes grouped by project (in the book's order), then the loose ones */
  const groups = () => {
    const projects = UxPalette.projects();
    const palettes = UxPalette.palettes();
    const grouped = projects.map((project) => ({ label: project.name, palettes: palettes.filter((palette) => palette.project === project.id) }))
      .filter((group) => group.palettes.length);
    const loose = palettes.filter((palette) => !palette.project || !projects.some((project) => project.id === palette.project));
    if (loose.length) grouped.push({ label: projects.length ? text("loose") : "", palettes: loose });
    return grouped;
  };
  /* what a list of choices shows: the palettes, their names and groups */
  const choicesKey = () => JSON.stringify(groups().map((group) => [group.label, group.palettes.map((palette) => [palette.id, palette.name])]));
  const choices = (container, type, checked) => {
    container.dataset.key = choicesKey();
    container.replaceChildren(...groups().map((group) => {
      const fieldset = document.createElement("fieldset");
      fieldset.className = "ux-palette-choices__group";
      const legend = document.createElement("legend");
      legend.className = "ux-palette-choices__project";
      legend.textContent = group.label;
      fieldset.append(legend, ...group.palettes.map((palette) => {
        const label = document.createElement("label");
        label.className = "ux-choice";
        const input = document.createElement("input");
        input.type = type;
        input.name = `ux-palette-${type}`;
        input.value = palette.id;
        input.checked = checked(palette);
        const box = document.createElement("span");
        box.className = `ux-choice__box ux-choice__box--${type}`;
        box.setAttribute("aria-hidden", "true");
        box.append(checkIcon());
        const name = document.createElement("span");
        name.className = "ux-choice__label";
        name.textContent = palette.name;
        label.append(input, box, name);
        return label;
      }));
      return fieldset;
    }));
  };
  /* an open list follows the store (#467): rebuilt when a palette is made, renamed, moved or deleted elsewhere, focus
     kept on the same palette's box (the first, if it went); otherwise only its ticks change, in place */
  const refresh = (container, type, checked) => {
    if (container.dataset.key === choicesKey()) {
      container.querySelectorAll("input").forEach((input) => {
        const palette = UxPalette.palette(input.value);
        if (palette) input.checked = checked(palette);
      });
      return;
    }
    const focused = container.contains(document.activeElement) ? document.activeElement.value : null;
    choices(container, type, checked);
    if (focused !== null) (container.querySelector(`input[value="${CSS.escape(focused)}"]`) || container.querySelector("input"))?.focus({ preventScroll: true });
  };
  /* why a tick didn't add: the palette is full, or it has gone (deleted in another tab or on another device) */
  const refused = (input) => {
    const palette = UxPalette.palette(input.value);
    return palette ? text("full", { name: palette.name }) : text("missing", { name: input.closest("label")?.textContent.trim() || "" });
  };

  /* --- Save to… --- */
  let saving = null;
  const saveTo = (colour, from) => {
    const dialog = sheet("save-to");
    if (!dialog || !colour?.h) return;
    wire(dialog);
    saving = colour;
    dialog.querySelector("[data-ux-sheet-title]").textContent = text("saveTo", { colour: shortName(colour.n) });
    error(dialog, "");
    choices(dialog.querySelector("[data-ux-palette-choices]"), "checkbox", (palette) => palette.colours.some((saved) => saved.h === colour.h));
    if (!dialog.dataset.listening) {
      dialog.dataset.listening = "true";
      dialog.querySelector("[data-ux-palette-choices]").addEventListener("change", (event) => {
        const input = event.target;
        if (!saving || input.type !== "checkbox") return;
        error(dialog, "");
        if (input.checked) {
          if (UxPalette.addTo(input.value, saving)) {
            UxPaletteTrack("palette_colour_added", { handle: saving.h, source: "save-to", palette: input.value });
          } else {
            input.checked = false;
            error(dialog, refused(input));
            refresh(dialog.querySelector("[data-ux-palette-choices]"), "checkbox", (palette) => palette.colours.some((saved) => saved.h === saving.h));
          }
        } else if (UxPalette.removeFrom(input.value, saving.h)) {
          UxPaletteTrack("palette_colour_removed", { handle: saving.h, source: "save-to", palette: input.value });
        }
      });
      dialog.querySelector("[data-ux-palette-new]").addEventListener("click", () => {
        const colour = saving;
        dialog.close();
        newPalette({ colour, from: opener });
      });
      /* while it's open it follows changes made elsewhere (#467) */
      document.addEventListener("ux-palette:change", () => {
        if (dialog.open && saving) refresh(dialog.querySelector("[data-ux-palette-choices]"), "checkbox", (palette) => palette.colours.some((saved) => saved.h === saving.h));
      });
    }
    open(dialog, from);
  };

  /* --- New palette --- */
  let creating = null;
  let created = null;
  /* a new palette straight away, named as the next free default (My palette 2…): adding a colour is never held up by
     a name (Matt, 21 Oct); Rename is on the palette. The project is the one asked for, or the palette in use's */
  const newPalette = ({ colour = null, project, onCreate = null } = {}) => {
    const id = UxPalette.createPalette({ name: "", project: project !== undefined ? project || null : UxPalette.active()?.project || null, colours: colour ? [colour] : [], activate: true });
    if (!id) {
      notify(text("tooMany"));
      return null;
    }
    UxPaletteTrack("palette_created", { palette: id, source: colour ? "save-to" : "switcher" });
    if (colour) added(colour, { palette: id, created: true });
    if (onCreate) onCreate(id);
    offerKeep();
    return id;
  };

  /* --- the switcher (from the tray) --- */
  const switcher = (from) => {
    const dialog = sheet("switcher");
    if (!dialog) return;
    wire(dialog);
    const active = UxPalette.active();
    choices(dialog.querySelector("[data-ux-palette-choices]"), "radio", (palette) => palette.id === active?.id);
    if (!dialog.dataset.listening) {
      dialog.dataset.listening = "true";
      dialog.querySelector("[data-ux-palette-choices]").addEventListener("change", (event) => {
        if (event.target.type !== "radio" || !event.target.checked) return;
        if (UxPalette.setActive(event.target.value)) UxPaletteTrack("palette_activated", { palette: event.target.value, source: "tray" });
        dialog.close();
      });
      dialog.querySelector("[data-ux-palette-new]").addEventListener("click", () => {
        dialog.close();
        newPalette({ from: opener });
      });
      /* while it's open it follows changes made elsewhere, as Save to… does (#477) */
      document.addEventListener("ux-palette:change", () => {
        if (dialog.open) refresh(dialog.querySelector("[data-ux-palette-choices]"), "radio", (palette) => palette.id === UxPalette.active()?.id);
      });
    }
    open(dialog, from);
  };

  /* --- the notice --- */
  let timer = 0;
  let undoing = null;
  let noticeColour = null;
  let noticeMs = 5000;
  const notice = () => document.querySelector("[data-ux-palette-notice]");
  /* the words go to a live region that is always in the page (data-ux-palette-say, visually hidden), emptied and
     filled on the next frame, so a screen reader hears each one (#471); it goes just before the notice wherever that
     is (a sheet or the drawer, where the page outside is inert; before, so the drawer's way home for it holds) */
  let sayToken = 0;
  const say = (message) => {
    const region = document.querySelector("[data-ux-palette-say]");
    if (!region) return;
    const box = notice();
    if (box && region.nextElementSibling !== box) box.before(region);
    const token = (sayToken += 1);
    region.textContent = "";
    if (message) requestAnimationFrame(() => token === sayToken && (region.textContent = message));
  };
  const hide = () => {
    const box = notice();
    if (box && !box.hidden) say("");
    if (box) box.hidden = true;
    undoing = null;
  };
  const schedule = () => {
    clearTimeout(timer);
    timer = setTimeout(hide, noticeMs);
  };
  const show = (message, colour, undo, ms = 5000) => {
    const box = notice();
    if (!box) return;
    if (!box.dataset.wired) {
      box.dataset.wired = "true";
      box.querySelector("[data-ux-palette-notice-change]").addEventListener("click", (event) => {
        const colourNow = noticeColour;
        hide();
        if (colourNow) saveTo(colourNow, event.currentTarget);
      });
      box.querySelector("[data-ux-palette-notice-undo]").addEventListener("click", () => {
        const fn = undoing;
        hide();
        if (fn) fn();
      });
      ["pointerenter", "focusin"].forEach((name) => box.addEventListener(name, () => clearTimeout(timer)));
      ["pointerleave", "focusout"].forEach((name) => box.addEventListener(name, () => !box.hidden && schedule()));
    }
    noticeColour = colour;
    noticeMs = ms;
    undoing = undo || null;
    box.querySelector("[data-ux-palette-notice-text]").textContent = message;
    const swatch = box.querySelector("[data-ux-palette-notice-swatch]");
    swatch.hidden = !colour;
    swatch.style.background = colour?.c || "var(--ux-grey)";
    box.querySelector("[data-ux-palette-notice-undo]").hidden = !undo;
    box.querySelector("[data-ux-palette-notice-change]").hidden = !colour;
    box.hidden = false;
    say(message);
    schedule();
  };
  const added = (colour, { palette = null, created = false } = {}) => {
    const target = palette ? UxPalette.palette(palette) : UxPalette.active();
    if (!target) return;
    const id = target.id;
    show(text("added", { name: target.name }), colour, () => {
      if (created) UxPalette.deletePalette(id);
      else UxPalette.removeFrom(id, colour.h);
    });
  };
  const full = (colour) => {
    const target = UxPalette.active();
    show(text("full", { name: target?.name || "" }), colour, null);
  };

  /* a general notice (Delete … with Undo for 8 s, Link copied) for the palette pages */
  const notify = (message, { undo = null, ms = 5000 } = {}) => show(message, null, undo, ms);

  /* --- accounts (#189; spec §5) --- */
  /* Sign in comes back to this page */
  const KEEP_OFFERED = "mylands-keep-offered";
  const keepLinks = (scope = document) => scope.querySelectorAll("[data-ux-keep-login]").forEach((link) => {
    const url = new URL(link.getAttribute("href"), window.location.origin);
    url.searchParams.set("return_url", `${window.location.pathname}${window.location.search}`);
    link.href = `${url.pathname}${url.search}`;
  });
  const keep = (from) => {
    const dialog = sheet("keep");
    if (!dialog) return;
    wire(dialog);
    keepLinks(dialog);
    open(dialog, from);
  };
  /* signed out, once, when the second palette is made: Keep your palettes (on the next page if this one moves on) */
  const KEEP_PENDING = "mylands-keep-pending";
  const offerKeep = () => {
    if (UxPalette.account?.customer || UxPalette.palettes().length !== 2 || !sheet("keep")) return;
    try {
      if (localStorage.getItem(KEEP_OFFERED)) return;
      localStorage.setItem(KEEP_OFFERED, "1");
      sessionStorage.setItem(KEEP_PENDING, "1");
    } catch (error) {
      return;
    }
    setTimeout(showPendingKeep, 600);
  };
  /* the offer stands until the sheet is closed, so a page that moves on straight away shows it on the next one */
  const showPendingKeep = () => {
    try {
      if (!sessionStorage.getItem(KEEP_PENDING)) return;
    } catch (error) {
      return;
    }
    const dialog = sheet("keep");
    if (!dialog || dialog.open || document.querySelector("dialog.ux-sheet[open]:not([data-ux-sheet-stays])")) return;
    dialog.addEventListener("close", () => {
      try {
        sessionStorage.removeItem(KEEP_PENDING);
      } catch (error) {}
    }, { once: true });
    keep(document.activeElement);
  };
  setTimeout(showPendingKeep, 800);
  /* a login merge (the device's palettes went into the account), and changes the account refused */
  document.addEventListener("ux-palette:merged", (event) => {
    notify(text(event.detail?.overLimit ? "mergedOver" : "merged"), { ms: 8000 });
  });
  /* a held palette (#476) went once there was room */
  document.addEventListener("ux-palette:saved", (event) => {
    const names = event.detail?.names || [];
    if (names.length) notify(text("heldSaved", { name: names.join(", ") }), { ms: 8000 });
  });
  /* This device stopped keeping palettes (#469: storage full or switched off). Said once each time it starts, on any
     page with a +: the account's "Not saved yet" notice is about reaching the account, and a guest never sees it */
  document.addEventListener("ux-palette:storage", (event) => {
    if (event.detail?.failing) notify(text("storageFailed"), { ms: 8000 });
  });
  /* A refusal (#460) names the palette from the copy that was sent (the account's book has already replaced it), and
     Undo gives the customer that copy back: a palette the account doesn't have (a new one over the cap) as itself, one
     it kept its own version of (an edit refused) as a palette beside it ("Hall 2"), so the account's copy isn't sent
     over again only to be refused again. Over 30 here too, Undo says so and nothing changes. */
  document.addEventListener("ux-palette:rejected", (event) => {
    const rejected = event.detail?.rejected || [];
    /* held palettes (#476) are kept, not undone: they save themselves once one is deleted */
    const heldNames = rejected.filter((entry) => entry.held).map((entry) => entry.entity?.name).filter(Boolean);
    if (heldNames.length) notify(text("heldPalette", { name: heldNames.join(", ") }), { ms: 10000 });
    const first = rejected.find((entry) => !entry.held);
    if (!first) return;
    const name = (first.kind !== "project" && first.entity?.name) || UxPalette.palette(first.id)?.name || "";
    const message = first.code === "palette_full" && name ? text("rejectedFull", { name })
      : first.code === "too_many_palettes" ? (name ? text("rejectedPalettesNamed", { name }) : text("rejectedPalettes"))
      : first.code === "too_many_projects" ? text("rejectedProjects")
      : name && first.kind !== "project" ? text("rejectedNamed", { name })
      : text("rejected");
    const kept = rejected.filter((entry) => entry.entity && !entry.held);
    const undo = kept.length ? () => {
      const gone = { palettes: [], projects: [], active: null };
      const beside = [];
      kept.forEach(({ kind, entity }) => {
        if (kind === "project") {
          if (!UxPalette.project(entity.id)) gone.projects.push(entity);
        } else if (UxPalette.palette(entity.id)) beside.push(entity);
        else gone.palettes.push(entity);
      });
      let ok = !(gone.palettes.length || gone.projects.length) || UxPalette.restore(gone);
      beside.forEach((palette) => {
        ok = Boolean(UxPalette.createPalette({ name: palette.name, project: palette.project, colours: palette.colours, activate: false })) && ok;
      });
      if (!ok) notify(text("tooMany"));
    } : null;
    notify(message, { undo, ms: 8000 });
  });

  /* --- surfaces (snippets/ux-surfaces.liquid; Matt, 21 Oct): what each colour is for, the website's Shop by project
     list, so a palette can be a room, a front door or a bench. The roles built before (walls, ceiling, woodwork,
     accent) stay valid and show as the surface they became --- */
  let surfaceData = null;
  const surfaceList = () => {
    if (!surfaceData) {
      try {
        surfaceData = JSON.parse(root()?.querySelector("[data-ux-surfaces]")?.textContent || "null");
      } catch (error) {
        surfaceData = null;
      }
      if (!surfaceData || !Array.isArray(surfaceData.surfaces)) surfaceData = { groups: {}, surfaces: [], legacy: {}, legacyTitles: {} };
    }
    return surfaceData;
  };
  const surface = (slug) => {
    if (!slug) return null;
    const data = surfaceList();
    const found = data.surfaces.find((one) => one.slug === slug) || data.surfaces.find((one) => one.slug === data.legacy[slug]);
    return found || { slug, group: "inside", title: data.legacyTitles[slug] || slug, finish: "", finishTitle: "" };
  };
  /* a select's surfaces in their groups, outside first once any of the palette's colours is outside (no setting:
     where a palette is for follows what's in it) */
  const surfaceOptions = (select, palette) => {
    const data = surfaceList();
    const outside = (palette?.colours || []).some((colour) => surface(colour.r)?.group === "outside");
    (outside ? ["outside", "inside"] : ["inside", "outside"]).forEach((group) => {
      const box = document.createElement("optgroup");
      box.label = data.groups[group] || group;
      data.surfaces.filter((one) => one.group === group).forEach((one) => box.append(new Option(one.title, one.slug)));
      if (box.children.length) select.append(box);
    });
  };
  /* the select's value for a stored role: the surface it became, or the old role itself when it became none (accent) */
  const surfaceValue = (select, role) => {
    if (!role) return "";
    const slug = surface(role).slug;
    if (![...select.options].some((option) => option.value === slug)) select.append(new Option(surface(role).title, slug));
    return slug;
  };

  return { text, shortName, saveTo, newPalette, switcher, added, full, hide, notify, open, wire, error, groups, choices, refresh, refused, keep, keepLinks, offerKeep, surfaceList, surface, surfaceOptions, surfaceValue };
})();
window.UxPaletteUi = UxPaletteUi;

/* Palette toggles and links (milestone 6, GitHub #152; v2 #187): one delegated handler; every toggle on the page (cards
   added later by Load more, filters or Show more too) follows the palette in use. A tap adds to (or takes out of) it
   and, after an add, says which (UxPaletteUi.added). Press and hold (500 ms), Shift+click or Shift+Enter opens Save to…
   instead. The colour page's link names the palette ("In Kitchen"). */
(() => {
  document.documentElement.classList.add("ux-palette-js");
  const HOLD_MS = 500;
  const sync = (root = document) => {
    const active = UxPalette.active();
    root.querySelectorAll?.("[data-ux-palette-toggle]").forEach((toggle) => {
      const on = UxPalette.has(toggle.dataset.handle);
      toggle.dataset.inPalette = String(on);
      /* the name says which palette and carries the state (#471): "Add Huguenot to Kitchen" / "Remove Huguenot from
         Kitchen"; "… to palette" before there is one. No aria-pressed as well: "Remove …, pressed" says it twice (Matt,
         20 Oct, as Leave out) */
      toggle.removeAttribute("aria-pressed");
      if (toggle.hasAttribute("aria-label")) {
        const label = active && (on ? toggle.dataset.labelRemoveFrom : toggle.dataset.labelAddTo);
        toggle.setAttribute("aria-label", label ? label.split("[name]").join(active.name) : on ? toggle.dataset.labelRemove : toggle.dataset.labelAdd);
      }
      const named = toggle.querySelector("[data-ux-palette-in-name]");
      if (named && active) named.textContent = UxPaletteUi.text("inNamed", { name: active.name });
    });
  };
  /* where a + was pressed, for analytics (#179) */
  const source = (toggle) => toggle.closest("[data-ux-palette-source]")?.dataset.uxPaletteSource
    || (toggle.closest("predictive-search, .predictive-search") ? "search"
      : toggle.closest("[data-ux-palette-pairs]") ? "pairings"
        : toggle.closest("ux-palette-page") ? "shared"
          : /\/products\//.test(window.location.pathname) ? "colour-page" : "card");
  const colourOf = (toggle) => ({ h: toggle.dataset.handle, n: toggle.dataset.name, c: toggle.dataset.colour });

  /* press and hold */
  let timer = 0;
  let held = null;
  let start = null;
  const cancel = () => clearTimeout(timer);
  document.addEventListener("pointerdown", (event) => {
    const toggle = event.target.closest("[data-ux-palette-toggle]");
    held = null;
    if (!toggle || event.button > 0) return;
    start = { x: event.clientX, y: event.clientY };
    cancel();
    timer = setTimeout(() => {
      held = toggle;
      UxPaletteUi.saveTo(colourOf(toggle), toggle);
    }, HOLD_MS);
  });
  /* the click that follows a hold is swallowed; any later one is a click again */
  document.addEventListener("pointerup", () => {
    cancel();
    if (held) setTimeout(() => (held = null), 50);
  });
  document.addEventListener("pointercancel", cancel);
  document.addEventListener("pointermove", (event) => {
    if (start && Math.hypot(event.clientX - start.x, event.clientY - start.y) > 10) cancel();
  });
  document.addEventListener("contextmenu", (event) => {
    if (held && event.target.closest("[data-ux-palette-toggle]")) event.preventDefault();
  });
  document.addEventListener("keydown", (event) => {
    const toggle = event.target.closest?.("[data-ux-palette-toggle]");
    if (!toggle || event.key !== "Enter" || !event.shiftKey) return;
    event.preventDefault();
    UxPaletteUi.saveTo(colourOf(toggle), toggle);
  });

  document.addEventListener("click", (event) => {
    const toggle = event.target.closest("[data-ux-palette-toggle]");
    if (!toggle) return;
    event.preventDefault();
    event.stopPropagation();
    if (held === toggle) {
      held = null;
      return;
    }
    const colour = colourOf(toggle);
    if (event.shiftKey) {
      UxPaletteUi.saveTo(colour, toggle);
      return;
    }
    const was = UxPalette.has(colour.h);
    const before = UxPalette.palettes().length;
    const now = UxPalette.toggle(colour);
    if (was || now) UxPaletteTrack(was ? "palette_colour_removed" : "palette_colour_added", { handle: colour.h, source: source(toggle) });
    if (now) UxPaletteUi.added(colour, { created: UxPalette.palettes().length > before });
    else if (!was) UxPaletteUi.full(colour);
  });
  document.addEventListener("ux-palette:change", () => sync());
  new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType !== 1) return;
        if (node.matches("[data-ux-palette-toggle]")) sync(node.parentNode);
        else if (node.querySelector("[data-ux-palette-toggle]")) sync(node);
      });
    });
  }).observe(document.body, { childList: true, subtree: true });
  sync();
})();

/* Palette tray and drawer row (milestone 6, GitHub #153; v2 #187): pinned to the bottom while any palette has a colour.
   All 12 places of the palette in use (its colours, then empty outlines; #389, Figma 783:8868), "Saving to" its name
   as a switcher, "In [project]" on desktop, and Open palette (?palette=<id>); the count is for screen readers. The
   colours and Open palette open the palette drawer (#389) over the page; with a modifier key (a new tab), or without
   the drawer, they go to the palette page. */
(() => {
  const tray = document.querySelector("[data-ux-palette-tray]");
  const row = document.querySelector("[data-ux-palette-row]");
  tray?.querySelectorAll("[data-ux-palette-open]").forEach((link) => {
    link.dataset.base = link.getAttribute("href");
    if (document.querySelector("[data-ux-palette-drawer]")) link.setAttribute("aria-haspopup", "dialog");
  });
  tray?.querySelector("[data-ux-palette-switch]")?.addEventListener("click", (event) => UxPaletteUi.switcher(event.currentTarget));
  tray?.addEventListener("click", (event) => {
    const link = event.target.closest("[data-ux-palette-open]");
    if (!link || event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (UxPaletteDrawer.open(link)) event.preventDefault();
  });
  const render = () => {
    const active = UxPalette.active();
    const colours = active?.colours || [];
    const count = colours.length;
    const anySaved = UxPalette.palettes().some((palette) => palette.colours.length);
    document.documentElement.classList.toggle("ux-palette-tray-open", Boolean(tray && anySaved && active));
    if (tray) {
      tray.hidden = !(anySaved && active);
      if (active) {
        const project = active.project ? UxPalette.project(active.project) : null;
        tray.querySelector("[data-ux-palette-name]").textContent = active.name;
        tray.querySelector("[data-ux-palette-project]").textContent = project ? (tray.dataset.inProject || "[project]").replace("[project]", project.name) : "";
        tray.querySelector("[data-ux-palette-switch]").setAttribute("aria-label", UxPaletteUi.text("switchLabel", { name: active.name }));
        tray.querySelector("[data-ux-palette-count]").textContent = count === 1 ? UxPaletteUi.text("countOne") : UxPaletteUi.text("countOther", { count });
        tray.querySelector("[data-ux-palette-swatches]").replaceChildren(...Array.from({ length: UxPalette.LIMITS.colours }, (_, index) => {
          const swatch = document.createElement("span");
          swatch.className = "ux-palette-tray__swatch";
          if (colours[index]) swatch.style.background = colours[index].c || "var(--ux-grey)";
          else swatch.classList.add("ux-palette-tray__swatch--empty");
          return swatch;
        }));
        tray.querySelectorAll("[data-ux-palette-open]").forEach((link) => {
          const base = link.dataset.base || link.getAttribute("href");
          link.setAttribute("href", `${base}${base.includes("?") ? "&" : "?"}palette=${encodeURIComponent(active.id)}`);
        });
      }
    }
    if (row) {
      row.hidden = !count;
      row.querySelector("[data-ux-palette-row-count]").textContent = count;
    }
  };
  document.addEventListener("ux-palette:change", render);
  render();
})();

/* Palette samples (#155, #389): the sample to order for each colour, each tagged with its palette (#179), and adding
   them. One choice everywhere a palette's samples are ordered (#457): once Colour Swatches are live (Theme settings ›
   Colour Swatches) the swatch, else the pot; the other where the first is out; colours with neither, or no longer
   sold, are left out. Used by the palette drawer, the Samples view, the menu and the account. */
var UxPaletteSamples = {
  formats() {
    return window.uxSwatchesLive ? ["peel", "sample"] : ["sample", "peel"];
  },
  pick(data) {
    return UxPaletteSamples.formats().find((format) => data?.[format]?.available) || null;
  },
  money(cents) {
    const currency = window.Shopify?.currency?.active || "GBP";
    return new Intl.NumberFormat(document.documentElement.lang || "en-GB", { style: "currency", currency }).format(cents / 100);
  },
  async choose(colours) {
    const entries = await Promise.all(colours.map(async (colour) => [colour, await UxPalette.colour(colour.h, { card: false })]));
    return entries.flatMap(([colour, data]) => {
      const choice = UxPaletteSamples.pick(data);
      return choice ? [{ handle: colour.h, choice, id: data[choice].id, price: data[choice].price }] : [];
    });
  },
  /* Order attribution (#179, spec 8): each sample carries the hidden line property _palette (the palette's id) so Odoo
     can tie the order to the palette. Shopify keeps lines with different properties apart, so a sample the basket
     already holds without it (added from a colour page) is added plainly, to the line it's on, rather than split.
     No palette (a shared one isn't the visitor's own): no tag. */
  async items(chosen, palette) {
    if (!palette) return chosen.map((item) => ({ id: item.id, quantity: 1 }));
    let lines = [];
    try {
      lines = (await (await fetch(`${window.Shopify.routes.root}cart.js`)).json()).items || [];
    } catch (error) {
      /* can't read the basket: tag everything */
    }
    return chosen.map((item) => {
      const existing = lines.filter((line) => line.variant_id === item.id);
      const plain = existing.length && !existing.some((line) => line.properties?._palette === palette);
      return plain ? { id: item.id, quantity: 1 } : { id: item.id, quantity: 1, properties: { _palette: palette } };
    });
  },
  /* adds them and tells the theme (the basket drawer opens); throws if the basket refuses */
  async add(chosen, palette) {
    return UxPaletteSamples.post(await UxPaletteSamples.items(chosen, palette));
  },
  /* cart lines ready to add (UxPaletteSamples.items, one call per palette when several are ordered at once, #448) */
  async post(items) {
    const root = window.Shopify.routes.root;
    const sections = [];
    document.documentElement.dispatchEvent(new CustomEvent("cart:prepare-bundled-sections", { bubbles: true, detail: { sections } }));
    const response = await fetch(`${root}cart/add.js`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ items, sections: sections.join(",") })
    });
    const added = await response.json();
    if (!response.ok) throw new Error(added.description || added.message || response.statusText);
    const cart = await (await fetch(`${root}cart.js`)).json();
    cart.sections = added.sections;
    document.documentElement.dispatchEvent(new CustomEvent("cart:change", { bubbles: true, detail: { baseEvent: "variant:add", cart } }));
    return items;
  }
};
window.UxPaletteSamples = UxPaletteSamples;

/* ---------------------------------------------------------------------------
   Palette drawer (GitHub #389; Figma: Website, Palette builder, Build · Palette drawer 754:5627, notes 759:7237;
   snippets/ux-palette-drawer.liquid). The tray opens into it (its colours and Open palette; a click with a modifier
   key still follows the link to the palette page): a modal bottom drawer over the page holding every palette, by
   project. A view toggle (By project | All palettes, remembered in the browser; v2, Figma 781:7237 / 781:26345 /
   781:27301): By project, each project a Belgravia panel side by side (Not in a project first, new projects last),
   one at a time behind tabs under 1000; All palettes, one grid (a list under 1000). No projects: no toggle, just the
   palettes. Under 1000 the palette in use is open and the others fold to their colours (Show opens one).
   A palette: its name, Adding here or Add here (makes it the one in use), and •••: Rename (the room menu: Inside /
   Outside, or your own name, in place) and Delete palette, with Undo. Under the name "In [project] ▾" moves it (Not
   in a project, each project, or New project…, named in place). Each colour's role ▾; reorder (drag the handle, or
   the arrow keys on it); remove with Undo; Order N samples (UxPaletteSamples: a pot each, tagged with the palette).
   New palette (the New palette sheet); New project and a project's ••• (rename in place, delete with Undo, its
   palettes kept). Menus are popovers over the drawer. Closes with ✕, Esc, a click on the scrim or a swipe down
   (under 1000). It redraws on every palette change (other tabs too), keeping focus and scroll, but not mid-rename.
   The Added / Undo notice moves into it while it's open, so it shows over the scrim and can be used.
   --------------------------------------------------------------------------- */
var UxPaletteDrawer = (() => {
  const wide = window.matchMedia("(min-width: 1000px)");
  const drawer = () => document.querySelector("[data-ux-palette-drawer]");
  const t = (key, values = {}) => Object.entries(values).reduce((string, [name, value]) => string.split(`[${name}]`).join(value), drawer()?.dataset[key] || "");
  const plural = (key, count) => (count === 1 ? t(`${key}One`) : t(`${key}Other`, { count }));
  const icon = (name) => drawer()?.querySelector(`[data-ux-pdrawer-icon="${name}"]`)?.content.firstElementChild?.cloneNode(true) || document.createTextNode("");
  const el = (tag, className, props = {}) => Object.assign(document.createElement(tag), className ? { className } : {}, props);
  const button = (className, action, label, props = {}) => {
    const node = el("button", className, { type: "button", ...props });
    node.dataset.uxPdrawerAction = action;
    if (label !== undefined) node.textContent = label;
    return node;
  };

  let tab = null; /* under 1000: the project shown ("" = not in a project) */
  const unfolded = new Set(); /* under 1000: folded palettes opened with Show */
  let editing = null; /* { kind: "palette" | "project" | "new-project" | "move-new", id } while a name is edited in place */
  const VIEW_KEY = "mylands-palette-drawer-view";
  let view = (() => {
    try {
      return window.localStorage.getItem(VIEW_KEY) === "all" ? "all" : "projects";
    } catch (error) {
      return "projects";
    }
  })();
  const setView = (next) => {
    view = next === "all" ? "all" : "projects";
    try {
      window.localStorage.setItem(VIEW_KEY, view);
    } catch (error) {
      /* storage blocked: this page view only */
    }
  };
  let opener = null;
  let notice = null;

  /* Not in a project first, when it holds any (or there are no projects: then it's just the palettes, unheaded), then
     the projects in the book's order, empty ones too, so a new project lands at the end (Matt, 15 Oct) */
  const groups = () => {
    const projects = UxPalette.projects();
    const palettes = UxPalette.palettes();
    const list = projects.map((project) => ({ id: project.id, name: project.name, palettes: palettes.filter((palette) => palette.project === project.id) }));
    const loose = palettes.filter((palette) => !palette.project || !projects.some((project) => project.id === palette.project));
    if (loose.length || !projects.length) list.unshift({ id: "", name: UxPaletteUi.text("loose"), palettes: loose, only: !projects.length });
    return list;
  };

  const roleLabel = (role) => (role ? UxPaletteUi.surface(role).title : "");
  const finishFor = (role, project) => {
    if (UxPaletteUi.surface(role)?.finishTitle && !LEGACY_ROLES.includes(role)) return UxPaletteUi.surface(role).finishTitle;
    if (role === "walls" || role === "ceiling") return project?.type === "walls-brick-render" && role === "walls" ? t("finishOutside") : t("finishWalls");
    if (role === "woodwork") return t("finishWoodwork");
    if (role === "accent") return t("roleAny");
    return "";
  };

  /* --- drawing --- */
  const nameForm = (value, label) => {
    const form = el("form", "ux-pdrawer__name-form", { noValidate: true });
    form.dataset.uxPdrawerNameForm = "";
    const input = el("input", "ux-pdrawer__name-input", { type: "text", name: "name", value, maxLength: UxPalette.LIMITS.name, autocomplete: "off" });
    input.setAttribute("aria-label", label);
    form.append(input, el("button", "button button--tertiary button--sm", { type: "submit", textContent: t("save") }));
    return form;
  };
  const row = (palette, colour, project) => {
    const item = drawer().querySelector("template[data-ux-pdrawer-row]").content.firstElementChild.cloneNode(true);
    const short = UxPaletteUi.shortName(colour.n);
    item.dataset.handle = colour.h;
    item.querySelector("[data-ux-erow-chip]").style.background = colour.c || "var(--ux-grey)";
    const name = item.querySelector("[data-ux-erow-name]");
    name.textContent = colour.n;
    name.href = `${window.Shopify?.routes?.root || "/"}products/${encodeURIComponent(colour.h)}`;
    const role = item.querySelector('[data-ux-pdrawer-action="role"]');
    role.classList.toggle("ux-erow__role--none", !colour.r);
    item.querySelector("[data-ux-erow-role-text]").textContent = colour.r ? [roleLabel(colour.r), finishFor(colour.r, project)].filter(Boolean).join(" · ") : t("roleAdd");
    role.setAttribute("aria-label", `${short}: ${colour.r ? roleLabel(colour.r) : t("roleAdd")}`);
    const handle = item.querySelector("[data-ux-erow-handle]");
    handle.setAttribute("aria-label", t("moveLabel", { colour: short }));
    handle.disabled = palette.colours.length < 2;
    item.querySelector('[data-ux-pdrawer-action="remove"]').setAttribute("aria-label", t("removeLabel", { colour: short, name: palette.name }));
    return item;
  };
  const card = (palette, active) => {
    const project = palette.project ? UxPalette.project(palette.project) : null;
    const folded = !wide.matches && !active && !unfolded.has(palette.id);
    const box = el("article", `ux-pdrawer__card${active ? " is-active" : ""}${folded ? " is-folded" : ""}`);
    box.dataset.palette = palette.id;
    box.setAttribute("aria-label", palette.name);
    const head = el("div", "ux-pdrawer__card-head");
    const renaming = editing?.kind === "palette" && editing.id === palette.id;
    if (renaming) head.append(nameForm(palette.name, t("nameLabel")));
    else head.append(el("h3", "ux-pdrawer__name", { textContent: palette.name }));
    if (active) head.append(el("span", "ux-pdrawer__tag", { textContent: t("adding") }));
    else {
      const use = button("button button--tertiary button--sm ux-pdrawer__use", "use", t("addHere"));
      use.setAttribute("aria-label", t("addHereLabel", { name: palette.name }));
      head.append(use);
    }
    if (!renaming) {
      const more = button("ux-menu-toggle ux-pdrawer__more", "palette-menu", "•••");
      more.setAttribute("aria-haspopup", "menu");
      more.setAttribute("aria-expanded", "false");
      more.setAttribute("aria-label", t("menuLabel", { name: palette.name }));
      head.append(more);
    }
    box.append(head);
    /* its project: the switcher that moves it (a new project is named in place) */
    if (editing?.kind === "move-new" && editing.id === palette.id) {
      const where = el("div", "ux-pdrawer__in-form");
      where.append(nameForm("", t("projectLabel")));
      box.append(where);
    } else if (UxPalette.projectsShown) {
      /* no projects outside consultancies: no "Not in a project" line at all, not one CSS hides (#475) */
      const where = project ? t("in", { project: project.name }) : UxPaletteUi.text("loose");
      const move = button("ux-pdrawer__in", "move", undefined);
      move.setAttribute("aria-haspopup", "menu");
      move.setAttribute("aria-expanded", "false");
      move.setAttribute("aria-label", t("moveLabel2", { name: palette.name, where }));
      move.append(el("span", "", { textContent: where }), icon("chevron"));
      box.append(move);
    }
    if (folded) {
      const fold = el("div", "ux-pdrawer__fold");
      const chips = el("span", "ux-pdrawer__chips");
      chips.setAttribute("aria-hidden", "true");
      chips.append(...palette.colours.slice(0, 6).map((colour) => {
        const chip = el("span", "ux-pdrawer__chip");
        chip.style.background = colour.c || "var(--ux-grey)";
        return chip;
      }));
      const show = button("ux-pdrawer__show", "show", undefined);
      show.setAttribute("aria-label", t("showLabel", { name: palette.name }));
      show.setAttribute("aria-expanded", "false");
      show.append(el("span", "", { textContent: t("show") }), icon("chevron"));
      fold.append(chips, el("span", "ux-pdrawer__fold-count", { textContent: plural("colours", palette.colours.length) }), show);
      box.append(fold);
      return box;
    }
    if (palette.colours.length) {
      const rows = el("ol", "ux-pdrawer__rows unstyled-list", { role: "list" });
      rows.append(...palette.colours.map((colour) => row(palette, colour, project)));
      box.append(rows);
      const order = button("button button--tertiary button--sm ux-pdrawer__order", "order", "", { hidden: true });
      const foot = el("div", "ux-pdrawer__card-foot");
      foot.append(order);
      box.append(foot);
      priceOrder(order, palette);
    } else {
      box.append(el("p", "ux-pdrawer__empty", { textContent: t("empty") }));
    }
    return box;
  };
  /* "Order 4 samples · £23.00" once the colours' samples are known (UxPalette caches them for the page) */
  const priceOrder = async (order, palette) => {
    const chosen = await UxPaletteSamples.choose(palette.colours);
    if (!order.isConnected || !chosen.length) return;
    const price = UxPaletteSamples.money(chosen.reduce((sum, item) => sum + item.price, 0));
    order.textContent = chosen.length === 1 ? t("orderOne", { price }) : t("orderOther", { count: chosen.length, price });
    order.hidden = false;
  };
  const column = (group, active) => {
    const col = el("section", `ux-pdrawer__col${group.only ? " ux-pdrawer__col--only" : ""}${group.id === tab ? " is-current" : ""}`);
    col.dataset.project = group.id;
    col.id = `ux-pdrawer-col-${group.id || "loose"}`;
    col.setAttribute("aria-label", group.name);
    if (!wide.matches) col.setAttribute("role", "tabpanel");
    const head = el("div", "ux-pdrawer__col-head");
    if (editing?.kind === "project" && editing.id === group.id) head.append(nameForm(group.name, t("projectLabel")));
    else if (!group.only) {
      const title = el("h3", "ux-pdrawer__project");
      title.append(el("span", "", { textContent: group.name }), el("span", "ux-pdrawer__project-count", { textContent: plural("palettes", group.palettes.length) }));
      head.append(title);
    }
    if (group.id && !(editing?.kind === "project" && editing.id === group.id)) {
      const more = button("ux-menu-toggle ux-pdrawer__more", "project-menu", "•••");
      more.setAttribute("aria-haspopup", "menu");
      more.setAttribute("aria-expanded", "false");
      more.setAttribute("aria-label", t("menuLabel", { name: group.name }));
      head.append(more);
    }
    const cards = el("div", "ux-pdrawer__cards");
    cards.append(...group.palettes.map((palette) => card(palette, palette.id === active?.id)));
    const add = button("button button--tertiary button--sm ux-pdrawer__new", "new-palette", group.id ? t("newIn", { project: group.name }) : group.only ? t("newPalette") : t("newLoose"));
    col.append(head, cards, add);
    return col;
  };
  const newProjectColumn = () => {
    const col = el("section", "ux-pdrawer__col ux-pdrawer__col--new is-current");
    const head = el("div", "ux-pdrawer__col-head");
    head.append(nameForm("", t("projectLabel")));
    col.append(head, el("p", "ux-pdrawer__error", { role: "alert", hidden: true }));
    col.dataset.project = "new";
    return col;
  };
  const tabs = (list) => {
    const box = drawer().querySelector("[data-ux-pdrawer-tabs]");
    box.hidden = list.length < 2;
    box.replaceChildren(...list.map((group) => {
      const one = button("ux-pdrawer__tab", "tab", group.name, { role: "tab" });
      one.dataset.project = group.id;
      one.setAttribute("aria-selected", String(group.id === tab));
      one.setAttribute("aria-controls", `ux-pdrawer-col-${group.id || "loose"}`);
      one.tabIndex = group.id === tab ? 0 : -1;
      return one;
    }));
  };

  /* what had focus (or a given control), to find again after a redraw */
  const focusKey = (node = document.activeElement) => {
    const dialog = drawer();
    if (!node || !dialog?.contains(node) || node === dialog) return null;
    const action = node.closest("[data-ux-pdrawer-action]");
    return {
      palette: node.closest("[data-palette]")?.dataset.palette || null,
      project: node.closest("[data-project]")?.dataset.project ?? null,
      handle: node.closest("[data-handle]")?.dataset.handle || null,
      action: action?.dataset.uxPdrawerAction || null,
      part: node.matches("[data-ux-erow-handle]") ? "handle" : node.matches("[data-ux-erow-name]") ? "name" : null
    };
  };
  const refocus = (key) => {
    if (!key) return;
    const dialog = drawer();
    const scope = (key.palette && dialog.querySelector(`[data-palette="${CSS.escape(key.palette)}"]`))
      || (key.project !== null && dialog.querySelector(`.ux-pdrawer__col[data-project="${CSS.escape(key.project)}"], .ux-pdrawer__tab[data-project="${CSS.escape(key.project)}"]`))
      || dialog;
    const within = key.handle ? scope.querySelector(`[data-handle="${CSS.escape(key.handle)}"]`) || scope : scope;
    const find = (selector) => (within.matches(selector) ? within : within.querySelector(selector));
    const target = (key.part === "handle" && find("[data-ux-erow-handle]"))
      || (key.part === "name" && find("[data-ux-erow-name]"))
      || (key.action && find(`[data-ux-pdrawer-action="${key.action}"]`))
      || (key.action && dialog.querySelector(`.ux-pdrawer__head [data-ux-pdrawer-action="${key.action}"], .ux-pdrawer__foot [data-ux-pdrawer-action="${key.action}"]`));
    target?.focus({ preventScroll: true });
  };

  const render = () => {
    const dialog = drawer();
    if (!dialog?.open) return;
    /* not mid-rename: the redraw that ends it brings the change in */
    if (editing && dialog.querySelector("[data-ux-pdrawer-name-form]")?.contains(document.activeElement)) return;
    const projects = UxPalette.projects().length;
    /* All palettes (or no projects): one group, unheaded */
    const flat = view === "all" || !projects;
    const list = flat ? [{ id: "", name: "", palettes: groups().flatMap((group) => group.palettes), only: true }] : groups();
    const active = UxPalette.active();
    if (flat) tab = "";
    else if (tab === null || !list.some((group) => group.id === tab)) tab = list.find((group) => group.palettes.some((palette) => palette.id === active?.id))?.id ?? list[0]?.id ?? "";
    const palettes = list.reduce((sum, group) => sum + group.palettes.length, 0);
    const views = dialog.querySelector("[data-ux-pdrawer-views]");
    views.hidden = !projects;
    views.querySelectorAll("input").forEach((input) => (input.checked = input.value === (flat ? "all" : "projects")));
    const palettesText = plural("palettes", palettes);
    dialog.querySelector("[data-ux-pdrawer-count]").textContent = projects ? t("count", { palettes: palettesText, projects: plural("projects", projects) }) : palettesText;
    const body = dialog.querySelector("[data-ux-pdrawer-body]");
    const key = focusKey();
    const scroll = { left: body.scrollLeft, top: body.scrollTop };
    tabs(flat ? [] : list);
    body.classList.toggle("ux-pdrawer__body--projects", !flat);
    body.replaceChildren(...list.map((group) => column(group, active)));
    if (editing?.kind === "new-project") {
      body.append(newProjectColumn());
      body.querySelectorAll(".ux-pdrawer__col:not(.ux-pdrawer__col--new)").forEach((col) => col.classList.remove("is-current"));
    }
    body.scrollLeft = scroll.left;
    body.scrollTop = scroll.top;
    const form = dialog.querySelector("[data-ux-pdrawer-name-form]");
    if (form) {
      form.querySelector("input").focus();
      form.querySelector("input").select();
    } else refocus(key);
  };

  /* --- menus (popovers over the drawer) --- */
  const pop = () => drawer().querySelector("[data-ux-pdrawer-pop]");
  const popSupported = typeof HTMLElement !== "undefined" && "showPopover" in HTMLElement.prototype;
  let popFor = null;
  const popOpen = () => (popSupported ? pop().matches(":popover-open") : !pop().hidden);
  const hidePop = () => {
    const box = pop();
    if (!box || !popOpen()) return;
    if (popSupported) box.hidePopover();
    else {
      box.hidden = true;
      popClosed();
    }
  };
  const popClosed = () => {
    const from = popFor;
    popFor = null;
    from?.setAttribute("aria-expanded", "false");
    const box = pop();
    if (from?.isConnected && (box.contains(document.activeElement) || document.activeElement === document.body || document.activeElement === drawer())) from.focus({ preventScroll: true });
  };
  const place = (box, trigger) => {
    const rect = trigger.getBoundingClientRect();
    const width = box.offsetWidth;
    const height = box.offsetHeight;
    const left = Math.max(8, Math.min(rect.left, window.innerWidth - width - 8));
    let top = rect.bottom + 4;
    if (top + height > window.innerHeight - 8) top = Math.max(8, rect.top - height - 4);
    box.style.left = `${left}px`;
    box.style.top = `${top}px`;
  };
  /* items: [{ heading } | { label, checked, run, detail } | { separator }]; columns: two side by side (rooms) */
  const menu = (trigger, sections, { label = "", radio = true, columns = false } = {}) => {
    const box = pop();
    hidePop();
    box.classList.toggle("ux-pdrawer__pop--columns", columns);
    box.setAttribute("aria-label", label);
    const runs = new Map();
    const item = (entry) => {
      const node = el("button", "ux-pdrawer__item", { type: "button", role: radio && entry.checked !== undefined ? "menuitemradio" : "menuitem" });
      if (entry.checked !== undefined) node.setAttribute("aria-checked", String(Boolean(entry.checked)));
      node.append(el("span", "ux-pdrawer__item-label", { textContent: entry.label }));
      if (entry.detail) node.append(el("span", "ux-pdrawer__item-detail", { textContent: entry.detail }));
      if (entry.checked) node.append(icon("check"));
      runs.set(node, entry.run);
      return node;
    };
    box.replaceChildren(...sections.map((section, index) => {
      const group = el("div", `ux-pdrawer__group${section.wide ? " ux-pdrawer__group--wide" : ""}`, { role: "group" });
      if (section.heading) {
        const heading = el("p", "ux-pdrawer__group-head", { textContent: section.heading, id: `ux-pdrawer-group-${index}` });
        heading.setAttribute("aria-hidden", "true");
        group.setAttribute("aria-labelledby", heading.id);
        group.append(heading);
      }
      group.append(...section.items.map(item));
      return group;
    }));
    box.onclick = (event) => {
      const node = event.target.closest(".ux-pdrawer__item");
      if (!node) return;
      const run = runs.get(node);
      const key = focusKey(popFor);
      hidePop();
      run?.();
      /* the change redrew the drawer: back to the control the menu came from, unless the choice moved focus on */
      requestAnimationFrame(() => {
        if (popOpen()) return; /* the choice opened another menu (Rename's rooms) */
        const at = document.activeElement;
        if (!at || at === document.body || at === drawer() || !at.isConnected || pop().contains(at)) refocus(key);
      });
    };
    popFor = trigger;
    trigger.setAttribute("aria-expanded", "true");
    if (popSupported) box.showPopover();
    else box.hidden = false;
    place(box, trigger);
    (box.querySelector('[aria-checked="true"]') || box.querySelector(".ux-pdrawer__item"))?.focus({ preventScroll: true });
  };
  const menuKeys = (event) => {
    const box = pop();
    const items = [...box.querySelectorAll(".ux-pdrawer__item")];
    const at = items.indexOf(document.activeElement);
    const step = { ArrowDown: 1, ArrowUp: -1 }[event.key];
    if (step) {
      event.preventDefault();
      items[(at + step + items.length) % items.length]?.focus();
    } else if (event.key === "Home" || event.key === "End") {
      event.preventDefault();
      items[event.key === "Home" ? 0 : items.length - 1]?.focus();
    } else if (event.key === "Tab") {
      event.preventDefault();
      hidePop();
    } else if (event.key === "Escape" && !popSupported) {
      event.preventDefault();
      hidePop();
    }
  };

  const roomMenu = (trigger, palette) => {
    const current = palette.name.trim().toLowerCase();
    const room = (name) => ({ label: name, checked: name.toLowerCase() === current, run: () => UxPalette.renamePalette(palette.id, name) && UxPaletteTrack("palette_room_set", { palette: palette.id, room: name, source: "drawer" }) });
    menu(trigger, [
      { heading: t("inside"), items: t("roomsInside").split("|").filter(Boolean).map(room) },
      { heading: t("outside"), items: t("roomsOutside").split("|").filter(Boolean).map(room) },
      { wide: true, items: [{ label: t("roomOwn"), run: () => edit({ kind: "palette", id: palette.id }) }] }
    ], { label: t("roomLabel", { name: palette.name }), columns: true });
  };
  const roleMenu = (trigger, palette, handle) => {
    const colour = palette.colours.find((saved) => saved.h === handle);
    if (!colour) return;
    const project = palette.project ? UxPalette.project(palette.project) : null;
    const set = (role) => () => {
      if (UxPalette.setRole(palette.id, handle, role)) UxPaletteTrack("palette_role_set", { palette: palette.id, handle, role, source: "drawer" });
    };
    menu(trigger, [{
      items: [
        ...UxPaletteUi.surfaceList().surfaces.map((one) => ({ label: one.title, detail: one.finishTitle, checked: UxPaletteUi.surface(colour.r)?.slug === one.slug, run: set(one.slug) })),
        { label: t("roleNone"), checked: !colour.r, run: set(null) }
      ]
    }], { label: t("roleTitle", { colour: UxPaletteUi.shortName(colour.n) }) });
  };
  const paletteMenu = (trigger, palette) => {
    menu(trigger, [{
      items: [
        { label: t("rename"), run: () => roomMenu(trigger, palette) },
        {
          label: t("deletePalette"),
          run: () => {
            const undo = UxPalette.deletePalette(palette.id);
            if (!undo) return;
            UxPaletteTrack("palette_deleted", { palette: palette.id, source: "drawer" });
            UxPaletteUi.notify(t("paletteDeleted", { name: palette.name }), { undo: () => UxPalette.restore(undo), ms: 8000 });
            drawer().querySelector(".ux-pdrawer__title").focus();
          }
        }
      ]
    }], { label: t("menuLabel", { name: palette.name }), radio: false });
  };
  const moveMenu = (trigger, palette) => {
    const move = (project) => () => {
      if (!UxPalette.movePalette(palette.id, project)) return;
      UxPaletteTrack("palette_moved", { palette: palette.id, project, source: "drawer" });
      if (view !== "all") tab = project || "";
    };
    menu(trigger, [
      {
        heading: t("moveTo"),
        items: [
          { label: UxPaletteUi.text("loose"), checked: !palette.project, run: move(null) },
          ...UxPalette.projects().map((project) => ({ label: project.name, checked: palette.project === project.id, run: move(project.id) }))
        ]
      },
      { wide: true, items: [{ label: t("newProjectItem"), run: () => edit({ kind: "move-new", id: palette.id }) }] }
    ], { label: t("moveLabel2", { name: palette.name, where: palette.project ? t("in", { project: UxPalette.project(palette.project)?.name || "" }) : UxPaletteUi.text("loose") }) });
  };
  const projectMenu = (trigger, projectId) => {
    const project = UxPalette.project(projectId);
    if (!project) return;
    menu(trigger, [{
      items: [
        { label: t("renameProject"), run: () => edit({ kind: "project", id: projectId }) },
        {
          label: t("deleteProject"),
          run: () => {
            const undo = UxPalette.deleteProject(projectId, { palettes: "keep" });
            if (!undo) return;
            if (tab === projectId) tab = "";
            UxPaletteTrack("project_deleted", { project: projectId, palettes: "keep", source: "drawer" });
            UxPaletteUi.notify(t("projectDeleted", { name: project.name }), { undo: () => UxPalette.restore(undo), ms: 8000 });
            drawer().querySelector(".ux-pdrawer__title").focus();
          }
        }
      ]
    }], { label: t("menuLabel", { name: project.name }), radio: false });
  };

  /* --- names in place: Enter or Save keeps, Escape cancels, leaving keeps --- */
  const edit = (what) => {
    editing = what;
    render();
  };
  const finishEdit = (keep) => {
    const form = drawer()?.querySelector("[data-ux-pdrawer-name-form]");
    if (!editing || !form) return;
    const what = editing;
    const name = form.elements.name.value.trim();
    if (what.kind === "new-project" && keep) {
      if (!name) {
        const error = form.closest(".ux-pdrawer__col").querySelector(".ux-pdrawer__error");
        error.textContent = t("projectName");
        error.hidden = false;
        form.elements.name.setAttribute("aria-invalid", "true");
        form.elements.name.focus();
        return;
      }
      editing = null;
      const id = UxPalette.createProject({ name });
      if (!id) {
        UxPaletteUi.notify(t("tooManyProjects"));
        render();
        return;
      }
      UxPaletteTrack("project_created", { project: id, source: "drawer" });
      tab = id;
      render();
      drawer().querySelector(`.ux-pdrawer__col[data-project="${CSS.escape(id)}"] [data-ux-pdrawer-action="new-palette"]`)?.focus();
      return;
    }
    if (what.kind === "move-new") {
      editing = null;
      const id = keep && name ? UxPalette.createProject({ name }) : null;
      if (keep && name && !id) UxPaletteUi.notify(t("tooManyProjects"));
      if (id) {
        UxPaletteTrack("project_created", { project: id, source: "drawer" });
        UxPalette.movePalette(what.id, id);
        UxPaletteTrack("palette_moved", { palette: what.id, project: id, source: "drawer" });
        if (view !== "all") tab = id;
      }
      render();
      drawer().querySelector(`[data-palette="${CSS.escape(what.id)}"] [data-ux-pdrawer-action="move"]`)?.focus({ preventScroll: true });
      return;
    }
    editing = null;
    if (keep && name && what.kind === "palette") UxPalette.renamePalette(what.id, name);
    if (keep && name && what.kind === "project") UxPalette.renameProject(what.id, name);
    render();
    const dialog = drawer();
    const back = what.kind === "palette" ? dialog.querySelector(`[data-palette="${CSS.escape(what.id)}"] [data-ux-pdrawer-action="palette-menu"]`)
      : what.kind === "project" ? dialog.querySelector(`.ux-pdrawer__col[data-project="${CSS.escape(what.id)}"] [data-ux-pdrawer-action="project-menu"]`)
        : dialog.querySelector('.ux-pdrawer__head [data-ux-pdrawer-action="new-project"]');
    back?.focus({ preventScroll: true });
  };

  /* --- reorder: arrow keys on the handle, or drag it --- */
  const say = (message) => {
    const status = drawer().querySelector("[data-ux-pdrawer-status]");
    status.textContent = "";
    requestAnimationFrame(() => (status.textContent = message));
  };
  const moveRow = (paletteId, handle, by) => {
    const palette = UxPalette.palette(paletteId);
    const order = palette.colours.map((colour) => colour.h);
    const at = order.indexOf(handle);
    const to = Math.max(0, Math.min(order.length - 1, at + by));
    if (at < 0 || to === at) return;
    order.splice(to, 0, order.splice(at, 1)[0]);
    if (UxPalette.reorderColours(paletteId, order)) say(t("movedRow", { colour: UxPaletteUi.shortName(palette.colours[at].n), position: to + 1 }));
  };
  const drag = (event) => {
    const handle = event.target.closest("[data-ux-erow-handle]");
    if (!handle || handle.disabled || event.button > 0) return;
    const item = handle.closest("[data-handle]");
    const rows = item.parentElement;
    const paletteId = item.closest("[data-palette]").dataset.palette;
    event.preventDefault();
    handle.setPointerCapture(event.pointerId);
    item.classList.add("ux-erow--dragging");
    const startY = event.clientY;
    let before = null;
    const onMove = (moveEvent) => {
      item.style.transform = `translateY(${moveEvent.clientY - startY}px)`;
      const others = [...rows.children].filter((other) => other !== item);
      before = others.find((other) => {
        const box = other.getBoundingClientRect();
        return moveEvent.clientY < box.top + box.height / 2;
      }) || null;
      others.forEach((other) => other.classList.toggle("ux-erow--before", other === before));
    };
    const onUp = () => {
      handle.removeEventListener("pointermove", onMove);
      handle.removeEventListener("pointerup", onUp);
      handle.removeEventListener("pointercancel", onUp);
      item.classList.remove("ux-erow--dragging");
      item.style.transform = "";
      [...rows.children].forEach((other) => other.classList.remove("ux-erow--before"));
      const order = [...rows.children].filter((other) => other !== item).map((other) => other.dataset.handle);
      const at = before ? order.indexOf(before.dataset.handle) : order.length;
      order.splice(at, 0, item.dataset.handle);
      const palette = UxPalette.palette(paletteId);
      if (order.join() !== palette.colours.map((colour) => colour.h).join() && UxPalette.reorderColours(paletteId, order)) {
        say(t("movedRow", { colour: UxPaletteUi.shortName(palette.colours.find((colour) => colour.h === item.dataset.handle)?.n), position: at + 1 }));
      }
    };
    handle.addEventListener("pointermove", onMove);
    handle.addEventListener("pointerup", onUp);
    handle.addEventListener("pointercancel", onUp);
  };

  /* --- actions --- */
  const order = async (trigger, paletteId) => {
    if (trigger.getAttribute("aria-busy") === "true") return;
    const palette = UxPalette.palette(paletteId);
    if (!palette) return;
    trigger.setAttribute("aria-busy", "true");
    try {
      /* stock as it is now: if that changes what the button offered, it says so and redraws instead of adding */
      const offered = await UxPaletteSamples.choose(palette.colours);
      palette.colours.forEach((colour) => UxPalette.forget(colour.h));
      const chosen = await UxPaletteSamples.choose(palette.colours);
      if (!chosen.length || chosen.map((item) => item.id).join() !== offered.map((item) => item.id).join()) {
        UxPaletteUi.notify(t("orderUpdated"), { ms: 8000 });
        render();
        return;
      }
      const items = await UxPaletteSamples.add(chosen, paletteId);
      UxPaletteTrack("palette_samples_added", { palette: paletteId, count: chosen.length, pots: chosen.filter((item) => item.choice === "sample").length, swatches: chosen.filter((item) => item.choice === "peel").length, tagged: items.filter((item) => item.properties).length, source: "drawer" });
      close();
    } catch (error) {
      UxPaletteUi.notify(t("orderFailed"), { ms: 8000 });
      render();
    } finally {
      trigger.removeAttribute("aria-busy");
    }
  };
  const onClick = (event) => {
    const dialog = drawer();
    if (event.target === dialog) return; /* the scrim: see wire() */
    const trigger = event.target.closest("[data-ux-pdrawer-action]");
    if (!trigger) return;
    const action = trigger.dataset.uxPdrawerAction;
    const paletteId = trigger.closest("[data-palette]")?.dataset.palette || null;
    const palette = paletteId ? UxPalette.palette(paletteId) : null;
    const projectId = trigger.closest("[data-project]")?.dataset.project ?? null;
    if (action === "close") close();
    else if (action === "use" && palette) {
      if (UxPalette.setActive(palette.id)) UxPaletteTrack("palette_activated", { palette: palette.id, source: "drawer" });
      requestAnimationFrame(() => dialog.querySelector(`[data-palette="${CSS.escape(palette.id)}"] [data-ux-pdrawer-action="palette-menu"]`)?.focus({ preventScroll: true }));
    } else if (action === "palette-menu" && palette) paletteMenu(trigger, palette);
    else if (action === "move" && palette) moveMenu(trigger, palette);
    else if (action === "role" && palette) roleMenu(trigger, palette, trigger.closest("[data-handle]").dataset.handle);
    else if (action === "remove" && palette) {
      const handle = trigger.closest("[data-handle]").dataset.handle;
      const colour = palette.colours.find((saved) => saved.h === handle);
      const next = trigger.closest("[data-handle]").nextElementSibling || trigger.closest("[data-handle]").previousElementSibling;
      const undo = UxPalette.removeFrom(palette.id, handle);
      if (!undo) return;
      UxPaletteTrack("palette_colour_removed", { handle, source: "drawer", palette: palette.id });
      UxPaletteUi.notify(t("removed", { colour: UxPaletteUi.shortName(colour?.n), name: palette.name }), { undo: () => UxPalette.restore(undo) });
      const card = dialog.querySelector(`[data-palette="${CSS.escape(palette.id)}"]`);
      (next && card?.querySelector(`[data-handle="${CSS.escape(next.dataset.handle)}"] [data-ux-pdrawer-action="remove"]`) || card?.querySelector('[data-ux-pdrawer-action="palette-menu"]'))?.focus({ preventScroll: true });
    } else if (action === "order" && palette) order(trigger, palette.id);
    else if (action === "show" && palette) {
      unfolded.add(palette.id);
      render();
      dialog.querySelector(`[data-palette="${CSS.escape(palette.id)}"] [data-ux-pdrawer-action="palette-menu"]`)?.focus({ preventScroll: true });
    } else if (action === "tab") {
      tab = trigger.dataset.project;
      editing = editing?.kind === "new-project" ? null : editing;
      render();
      dialog.querySelector(`.ux-pdrawer__tab[data-project="${CSS.escape(tab)}"]`)?.focus();
    } else if (action === "new-palette" || action === "new-palette-here") {
      /* All palettes: the sheet starts in the project of the palette in use */
      const project = view === "all" && UxPalette.projects().length ? undefined : action === "new-palette" ? projectId || null : tab || null;
      UxPaletteUi.newPalette({
        from: trigger,
        project,
        onCreate: (id) => requestAnimationFrame(() => {
          const created = drawer().querySelector(`[data-palette="${CSS.escape(id)}"]`);
          created?.scrollIntoView({ block: "nearest", inline: "nearest" });
          created?.querySelector('[data-ux-pdrawer-action="palette-menu"]')?.focus({ preventScroll: true });
        })
      });
    } else if (action === "new-project") {
      setView("projects");
      edit({ kind: "new-project" });
    }
    else if (action === "project-menu" && projectId) projectMenu(trigger, projectId);
  };
  const onKey = (event) => {
    if (pop().contains(event.target)) {
      menuKeys(event);
      return;
    }
    const input = event.target.closest(".ux-pdrawer__name-input");
    if (input && event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      finishEdit(false);
      return;
    }
    const tabButton = event.target.closest(".ux-pdrawer__tab");
    if (tabButton && (event.key === "ArrowRight" || event.key === "ArrowLeft")) {
      event.preventDefault();
      const all = [...drawer().querySelectorAll(".ux-pdrawer__tab")];
      const next = all[(all.indexOf(tabButton) + (event.key === "ArrowRight" ? 1 : -1) + all.length) % all.length];
      next?.click();
      return;
    }
    const handle = event.target.closest("[data-ux-erow-handle]");
    const by = handle && { ArrowUp: -1, ArrowDown: 1, Home: -99, End: 99 }[event.key];
    if (by) {
      event.preventDefault();
      moveRow(handle.closest("[data-palette]").dataset.palette, handle.closest("[data-handle]").dataset.handle, by);
    }
  };

  /* --- swipe down to close (under 1000) --- */
  const swipe = (event) => {
    if (wide.matches || event.button > 0 || event.target.closest("button, a, input, label")) return;
    const dialog = drawer();
    const startY = event.clientY;
    let dy = 0;
    const target = event.currentTarget;
    target.setPointerCapture(event.pointerId);
    dialog.classList.add("is-dragging");
    const onMove = (moveEvent) => {
      dy = Math.max(0, moveEvent.clientY - startY);
      dialog.style.transform = `translateY(${dy}px)`;
    };
    const onUp = () => {
      target.removeEventListener("pointermove", onMove);
      target.removeEventListener("pointerup", onUp);
      target.removeEventListener("pointercancel", onUp);
      dialog.classList.remove("is-dragging");
      dialog.style.transform = "";
      if (dy > 80) close({ from: dy });
    };
    target.addEventListener("pointermove", onMove);
    target.addEventListener("pointerup", onUp);
    target.addEventListener("pointercancel", onUp);
  };

  const wire = (dialog) => {
    if (dialog.dataset.wired) return;
    dialog.dataset.wired = "true";
    dialog.addEventListener("click", onClick);
    dialog.addEventListener("keydown", onKey);
    dialog.addEventListener("pointerdown", (event) => {
      if (event.target.closest("[data-ux-erow-handle]")) drag(event);
    });
    /* the scrim closes it, but only a press that started on it */
    let downOnScrim = false;
    dialog.addEventListener("pointerdown", (event) => {
      downOnScrim = event.target === dialog;
    });
    dialog.addEventListener("click", (event) => {
      if (event.target === dialog && downOnScrim) close();
      downOnScrim = false;
    });
    dialog.querySelectorAll("[data-ux-pdrawer-grab], [data-ux-pdrawer-swipe]").forEach((part) => part.addEventListener("pointerdown", swipe));
    dialog.querySelector("[data-ux-pdrawer-views]")?.addEventListener("change", (event) => {
      setView(event.target.value);
      editing = null;
      render();
      UxPaletteTrack("palette_drawer_view", { view });
      dialog.querySelector(`[data-ux-pdrawer-views] input[value="${view}"]`)?.focus();
    });
    dialog.addEventListener("submit", (event) => {
      if (!event.target.matches("[data-ux-pdrawer-name-form]")) return;
      event.preventDefault();
      finishEdit(true);
    });
    dialog.addEventListener("focusout", (event) => {
      const form = event.target.closest?.("[data-ux-pdrawer-name-form]");
      if (!form || form.contains(event.relatedTarget)) return;
      setTimeout(() => {
        if (!editing || form.contains(document.activeElement) || !form.isConnected) return;
        if ((editing.kind === "new-project" || editing.kind === "move-new") && !form.elements.name.value.trim()) finishEdit(false);
        else finishEdit(true);
      }, 0);
    });
    /* Esc: a menu or a name being edited first, then the drawer (sliding down, as the ✕ does) */
    dialog.addEventListener("cancel", (event) => {
      event.preventDefault();
      if (popOpen()) hidePop();
      else if (editing) finishEdit(false);
      else close();
    });
    dialog.addEventListener("close", () => {
      hidePop();
      editing = null;
      document.documentElement.classList.remove("ux-pdrawer-open");
      if (notice?.home) notice.home.parent.insertBefore(notice.node, notice.home.next);
      notice = null;
      if (opener?.isConnected) opener.focus({ preventScroll: true });
    });
    const box = pop();
    if (popSupported) box.addEventListener("toggle", (event) => event.newState === "closed" && popClosed());
    else {
      box.hidden = true;
      document.addEventListener("pointerdown", (event) => {
        if (!box.hidden && !box.contains(event.target) && event.target !== popFor) hidePop();
      });
    }
    wide.addEventListener("change", () => {
      hidePop();
      render();
    });
    document.addEventListener("ux-palette:change", render);
  };

  const open = (from, { at = null } = {}) => {
    const dialog = drawer();
    if (!dialog || typeof dialog.showModal !== "function") return false;
    wire(dialog);
    opener = from || document.activeElement;
    tab = null;
    editing = null;
    unfolded.clear();
    document.querySelectorAll("dialog.ux-sheet[open]").forEach((sheet) => sheet.close());
    /* the Added / Undo notice joins the drawer while it's open (outside it the page is inert under the scrim) */
    const node = document.querySelector("[data-ux-palette-notice]");
    if (node) {
      notice = { node, home: { parent: node.parentNode, next: node.nextSibling } };
      dialog.append(node);
    }
    if (dialog.classList.contains("is-closing")) settle(dialog);
    dialog.showModal();
    document.documentElement.classList.add("ux-pdrawer-open");
    render();
    const active = UxPalette.active();
    const current = active && dialog.querySelector(`[data-palette="${CSS.escape(active.id)}"]`);
    current?.closest(".ux-pdrawer__col")?.scrollIntoView({ block: "nearest", inline: "nearest" });
    /* opened at a palette (the palette page's dock, MP3-8): unless its head (name, Adding here, the first colour) is
       already in view, it comes to the top of the drawer (measured in the drawer, which is still sliding in) */
    const target = at && dialog.querySelector(`[data-palette="${CSS.escape(at)}"]`);
    const body = dialog.querySelector("[data-ux-pdrawer-body]");
    if (target && body) {
      target.closest(".ux-pdrawer__col")?.scrollIntoView({ block: "nearest", inline: "nearest" });
      const view = body.getBoundingClientRect();
      const above = target.getBoundingClientRect().top - view.top - (parseFloat(getComputedStyle(body).paddingTop) || 0);
      if (above < 0 || view.top + above + 160 > view.bottom) body.scrollTop += above;
    }
    dialog.querySelector(".ux-pdrawer__title").focus({ preventScroll: true });
    UxPaletteTrack("palette_drawer_opened", { palettes: UxPalette.palettes().length });
    return true;
  };
  /* Close slides it down first (theme.css .is-closing), then closes the dialog; from: a swipe's distance, so the
     slide carries on from where the finger let go. Reduced motion closes at once. */
  let closing = 0; /* the latest close, so an earlier one's timer can't cut a later slide short */
  const settle = (dialog) => {
    dialog.classList.remove("is-closing");
    dialog.style.removeProperty("--ux-pdrawer-from");
    if (dialog.open) dialog.close();
  };
  const close = ({ from = 0 } = {}) => {
    const dialog = drawer();
    if (!dialog?.open || dialog.classList.contains("is-closing")) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      dialog.close();
      return;
    }
    dialog.style.setProperty("--ux-pdrawer-from", `${from}px`);
    dialog.classList.add("is-closing");
    const token = ++closing;
    let done = false;
    const finish = () => {
      if (done || token !== closing || !dialog.classList.contains("is-closing")) return;
      done = true;
      dialog.removeEventListener("animationend", onEnd);
      settle(dialog);
    };
    const onEnd = (event) => event.target === dialog && event.animationName === "ux-pdrawer-down" && finish();
    dialog.addEventListener("animationend", onEnd);
    setTimeout(finish, 400); /* if the animation never runs (a hidden tab) */
  };

  return { open, close, isOpen: () => Boolean(drawer()?.open) };
})();
window.UxPaletteDrawer = UxPaletteDrawer;

/* Basket colours join a palette (MP3-7, GitHub #408; Matt: "palettes should be automatically updated with colours
   added to your basket (without having to add to palette manually)", in no specific project or room). The first paint
   colour that goes into the basket makes "My palette" (not in a project, the default name: no room; not made the one
   in use), or uses the one there is; each colour then joins it once per basket, with the Added notice (Change, Undo).
   A colour already in any of the visitor's palettes is already "in the palettes": it's left where it is.
   A colour is anything in the all-colours list (UxColours), so primers, tools and wood care stay out; lines tagged
   _palette (ordered from a palette) are left alone. Once My palette is full the rest start "My palette 2" (then 3…;
   the store numbers them), each filled in turn, so no basket colour is dropped (#461); a colour that can't fit even
   so (30 palettes) is counted in one notice, once per basket. Taking a line out of the basket leaves the palette
   alone, and so does a colour already handled for this basket (taking it out of the palette sticks). Every add ends
   in a cart:change carrying the whole basket, so that's what this watches. */
(() => {
  const SEEN = "mylands-basket-palette";
  const read = () => {
    try {
      return JSON.parse(window.localStorage.getItem(SEEN) || "null");
    } catch (error) {
      return null;
    }
  };
  const write = (value) => {
    try {
      window.localStorage.setItem(SEEN, JSON.stringify(value));
    } catch (error) {
      /* storage blocked: this page view only */
    }
  };
  let queue = Promise.resolve();
  document.addEventListener("cart:change", (event) => {
    const cart = event.detail?.cart;
    if (!Array.isArray(cart?.items)) return;
    queue = queue.then(() => take(cart)).catch(() => {});
  });
  const take = async (cart) => {
    let seen = read();
    if (!seen || seen.token !== (cart.token || "")) seen = { token: cart.token || "", handles: [] };
    const fresh = [...new Set(cart.items.filter((line) => line.handle && !line.properties?._palette && !seen.handles.includes(line.handle)).map((line) => line.handle))];
    if (!fresh.length) return;
    const data = await UxColours.load();
    const byHandle = new Map((data?.colours || []).map((colour) => [colour.h, colour]));
    seen.handles.push(...fresh);
    write(seen);
    const colours = fresh.map((handle) => byHandle.get(handle)).filter((colour) => colour && !UxPalette.palettesWith(colour.h).length).map((colour) => ({ h: colour.h, n: colour.n, c: colour.c }));
    if (!colours.length) return;
    const name = window.uxPaletteDefaultName || "My palette";
    /* My palette, then My palette 2, 3…: the first with room takes what it can, and a new one the rest */
    const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const series = new RegExp(`^${escaped}(?: \\d+)?$`, "i");
    let left = colours;
    const filled = [];
    UxPalette.palettes().filter((one) => !one.project && series.test(one.name)).forEach((palette) => {
      const added = [];
      for (const colour of left) {
        if (!UxPalette.addTo(palette.id, colour)) break; /* full */
        added.push(colour);
      }
      if (!added.length) return;
      left = left.filter((colour) => !added.includes(colour));
      filled.push({ id: palette.id, colours: added, created: false });
    });
    while (left.length) {
      const chunk = left.slice(0, UxPalette.LIMITS.colours);
      const id = UxPalette.createPalette({ name, project: null, colours: chunk, activate: false });
      if (!id) break; /* 30 palettes */
      left = left.slice(chunk.length);
      filled.push({ id, colours: chunk, created: true });
    }
    filled.forEach((one) => one.colours.forEach((colour) => UxPaletteTrack("palette_colour_added", { handle: colour.h, source: "basket", palette: one.id })));
    /* some didn't fit: said once for this basket, not on every change after */
    if (left.length) {
      if (seen.told) return;
      seen.told = true;
      write(seen);
      UxPaletteUi.notify(UxPaletteUi.text(left.length === 1 ? "basketLeftOne" : "basketLeftOther", { count: left.length }), { ms: 8000 });
      return;
    }
    if (!filled.length) return;
    const count = filled.reduce((sum, one) => sum + one.colours.length, 0);
    if (count === 1) {
      UxPaletteUi.added(filled[0].colours[0], { palette: filled[0].id, created: filled[0].created });
      return;
    }
    const names = filled.map((one) => UxPalette.palette(one.id)?.name || name);
    const message = names.length === 1 ? UxPaletteUi.text("basketMany", { count, name: names[0] })
      : UxPaletteUi.text("basketSplit", { count, names: names.slice(0, -1).join(", "), name: names[names.length - 1] });
    UxPaletteUi.notify(message, {
      undo: () => filled.forEach((one) => {
        if (one.created) UxPalette.deletePalette(one.id);
        else one.colours.forEach((colour) => UxPalette.removeFrom(one.id, colour.h));
      })
    });
  };
})();

/* Empty-basket palette prompt (milestone 6, GitHub #139; snippets/ux-basket-palette.liquid): shown while colours are
   saved, with the count and up to six swatches. The basket drawer redraws its body as the basket changes, so prompts
   are filled as they arrive. */
(() => {
  const fill = (prompt) => {
    const colours = UxPalette.list();
    const count = colours.length;
    prompt.hidden = !count;
    if (!count) return;
    prompt.querySelector("[data-ux-basket-palette-count]").textContent = count === 1 ? prompt.dataset.one : prompt.dataset.other.replace("[count]", count);
    const text = prompt.querySelector("[data-ux-basket-palette-text]");
    if (text) text.textContent = count === 1 ? prompt.dataset.textOne : prompt.dataset.textOther;
    prompt.querySelector("[data-ux-basket-palette-swatches]").replaceChildren(...colours.slice(0, 6).map((colour) => {
      const swatch = document.createElement("span");
      swatch.className = "ux-basket-palette__swatch";
      swatch.style.background = colour.c || "var(--ux-grey)";
      return swatch;
    }));
  };
  const fillAll = () => document.querySelectorAll("[data-ux-basket-palette]").forEach(fill);
  document.addEventListener("ux-palette:change", fillAll);
  new MutationObserver((mutations) => {
    mutations.forEach((mutation) => mutation.addedNodes.forEach((node) => {
      if (node.nodeType !== 1) return;
      if (node.matches("[data-ux-basket-palette]")) fill(node);
      else node.querySelectorAll("[data-ux-basket-palette]").forEach(fill);
    }));
  }).observe(document.body, { childList: true, subtree: true });
  fillAll();
})();

/* ---------------------------------------------------------------------------
   Your palettes (Palettes v2, GitHub #185; sections/ux-palette.liquid <ux-palettes>). Shown by the palette page
   without ?palette= once anything is saved: the head (device or account eyebrow, the count line, New palette, New
   project), then one group per project and Not in a project, each palette a card (its colours, the name linking to
   ?palette=<id>, "4 colours · changed today", a ••• menu). Menus are buttons in a role=menu list (arrows, Escape).
   Rename, Move, New project and Delete project are sheets (UxPaletteUi's dialogs); deletes leave an Undo for 8 s.
   --------------------------------------------------------------------------- */
var UxPalettes = class extends HTMLElement {
  connectedCallback() {
    if (this._wired) return;
    this._wired = true;
    try {
      this.types = JSON.parse(this.querySelector("[data-ux-palette-types]")?.textContent || "[]");
    } catch (error) {
      this.types = [];
    }
    this.groupsBox = this.querySelector("[data-ux-palettes-groups]");
    this.addEventListener("click", (event) => this.onClick(event));
    this.addEventListener("keydown", (event) => this.onKey(event));
    document.addEventListener("click", (event) => {
      if (!event.target.closest?.(".ux-menu-wrap")) this.closeMenus();
    });
    document.addEventListener("ux-palette:change", () => !this.hidden && this.render());
    /* on its own (the account's Your palettes, #289) it shows itself; the palette page shows it otherwise */
    if (!this.closest("ux-palette-page")) {
      this.hidden = false;
      this.render();
      /* P2 (#303): the overview's Start a project (#new-project) and project cards (#project-<id>) land here */
      if (this.dataset.context === "pro") {
        requestAnimationFrame(() => this.followHash());
        window.addEventListener("hashchange", () => this.followHash());
      }
    }
  }
  followHash() {
    const hash = decodeURIComponent(window.location.hash.slice(1));
    if (hash === "new-project") {
      /* not at the 30-project cap: the button says why it's unavailable */
      const button = this.querySelector('[data-ux-palettes-action="new-project"]');
      if (button && !button.disabled) this.newProject(button);
    } else if (hash.startsWith("project-")) {
      const group = this.querySelector(`[data-project="${CSS.escape(hash.slice(8))}"]`);
      if (!group) return;
      group.scrollIntoView({ block: "start" });
      const name = group.querySelector("[data-ux-pgroup-name]");
      name.tabIndex = -1;
      name.focus({ preventScroll: true });
    }
  }
  t(key, values = {}) {
    return Object.entries(values).reduce((string, [name, value]) => string.split(`[${name}]`).join(value), this.dataset[key] || "");
  }
  plural(key, count) {
    return count === 1 ? this.t(`${key}One`) : this.t(`${key}Other`, { count });
  }
  say(text) {
    const status = this.querySelector("[data-ux-palettes-status]");
    status.textContent = "";
    requestAnimationFrame(() => (status.textContent = text));
  }
  pageUrl(id) {
    /* a palette opens on the palette page; on that page, in place */
    const url = this.closest("ux-palette-page") ? new URL(window.location.href) : UxPalette.pageUrl();
    url.searchParams.delete("c");
    url.searchParams.set("palette", id);
    return `${url.pathname}${url.search}`;
  }
  typeLabel(slug) {
    return this.types.find((type) => type.slug === slug)?.label || "";
  }
  changed(iso) {
    const date = new Date(iso || 0);
    if (Number.isNaN(date.getTime()) || !iso) return "";
    const day = (value) => new Date(value.getFullYear(), value.getMonth(), value.getDate()).getTime();
    const days = Math.round((day(new Date()) - day(date)) / 86400000);
    if (days <= 0) return this.t("changedToday");
    if (days === 1) return this.t("changedYesterday");
    return this.t("changed", { date: new Intl.DateTimeFormat(document.documentElement.lang || "en-GB", { day: "numeric", month: "short" }).format(date) });
  }
  render() {
    const palettes = UxPalette.palettes();
    const projects = UxPalette.projects();
    this.querySelector("[data-ux-palettes-eyebrow]").textContent = UxPalette.account?.customer ? this.dataset.eyebrowAccount : this.dataset.eyebrowDevice;
    const summary = projects.length
      ? this.t("inProjects", { palettes: this.plural("palettes", palettes.length), projects: this.plural("projects", projects.length) })
      : this.plural("palettes", palettes.length);
    /* the account's Your palettes is shown empty too (the palette page isn't): no "0 palettes" */
    this.querySelector("[data-ux-palettes-lead]").textContent = !palettes.length && this.dataset.leadEmpty ? this.dataset.leadEmpty : this.t("lead", { summary });
    const empty = this.querySelector("[data-ux-palettes-empty]");
    if (empty) empty.hidden = palettes.length > 0;
    const groupTemplate = this.querySelector("template[data-ux-pgroup]");
    const groups = projects.map((project) => ({ project, palettes: palettes.filter((palette) => palette.project === project.id) }));
    const loose = palettes.filter((palette) => !palette.project || !projects.some((project) => project.id === palette.project));
    if (loose.length) groups.push({ project: null, palettes: loose });
    this.groupsBox.replaceChildren(...groups.map(({ project, palettes: inside }, index) => {
      const group = groupTemplate.content.firstElementChild.cloneNode(true);
      const nameId = `ux-pgroup-${project?.id || "loose"}`;
      group.setAttribute("aria-labelledby", nameId);
      group.dataset.project = project?.id || "";
      if (project) group.id = `project-${project.id}`;
      const name = group.querySelector("[data-ux-pgroup-name]");
      name.id = nameId;
      name.textContent = project ? project.name : UxPalette.projectsShown ? UxPaletteUi.text("loose") : "";
      const meta = group.querySelector("[data-ux-pgroup-meta]");
      if (project) {
        /* P2 (#303): "For Mr and Mrs Clarke · 3 palettes · Walls & ceilings" */
        const client = project.client && this.dataset.forClient ? this.t("forClient", { client: project.client }) : "";
        meta.textContent = [client, this.plural("palettes", inside.length), this.typeLabel(project.type)].filter(Boolean).join(" · ");
        group.querySelectorAll('[data-ux-palettes-action="share-project"], [data-ux-palettes-action="order-project"]').forEach((button) => (button.disabled = !inside.some((palette) => palette.colours.length)));
        group.querySelector("[data-ux-menu-toggle]").setAttribute("aria-label", this.t("menuLabel", { name: project.name }));
      } else {
        meta.remove();
        group.querySelector("[data-ux-pgroup-actions]").remove();
        group.querySelector("[data-ux-pgroup-more]").remove();
        group.classList.add("ux-pgroup--loose");
      }
      const cards = group.querySelector("[data-ux-pgroup-cards]");
      cards.append(...inside.map((palette) => this.card(palette)));
      if (project) {
        const tile = this.querySelector("template[data-ux-pnew]").content.firstElementChild.cloneNode(true);
        tile.querySelector("[data-ux-pnew-label]").textContent = this.t("newIn", { project: project.name });
        cards.append(tile);
      }
      return group;
    }));
    /* at the caps (30 palettes, 30 projects) the create actions are unavailable and say why */
    /* Keep your palettes (#189): signed out, until Not now */
    const keepPanel = this.querySelector("[data-ux-keep]");
    if (keepPanel) {
      let dismissed = false;
      try {
        dismissed = Boolean(localStorage.getItem("mylands-keep-dismissed"));
      } catch (error) {}
      keepPanel.hidden = dismissed || !palettes.length || Boolean(UxPalette.account?.customer);
      if (!keepPanel.hidden) UxPaletteUi.keepLinks(keepPanel);
    }
    const fullPalettes = palettes.length >= UxPalette.LIMITS.palettes;
    const fullProjects = projects.length >= UxPalette.LIMITS.projects;
    this.querySelectorAll('[data-ux-palettes-action="new-palette"], [data-ux-palettes-action="new-palette-in"]').forEach((button) => (button.disabled = fullPalettes));
    this.querySelector('[data-ux-palettes-action="new-project"]').disabled = fullProjects;
    const limit = this.querySelector("[data-ux-palettes-limit]");
    limit.hidden = !fullPalettes && !fullProjects;
    if (palettes.length > UxPalette.LIMITS.palettes) {
      /* over the limit after a login merge (over_limit): nothing has been lost */
      const title = Object.assign(document.createElement("strong"), { className: "ux-palettes__limit-title", textContent: limit.dataset.overTitle.replace("[count]", palettes.length) });
      limit.replaceChildren(title, document.createTextNode([limit.dataset.over, fullProjects && limit.dataset.projects].filter(Boolean).join(" ")));
    } else {
      limit.textContent = [fullPalettes && limit.dataset.palettes, fullProjects && limit.dataset.projects].filter(Boolean).join(" ");
    }
  }
  card(palette) {
    const card = this.querySelector("template[data-ux-pcard]").content.firstElementChild.cloneNode(true);
    card.dataset.palette = palette.id;
    const colours = card.querySelector("[data-ux-pcard-colours]");
    colours.classList.toggle("ux-pcard__colours--empty", !palette.colours.length);
    colours.replaceChildren(...palette.colours.slice(0, UxPalette.LIMITS.colours).map((colour) => {
      const block = document.createElement("span");
      block.style.background = colour.c || "var(--ux-grey)";
      return block;
    }));
    const link = card.querySelector("[data-ux-pcard-link]");
    link.href = this.pageUrl(palette.id);
    link.textContent = palette.name;
    /* P2 (#303): "roles set" once every colour has its surface, for a designer's specification; else when it changed */
    const rolesSet = this.dataset.rolesSet && palette.colours.length && palette.colours.every((colour) => colour.r);
    const detail = [this.plural("colours", palette.colours.length), rolesSet ? this.dataset.rolesSet : this.changed(palette.updated)].filter(Boolean);
    const detailBox = card.querySelector("[data-ux-pcard-detail]");
    detailBox.innerHTML = "";
    detailBox.append(detail[0]);
    if (detail[1]) {
      const changed = document.createElement("span");
      changed.className = "ux-pcard__changed";
      changed.textContent = ` · ${detail[1]}`;
      detailBox.append(changed);
    }
    card.querySelector("[data-ux-menu-toggle]").setAttribute("aria-label", this.t("menuLabel", { name: palette.name }));
    return card;
  }

  /* --- menus --- */
  closeMenus(except = null) {
    this.querySelectorAll(".ux-menu:not([hidden])").forEach((menu) => {
      if (menu === except) return;
      menu.hidden = true;
      menu.previousElementSibling?.setAttribute("aria-expanded", "false");
    });
  }
  toggleMenu(toggle) {
    const menu = toggle.nextElementSibling;
    const open = menu.hidden;
    this.closeMenus(menu);
    menu.hidden = !open;
    toggle.setAttribute("aria-expanded", String(open));
    if (open) menu.querySelector("[role=menuitem]")?.focus();
  }
  onKey(event) {
    const menu = event.target.closest(".ux-menu");
    if (!menu) return;
    const items = [...menu.querySelectorAll("[role=menuitem]")];
    const at = items.indexOf(event.target);
    if (event.key === "Escape") {
      event.preventDefault();
      menu.hidden = true;
      menu.previousElementSibling.setAttribute("aria-expanded", "false");
      menu.previousElementSibling.focus();
    } else if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      items[(at + (event.key === "ArrowDown" ? 1 : -1) + items.length) % items.length]?.focus();
    } else if (event.key === "Home" || event.key === "End") {
      event.preventDefault();
      items[event.key === "Home" ? 0 : items.length - 1]?.focus();
    } else if (event.key === "Tab") {
      menu.hidden = true;
      menu.previousElementSibling.setAttribute("aria-expanded", "false");
    }
  }

  /* --- actions --- */
  onClick(event) {
    const toggle = event.target.closest("[data-ux-menu-toggle]");
    if (toggle) {
      this.toggleMenu(toggle);
      return;
    }
    if (event.target.closest("[data-ux-keep-dismiss]")) {
      try {
        localStorage.setItem("mylands-keep-dismissed", "1");
      } catch (error) {}
      this.render();
      this.querySelector("[data-ux-palettes-action=\"new-palette\"]")?.focus();
      return;
    }
    const button = event.target.closest("[data-ux-palettes-action]");
    if (!button) return;
    const from = button.closest(".ux-menu-wrap")?.querySelector("[data-ux-menu-toggle]") || button;
    this.closeMenus();
    const paletteId = button.closest("[data-palette]")?.dataset.palette;
    const projectId = button.closest("[data-project]")?.dataset.project || null;
    const action = button.dataset.uxPalettesAction;
    if (action === "new-palette") UxPaletteUi.newPalette({ from, project: null, onCreate: (id) => (window.location.href = this.pageUrl(id)) });
    else if (action === "new-palette-in") UxPaletteUi.newPalette({ from, project: projectId, onCreate: (id) => (window.location.href = this.pageUrl(id)) });
    else if (action === "new-project") this.newProject(from);
    else if (action === "rename-project") this.rename({ kind: "project", id: projectId, from });
    else if (action === "delete-project") this.deleteProject(projectId, from);
    else if (action === "rename-palette") this.rename({ kind: "palette", id: paletteId, from });
    else if (action === "duplicate") this.duplicate(paletteId);
    else if (action === "move") this.move(paletteId, from);
    else if (action === "share") this.share(paletteId);
    else if (action === "delete-palette") this.deletePalette(paletteId);
    else if (action === "share-project") this.shareProject(projectId);
    else if (action === "order-project") this.orderProject(projectId, button);
    else if (action === "client-project") this.client(projectId, from);
  }
  /* P2 Share with client (#303): one message, each palette's link (the read-only palette page, ?c=) under the project's
     name, shared or copied as P1 does (uxProSend). A project link of its own waits on the store (#282). */
  shareProject(id) {
    const project = UxPalette.project(id);
    if (!project) return;
    const own = UxPalette.palettes().filter((palette) => palette.project === id && palette.colours.length);
    if (!own.length) return;
    const title = this.t("projectShare", { name: project.name });
    const lines = own.map((palette) => `${palette.name}: ${UxPalette.shareUrl(palette.colours.map((colour) => colour.h))}`);
    uxProSend(title, [title, ...lines].join("\n"), this.dataset.projectCopied);
  }
  /* P2 Order samples (#303): a sample of every colour in the project, one per colour (a Colour Swatch once they're live,
     else the pot: UxPaletteSamples), each tagged with its palette (#179); the basket drawer opens */
  async orderProject(id, button) {
    const own = UxPalette.palettes().filter((palette) => palette.project === id && palette.colours.length);
    if (!own.length || button.getAttribute("aria-busy") === "true") return;
    button.setAttribute("aria-busy", "true");
    try {
      const seen = new Set();
      const items = [];
      for (const palette of own) {
        const fresh = palette.colours.filter((colour) => !seen.has(colour.h));
        fresh.forEach((colour) => seen.add(colour.h));
        const chosen = await UxPaletteSamples.choose(fresh);
        items.push(...(await UxPaletteSamples.items(chosen, palette.id)));
      }
      if (!items.length) {
        this.say(this.dataset.samplesNone);
        UxPaletteUi.notify(this.dataset.samplesNone);
        return;
      }
      await UxPaletteSamples.post(items);
      this.say(this.t("samplesAdded", { count: items.length }));
    } catch (error) {
      UxPaletteUi.notify(this.dataset.samplesFailed);
    } finally {
      button.removeAttribute("aria-busy");
    }
  }
  /* P2 Client (#303): who the project is for, shown as "For …" on the project. Offered once Odoo stores it */
  client(id, from) {
    const project = UxPalette.project(id);
    if (!project || !document.querySelector('[data-ux-palettes-sheet="client"]')) return;
    const dialog = this.sheet("client");
    dialog.querySelector("[data-ux-sheet-title]").textContent = this.t("clientTitle", { name: project.name });
    const form = dialog.querySelector("form");
    form.elements.client.value = project.client || "";
    form.onsubmit = (event) => {
      event.preventDefault();
      UxPalette.setProjectClient(id, form.elements.client.value);
      dialog.close();
    };
    UxPaletteUi.open(dialog, from);
    requestAnimationFrame(() => form.elements.client.focus());
  }
  sheet(kind) {
    const dialog = (this.closest("ux-palette-page") || document).querySelector(`[data-ux-palettes-sheet="${kind}"]`);
    UxPaletteUi.wire(dialog);
    return dialog;
  }
  rename({ kind, id, from }) {
    const target = kind === "project" ? UxPalette.project(id) : UxPalette.palette(id);
    if (!target) return;
    const dialog = this.sheet("rename");
    dialog.querySelector("[data-ux-sheet-title]").textContent = kind === "project" ? this.dataset.renameProject : this.dataset.renamePalette;
    const form = dialog.querySelector("form");
    form.elements.name.value = target.name;
    UxPaletteUi.error(dialog, "");
    form.onsubmit = (event) => {
      event.preventDefault();
      const name = form.elements.name.value.trim();
      if (!name) {
        UxPaletteUi.error(dialog, kind === "project" ? this.dataset.nameNeeded : UxPaletteUi.text("nameNeeded"));
        return;
      }
      if (kind === "project") UxPalette.renameProject(id, name);
      else UxPalette.renamePalette(id, name);
      dialog.close();
    };
    UxPaletteUi.open(dialog, from);
    form.elements.name.select();
  }
  newProject(from) {
    const dialog = this.sheet("new-project");
    const form = dialog.querySelector("form");
    form.elements.name.value = "";
    UxPaletteUi.error(dialog, "");
    /* one Shop by project type (the spec keeps one per project); a second tap clears it */
    const chips = dialog.querySelector("[data-ux-type-chips]");
    chips.replaceChildren(...this.types.map((type) => {
      const label = document.createElement("label");
      label.className = "ux-chip";
      const input = document.createElement("input");
      input.type = "checkbox";
      input.name = "type";
      input.value = type.slug;
      const icon = document.querySelector("[data-ux-icon-check]")?.content.cloneNode(true);
      const box = document.createElement("span");
      box.className = "ux-chip__check";
      box.setAttribute("aria-hidden", "true");
      if (icon) box.append(icon);
      const name = document.createElement("span");
      name.textContent = type.label;
      label.append(input, box, name);
      return label;
    }));
    chips.onchange = (event) => {
      if (!event.target.checked) return;
      chips.querySelectorAll("input").forEach((input) => input !== event.target && (input.checked = false));
    };
    form.onsubmit = (event) => {
      event.preventDefault();
      const name = form.elements.name.value.trim();
      if (!name) {
        UxPaletteUi.error(dialog, this.dataset.nameNeeded);
        form.elements.name.setAttribute("aria-invalid", "true");
        form.elements.name.focus();
        return;
      }
      const type = chips.querySelector("input:checked")?.value || null;
      const id = UxPalette.createProject({ name, type });
      if (!id) {
        UxPaletteUi.error(dialog, this.dataset.tooManyProjects);
        return;
      }
      UxPaletteTrack("project_created", { project: id, type });
      dialog.close();
    };
    UxPaletteUi.open(dialog, from);
    form.elements.name.focus();
  }
  move(id, from) {
    const palette = UxPalette.palette(id);
    if (!palette) return;
    const dialog = this.sheet("move");
    dialog.querySelector("[data-ux-sheet-title]").textContent = this.t("moveTitle", { name: palette.name });
    const box = dialog.querySelector("[data-ux-move-choices]");
    const options = [...UxPalette.projects().map((project) => ({ id: project.id, name: project.name })), { id: "", name: UxPaletteUi.text("loose") }];
    const check = document.querySelector("[data-ux-icon-check]");
    box.replaceChildren(...options.map((option) => {
      const label = document.createElement("label");
      label.className = "ux-choice";
      const input = document.createElement("input");
      input.type = "radio";
      input.name = "ux-move";
      input.value = option.id;
      input.checked = (palette.project || "") === option.id;
      const mark = document.createElement("span");
      mark.className = "ux-choice__box ux-choice__box--radio";
      mark.setAttribute("aria-hidden", "true");
      if (check) mark.append(check.content.cloneNode(true));
      const name = document.createElement("span");
      name.className = "ux-choice__label";
      name.textContent = option.name;
      label.append(input, mark, name);
      return label;
    }));
    box.onchange = (event) => {
      const project = event.target.value || null;
      if (UxPalette.movePalette(id, project)) {
        UxPaletteUi.notify(this.t("moved", { project: project ? UxPalette.project(project)?.name : UxPaletteUi.text("loose") }));
      }
      dialog.close();
    };
    UxPaletteUi.open(dialog, from);
  }
  duplicate(id) {
    const copy = UxPalette.duplicatePalette(id);
    if (copy) {
      UxPaletteTrack("palette_duplicated", { palette: copy, from: id });
      UxPaletteUi.notify(this.t("duplicated", { name: UxPalette.palette(copy)?.name || "" }));
    }
  }
  async share(id) {
    const palette = UxPalette.palette(id);
    if (!palette) return;
    const url = UxPalette.shareUrl(palette.colours.map((colour) => colour.h));
    UxPaletteTrack("palette_shared", { palette: id, count: palette.colours.length, via: "menu" });
    if (navigator.share && window.matchMedia("(pointer: coarse)").matches) {
      try {
        await navigator.share({ title: palette.name || this.dataset.shareTitle, url });
        return;
      } catch (error) {
        if (error?.name === "AbortError") return;
      }
    }
    try {
      await navigator.clipboard.writeText(url);
      UxPaletteUi.notify(this.dataset.copied);
    } catch (error) {
      window.prompt(this.dataset.shareTitle, url);
    }
  }
  deletePalette(id) {
    const palette = UxPalette.palette(id);
    const undo = UxPalette.deletePalette(id);
    if (!undo) return;
    UxPaletteTrack("palette_deleted", { palette: id });
    UxPaletteUi.notify(this.t("deleted", { name: palette.name }), { undo: () => UxPalette.restore(undo), ms: 8000 });
  }
  deleteProject(id, from) {
    const project = UxPalette.project(id);
    if (!project) return;
    const inside = UxPalette.palettes().filter((palette) => palette.project === id);
    const dialog = this.sheet("delete-project");
    dialog.querySelector("[data-ux-sheet-title]").textContent = this.t("deleteTitle", { name: project.name });
    const names = inside.map((palette) => palette.name);
    const list = names.length > 1 ? `${names.slice(0, -1).join(", ")} ${this.dataset.and} ${names[names.length - 1]}` : names[0] || "";
    dialog.querySelector("[data-ux-delete-has]").textContent = !inside.length ? this.dataset.deleteHasNone
      : inside.length === 1 ? this.t("deleteHasOne", { names: list }) : this.t("deleteHasOther", { count: inside.length, names: list });
    dialog.querySelector("[data-ux-delete-choices]").hidden = !inside.length;
    const form = dialog.querySelector("form");
    form.elements.palettes.value = "keep";
    form.onsubmit = (event) => {
      event.preventDefault();
      const undo = UxPalette.deleteProject(id, { palettes: form.elements.palettes.value || "keep" });
      dialog.close();
      if (!undo) return;
      UxPaletteTrack("project_deleted", { project: id, palettes: form.elements.palettes.value });
      UxPaletteUi.notify(this.t("deleted", { name: project.name }), { undo: () => UxPalette.restore(undo), ms: 8000 });
    };
    UxPaletteUi.open(dialog, from);
  }
};
if (!window.customElements.get("ux-palettes")) {
  window.customElements.define("ux-palettes", UxPalettes);
}

/* ---------------------------------------------------------------------------
   Palette editor (Palettes v2, GitHub #186; sections/ux-palette.liquid <ux-palette-editor>). The page with
   ?palette=<id> (UxPalettePage sets paletteId). "In [project]" (a select: moves it), the sync status, the name (H1;
   Rename edits it in place: Enter or Save keeps, Escape cancels), the room preview once two roles are set (a flat
   elevation: walls, ceiling, woodwork, accent), the colour rows (drag the handle, or focus it and use the arrow keys,
   to reorder; the role opens "Where will … go?" with each role's suggested finish; remove leaves an Undo), the slot
   ("· 8 spaces left"), then Order samples, Share, How much paint, Duplicate, Move to project and Delete palette.
   --------------------------------------------------------------------------- */
var UxPaletteEditor = class extends HTMLElement {
  connectedCallback() {
    if (this._wired) return;
    this._wired = true;
    this.page = this.closest("ux-palette-page");
    this.rows = this.querySelector("[data-ux-editor-rows]");
    this.addEventListener("click", (event) => this.onClick(event));
    this.addEventListener("keydown", (event) => this.onKey(event));
    this.rows.addEventListener("pointerdown", (event) => this.dragStart(event));
    const form = this.querySelector("[data-ux-editor-rename-form]");
    form.addEventListener("submit", (event) => {
      event.preventDefault();
      this.finishRename(true);
    });
    form.elements.name.addEventListener("keydown", (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        this.finishRename(false);
      }
    });
    form.elements.name.addEventListener("blur", () => setTimeout(() => this.renaming && this.finishRename(true), 0));
    this.querySelector("[data-ux-editor-project]").addEventListener("change", (event) => {
      const project = event.target.value || null;
      if (UxPalette.movePalette(this.paletteId, project)) {
        UxPaletteUi.notify(this.t("moved", { project: project ? UxPalette.project(project)?.name : UxPaletteUi.text("loose") }));
      }
    });
  }
  t(key, values = {}) {
    return Object.entries(values).reduce((string, [name, value]) => string.split(`[${name}]`).join(value), this.dataset[key] || "");
  }
  say(text) {
    const status = this.querySelector("[data-ux-editor-status]");
    status.textContent = "";
    requestAnimationFrame(() => (status.textContent = text));
  }
  palette() {
    return UxPalette.palette(this.paletteId);
  }
  finishFor(role, project) {
    if (UxPaletteUi.surface(role)?.finishTitle && !LEGACY_ROLES.includes(role)) return UxPaletteUi.surface(role).finishTitle;
    if (role === "walls" || role === "ceiling") return project?.type === "walls-brick-render" && role === "walls" ? this.dataset.finishOutside : this.dataset.finishWalls;
    if (role === "woodwork") return this.dataset.finishWoodwork;
    if (role === "accent") return this.dataset.roleAny;
    return "";
  }
  roleLabel(role) {
    return role ? UxPaletteUi.surface(role).title : "";
  }

  render() {
    /* hidden by the layout (the wall's split view): drawn when it shows, not on every change (#472) */
    if (!this.getClientRects().length) {
      this.stale = true;
      this.shown ??= new ResizeObserver(() => this.stale && this.getClientRects().length && this.render());
      this.shown.observe(this);
      return;
    }
    this.stale = false;
    const palette = this.palette();
    if (!palette) return;
    const project = palette.project ? UxPalette.project(palette.project) : null;
    /* project select */
    const select = this.querySelector("[data-ux-editor-project]");
    select.replaceChildren(...UxPalette.projects().map((one) => new Option(one.name, one.id)), new Option(UxPaletteUi.text("loose"), ""));
    select.value = palette.project || "";
    /* sync status: guests keep palettes in this browser; for an account, pending work says so */
    const customer = UxPalette.account?.customer;
    const pending = customer && (UxPalette.store().pending?.palettes || []).includes(palette.id);
    const sync = this.querySelector("[data-ux-editor-sync]");
    sync.classList.toggle("ux-editor__sync--pending", Boolean(pending));
    this.querySelector("[data-ux-editor-sync-text]").textContent = pending ? this.dataset.pending : customer ? this.dataset.savedAccount : this.dataset.savedDevice;
    /* name */
    if (!this.renaming) this.querySelector("[data-ux-editor-name]").textContent = palette.name;
    /* rows: keep focus on the same control of the same colour */
    const focused = this.rows.contains(document.activeElement) ? { handle: document.activeElement.closest("[data-handle]")?.dataset.handle, part: [...document.activeElement.attributes].find((attr) => attr.name.startsWith("data-ux-erow-"))?.name } : null;
    const template = this.querySelector("template[data-ux-editor-row]");
    this.rows.replaceChildren(...palette.colours.map((colour, index) => {
      const row = template.content.firstElementChild.cloneNode(true);
      const short = UxPaletteUi.shortName(colour.n);
      row.dataset.handle = colour.h;
      row.querySelector("[data-ux-erow-chip]").style.background = colour.c || "var(--ux-grey)";
      const name = row.querySelector("[data-ux-erow-name]");
      name.textContent = colour.n;
      name.href = `${window.Shopify?.routes?.root || "/"}products/${encodeURIComponent(colour.h)}`;
      const role = row.querySelector("[data-ux-erow-role]");
      role.classList.toggle("ux-erow__role--none", !colour.r);
      row.querySelector("[data-ux-erow-role-text]").textContent = colour.r ? [this.roleLabel(colour.r), this.finishFor(colour.r, project)].filter(Boolean).join(" · ") : this.dataset.roleAdd;
      role.setAttribute("aria-label", `${short}: ${colour.r ? this.roleLabel(colour.r) : this.dataset.roleAdd}`);
      const handle = row.querySelector("[data-ux-erow-handle]");
      handle.setAttribute("aria-label", this.t("moveLabel", { colour: short }));
      handle.disabled = palette.colours.length < 2;
      row.querySelector("[data-ux-erow-remove]").setAttribute("aria-label", this.t("removeLabel", { colour: short }));
      row.setAttribute("aria-posinset", String(index + 1));
      return row;
    }));
    if (focused?.handle && focused.part) this.rows.querySelector(`[data-handle="${CSS.escape(focused.handle)}"] [${focused.part}]`)?.focus();
    /* the slot */
    const left = UxPalette.LIMITS.colours - palette.colours.length;
    const slot = this.querySelector("[data-ux-editor-slot]");
    slot.textContent = left <= 0 ? this.dataset.full : left === 1 ? this.dataset.slotOne : this.t("slotOther", { count: left });
    slot.classList.toggle("ux-editor__slot--full", left <= 0);
    /* room preview: once two roles are set */
    const roles = {};
    palette.colours.forEach((colour) => colour.r && !roles[colour.r] && (roles[colour.r] = colour.c));
    const room = this.querySelector("[data-ux-editor-room]");
    room.hidden = Object.keys(roles).length < 2;
    if (!room.hidden) {
      const box = room.querySelector(".ux-room");
      box.style.setProperty("--room-walls", roles.walls || "#ECEAE4");
      box.style.setProperty("--room-ceiling", roles.ceiling || "#F4F2EE");
      box.style.setProperty("--room-woodwork", roles.woodwork || "#F4F2EE");
      box.style.setProperty("--room-accent", roles.accent || roles.woodwork || "#F4F2EE");
    }
  }

  /* --- rename in place --- */
  startRename() {
    const palette = this.palette();
    if (!palette) return;
    this.renaming = true;
    const form = this.querySelector("[data-ux-editor-rename-form]");
    this.querySelector("[data-ux-editor-name]").hidden = true;
    form.hidden = false;
    form.elements.name.value = palette.name;
    const button = this.querySelector("[data-ux-editor-rename]");
    button.dataset.label ??= button.textContent;
    button.textContent = button.dataset.save;
    form.elements.name.focus();
    form.elements.name.select();
  }
  finishRename(keep) {
    if (!this.renaming) return;
    const form = this.querySelector("[data-ux-editor-rename-form]");
    const name = form.elements.name.value.trim();
    this.renaming = false;
    form.hidden = true;
    const heading = this.querySelector("[data-ux-editor-name]");
    heading.hidden = false;
    const button = this.querySelector("[data-ux-editor-rename]");
    button.textContent = button.dataset.label;
    if (keep && name) UxPalette.renamePalette(this.paletteId, name);
    this.render();
    button.focus();
  }

  /* --- actions --- */
  onClick(event) {
    if (event.target.closest("[data-ux-editor-rename]")) {
      if (this.renaming) this.finishRename(true);
      else this.startRename();
      return;
    }
    const back = event.target.closest("[data-ux-editor-back]");
    if (back) return;
    const row = event.target.closest("[data-handle]");
    if (row && event.target.closest("[data-ux-erow-role]")) {
      this.chooseRole(row.dataset.handle, event.target.closest("[data-ux-erow-role]"));
      return;
    }
    if (row && event.target.closest("[data-ux-erow-remove]")) {
      const palette = this.palette();
      const colour = palette.colours.find((saved) => saved.h === row.dataset.handle);
      const next = row.nextElementSibling || row.previousElementSibling;
      const undo = UxPalette.removeFrom(this.paletteId, row.dataset.handle);
      if (undo) {
        UxPaletteTrack("palette_colour_removed", { handle: row.dataset.handle, source: "editor" });
        UxPaletteUi.notify(this.t("removed", { colour: UxPaletteUi.shortName(colour?.n), name: palette.name }), { undo: () => UxPalette.restore(undo) });
        const nextHandle = next?.dataset.handle;
        requestAnimationFrame(() => (this.rows.querySelector(`[data-handle="${CSS.escape(nextHandle || "")}"] [data-ux-erow-remove]`) || this.querySelector("[data-ux-editor-name]")).focus());
      }
      return;
    }
    const action = event.target.closest("[data-ux-editor-action]")?.dataset.uxEditorAction;
    if (!action) return;
    const overview = this.page?.querySelector("ux-palettes");
    const from = event.target.closest("button");
    /* Share: the share sheet on touch devices; otherwise the share block with the link and tiles (#190) */
    if (action === "share") {
      if (navigator.share && window.matchMedia("(pointer: coarse)").matches) overview?.share(this.paletteId);
      else if (!this.page?.toShareBlock?.()) overview?.share(this.paletteId);
    }
    else if (action === "move") overview?.move(this.paletteId, from);
    else if (action === "duplicate") {
      const copy = UxPalette.duplicatePalette(this.paletteId);
      if (copy) {
        UxPalette.setActive(this.paletteId); /* stay on this one; the copy waits on Your palettes */
        UxPaletteTrack("palette_duplicated", { palette: copy, from: this.paletteId });
        UxPaletteUi.notify(this.t("duplicated", { name: UxPalette.palette(copy)?.name || "" }));
      }
    } else if (action === "delete") {
      const palette = this.palette();
      const id = this.paletteId;
      const undo = UxPalette.deletePalette(id);
      if (!undo) return;
      UxPaletteTrack("palette_deleted", { palette: id });
      UxPaletteUi.notify(this.t("deleted", { name: palette.name }), {
        undo: () => {
          UxPalette.restore(undo);
          this.page?.open(id);
        },
        ms: 8000
      });
    }
  }
  chooseRole(handle, from) {
    const palette = this.palette();
    const colour = palette?.colours.find((saved) => saved.h === handle);
    if (!colour) return;
    const dialog = this.page.querySelector('[data-ux-palettes-sheet="role"]');
    UxPaletteUi.wire(dialog);
    const project = palette.project ? UxPalette.project(palette.project) : null;
    dialog.querySelector("[data-ux-sheet-title]").textContent = this.t("roleTitle", { colour: UxPaletteUi.shortName(colour.n) });
    dialog.querySelectorAll("[data-ux-role-finish]").forEach((cell) => {
      cell.textContent = this.finishFor(cell.dataset.uxRoleFinish, project);
    });
    dialog.querySelectorAll("input[name=ux-role]").forEach((input) => {
      input.checked = input.value === (colour.r || "");
    });
    const box = dialog.querySelector("[data-ux-role-choices]");
    box.onchange = (event) => {
      UxPalette.setRole(this.paletteId, handle, event.target.value || null);
      UxPaletteTrack("palette_role_set", { palette: this.paletteId, handle, role: event.target.value || null });
      dialog.close();
    };
    UxPaletteUi.open(dialog, from);
  }

  /* --- reorder: arrow keys on the handle, or drag it --- */
  move(handle, by) {
    const palette = this.palette();
    const order = palette.colours.map((colour) => colour.h);
    const at = order.indexOf(handle);
    const to = Math.max(0, Math.min(order.length - 1, at + by));
    if (at < 0 || to === at) return;
    order.splice(to, 0, order.splice(at, 1)[0]);
    if (UxPalette.reorderColours(this.paletteId, order)) {
      this.say(this.t("movedRow", { colour: UxPaletteUi.shortName(palette.colours[at].n), position: to + 1 }));
    }
  }
  onKey(event) {
    const handle = event.target.closest("[data-ux-erow-handle]");
    if (!handle) return;
    const row = handle.closest("[data-handle]");
    const by = { ArrowUp: -1, ArrowDown: 1, Home: -99, End: 99 }[event.key];
    if (by === undefined) return;
    event.preventDefault();
    this.move(row.dataset.handle, by);
  }
  dragStart(event) {
    const handle = event.target.closest("[data-ux-erow-handle]");
    if (!handle || handle.disabled || event.button > 0) return;
    const row = handle.closest("[data-handle]");
    event.preventDefault();
    handle.setPointerCapture(event.pointerId);
    row.classList.add("ux-erow--dragging");
    const startY = event.clientY;
    const onMove = (moveEvent) => {
      row.style.transform = `translateY(${moveEvent.clientY - startY}px)`;
      const others = [...this.rows.children].filter((other) => other !== row);
      const before = others.find((other) => {
        const box = other.getBoundingClientRect();
        return moveEvent.clientY < box.top + box.height / 2;
      });
      others.forEach((other) => other.classList.toggle("ux-erow--before", other === before));
      this._dropBefore = before || null;
    };
    const onUp = () => {
      handle.removeEventListener("pointermove", onMove);
      handle.removeEventListener("pointerup", onUp);
      handle.removeEventListener("pointercancel", onUp);
      row.classList.remove("ux-erow--dragging");
      row.style.transform = "";
      [...this.rows.children].forEach((other) => other.classList.remove("ux-erow--before"));
      const order = [...this.rows.children].filter((other) => other !== row).map((other) => other.dataset.handle);
      const at = this._dropBefore ? order.indexOf(this._dropBefore.dataset.handle) : order.length;
      order.splice(at, 0, row.dataset.handle);
      this._dropBefore = null;
      const palette = this.palette();
      if (order.join() !== palette.colours.map((colour) => colour.h).join() && UxPalette.reorderColours(this.paletteId, order)) {
        this.say(this.t("movedRow", { colour: UxPaletteUi.shortName(palette.colours.find((colour) => colour.h === row.dataset.handle)?.n), position: at + 1 }));
      }
    };
    handle.addEventListener("pointermove", onMove);
    handle.addEventListener("pointerup", onUp);
    handle.addEventListener("pointercancel", onUp);
  }
};
if (!window.customElements.get("ux-palette-editor")) {
  window.customElements.define("ux-palette-editor", UxPaletteEditor);
}

/* ---------------------------------------------------------------------------
   Your palette page (milestone 6, GitHub #154; sections/ux-palette.liquid). Renders the palette from the store right
   away (saved name and colour), then fills each tile from the colour's data; a colour that no longer resolves is shown
   faded with Remove only. Share uses the native share sheet on touch devices, else copies the link.
   --------------------------------------------------------------------------- */
var UxPalettePage = class extends HTMLElement {
  connectedCallback() {
    if (this._wired) return;
    this._wired = true;
    this.tiles = this.querySelector("[data-ux-palette-tiles]");
    this.status = this.querySelector("[data-ux-palette-status]");
    this.actions = this.querySelector("[data-ux-palette-actions]");
    this.sharedActions = this.querySelector("[data-ux-palette-shared-actions]");
    this.confirm = this.querySelector(".ux-palette__confirm");
    this.clearButton = this.querySelector("[data-ux-palette-clear]");
    this.eyebrow = this.querySelector("[data-ux-palette-eyebrow]");
    this.heading = this.querySelector("[data-ux-palette-title]");
    this.ownEyebrow = this.eyebrow.textContent;
    this.ownTitle = this.heading.textContent;
    this.tiles.hidden = false;
    this.addEventListener("click", (event) => this.onClick(event));
    /* the story image is drawn before the Instagram tap lands: phones only share inside the gesture (#190) */
    ["pointerdown", "focusin"].forEach((name) => this.addEventListener(name, (event) => {
      const tile = event.target.closest?.('[data-ux-share-to="instagram"]');
      if (tile && this.shareData?.url) this.story ??= this.drawStory(tile.closest("[data-ux-share]"), this.shareData);
    }));
    /* A shared palette doesn't change with the visitor's own; only the "already saved" note does (the toggles follow
       the store by themselves), so the tiles aren't redrawn under the pointer */
    document.addEventListener("ux-palette:change", () => {
      if (this.shared) this.renderNote();
      /* the palette in use changed elsewhere (the palette drawer's Add here, MP3-8): the open palette follows it */
      else if (this.chosen && UxPalette.palette(this.chosen) && UxPalette.active() && UxPalette.active().id !== this.chosen) this.open(UxPalette.active().id);
      else this.render();
    });
    this.overview = this.querySelector("ux-palettes");
    /* ?palette=<id> opens that palette (Palettes v2, #185/#186): it becomes the palette in use, so everything below
       works on it; without it, once anything is saved, the page is the Your palettes overview */
    const chosen = new URLSearchParams(window.location.search).get("palette");
    if (chosen && UxPalette.palette(chosen)) {
      this.chosen = chosen;
      UxPalette.setActive(chosen);
    }
    const handles = UxPalette.sharedHandles();
    if (handles.length) this.loadShared(handles);
    else {
      this.render();
      UxPaletteTrack("palette_viewed", { count: UxPalette.count() });
    }
  }
  colours() {
    return this.shared || (this.chosen ? UxPalette.palette(this.chosen)?.colours || [] : UxPalette.list());
  }
  /* open a palette in place (Undo of a delete): ?palette=<id>, the palette in use, the editor */
  open(id) {
    if (!UxPalette.palette(id)) return;
    this.chosen = id;
    UxPalette.setActive(id);
    const url = new URL(window.location.href);
    url.searchParams.set("palette", id);
    window.history.replaceState(window.history.state, "", url);
    this.render();
  }
  overviewUrl() {
    const url = new URL(window.location.href);
    url.searchParams.delete("palette");
    url.searchParams.delete("c");
    return `${url.pathname}${url.search}`;
  }
  say(text) {
    this.status.textContent = "";
    requestAnimationFrame(() => (this.status.textContent = text));
  }
  /* Shared links (#159): ?c=handle,handle shows those colours, not the visitor's. Handles that no longer resolve are
     left out (with a notice); the visitor's palette is untouched until they save. */
  async loadShared(handles) {
    handles = handles.slice(0, UxPalette.LIMITS.colours); /* sharedHandles caps them too (#468) */
    this.shared = [];
    this.eyebrow.textContent = this.dataset.sharedEyebrow;
    this.heading.textContent = this.dataset.sharedTitle;
    this.querySelector("[data-ux-palette-lead]").textContent = "";
    this.classList.add("ux-palette--shared");
    const found = (await Promise.all(handles.map((handle) => UxPalette.colour(handle)))).filter(Boolean);
    this.shared = found.map((data) => ({ h: data.handle, n: data.name, c: data.colour })).slice(0, UxPalette.LIMITS.colours);
    this.sharedMissing = handles.length - found.length;
    this.render();
    UxPaletteTrack("shared_palette_opened", { count: handles.length, found: found.length });
  }
  /* Save this palette (Palettes v2, #188): a shared link's colours become a new palette of the visitor's own, named
     (default "Shared palette") and placed in a project or none; nobody else's palette changes. Then it opens. */
  saveShared(from) {
    const dialog = this.querySelector('[data-ux-palettes-sheet="save-shared"]');
    if (!dialog || !this.shared?.length) return;
    UxPaletteUi.wire(dialog);
    const form = dialog.querySelector("form");
    dialog.querySelector("[data-ux-shared-colours]").replaceChildren(...this.shared.slice(0, UxPalette.LIMITS.colours).map((colour) => {
      const block = document.createElement("span");
      block.style.background = colour.c || "var(--ux-grey)";
      return block;
    }));
    form.elements.name.value = dialog.dataset.defaultName;
    form.elements.project.replaceChildren(new Option(UxPaletteUi.text("loose"), ""), ...UxPalette.projects().map((project) => new Option(project.name, project.id)));
    form.elements.project.value = "";
    /* no projects outside consultancies (dffaf09): no project field (#475) */
    form.elements.project.closest(".ux-field").hidden = !UxPalette.projectsShown;
    UxPaletteUi.error(dialog, "");
    form.onsubmit = (event) => {
      event.preventDefault();
      const name = form.elements.name.value.trim() || dialog.dataset.defaultName;
      const id = UxPalette.createPalette({ name, project: form.elements.project.value || null, colours: this.shared, activate: true });
      if (!id) {
        UxPaletteUi.error(dialog, UxPaletteUi.text("tooMany"));
        return;
      }
      UxPaletteTrack("shared_palette_saved", { count: this.shared.length, added: this.shared.length, palette: id });
      dialog.close();
      this.shared = null;
      this.sharedMissing = 0;
      this.pairsShown = null;
      this.eyebrow.textContent = this.ownEyebrow;
      this.heading.textContent = this.ownTitle;
      this.classList.remove("ux-palette--shared", "ux-palette--shared-none");
      const url = new URL(window.location.href);
      url.searchParams.delete("c");
      window.history.replaceState(window.history.state, "", url);
      this.open(id);
      this.say(UxPaletteUi.text("added", { name: UxPalette.palette(id)?.name || name }));
      this.querySelector("[data-ux-editor-name]")?.focus();
    };
    UxPaletteUi.open(dialog, from);
    form.elements.name.select();
  }
  renderNote() {
    const note = this.querySelector("[data-ux-palette-note]");
    const notice = this.querySelector("[data-ux-palette-notice]");
    const shared = this.shared;
    const own = UxPalette.count();
    note.hidden = !shared?.length || !own;
    if (!note.hidden) note.textContent = own === 1 ? this.dataset.sharedNoteOne : this.dataset.sharedNoteOther.replace("[count]", own);
    const missing = shared?.length ? this.sharedMissing : 0;
    notice.hidden = !missing;
    if (missing) {
      const left = missing === 1 ? this.dataset.sharedMissingOne : this.dataset.sharedMissingOther.replace("[count]", missing);
      const rest = shared.length === 1 ? this.dataset.sharedRestOne : this.dataset.sharedRestOther.replace("[count]", shared.length);
      notice.textContent = `${left} ${rest}`;
    }
  }
  /* which page this is: the app, or a shared link's colours. The app always shows (#455): with no ?palette (or one
     deleted) it opens the palette in use; with nothing saved yet it still shows, and the first colour added makes
     "My palette" (UxColourCard.toggle). The Your palettes overview is retired (the dock holds every palette). */
  mode() {
    if (this.chosen && !UxPalette.palette(this.chosen)) this.chosen = null;
    if (!this.shared && !this.chosen && UxPalette.active()) {
      this.chosen = UxPalette.active().id;
      UxPalette.setActive(this.chosen);
    }
    if (!this.shared) {
      const url = new URL(window.location.href);
      if (this.chosen) url.searchParams.set("palette", this.chosen);
      else url.searchParams.delete("palette");
      if (url.href !== window.location.href) window.history.replaceState(window.history.state, "", url);
    }
    const overview = false;
    this.classList.toggle("ux-palette--overview", overview);
    this.classList.toggle("ux-palette--open", !this.shared);
    if (this.overview) {
      this.overview.hidden = !overview;
      if (overview) this.overview.render();
    }
    const crumb = this.querySelector("[data-ux-palette-crumb]");
    if (crumb) {
      crumb.dataset.own ??= crumb.textContent;
      crumb.textContent = overview ? crumb.dataset.overview : crumb.dataset.own;
    }
    /* the app: the editor (#186) once a palette is open, the colour card, and the crumbs (Your palettes / project / palette) */
    const open = !this.shared;
    const workspace = this.querySelector("[data-ux-palette-workspace]");
    if (workspace) {
      workspace.hidden = !open;
      const editor = workspace.querySelector("ux-palette-editor");
      if (open && this.chosen && editor) {
        editor.paletteId = this.chosen;
        editor.querySelector("[data-ux-editor-back]").href = this.overviewUrl();
        editor.render();
      }
      /* the colour card (#182) adds to the open palette; leaving it closes the mobile sheet */
      const card = this.querySelector("ux-colour-card");
      if (open && typeof card?.open === "function") card.open(this.chosen);
      const sheet = this.querySelector("[data-ux-card-sheet]");
      if (!open && sheet?.open) sheet.close();
    }
    this.renderCrumbs(open);
    return overview;
  }
  renderCrumbs(open) {
    const list = this.querySelector(".ux-crumbs ol");
    if (!list) return;
    this._crumbs ??= list.innerHTML;
    if (!open) {
      if (this._crumbsOpen) list.innerHTML = this._crumbs;
      this._crumbsOpen = false;
      return;
    }
    const palette = UxPalette.palette(this.chosen);
    const project = palette?.project ? UxPalette.project(palette.project) : null;
    const item = (text, href) => {
      const li = document.createElement("li");
      const node = document.createElement(href ? "a" : "span");
      if (href) node.href = href;
      else node.setAttribute("aria-current", "page");
      node.textContent = text;
      li.append(node);
      return li;
    };
    list.replaceChildren(...[item(this.querySelector("ux-palette-editor")?.dataset.overview || "", this.overviewUrl()), project && item(project.name), palette && item(palette.name)].filter(Boolean));
    this._crumbsOpen = true;
  }
  render() {
    if (this.mode()) return;
    const shared = this.shared;
    const colours = this.colours();
    const count = colours.length;
    const lead = this.querySelector("[data-ux-palette-lead]");
    if (shared) lead.textContent = !count ? this.dataset.sharedNone : count === 1 ? this.dataset.sharedLeadOne : this.dataset.sharedLeadOther.replace("[count]", count);
    else lead.textContent = !count ? this.dataset.countNone : count === 1 ? this.dataset.countOne : this.dataset.countOther.replace("[count]", count);
    this.actions.hidden = Boolean(shared) || !count;
    this.sharedActions.hidden = !shared || !count;
    this.classList.toggle("ux-palette--empty", !shared && !count);
    this.classList.toggle("ux-palette--shared", Boolean(shared));
    this.classList.toggle("ux-palette--shared-none", Boolean(shared) && !count);
    this.renderNote();
    this.tiles.hidden = Boolean(shared) && !count;
    const tileTemplate = this.querySelector("template[data-ux-palette-tile]");
    const toggleTemplate = this.querySelector("template[data-ux-palette-shared-toggle]");
    const items = colours.map((colour) => {
      const item = tileTemplate.content.firstElementChild.cloneNode(true);
      item.dataset.handle = colour.h;
      item.querySelector("[data-ux-swatch]").style.setProperty("--ux-paint", colour.c || "var(--ux-grey)");
      item.querySelector("[data-ux-name]").textContent = colour.n;
      const remove = item.querySelector("[data-ux-remove]");
      if (shared) {
        /* a shared colour gets the + toggle (Palette toggle 104:11) in place of remove */
        remove.remove();
        const toggle = toggleTemplate.content.firstElementChild.cloneNode(true);
        const short = colour.n.split(" No.")[0].replace("™", "").trim();
        Object.assign(toggle.dataset, { handle: colour.h, name: colour.n, colour: colour.c, labelAdd: toggle.dataset.templateAdd.replace("[colour]", short), labelRemove: toggle.dataset.templateRemove.replace("[colour]", short) });
        const on = UxPalette.has(colour.h);
        toggle.dataset.inPalette = String(on);
        toggle.setAttribute("aria-label", on ? toggle.dataset.labelRemove : toggle.dataset.labelAdd);
        item.querySelector("[data-ux-swatch]").append(toggle);
      } else {
        remove.setAttribute("aria-label", remove.dataset.label.replace("[colour]", colour.n));
      }
      this.fill(item, colour);
      return item;
    });
    if (!shared) {
      const addTile = this.querySelector("template[data-ux-palette-add]").content.firstElementChild.cloneNode(true);
      if (!count) {
        const add = addTile.querySelector(".ux-palette-add");
        add.querySelector(".ux-palette-add__label").textContent = add.dataset.emptyLabel;
        add.querySelector(".ux-palette-add__hint").textContent = add.dataset.emptyHint;
      }
      items.push(addTile);
    }
    this.tiles.replaceChildren(...items);
    this.renderTry(colours);
    this.renderPairs(colours);
  }
  /* Colours that pair (#156): each palette colour's curated pairings taken in turn, so every colour gets a say, without
     the palette's own colours; up to six cards. Cards already showing keep their place (one added from here stays,
     ticked, so the toggle under the pointer doesn't vanish and can take it out again). */
  async renderPairs(colours) {
    const section = this.querySelector("[data-ux-palette-pairs]");
    if (!section) return;
    const token = (this._pairsToken = Symbol("pairs"));
    const lists = await Promise.all(colours.map(async (colour) => (await UxPalette.colour(colour.h))?.pairs || []));
    if (token !== this._pairsToken) return;
    const shown = this.pairsShown || [];
    const saved = new Set(colours.map((colour) => colour.h).filter((handle) => !shown.includes(handle)));
    const handles = [];
    for (let index = 0; lists.some((list) => index < list.length); index += 1) {
      lists.forEach((list) => {
        const handle = list[index];
        if (handle && !saved.has(handle) && !handles.includes(handle)) handles.push(handle);
      });
    }
    const ordered = [...shown.filter((handle) => handles.includes(handle)), ...handles.filter((handle) => !shown.includes(handle))];
    const found = await Promise.all(ordered.slice(0, 12).map((handle) => UxPalette.colour(handle)));
    if (token !== this._pairsToken) return;
    const cards = found.filter((data) => data?.card).slice(0, 6);
    const next = cards.map((data) => data.handle);
    section.hidden = !next.length;
    if (next.join() === shown.join()) return;
    this.pairsShown = next;
    section.querySelector("[data-ux-pairs-cards]").replaceChildren(...cards.map((data) => {
      const item = document.createElement("li");
      item.innerHTML = data.card;
      return item;
    }));
  }
  /* Try your palette at home (#155; States · availability 115:2013): the Colour Swatch chosen by default (swatches are
     the focus, Matt 19 Oct); swatch out or not made: the pot; both out: nothing chosen, Notify me; a format that isn't made is left out; colours no longer sold aren't listed. */
  money(cents) {
    const currency = window.Shopify?.currency?.active || "GBP";
    return new Intl.NumberFormat(document.documentElement.lang || "en-GB", { style: "currency", currency }).format(cents / 100);
  }
  async renderTry(colours) {
    const section = this.querySelector("[data-ux-palette-try]");
    if (!section) return;
    /* hidden by the layout (the app's Samples view is Try it first, #450): drawn when it shows, not on every change (#472) */
    if (!section.hidden && !section.getClientRects().length) {
      this._tryStale = true;
      this._tryShown ??= new ResizeObserver(() => this._tryStale && section.getClientRects().length && this.renderTry(this.colours()));
      this._tryShown.observe(section);
      return;
    }
    this._tryStale = false;
    const token = (this._tryToken = Symbol("try"));
    const entries = await Promise.all(colours.map(async (colour) => [colour, await UxPalette.colour(colour.h, { card: false })]));
    if (token !== this._tryToken) return;
    const live = entries.filter(([, data]) => data && (data.sample || data.peel));
    section.hidden = !live.length;
    const order = this.querySelector("[data-ux-palette-order]");
    if (order) order.hidden = !live.length;
    if (!live.length) return;
    this.choices ??= new Map();
    const template = this.querySelector("template[data-ux-try-row]");
    if (live.some(([, data]) => data.peel)) section.querySelector("[data-ux-try-lead]").textContent = section.dataset.leadPeel;
    section.querySelector("[data-ux-try-soon-swatch]")?.style.setProperty("--ux-paint", live[0][1].colour || live[0][0].c || "var(--ux-grey)");
    const rows = live.map(([colour, data]) => {
      const row = template.content.firstElementChild.cloneNode(true);
      row.dataset.handle = colour.h;
      row.style.setProperty("--ux-paint", data.colour || colour.c || "var(--ux-grey)");
      row.querySelector("[data-ux-name]").textContent = data.name;
      const legend = row.querySelector("[data-ux-legend]");
      legend.textContent = legend.dataset.template.replace("[colour]", data.name);
      const formats = { sample: data.sample, peel: data.peel };
      let choice = this.choices.get(colour.h);
      if (!choice || !formats[choice]?.available) choice = formats.peel?.available ? "peel" : formats.sample?.available ? "sample" : null;
      this.choices.set(colour.h, choice);
      row.querySelectorAll("[data-format]").forEach((label) => {
        const format = formats[label.dataset.format];
        if (!format) {
          label.remove();
          return;
        }
        const input = label.querySelector("input");
        input.name = `ux-try-${colour.h}`;
        input.disabled = !format.available;
        input.checked = label.dataset.format === choice;
        label.classList.toggle("is-out", !format.available);
        label.querySelector("[data-ux-price]").textContent = this.money(format.price);
      });
      const descriptor = row.querySelector("[data-ux-descriptor]");
      descriptor.textContent = choice ? data.descriptor || "" : descriptor.dataset.out;
      const notify = row.querySelector("[data-ux-try-notify]");
      const notifyVariant = formats.sample && !formats.sample.available ? formats.sample : !choice && formats.peel ? formats.peel : null;
      if (notifyVariant) {
        notify.hidden = false;
        notify.dataset.variantId = notifyVariant.id;
      }
      return row;
    });
    section.querySelector("[data-ux-try-rows]").replaceChildren(...rows);
    this.tryData = new Map(live.map(([colour, data]) => [colour.h, data]));
    this.skipped = live.filter(([colour]) => !this.choices.get(colour.h)).length;
    if (!section._wired) {
      section._wired = true;
      section.addEventListener("change", (event) => {
        const row = event.target.closest("[data-handle]");
        if (!row) return;
        this.choices.set(row.dataset.handle, event.target.value);
        this.updateTotal();
      });
      section.addEventListener("click", (event) => {
        const notify = event.target.closest("[data-ux-try-notify]");
        if (notify && window.showBackInStockPopup) window.showBackInStockPopup(Number(notify.dataset.variantId));
        const add = event.target.closest("[data-ux-try-add]");
        if (add) this.addSamples(add);
      });
    }
    this.updateTotal();
  }
  chosenItems() {
    const items = [];
    (this.tryData || new Map()).forEach((data, handle) => {
      const choice = this.choices.get(handle);
      const format = choice && data[choice];
      if (format && format.available) items.push({ handle, choice, id: format.id, price: format.price });
    });
    return items;
  }
  updateTotal() {
    const section = this.querySelector("[data-ux-palette-try]");
    const items = this.chosenItems();
    const count = items.length;
    const total = items.reduce((sum, item) => sum + item.price, 0);
    const sum = section.querySelector("[data-ux-try-sum]");
    const sub = section.querySelector("[data-ux-try-sub]");
    const add = section.querySelector("[data-ux-try-add]");
    if (!count) {
      sum.textContent = section.dataset.none;
      sub.textContent = section.dataset.noneSub;
      add.textContent = section.dataset.addNone;
      add.disabled = true;
    } else {
      sum.textContent = (count === 1 ? section.dataset.summaryOne : section.dataset.summaryOther.replace("[count]", count)).replace("[price]", this.money(total));
      sub.textContent = this.skipped ? (this.skipped === 1 ? section.dataset.skippedOne : section.dataset.skippedOther.replace("[count]", this.skipped)) : section.dataset.offerSub;
      add.textContent = count === 1 ? section.dataset.addOne : section.dataset.addOther.replace("[count]", count);
      add.disabled = false;
    }
    section.querySelector("ux-offer-progress")?.render(items.filter((item) => item.choice === "sample").length);
  }
  async addSamples(button) {
    if (button.getAttribute("aria-busy") === "true") return;
    const section = this.querySelector("[data-ux-palette-try]");
    const notice = section.querySelector("[data-ux-try-notice]");
    notice.hidden = true;
    button.setAttribute("aria-busy", "true");
    const before = this.chosenItems();
    /* Re-check stock just before adding: if anything sold out meanwhile, redraw and say so */
    before.forEach((item) => UxPalette.forget(item.handle));
    await this.renderTry(this.colours());
    const after = this.chosenItems();
    const changed = after.length !== before.length || after.some((item, index) => item.id !== before[index]?.id);
    if (changed || !after.length) {
      notice.textContent = section.dataset.updated;
      notice.hidden = false;
      button.removeAttribute("aria-busy");
      return;
    }
    const root = window.Shopify.routes.root;
    try {
      const sections = [];
      document.documentElement.dispatchEvent(new CustomEvent("cart:prepare-bundled-sections", { bubbles: true, detail: { sections } }));
      const items = await this.paletteItems(after);
      const response = await fetch(`${root}cart/add.js`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items, sections: sections.join(",") })
      });
      const added = await response.json();
      if (!response.ok) throw new Error(added.description || added.message || response.statusText);
      UxPaletteTrack("palette_samples_added", { count: after.length, pots: after.filter((item) => item.choice === "sample").length, swatches: after.filter((item) => item.choice === "peel").length, tagged: items.filter((item) => item.properties).length });
      const cart = await (await fetch(`${root}cart.js`)).json();
      cart.sections = added.sections;
      document.documentElement.dispatchEvent(new CustomEvent("cart:change", { bubbles: true, detail: { baseEvent: "variant:add", cart } }));
    } catch (error) {
      after.forEach((item) => UxPalette.forget(item.handle));
      await this.renderTry(this.colours());
      notice.textContent = section.dataset.failed;
      notice.hidden = false;
    } finally {
      button.removeAttribute("aria-busy");
    }
  }
  /* Order attribution (#179, spec 8): UxPaletteSamples tags each sample with the palette. A shared palette isn't the
     visitor's own: no id. */
  async paletteItems(chosen) {
    return UxPaletteSamples.items(chosen, this.shared ? null : UxPalette.active()?.id);
  }
  async fill(item, colour) {
    const data = await UxPalette.colour(colour.h);
    if (!item.isConnected) return;
    const link = item.querySelector("[data-ux-link]");
    const view = item.querySelector("[data-ux-view]");
    const descriptor = item.querySelector("[data-ux-descriptor]");
    if (!data) {
      item.classList.add("ux-palette-tile--gone");
      item.querySelector("[data-ux-gone]").hidden = false;
      descriptor.textContent = descriptor.dataset.gone;
      link.removeAttribute("href");
      view.hidden = true;
      item.querySelector("[data-ux-remove-text]").hidden = false;
      return;
    }
    link.href = data.url;
    view.href = data.url;
    descriptor.textContent = data.descriptor || "";
    if (data.colour) item.querySelector("[data-ux-swatch]").style.setProperty("--ux-paint", data.colour);
  }
  onClick(event) {
    const remove = event.target.closest("[data-ux-remove], [data-ux-remove-text]");
    if (remove) {
      const item = remove.closest("[data-handle]");
      const next = item.nextElementSibling;
      const name = item.querySelector("[data-ux-name]").textContent;
      UxPalette.remove(item.dataset.handle);
      UxPaletteTrack("palette_colour_removed", { handle: item.dataset.handle, source: "palette-page" });
      this.say(this.dataset.removed.replace("[colour]", name));
      const handle = next?.dataset.handle;
      const target = handle ? this.tiles.querySelector(`[data-handle="${CSS.escape(handle)}"] [data-ux-remove]`) : this.tiles.querySelector(".ux-palette-add");
      target?.focus();
      return;
    }
    if (event.target.closest("[data-ux-palette-save]")) {
      this.saveShared(event.target.closest("[data-ux-palette-save]"));
      return;
    }
    if (event.target.closest("[data-ux-palette-consult]")) {
      UxPaletteTrack("palette_consultation_clicked", { count: this.colours().length });
      return;
    }
    const use = event.target.closest("[data-ux-palette-use]");
    if (use) {
      this.usePalette(use);
      return;
    }
    const copy = event.target.closest("[data-ux-palette-copy]");
    if (copy) {
      this.copy(copy, this.querySelector("[data-ux-palette-link]").value);
      return;
    }
    const tile = event.target.closest("[data-ux-share-to]");
    if (tile) {
      this.shareTo(tile, event);
      return;
    }
    if (event.target.matches("[data-ux-palette-link]")) {
      event.target.select();
      return;
    }
    if (event.target.closest("[data-ux-palette-share]")) {
      this.share(event.target.closest("[data-ux-palette-share]"));
      return;
    }
    if (event.target.closest("[data-ux-palette-clear]")) {
      const open = this.confirm.hidden;
      this.confirm.hidden = !open;
      this.clearButton.setAttribute("aria-expanded", String(open));
      this.confirm.querySelector("[data-ux-palette-confirm-text]").textContent = this.confirm.querySelector("[data-ux-palette-confirm-text]").dataset.template.replace("[count]", UxPalette.count());
      if (open) this.confirm.querySelector("[data-ux-palette-clear-yes]").focus();
      return;
    }
    if (event.target.closest("[data-ux-palette-clear-yes]")) {
      UxPalette.clear();
      this.confirm.hidden = true;
      this.clearButton.setAttribute("aria-expanded", "false");
      this.say(this.dataset.cleared);
      this.tiles.querySelector(".ux-palette-add")?.focus();
      return;
    }
    if (event.target.closest("[data-ux-palette-clear-no]")) {
      this.confirm.hidden = true;
      this.clearButton.setAttribute("aria-expanded", "false");
      this.clearButton.focus();
      return;
    }
    const add = event.target.closest("[data-ux-palette-open-search]");
    const search = document.querySelector(".search-box .header-search__input");
    if (add && search) {
      event.preventDefault();
      window.scrollTo({ top: 0 });
      search.focus();
    }
  }
  /* Start from a palette (#158): adds the palette's colours (never replaces) and says how many were new */
  usePalette(button) {
    const idea = button.closest("[data-ux-palette-idea]");
    const swatches = idea.querySelectorAll("[data-handle]");
    /* a Colour Swatch pack (#452): saved as a new palette in its name, the first in use; 12 a palette, so a pack of
       24 makes two */
    if (button.hasAttribute("data-ux-palette-new")) {
      const colours = [...swatches].map((swatch) => ({ h: swatch.dataset.handle, n: swatch.dataset.name, c: swatch.dataset.colour }));
      const ids = [];
      for (let index = 0; index < colours.length; index += UxPalette.LIMITS.colours) {
        const id = UxPalette.createPalette({ name: idea.dataset.name, colours: colours.slice(index, index + UxPalette.LIMITS.colours), activate: !ids.length });
        if (!id) break; /* 30 palettes */
        ids.push(id);
      }
      ids.forEach((id) => UxPalette.palette(id)?.colours.forEach((colour) => UxPaletteTrack("palette_colour_added", { handle: colour.h, source: "start-from-pack", palette: id })));
      const text = ids.length ? this.dataset.usedNew.replace("[name]", UxPalette.palette(ids[0])?.name || idea.dataset.name) : this.dataset.usedNone;
      clearTimeout(button._usedTimer);
      button._label ??= button.textContent;
      button.textContent = text;
      this.say(text);
      button._usedTimer = setTimeout(() => (button.textContent = button._label), 2500);
      return;
    }
    const had = new Set(UxPalette.list().map((colour) => colour.h));
    const added = UxPalette.addMany([...swatches].map((swatch) => ({ h: swatch.dataset.handle, n: swatch.dataset.name, c: swatch.dataset.colour })));
    UxPalette.list().filter((colour) => !had.has(colour.h)).forEach((colour) => UxPaletteTrack("palette_colour_added", { handle: colour.h, source: "start-from" }));
    const text = !added ? this.dataset.usedNone : added === 1 ? this.dataset.usedOne : this.dataset.usedOther.replace("[count]", added);
    clearTimeout(button._usedTimer);
    button._label ??= button.textContent;
    button.textContent = text;
    this.say(text);
    button._usedTimer = setTimeout(() => (button.textContent = button._label), 2500);
  }
  async share(button) {
    const url = UxPalette.shareUrl();
    if (navigator.share && window.matchMedia("(pointer: coarse)").matches) {
      try {
        await navigator.share({ title: this.dataset.shareTitle, url });
        UxPaletteTrack("palette_shared", { count: UxPalette.count() });
        return;
      } catch (error) {
        if (error.name === "AbortError") return;
      }
    }
    /* no share sheet: the share block, where the link and the tiles are (#190) */
    if (this.toShareBlock()) return;
    this.copy(button, url);
  }
  toShareBlock() {
    const block = this.querySelector("[data-ux-share]");
    if (!block || block.closest("[hidden]") || !block.offsetParent) return false;
    block.scrollIntoView({ behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth", block: "center" });
    block.querySelector("[data-ux-palette-copy]")?.focus({ preventScroll: true });
    return true;
  }
  async copy(button, url, quiet = false) {
    try {
      await navigator.clipboard.writeText(url);
    } catch (error) {
      if (quiet) return false;
      window.prompt(this.dataset.shareTitle, url);
      return false;
    }
    UxPaletteTrack("palette_link_copied", { count: this.colours().length });
    if (quiet || !button) return true;
    clearTimeout(button._copiedTimer);
    const label = button.querySelector("[data-ux-share-copy-label]");
    if (label) {
      /* the share block's inline Copy: "Copied" with a tick for 3 s */
      const block = button.closest("[data-ux-share]");
      label.textContent = block.dataset.copied;
      button.querySelector("[data-ux-share-copy-icon]").hidden = true;
      button.querySelector("[data-ux-share-copied-icon]").hidden = false;
      this.say(block.dataset.copiedSay);
      button._copiedTimer = setTimeout(() => {
        label.textContent = block.dataset.copy;
        button.querySelector("[data-ux-share-copy-icon]").hidden = false;
        button.querySelector("[data-ux-share-copied-icon]").hidden = true;
      }, 3000);
      return true;
    }
    button._label ??= button.textContent;
    button.textContent = this.dataset.copied;
    this.say(this.dataset.copied);
    button._copiedTimer = setTimeout(() => (button.textContent = button._label), 2000);
    return true;
  }
  /* the share tiles (#190): WhatsApp and Facebook are links; More is the share sheet; Instagram is the story image */
  async shareTo(tile, event) {
    const to = tile.dataset.uxShareTo;
    const data = this.shareData;
    if (!data?.url) return;
    if (to === "whatsapp" || to === "facebook") {
      UxPaletteTrack("palette_shared", { count: data.colours.length, via: to });
      return;
    }
    event.preventDefault();
    if (to === "more") {
      try {
        await navigator.share({ title: data.name || tile.closest("[data-ux-share]").dataset.title, text: data.text, url: data.url });
        UxPaletteTrack("palette_shared", { count: data.colours.length, via: "more" });
      } catch (error) {
        /* closed without sharing */
      }
      return;
    }
    if (to === "instagram") this.shareStory(tile);
  }
  /* Instagram takes no web links: a 1080 x 1920 story image of the palette. Phones share it (and the link is copied
     for a link sticker); elsewhere it downloads, the link is copied and the block says so for 6 s. */
  /* The Plan sidebar's tile (#474) passes its own block (the strings and the notice), palette and cache. */
  async shareStory(tile, { block = tile.closest("[data-ux-share]"), data = this.shareData, cache = this } = {}) {
    if (tile.getAttribute("aria-busy") === "true") return;
    tile.setAttribute("aria-busy", "true");
    try {
      cache.story ??= this.drawStory(block, data);
      const blob = await cache.story;
      const file = new File([blob], `${block.dataset.storyFile}.png`, { type: "image/png" });
      const copied = await this.copy(null, data.url, true);
      let shared = false;
      if (window.matchMedia("(pointer: coarse)").matches && navigator.canShare?.({ files: [file] })) {
        try {
          await navigator.share({ files: [file] });
          shared = true;
        } catch (error) {
          if (error?.name === "AbortError") return;
        }
      }
      if (!shared) {
        const link = Object.assign(document.createElement("a"), { href: URL.createObjectURL(blob), download: file.name });
        document.body.append(link);
        link.click();
        link.remove();
        setTimeout(() => URL.revokeObjectURL(link.href), 4000);
      }
      UxPaletteTrack("palette_shared", { count: data.colours.length, via: "instagram", image: shared ? "shared" : "downloaded" });
      const notice = block.querySelector("[data-ux-share-notice]");
      notice.querySelector("[data-ux-share-notice-title]").textContent = shared ? block.dataset.storyShared : copied ? block.dataset.storySaved : block.dataset.storySaved.split(",")[0];
      block.querySelector("[data-ux-share-thumb]").replaceChildren(...data.colours.map((colour) => {
        const band = document.createElement("span");
        band.style.background = colour.c || "var(--ux-grey)";
        return band;
      }));
      notice.hidden = false;
      clearTimeout(this.storyTimer);
      this.storyTimer = setTimeout(() => (notice.hidden = true), 6000);
    } catch (error) {
      cache.story = null;
    } finally {
      tile.removeAttribute("aria-busy");
    }
  }
  /* the story image (Figma 238:2463): logo, the palette's name, a band per colour with its name, the address */
  async drawStory(block, data) {
    const canvas = Object.assign(document.createElement("canvas"), { width: 1080, height: 1920 });
    const context = canvas.getContext("2d");
    const style = getComputedStyle(document.documentElement);
    const heading = style.getPropertyValue("--heading-font-family").trim() || "sans-serif";
    const body = style.getPropertyValue("--text-font-family").trim() || "serif";
    const weight = style.getPropertyValue("--heading-font-weight").trim() || "500";
    const ink = "#1c1c1c";
    await Promise.all([document.fonts?.load(`${weight} 96px ${heading}`), document.fonts?.load(`36px ${body}`)]).catch(() => {});
    context.fillStyle = "#fff";
    context.fillRect(0, 0, 1080, 1920);
    /* the logo: the header's own SVG, drawn in ink */
    const logo = document.querySelector(".header__logo svg.icon-logo-svg, .header__logo svg");
    if (logo) {
      const copy = logo.cloneNode(true);
      copy.setAttribute("xmlns", "http://www.w3.org/2000/svg");
      copy.setAttribute("width", "298");
      copy.setAttribute("height", String(Math.round(298 * (logo.viewBox.baseVal.height / logo.viewBox.baseVal.width || 0.43))));
      copy.setAttribute("color", ink);
      copy.style.color = ink;
      const source = new XMLSerializer().serializeToString(copy).replace(/currentColor/g, ink);
      const image = new Image();
      image.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(source)}`;
      await image.decode().then(() => context.drawImage(image, 107, 261, 298, Number(copy.getAttribute("height")))).catch(() => {});
    }
    context.fillStyle = ink;
    context.textBaseline = "middle";
    context.font = `${weight} 96px ${heading}`;
    context.fillText((data.name || block.dataset.title).toUpperCase(), 96, 516, 888);
    /* the bands share what's left between the title and the footer, 200 tall at most */
    const top = 616;
    const room = 1585 - top;
    const count = Math.max(1, data.colours.length);
    const band = Math.min(200, (room - (count - 1) * 16) / count);
    const size = Math.round(Math.min(40, band * 0.36));
    data.colours.forEach((colour, index) => {
      const y = top + index * (band + 16);
      context.fillStyle = colour.c || "#e0e0d9";
      context.fillRect(96, y, 888, band);
      context.fillStyle = (UxColours.lch(colour.c).l < 60) ? "#fff" : ink;
      context.font = `${weight} ${size}px ${heading}`;
      context.textBaseline = "alphabetic";
      context.fillText(String(colour.n || colour.h).toUpperCase(), 128, y + band - Math.max(16, band * 0.24), 824);
    });
    context.textBaseline = "alphabetic";
    context.fillStyle = ink;
    context.font = `36px ${body}`;
    context.fillText(block.dataset.storyUrl, 96, 1660);
    context.fillStyle = "rgba(28, 28, 28, 0.65)";
    context.font = `28px ${body}`;
    context.textAlign = "right";
    context.fillText(block.dataset.storyLine, 984, 1660);
    return new Promise((resolve, reject) => canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error("canvas"))), "image/png"));
  }
};
if (!window.customElements.get("ux-palette-page")) {
  window.customElements.define("ux-palette-page", UxPalettePage);
}

/* ---------------------------------------------------------------------------
   Colour consultancy hand-offs (spec docs/palette-accounts.md §14; Odoo side md_colour_consultancy, odoo-a3).
   - Links (#325, #335): every a[data-ux-palette-consult] goes to the consultation booking page (Theme settings ›
     Palette). From the palette page (data-ux-palette-consult="palette") only the palette in view goes with it (Figma 02,
     416:1748): ?palettes=<uid> when logged in, ?c=<handle>,… for a guest, ?c=…&from=shared for a shared palette, and
     nothing when it has no colours; Odoo's booking lists the rest of the book to tick. Anywhere else (the account, the
     landing page) nothing goes. Kept current as the palette changes, so a copied or middle-clicked link is right too.
   - <ux-consult-basket> (#326): on the cart page, ?consultation=<token>&rooms=… puts a consultation's products in
     the basket with its discount code. Milestone 11 (#334, Figma 08 460:5334 / 460:6088): it's the banner under "Your
     basket" (snippets/ux-consult-basket-banner.liquid): the rooms come from the basket's lines; the consultant, the
     consultation page and its message link are kept per reference (localStorage) from Odoo's answer (§14.9.1); each
     state has its own button (Your consultation, Ask Gemma naming what couldn't be added, Contact us, Try again).
   - <ux-consult-booking> (#327): Odoo's booking form in a frame, with a hand-off token for a logged-in customer.
     On the booking page (#336, data-features) it tells the frame what the page can do (§14.9.4 "parent"), reports
     the visible part of the frame ("viewport"), pins the frame's "bar" to the bottom of the screen (Details and
     Continue go back to it) and opens a consultant's profile in the theme's drawer ("profile"; "choose" goes back).
   - <ux-consult-popup> (#327): a link that opens that frame in a dialog (the discovery call).
   --------------------------------------------------------------------------- */
const UxConsult = (() => {
  const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
  const HANDLE = /^[a-z0-9][a-z0-9-]{0,99}$/;
  const MAX_PALETTES = 10;
  const MAX_COLOURS = 12;
  const proxy = () => uxAppProxy();
  const root = () => window.Shopify?.routes?.root || "/";
  const unique = (items) => items.filter((item, index) => items.indexOf(item) === index);
  const list = (value, pattern, max) => unique(String(value || "").toLowerCase().split(",").map((item) => item.trim()).filter((item) => pattern.test(item))).slice(0, max);
  /* commas stay commas: every value is a UUID or a handle */
  const withQuery = (url, pairs) => {
    pairs.forEach(([key]) => url.searchParams.delete(key));
    url.search = [url.searchParams.toString(), ...pairs.filter(([, value]) => value).map(([key, value]) => `${key}=${value}`)].filter(Boolean).join("&");
    return url;
  };

  /* --- the links (#325, #335) --- */
  const handover = (link) => {
    if (link.dataset.uxPaletteConsult !== "palette") return {};
    const shared = link.closest("ux-palette-page")?.shared;
    if (shared?.length) return { c: list(shared.map((colour) => colour.h).join(","), HANDLE, MAX_COLOURS), from: "shared" };
    const active = UxPalette.active();
    if (!active?.colours?.length) return {};
    if (UxPalette.account?.customer && UUID.test(active.id)) return { palettes: [active.id] };
    return { c: active.colours.map((colour) => colour.h).slice(0, MAX_COLOURS) };
  };
  const point = (link) => {
    link.dataset.uxConsultHref ??= window.uxConsultPage || link.getAttribute("href") || "";
    if (!link.dataset.uxConsultHref) return;
    const { palettes = [], c = [], from = "" } = handover(link);
    const url = withQuery(new URL(link.dataset.uxConsultHref, window.location.origin), [["palettes", palettes.join(",")], ["c", palettes.length ? "" : c.join(",")], ["from", c.length && !palettes.length ? from : ""]]);
    link.href = url.origin === window.location.origin ? `${url.pathname}${url.search}${url.hash}` : url.href;
  };
  const pointAll = () => document.querySelectorAll("a[data-ux-palette-consult]").forEach(point);
  /* capture: before the palette page's own click handler and before the browser follows the link */
  ["pointerdown", "focusin", "click"].forEach((type) => document.addEventListener(type, (event) => {
    const link = event.target.closest?.("a[data-ux-palette-consult]");
    if (link) point(link);
  }, true));
  document.addEventListener("ux-palette:change", pointAll);
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", pointAll);
  else pointAll();

  /* --- the basket (#326, #334) --- */
  const FLASH = "ux-consult-basket";
  const KEEP = "ux-consult-basket:";
  const TOKEN = /^[A-Za-z0-9._~-]{8,1024}$/;
  const ROOMS = /^[A-Za-z0-9_-]{1,64}(,[A-Za-z0-9_-]{1,64}){0,49}$/;
  const REF = /^[A-Za-z0-9-]{1,64}$/;
  const consultationOf = (properties) => (properties && typeof properties._consultation === "string" ? properties._consultation : "");
  /* "a1b2c3d4e5f6,0f9e8d7c6b5a": the specification lines a cart line holds (07d), "" on a line from before */
  const keysOf = (properties) => (properties && typeof properties._line === "string" ? properties._line : "");
  /* "CC-2026-0042/v2/Hall, stairs and landing" → { ref, version: 2, room }; since 11 Oct 2026 there are no versions:
     "CC-2026-0042/Kitchen" → { ref, version: 0, room } */
  const versionOf = (value) => {
    const match = /^([^/]+)\/(?:v(\d+)\/)?(.+)$/.exec(value || "");
    return match ? { ref: match[1], version: match[2] ? Number(match[2]) : 0, room: match[3] } : null;
  };
  const cleanLine = (line) => {
    const id = Number(line?.variant_id);
    const quantity = Number(line?.quantity);
    if (!Number.isInteger(id) || id <= 0 || !Number.isInteger(quantity) || quantity < 1 || quantity > 999) return null;
    const properties = {};
    Object.entries(line.properties && typeof line.properties === "object" ? line.properties : {}).forEach(([key, value]) => {
      if (typeof value === "string" || typeof value === "number") properties[String(key).slice(0, 100)] = String(value).slice(0, 255);
    });
    return { id, quantity, properties };
  };
  const text = (value, max = 200) => (typeof value === "string" ? value.trim().slice(0, max) : "");
  /* links from Odoo: http(s) only */
  const link = (value) => {
    try {
      const url = new URL(text(value, 2048), window.location.origin);
      return /^https?:$/.test(url.protocol) ? url.href : "";
    } catch (error) {
      return "";
    }
  };
  /* what the banner needs on later visits, kept per consultation reference */
  const remember = (ref, known) => {
    try {
      if (REF.test(ref || "")) window.localStorage.setItem(KEEP + ref, JSON.stringify({ ...known, at: Date.now() }));
    } catch (error) {
      /* the banner goes without the name and link */
    }
  };
  const recall = (ref) => {
    try {
      return REF.test(ref || "") ? JSON.parse(window.localStorage.getItem(KEEP + ref) || "null") : null;
    } catch (error) {
      return null;
    }
  };
  const known = (data) => ({ consultant: text(data?.consultant, 80), url: link(data?.consultation_url), message: link(data?.message_url) });
  /* "Kitchen", "Kitchen, and Hall, stairs and landing" */
  const names = (items, and) => (items.length < 2 ? items[0] || "" : `${items.slice(0, -1).join(", ")}${and}${items[items.length - 1]}`);
  let started = false;

  class UxConsultBasket extends HTMLElement {
    connectedCallback() {
      this.known = recall(this.dataset.ref) || {};
      if (this.known.consultant) this.querySelector("[data-ux-consult-basket-title]").textContent = this.dataset.titleWith.replace("[name]", this.known.consultant);
      this.button(this.known.url, this.dataset.linkLabel);
      this.querySelector("[data-ux-consult-basket-retry]")?.addEventListener("click", () => window.location.reload());
      const params = new URLSearchParams(window.location.search);
      const token = params.get("consultation");
      /* once per page: ux-basket redraws this element on every basket change */
      if (token && !started) {
        started = true;
        this.add(token, params.get("rooms"));
        return;
      }
      let flash = null;
      try {
        flash = JSON.parse(window.sessionStorage.getItem(FLASH) || "null");
        window.sessionStorage.removeItem(FLASH);
      } catch (error) {
        /* nothing to say */
      }
      if (flash?.state) this.show(flash.state, flash);
    }

    button(href, label) {
      const button = this.querySelector("[data-ux-consult-basket-link]");
      button.hidden = !href;
      if (!href) return;
      button.href = href;
      button.textContent = label;
    }

    /* adding | added | unavailable | unavailable_all | expired | nothing | failed */
    show(state, info = {}) {
      const key = state.replace(/_(\w)/g, (_, letter) => letter.toUpperCase());
      let message = this.dataset[key];
      if (!message) return;
      this.dataset.state = state;
      const consultant = info.consultant || this.known.consultant || "";
      const consultation = info.url || this.known.url || "";
      if (state === "unavailable") {
        const titles = (Array.isArray(info.titles) ? info.titles : []).filter(Boolean);
        message = titles.length ? this.dataset.unavailableNamed.replace("[names]", names(titles, this.dataset.and)) : message.replace("[count]", info.count || 1);
      }
      /* added: the server's line names the rooms; without lines to name, the built message */
      if (!(state === "added" && this.hasAttribute("data-rooms"))) this.querySelector("[data-ux-consult-basket-text]").textContent = message;
      this.querySelector("[data-ux-consult-basket-retry]").hidden = state !== "failed";
      if (state === "unavailable") this.button(info.message || this.known.message || consultation, consultant ? this.dataset.ask.replace("[name]", consultant) : this.dataset.askPlain);
      else if (state === "unavailable_all") this.button(this.dataset.contactUrl, this.dataset.contact);
      else if (state === "expired" || state === "nothing") this.button(consultation, this.dataset.linkLabel);
      else if (state === "signoff") this.button(consultation, this.dataset.signoffLabel);
      else if (state !== "added") this.button("", "");
    }

    request(path, body) {
      return fetch(`${root()}${path}`, {
        method: body ? "POST" : "GET",
        headers: body ? { "Content-Type": "application/json", Accept: "application/json" } : { Accept: "application/json" },
        body: body ? JSON.stringify(body) : undefined
      });
    }

    async add(token, rooms) {
      if (!TOKEN.test(token)) {
        this.show("failed");
        return;
      }
      this.show("adding");
      try {
        const url = new URL(`${proxy()}/consultancy/basket`, window.location.origin);
        url.searchParams.set("t", token);
        if (rooms && ROOMS.test(rooms)) url.searchParams.set("rooms", rooms);
        const response = await fetch(url, { headers: { Accept: "application/json" }, credentials: "same-origin" });
        const data = await response.json().catch(() => null);
        if (!data?.ok) {
          /* not_found: a bad or old token, so the same advice as an expired one: open it again from the consultation.
             expired and nothing_approved name the consultation when the link still does (§14.9.1) */
          /* 11 Oct 2026: nothing_to_add (everything ordered, or a surface still needs its tins); not_signed (the one
             sign-off comes first, on the specification) */
          const state = { expired: "expired", not_found: "expired", nothing_approved: "nothing", nothing_to_add: "nothing", not_signed: "signoff" }[data?.error] || "failed";
          this.show(state, state === "signoff" ? { ...known(data), url: link(data?.signoff_url) || known(data).url } : known(data));
          return;
        }
        const entries = (Array.isArray(data.lines) ? data.lines : []).map((raw) => ({ line: cleanLine(raw), title: text(raw?.title, 120) })).filter((entry) => entry.line).slice(0, 100);
        const lines = entries.map((entry) => entry.line);
        if (!lines.length) {
          this.show("nothing", known(data));
          return;
        }
        const reference = text(data.ref, 64) || consultationOf(lines[0].properties).split("/")[0] || null;
        const keep = known(data);
        remember(reference, keep);
        /* again: a line of the same consultation already in the basket gets the consultation's quantity, never twice */
        const inBasket = async () => (await (await this.request("cart.js")).json()).items || [];
        /* one cart line per (variant, _consultation, _line): _line names the specification lines it holds (07d), so a
           line whose keys changed is replaced, never topped up, and the order import marks the right lines ordered */
        const sameLine = (item, line) => item.variant_id === line.id && consultationOf(item.properties) === consultationOf(line.properties) && keysOf(item.properties) === keysOf(line.properties);
        const find = (items, line) => consultationOf(line.properties) && items.find((item) => sameLine(item, line));
        let items = await inBasket();
        const updates = {};
        const adds = [];
        entries.forEach((entry) => {
          const item = find(items, entry.line);
          if (item) updates[item.key] = entry.line.quantity;
          else adds.push(entry);
        });
        if (Array.isArray(data.approved)) {
          /* §14.9 (07d): what the specification no longer approves leaves the basket. In this link's rooms, a line of
             this consultation stays only as one of the payload's lines; in its other rooms, while every specification
             line it holds is still approved (a line from before _line: while its room has approved lines) */
          const approved = new Map(data.approved.filter((room) => room && typeof room.name === "string").map((room) => [room.name, new Set((Array.isArray(room.lines) ? room.lines : []).map(String))]));
          const scope = new Set((Array.isArray(data.rooms) && data.rooms.length ? data.rooms : data.approved).map((room) => room && room.name).filter(Boolean));
          items.forEach((item) => {
            const mine = versionOf(consultationOf(item.properties));
            if (!mine || mine.ref !== reference || item.key in updates || lines.some((line) => sameLine(item, line))) return;
            const keys = keysOf(item.properties).split(",").filter(Boolean);
            const still = approved.get(mine.room);
            if (scope.has(mine.room) || !still || keys.some((key) => !still.has(key))) updates[item.key] = 0;
          });
        } else if (lines.every((line) => versionOf(consultationOf(line.properties))?.version === 0)) {
          /* since 11 Oct 2026 (no versions): in this link's rooms, a line of this consultation stays only as one of the
             payload's lines (a surface whose tins changed comes in with new keys and replaces its old line) */
          const scope = new Set((Array.isArray(data.rooms) ? data.rooms : []).map((room) => room && room.name).filter(Boolean));
          items.forEach((item) => {
            const mine = versionOf(consultationOf(item.properties));
            if (!mine || mine.ref !== reference || item.key in updates || lines.some((line) => sameLine(item, line))) return;
            if (scope.has(mine.room)) updates[item.key] = 0;
          });
        } else {
          /* before 07d: a room approved again in a later version (_consultation carries the version it was approved in,
             §14.9.1) replaces that room's older lines; a room carried over unchanged keeps its version, so it matches */
          const incoming = lines.map((line) => versionOf(consultationOf(line.properties))).filter(Boolean);
          items.forEach((item) => {
            const mine = versionOf(consultationOf(item.properties));
            if (mine && incoming.some((next) => next.ref === mine.ref && next.room === mine.room && next.version > mine.version)) updates[item.key] = 0;
          });
        }
        if (Object.keys(updates).length) await this.request("cart/update.js", { updates });
        const missing = [];
        /* the basket lists the latest first: add the specification's last line first, so it reads in its order */
        adds.reverse();
        if (adds.length && !(await this.request("cart/add.js", { items: adds.map((entry) => entry.line) })).ok) {
          /* one sold-out or deleted product refuses the lot: add the rest one at a time */
          items = await inBasket();
          for (const entry of adds) {
            if (find(items, entry.line)) continue;
            if (!(await this.request("cart/add.js", { items: [entry.line] })).ok) missing.push(entry.title);
          }
        }
        missing.reverse();
        const failed = missing.length;
        UxPaletteTrack("consultation_basket_added", { consultation: reference, lines: lines.length - failed, failed });
        if (failed === lines.length) {
          this.show("unavailable_all", keep);
          return;
        }
        try {
          window.sessionStorage.setItem(FLASH, JSON.stringify({ state: failed ? "unavailable" : "added", count: failed, titles: missing.filter(Boolean), ...keep }));
        } catch (error) {
          /* the basket shows without the message */
        }
        const cart = `${root()}cart`;
        const code = typeof data.discount_code === "string" ? data.discount_code.trim() : "";
        /* /discount/<code> keeps the code for checkout, then goes back to the basket */
        window.location.replace(code ? `/discount/${encodeURIComponent(code)}?redirect=${encodeURIComponent(cart)}` : cart);
      } catch (error) {
        this.show("failed");
      }
    }
  }
  if (!window.customElements.get("ux-consult-basket")) window.customElements.define("ux-consult-basket", UxConsultBasket);

  /* --- the booking frame (#327, #336) --- */
  const KINDS = ["home", "video", "phone", "venue"];
  const TYPE = /^([0-9]{1,9}|[a-z0-9][a-z0-9_-]{0,63})$/;
  const KEY = /^[a-z0-9][a-z0-9_-]{0,63}$/;
  const FEATURES = ["bar", "profile", "viewport"];
  const QUIET_MS = 10000;
  const plain = (value, max) => (typeof value === "string" ? value.replace(/\s+/g, " ").trim().slice(0, max) : "");
  const stickyTop = () => parseFloat(getComputedStyle(document.documentElement).getPropertyValue("--sticky-area-height")) || 0;
  const motion = () => (window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth");
  /* 04b (Figma 549:7480): payment and Booked open on Odoo's own page, in the whole window. The frame's "pay" goes to
     its own pay route only; back from it, resume= and a one-time booking= key go into the frame; Booked links back
     with booked=<conversion id>, counted once per booking */
  const RESUME_KEY = /^[A-Za-z0-9_-]{16,128}$/;
  const CONVERSION = /^[0-9a-f]{16}$/;
  const SEEN_BOOKED = "ux-consult-booked";
  const payUrl = (value, origin) => {
    if (typeof value !== "string" || value.length > 2048 || !value.startsWith(`${origin}/appointment/book/pay/`)) return "";
    try {
      const url = new URL(value);
      return url.origin === origin && url.pathname.startsWith("/appointment/book/pay/") ? url.href : "";
    } catch (error) {
      return "";
    }
  };
  /* one-time keys and ids leave the address bar, so a reload or a shared link doesn't use them again */
  const forget = (names) => {
    const url = new URL(window.location.href);
    if (!names.some((name) => url.searchParams.has(name))) return;
    names.forEach((name) => url.searchParams.delete(name));
    try {
      window.history.replaceState(window.history.state, "", `${url.pathname}${url.search}${url.hash}`);
    } catch (error) {
      /* the address stays as it was */
    }
  };
  const firstBooked = (id) => {
    try {
      const seen = JSON.parse(localStorage.getItem(SEEN_BOOKED) || "[]");
      const ids = Array.isArray(seen) ? seen.filter((item) => CONVERSION.test(item)) : [];
      if (ids.includes(id)) return false;
      localStorage.setItem(SEEN_BOOKED, JSON.stringify([...ids, id].slice(-20)));
    } catch (error) {
      /* no storage: count it */
    }
    return true;
  };

  class UxConsultBooking extends HTMLElement {
    connectedCallback() {
      /* data-lazy: built when its dialog first opens (<ux-consult-popup>) */
      if (!this.hasAttribute("data-lazy")) this.start();
    }

    async start() {
      if (this.started) return;
      this.started = true;
      let base;
      try {
        base = new URL(this.dataset.base);
      } catch (error) {
        return; /* the link stays */
      }
      if (base.protocol !== "https:" && !["localhost", "127.0.0.1"].includes(base.hostname)) return;
      const page = new URLSearchParams(window.location.search);
      /* back from the payment page (resume=details|times&booking=<one-time key>), or from Booked (booked=<id>) */
      const resume = ["details", "times"].includes(page.get("resume")) && RESUME_KEY.test(page.get("booking") || "") ? page.get("resume") : "";
      const resumeKey = resume ? page.get("booking") : "";
      const booked = page.get("booked") || "";
      forget(["resume", "booking", "booked"]);
      if (CONVERSION.test(booked) && firstBooked(booked)) UxPaletteTrack("consultation_booked", { id: booked, source: "return", types: this.dataset.types || null });
      const types = list(this.dataset.types, TYPE, 20);
      const token = window.uxCustomer?.id ? await this.handoff() : null;
      const colours = list(page.get("c"), HANDLE, MAX_COLOURS);
      const keyOf = (name) => (KEY.test(page.get(name) || "") ? page.get(name) : "");
      withQuery(base, [
        ["types", types.join(",")],
        ["handoff", token ? encodeURIComponent(token) : ""],
        ["palettes", list(page.get("palettes"), UUID, MAX_PALETTES).join(",")],
        ["c", colours.join(",")],
        /* §14.9.4: a service or consultant chosen by the link, and colours from a shared palette */
        ["service", keyOf("service")],
        ["consultant", keyOf("consultant")],
        ["from", colours.length && page.get("from") === "shared" ? "shared" : ""],
        ["resume", resume],
        ["booking", resumeKey]
      ]);
      this.features = list(this.dataset.features, /^[a-z]+$/, 5).filter((feature) => FEATURES.includes(feature));
      this.profiles = list(this.dataset.profiles, KEY, 50);
      this.bar = this.features.includes("bar") ? this.closest(".ux-consult-booking")?.querySelector("[data-ux-consult-bar]") : null;
      const frame = document.createElement("iframe");
      frame.className = "ux-consult-booking__frame";
      frame.src = base.href;
      frame.title = this.dataset.title || "";
      frame.allow = "payment";
      frame.referrerPolicy = "strict-origin-when-cross-origin";
      this.frame = frame;
      this.origin = base.origin;
      this.listener = (event) => this.message(event);
      window.addEventListener("message", this.listener);
      this.replaceChildren(frame);
      /* Odoo's form speaks first (its height). Silence means it didn't load (an unknown type is a 404, a blocked or
         unreachable frame says nothing): offer it in a new tab instead, until it does speak */
      this.quiet = setTimeout(() => this.unanswered(), QUIET_MS);
    }

    disconnectedCallback() {
      if (this.listener) window.removeEventListener("message", this.listener);
      if (this.onViewport) {
        window.removeEventListener("scroll", this.onViewport);
        window.removeEventListener("resize", this.onViewport);
      }
      if (this.onChoose) document.removeEventListener("click", this.onChoose);
      clearTimeout(this.quiet);
    }

    post(data) {
      this.frame?.contentWindow?.postMessage({ type: "mylands:consultancy", ...data }, this.origin);
    }

    /* the page's side of §14.9.4, once the frame has spoken */
    greet() {
      if (!this.features.length) return;
      this.post({ event: "parent", features: this.features, profiles: this.features.includes("profile") ? this.profiles : [] });
      if (this.bar) {
        this.bar.querySelector("[data-ux-consult-bar-continue]").addEventListener("click", () => this.post({ event: "continue" }));
        this.bar.querySelector("[data-ux-consult-bar-details]").addEventListener("click", () => this.post({ event: "details" }));
      }
      if (this.features.includes("profile")) {
        /* the theme's drawer moves itself to <body> when it opens: listen on the document */
        this.onChoose = (event) => {
          const choose = event.target.closest?.("[data-ux-consult-choose]");
          if (!choose || !KEY.test(choose.dataset.uxConsultChoose || "")) return;
          this.post({ event: "choose", consultant: choose.dataset.uxConsultChoose });
          choose.closest("ux-drawer")?.hide?.();
        };
        document.addEventListener("click", this.onChoose);
      }
      if (this.features.includes("viewport")) {
        let queued = false;
        const send = () => {
          queued = false;
          if (!this.frame || this.frame.hidden) return;
          const rect = this.frame.getBoundingClientRect();
          const top = stickyTop();
          const bottom = window.innerHeight - (this.bar && !this.bar.hidden ? this.bar.offsetHeight : 0);
          const visible = { top: Math.max(0, Math.round(top - rect.top)), height: Math.max(0, Math.round(Math.min(rect.bottom, bottom) - Math.max(rect.top, top))) };
          const said = `${visible.top},${visible.height}`;
          if (said === this.said) return;
          this.said = said;
          this.post({ event: "viewport", ...visible });
        };
        this.onViewport = () => {
          if (queued) return;
          queued = true;
          requestAnimationFrame(send);
        };
        window.addEventListener("scroll", this.onViewport, { passive: true });
        window.addEventListener("resize", this.onViewport);
        this.onViewport();
      }
    }

    /* the frame's "bar": shown while its last one isn't hidden (the frame's own breakpoint decides) */
    showBar(data) {
      if (!this.bar) return;
      const hide = data.hidden === true || !plain(data.label, 80);
      this.bar.hidden = hide;
      document.documentElement.classList.toggle("ux-consult-bar-open", !hide);
      if (!hide) {
        this.bar.querySelector("[data-ux-consult-bar-label]").textContent = plain(data.label, 80);
        const price = this.bar.querySelector("[data-ux-consult-bar-price]");
        price.textContent = plain(data.price, 40);
        price.hidden = !price.textContent;
        const action = this.bar.querySelector("[data-ux-consult-bar-continue]");
        action.dataset.label ??= action.textContent;
        action.textContent = plain(data.action, 40) || action.dataset.label;
        action.disabled = data.enabled === false;
      }
      document.documentElement.style.setProperty("--ux-consult-bar-height", hide ? "0px" : `${this.bar.offsetHeight}px`);
      this.onViewport?.();
    }

    /* the frame's "profile": the consultant's drawer (ux-consult-profile), labelled with the frame's action */
    openProfile(data) {
      const key = plain(data.consultant, 64);
      if (!this.features.includes("profile") || !this.profiles.includes(key)) return;
      const drawer = document.getElementById(`ux-consult-profile-${key}`);
      if (!drawer?.show) return;
      const action = plain(data.action, 60);
      const choose = drawer.querySelector("[data-ux-consult-choose]");
      if (choose && action) choose.textContent = action;
      drawer.show();
    }

    unanswered() {
      if (this.heard || !this.frame) return;
      this.frame.hidden = true;
      const note = document.createElement("p");
      note.className = "ux-consult-booking__unanswered";
      note.dataset.uxConsultUnanswered = "";
      const link = document.createElement("a");
      link.href = this.frame.src;
      link.target = "_blank";
      link.rel = "noopener";
      link.textContent = this.dataset.unansweredLink || this.frame.src;
      note.append(`${this.dataset.unanswered || ""} `, link);
      this.append(note);
    }

    /* the customer, signed for Odoo by the app proxy; without it they book as a guest */
    async handoff() {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 5000);
      try {
        const response = await fetch(`${proxy()}/consultancy/handoff`, { headers: { Accept: "application/json" }, credentials: "same-origin", signal: controller.signal });
        const data = await response.json();
        return data?.ok && typeof data.token === "string" && data.token.length <= 2048 ? data.token : null;
      } catch (error) {
        return null;
      } finally {
        clearTimeout(timer);
      }
    }

    message(event) {
      if (event.origin !== this.origin || event.source !== this.frame?.contentWindow) return;
      const data = event.data;
      if (!data || typeof data !== "object" || data.type !== "mylands:consultancy") return;
      if (!this.heard) {
        this.heard = true;
        clearTimeout(this.quiet);
        this.frame.hidden = false;
        this.querySelector("[data-ux-consult-unanswered]")?.remove();
        this.greet();
      }
      if (data.event === "height") {
        const height = Math.ceil(Number(data.height));
        if (Number.isFinite(height) && height > 0) this.frame.style.height = `${Math.min(Math.max(height, 320), 20000)}px`;
        this.onViewport?.();
      } else if (data.event === "scroll") {
        /* top: px from the frame's top edge to what the step wants in view (the summary, after Details) */
        const offset = Number(data.top);
        if (Number.isFinite(offset) && offset > 0 && offset < 20000) {
          window.scrollTo({ top: window.scrollY + this.frame.getBoundingClientRect().top + offset - stickyTop() - 16, behavior: motion() });
        } else {
          const top = this.getBoundingClientRect().top;
          if (top < 0 || top > window.innerHeight / 2) this.scrollIntoView({ block: "start", behavior: motion() });
        }
      } else if (data.event === "bar") {
        this.showBar(data);
      } else if (data.event === "profile") {
        this.openProfile(data);
      } else if (data.event === "step") {
        const step = Number(data.step);
        if (Number.isInteger(step) && step > 0 && step < 10) this.dataset.step = String(step);
      } else if (data.event === "pay") {
        const url = payUrl(data.url, this.origin);
        if (url && !this.paying) {
          this.paying = true;
          window.location.assign(url);
        }
      } else if (data.event === "booked" && !this.booked) {
        this.booked = true;
        const id = CONVERSION.test(data.id || "") ? data.id : null;
        if (!id || firstBooked(id)) UxPaletteTrack("consultation_booked", { ref: typeof data.ref === "string" ? data.ref.slice(0, 64) : null, kind: KINDS.includes(data.kind) ? data.kind : null, types: this.dataset.types || null, ...(id ? { id } : {}) });
      }
    }
  }
  if (!window.customElements.get("ux-consult-booking")) window.customElements.define("ux-consult-booking", UxConsultBooking);

  /* <ux-consult-popup>: its link opens the booking form in a dialog (a .ux-sheet: bottom sheet on mobile) instead of
     a new tab; the frame is built on first open. The residential page's discovery call (#327) */
  class UxConsultPopup extends HTMLElement {
    connectedCallback() {
      if (this.wired) return;
      this.wired = true;
      this.dialog = this.querySelector("dialog");
      this.opener = this.querySelector("[data-ux-consult-open]");
      if (!this.dialog?.showModal || !this.opener) return;
      this.opener.addEventListener("click", (event) => {
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.button) return; /* a new tab was asked for */
        event.preventDefault();
        this.open();
      });
      this.dialog.querySelector("[data-ux-sheet-close]")?.addEventListener("click", () => this.dialog.close());
      let downOnBackdrop = false;
      this.dialog.addEventListener("pointerdown", (event) => {
        downOnBackdrop = event.target === this.dialog;
      });
      this.dialog.addEventListener("click", (event) => {
        if (event.target === this.dialog && downOnBackdrop) this.dialog.close();
        downOnBackdrop = false;
      });
      this.dialog.addEventListener("close", () => {
        if (!document.querySelector("dialog.ux-sheet[open]")) document.documentElement.classList.remove("ux-sheet-open");
        this.opener.focus({ preventScroll: true });
      });
    }

    open() {
      this.dialog.showModal();
      document.documentElement.classList.add("ux-sheet-open");
      this.dialog.querySelector("ux-consult-booking")?.start();
      UxPaletteTrack("consultation_popup_opened", { types: this.querySelector("ux-consult-booking")?.dataset.types || null });
    }
  }
  if (!window.customElements.get("ux-consult-popup")) window.customElements.define("ux-consult-popup", UxConsultPopup);

  return { point, handover };
})();
window.UxConsult = UxConsult;

/* ---------------------------------------------------------------------------
   Colour Swatches picks (milestone 7, GitHub #204 / #205): the colours picked on the Colour Swatches page, kept for
   this browser session (sessionStorage; in memory when it's blocked) until they go in the basket. Each pick is the
   colour's /CS variant: { id, name, colour, price } (price in pence). Changes fire "ux-swatches:change" on document.
   --------------------------------------------------------------------------- */
var UxSwatchPicks = (() => {
  const KEY = "mylands-swatch-picks";
  let memory = [];
  const clean = (pick) => ({ id: String(pick.id), name: String(pick.name || ""), colour: String(pick.colour || ""), price: Number(pick.price) || 0 });
  const read = () => {
    try {
      const data = JSON.parse(window.sessionStorage.getItem(KEY) || "null");
      if (Array.isArray(data)) return data.filter((pick) => pick && pick.id).map(clean);
    } catch (error) {
      /* storage unavailable: the in-memory list */
    }
    return memory.slice();
  };
  const write = (picks) => {
    memory = picks.map(clean);
    try {
      window.sessionStorage.setItem(KEY, JSON.stringify(memory));
    } catch (error) {
      /* keep it in memory */
    }
    document.dispatchEvent(new CustomEvent("ux-swatches:change", { detail: { picks: memory.slice() } }));
  };
  const api = {
    list: read,
    has: (id) => read().some((pick) => pick.id === String(id)),
    toggle(pick) {
      const picks = read();
      const index = picks.findIndex((item) => item.id === String(pick.id));
      if (index > -1) picks.splice(index, 1);
      else picks.push(clean(pick));
      write(picks);
      return index === -1;
    },
    remove(id) {
      write(read().filter((pick) => pick.id !== String(id)));
    },
    clear() {
      write([]);
    }
  };
  return api;
})();
window.UxSwatchPicks = UxSwatchPicks;

/* Colour picker (#204; sections/ux-swatches-picker.liquid): search and family filter the tiles, 18 at a time (8 on
   mobile) with Show more; a tile toggles its pick. Focus moves to the first newly shown tile after Show more. */
var UxSwatchPicker = class extends HTMLElement {
  connectedCallback() {
    if (this._wired) return;
    this._wired = true;
    this.items = [...this.querySelectorAll("[data-ux-pick-item]")];
    this.input = this.querySelector("[data-ux-pick-search]");
    this.status = this.querySelector("[data-ux-pick-status]");
    this.family = "";
    this.shown = this.pageSize();
    let timer;
    this.input?.addEventListener("input", () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        this.shown = this.pageSize();
        this.apply();
      }, 150);
    });
    this.addEventListener("click", (event) => this.onClick(event));
    document.addEventListener("ux-swatches:change", () => this.sync());
    this.sync();
    this.apply();
  }
  pageSize() {
    return window.matchMedia("(min-width: 1000px)").matches ? 18 : 8;
  }
  say(text) {
    this.status.textContent = "";
    requestAnimationFrame(() => (this.status.textContent = text));
  }
  matching() {
    const query = (this.input?.value || "").trim().toLowerCase().replace(/™/g, "");
    return this.items.filter((item) => (!this.family || item.dataset.families.split(" ").includes(this.family)) && (!query || item.dataset.search.includes(query)));
  }
  apply() {
    const matching = this.matching();
    this.items.forEach((item) => (item.hidden = true));
    matching.slice(0, this.shown).forEach((item) => (item.hidden = false));
    const shown = Math.min(this.shown, matching.length);
    this.querySelector("[data-ux-pick-count]").textContent = this.dataset.showing.replace("[shown]", shown).replace("[total]", matching.length);
    this.querySelector("[data-ux-pick-more]").hidden = matching.length <= this.shown;
    this.querySelector("[data-ux-pick-more-wrap]").hidden = !matching.length;
    const none = this.querySelector("[data-ux-pick-none]");
    none.hidden = matching.length > 0;
    if (!matching.length) none.querySelector("[data-ux-pick-none-title]").textContent = this.dataset.none.replace("[query]", this.input.value.trim());
    return matching;
  }
  sync() {
    this.querySelectorAll("[data-ux-pick]").forEach((tile) => tile.setAttribute("aria-pressed", String(UxSwatchPicks.has(tile.dataset.id))));
  }
  onClick(event) {
    const tile = event.target.closest("[data-ux-pick]");
    if (tile && !tile.disabled) {
      const added = UxSwatchPicks.toggle({ id: tile.dataset.id, name: tile.dataset.name, colour: tile.dataset.colour, price: tile.dataset.price });
      this.say((added ? this.dataset.added : this.dataset.removed).replace("[colour]", tile.dataset.name));
      return;
    }
    const family = event.target.closest("[data-ux-pick-family]");
    if (family) {
      this.family = family.dataset.uxPickFamily;
      this.querySelectorAll("[data-ux-pick-family]").forEach((button) => button.setAttribute("aria-pressed", String(button === family)));
      this.shown = this.pageSize();
      this.apply();
      return;
    }
    if (event.target.closest("[data-ux-pick-more]")) {
      const before = this.shown;
      this.shown += this.pageSize();
      const matching = this.apply();
      matching[before]?.querySelector("[data-ux-pick]")?.focus();
      return;
    }
    if (event.target.closest("[data-ux-pick-clear]")) {
      this.input.value = "";
      this.shown = this.pageSize();
      this.apply();
      this.input.focus();
    }
  }
};
if (!window.customElements.get("ux-swatch-picker")) {
  window.customElements.define("ux-swatch-picker", UxSwatchPicker);
}

/* Sample basket bar (milestone 7, GitHub #205; sections/ux-swatches-picker.liquid): follows UxSwatchPicks. Chips
   remove a pick (focus moves to the next chip, or the Add button); on mobile the summary opens the chips above the
   bar. Add to basket adds every pick in one request, opens the basket drawer (variant:add) and clears the picks. */
var UxSwatchBar = class extends HTMLElement {
  connectedCallback() {
    if (this._wired) return;
    this._wired = true;
    this.chips = this.querySelector("[data-ux-swatch-chips]");
    this.toggle = this.querySelector("[data-ux-swatch-toggle]");
    this.addEventListener("click", (event) => this.onClick(event));
    document.addEventListener("ux-swatches:change", () => this.render());
    this.render();
  }
  money(cents) {
    const currency = window.Shopify?.currency?.active || "GBP";
    return new Intl.NumberFormat(document.documentElement.lang || "en-GB", { style: "currency", currency }).format(cents / 100);
  }
  say(text) {
    const status = this.querySelector("[data-ux-swatch-status]");
    status.textContent = "";
    requestAnimationFrame(() => (status.textContent = text));
  }
  render() {
    const picks = UxSwatchPicks.list();
    const count = picks.length;
    const wasHidden = this.hidden;
    this.hidden = !count;
    document.documentElement.classList.toggle("ux-swatch-bar-open", Boolean(count));
    if (!count) {
      this.setOpen(false);
      return;
    }
    const total = this.money(picks.reduce((sum, pick) => sum + pick.price, 0));
    const label = count === 1 ? this.dataset.one : this.dataset.other.replace("[count]", count);
    this.querySelector("[data-ux-swatch-count]").textContent = label;
    this.querySelector("[data-ux-swatch-sub]").textContent = `${total} · ${this.dataset.edit}`;
    this.querySelector("[data-ux-swatch-sum]").textContent = `${label} · ${total}`;
    this.querySelector("[data-ux-swatch-stack]").replaceChildren(...picks.slice(-3).map((pick) => {
      const swatch = document.createElement("span");
      swatch.style.background = pick.colour || "var(--ux-grey)";
      return swatch;
    }));
    const template = this.querySelector("template[data-ux-swatch-chip]");
    this.chips.replaceChildren(...picks.map((pick) => {
      const chip = template.content.firstElementChild.cloneNode(true);
      chip.dataset.id = pick.id;
      chip.querySelector(".ux-swatch-chip__swatch").style.background = pick.colour || "var(--ux-grey)";
      chip.querySelector("[data-ux-chip-name]").textContent = pick.name;
      const remove = chip.querySelector("[data-ux-chip-remove]");
      remove.setAttribute("aria-label", remove.dataset.label.replace("[colour]", pick.name));
      return chip;
    }));
    if (wasHidden) this.querySelector("[data-ux-swatch-error]").hidden = true;
  }
  setOpen(open) {
    this.classList.toggle("ux-swatch-bar--open", open);
    this.toggle.setAttribute("aria-expanded", String(open));
  }
  onClick(event) {
    const remove = event.target.closest("[data-ux-chip-remove]");
    if (remove) {
      const chip = remove.closest("[data-id]");
      const nextId = chip.nextElementSibling?.dataset.id || chip.previousElementSibling?.dataset.id;
      const name = chip.querySelector("[data-ux-chip-name]").textContent;
      UxSwatchPicks.remove(chip.dataset.id);
      this.say(this.dataset.removed.replace("[colour]", name));
      const target = nextId ? this.chips.querySelector(`[data-id="${nextId}"] [data-ux-chip-remove]`) : null;
      (target || document.querySelector("[data-ux-pick-search]"))?.focus();
      return;
    }
    if (event.target.closest("[data-ux-swatch-toggle]")) {
      this.setOpen(this.toggle.getAttribute("aria-expanded") !== "true");
      return;
    }
    const add = event.target.closest("[data-ux-swatch-add]");
    if (add) this.addAll(add);
  }
  async addAll(button) {
    if (button.getAttribute("aria-busy") === "true") return;
    const picks = UxSwatchPicks.list();
    if (!picks.length) return;
    const error = this.querySelector("[data-ux-swatch-error]");
    error.hidden = true;
    button.setAttribute("aria-busy", "true");
    const root = window.Shopify?.routes?.root || "/";
    try {
      const sections = [];
      document.documentElement.dispatchEvent(new CustomEvent("cart:prepare-bundled-sections", { bubbles: true, detail: { sections } }));
      const response = await fetch(`${root}cart/add.js`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items: picks.map((pick) => ({ id: Number(pick.id), quantity: 1 })), sections: sections.join(",") })
      });
      const added = await response.json();
      if (!response.ok) throw new Error(added.description || added.message || response.statusText);
      const cart = await (await fetch(`${root}cart.js`)).json();
      cart.sections = added.sections;
      UxSwatchPicks.clear();
      this.say(this.dataset.added);
      document.documentElement.dispatchEvent(new CustomEvent("cart:change", { bubbles: true, detail: { baseEvent: "variant:add", cart } }));
    } catch (failure) {
      error.textContent = this.dataset.failed;
      error.hidden = false;
    } finally {
      button.removeAttribute("aria-busy");
    }
  }
};
if (!window.customElements.get("ux-swatch-bar")) {
  window.customElements.define("ux-swatch-bar", UxSwatchBar);
}

/* Tabs (milestone 8, GitHub #239): ARIA tabs for <ux-tabs> ([role=tab] buttons, [role=tabpanel] panels). Click or
   ←/→/Home/End move the selection with a roving tabindex; in a sideways strip the chosen tab scrolls into view. */
var UxTabs = class extends HTMLElement {
  connectedCallback() {
    if (this._wired) return;
    this._wired = true;
    this.tabs = [...this.querySelectorAll('[role="tab"]')];
    this.tabs.forEach((tab) => tab.addEventListener("click", () => this.select(tab)));
    // Theme editor: selecting a block (a tab, or anything in a panel) shows its tab.
    this.addEventListener("shopify:block:select", (event) => {
      const panel = event.target.closest('[role="tabpanel"]');
      const tab = event.target.closest('[role="tab"]') || this.tabs.find((t) => panel && t.getAttribute("aria-controls") === panel.id);
      if (tab) this.select(tab);
    });
    this.addEventListener("keydown", (event) => {
      const index = this.tabs.indexOf(event.target);
      if (index < 0) return;
      const last = this.tabs.length - 1;
      const next = { ArrowRight: index === last ? 0 : index + 1, ArrowLeft: index === 0 ? last : index - 1, Home: 0, End: last }[event.key];
      if (next === undefined) return;
      event.preventDefault();
      this.tabs[next].focus({ preventScroll: true });
      this.select(this.tabs[next]);
    });
  }
  select(chosen) {
    this.tabs.forEach((tab) => {
      const on = tab === chosen;
      tab.setAttribute("aria-selected", String(on));
      tab.tabIndex = on ? 0 : -1;
      const panel = this.querySelector(`#${CSS.escape(tab.getAttribute("aria-controls"))}`);
      if (panel) panel.hidden = !on;
    });
    this.reveal(chosen);
  }
  // Scroll only the strip (scrollIntoView would also shift overflow-clipped ancestors sideways).
  reveal(tab) {
    const strip = tab.parentElement;
    if (strip.scrollWidth <= strip.clientWidth) return;
    const box = strip.getBoundingClientRect();
    const { left, right } = tab.getBoundingClientRect();
    const delta = left < box.left ? left - box.left : right > box.right ? right - box.right : 0;
    if (delta) strip.scrollBy({ left: delta, behavior: matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth" });
  }
};
if (!window.customElements.get("ux-tabs")) {
  window.customElements.define("ux-tabs", UxTabs);
}

/* Paint calculator deep link (milestone 8, GitHub #241): a colour page opened at #paint-calculator opens its
   calculator drawer, so "Paint calculator" links (Paint landing, Need a hand?) land on the tool itself. */
if (window.location.hash === "#paint-calculator") {
  window.customElements.whenDefined("ux-drawer").then(() => document.getElementById("ux-pdp-calc-drawer")?.show());
}

/* All colours (Palettes v2, GitHub #181): the colour card and the wall of colour read every colour from one request,
   /collections/all-colours?view=palette-colours (templates/collection.palette-colours.liquid), cached for the page
   view and in sessionStorage for an hour. Both orders come from here:
   - byColour(): families in nav order (Whites … Metallics, then More colours), light to dark inside each by CIE L*.
   - wallColumns(rows): neutrals first (CIE LCh chroma < 9), then hue from red-purple (330°), chunked into columns of
     `rows`, each column light to dark; the metallics close it in their own column(s). Fixed for a given data set. */
var UxColours = (() => {
  const URL = "collections/all-colours?view=palette-colours";
  /* versioned: a change to what the data carries (shades #445, cards #431) must not meet an older visitor's copy */
  const STORE_KEY = "mylands-colours-v3";
  const MAX_AGE = 60 * 60 * 1000;
  const NEUTRAL_CHROMA = 9;
  const HUE_START = 330;
  let loading = null;

  // sRGB hex → CIE L*, C*, h° (D65)
  function lch(hex) {
    const value = String(hex || "").replace("#", "").trim();
    const full = value.length === 3 ? value.split("").map((part) => part + part).join("") : value;
    if (!/^[0-9a-f]{6}$/i.test(full)) return { l: 0, c: 0, h: 0 };
    const [r, g, b] = [0, 2, 4].map((at) => {
      const channel = parseInt(full.slice(at, at + 2), 16) / 255;
      return channel <= 0.04045 ? channel / 12.92 : Math.pow((channel + 0.055) / 1.055, 2.4);
    });
    const xyz = [
      (0.4124564 * r + 0.3575761 * g + 0.1804375 * b) / 0.95047,
      0.2126729 * r + 0.7151522 * g + 0.072175 * b,
      (0.0193339 * r + 0.119192 * g + 0.9503041 * b) / 1.08883
    ].map((t) => (t > 216 / 24389 ? Math.cbrt(t) : (24389 / 27 * t + 16) / 116));
    const l = 116 * xyz[1] - 16;
    const a = 500 * (xyz[0] - xyz[1]);
    const bb = 200 * (xyz[1] - xyz[2]);
    const h = (Math.atan2(bb, a) * 180 / Math.PI + 360) % 360;
    return { l, c: Math.hypot(a, bb), h };
  }

  function prepare(data) {
    data.colours.forEach((colour) => {
      colour.lch = lch(colour.c);
    });
    return data;
  }

  function cached() {
    try {
      const stored = JSON.parse(sessionStorage.getItem(STORE_KEY) || "null");
      if (stored && Date.now() - stored.at < MAX_AGE && Array.isArray(stored.data?.colours)) return stored.data;
    } catch (error) {}
    return null;
  }

  function load() {
    if (!loading) {
      const stored = cached();
      loading = stored ? Promise.resolve(prepare(stored)) : fetch(`${window.Shopify?.routes?.root || "/"}${URL}`)
        .then((response) => {
          if (!response.ok) throw new Error(response.statusText);
          return response.json();
        })
        .then((data) => {
          try {
            sessionStorage.setItem(STORE_KEY, JSON.stringify({ at: Date.now(), data }));
          } catch (error) {}
          return prepare(data);
        })
        .catch((error) => {
          loading = null;
          throw error;
        });
    }
    return loading;
  }

  const lightToDark = (a, b) => b.lch.l - a.lch.l;
  const fromRedPurple = (colour) => (colour.lch.h - HUE_START + 360) % 360;

  // [{ slug, label, url, colours }] in nav order; empty families dropped
  function byColour(data) {
    return data.families
      .map((family) => ({ ...family, colours: data.colours.filter((colour) => colour.f === family.slug).sort(lightToDark) }))
      .filter((family) => family.colours.length);
  }

  // [[colour, …], …]: columns of `rows`, top to bottom
  function wallColumns(data, rows) {
    const size = Math.max(1, rows | 0);
    const metallics = data.colours.filter((colour) => colour.f === "metallics").sort(lightToDark);
    const others = data.colours.filter((colour) => colour.f !== "metallics");
    const byHue = (a, b) => fromRedPurple(a) - fromRedPurple(b) || lightToDark(a, b);
    const neutrals = others.filter((colour) => colour.lch.c < NEUTRAL_CHROMA).sort(byHue);
    const chromatic = others.filter((colour) => colour.lch.c >= NEUTRAL_CHROMA).sort(byHue);
    const chunk = (list) => {
      const columns = [];
      for (let at = 0; at < list.length; at += size) columns.push(list.slice(at, at + size).sort(lightToDark));
      return columns;
    };
    return [...chunk([...neutrals, ...chromatic]), ...chunk(metallics)];
  }

  return { load, lch, byColour, wallColumns };
})();
window.UxColours = UxColours;

/* ---------------------------------------------------------------------------
   The colour card (Palettes v2, GitHub #182; sections/ux-palette.liquid <ux-colour-card>): the toolbar (title and
   count, Find, Spotlight) over the wall of colour (<ux-colour-wall>, below), its one view since By colour went (Matt,
   16 Oct, GitHub #436). It holds the palette the wall adds to: UxPalettePage calls open(id), and a tap on the wall
   toggles that colour in it, with a notice and Undo (a full palette says so). Desktop (MP3-8, GitHub #415): the card
   fills the window under the header, the dock along the bottom and, from 1200 px, the colour panel on the right; the
   open palette's editor leaves the page (its jobs are in the palette drawer, opened from the dock). Mobile: the
   editor's Add colours moves the card into the full-screen sheet (Close, "Colour card", the dock at the foot) and
   back when it closes.
   --------------------------------------------------------------------------- */
var UxColourCard = class extends HTMLElement {
  static fullName(colour) {
    return String(colour?.n || "").replace(/™/g, "").replace(/\s+-\s+/, " ").trim();
  }
  static label(colour) {
    return UxColourCard.fullName(colour).replace(/\s+(No\.|BH\.|JB\.)\s*\S+$/, "");
  }
  /* the card in its place on the page (not in a sheet): the app's layout, at every width since phones got the app
     too (Matt, 20 Oct: one app, Discover · Plan · Try, not a different page on a phone) */
  split() {
    return !this.closest("dialog");
  }
  /* the page takes the wall's layout on desktop, and the wall starts once a palette is open */
  syncSplit() {
    const split = this.split();
    this.page?.classList.toggle("ux-palette--split", split);
    this.page?.querySelector("[data-ux-palette-workspace]")?.classList.toggle("ux-palette-workspace--split", split);
    if (!this.wall) return;
    this.sizeWall();
    if (this.page && !this.page.shared) this.wall.start?.().then(() => this.wall.reveal());
  }
  /* desktop: the toolbar, the wall and the dock fill the window below where the card starts (all of it once
     scrolled) */
  sizeWall() {
    const top = this.getBoundingClientRect().top + window.scrollY;
    const height = top < window.innerHeight / 2 ? window.innerHeight - top : window.innerHeight;
    this.style.setProperty("--ux-wall-height", `${Math.max(520, Math.round(height))}px`);
  }
  connectedCallback() {
    if (this._wired) return;
    this._wired = true;
    this.page = this.closest("ux-palette-page");
    this.home = this.parentElement;
    /* the wall laid out to fill the screen from 768 (tablets, #451); under it, six across, scrolling */
    this.wide = window.matchMedia("(min-width: 768px)");
    this.sheet = this.page?.querySelector("[data-ux-card-sheet]");
    this.wall = this.querySelector("ux-colour-wall");
    this.syncSplit();
    ["resize", "load"].forEach((name) => window.addEventListener(name, () => this.wall && this.sizeWall()));
    /* mobile: Add colours opens the sheet (the link to All colours stays the no-script way) */
    this.page?.querySelector("[data-ux-editor-add-colours]")?.addEventListener("click", (event) => {
      if (this.wide.matches || !this.sheet) return;
      event.preventDefault();
      this.openSheet(event.currentTarget);
    });
    /* across 1000 px: the sheet closes (the card goes home and takes the wall's layout), or the layout follows */
    this.wide.addEventListener("change", (event) => {
      if (event.matches && this.sheet?.open) this.sheet.close();
      else this.syncSplit();
    });
    if (this.page && !this.page.shared) this.open(this.page.chosen);
  }
  t(key, values = {}) {
    return Object.entries(values).reduce((string, [name, value]) => string.split(`[${name}]`).join(value), this.dataset[key] || "");
  }
  palette() {
    return this.paletteId ? UxPalette.palette(this.paletteId) : null;
  }
  /* the palette the wall adds to (the page's open palette, or none yet); the wall loads the colours the first time */
  open(id) {
    this.paletteId = id || null;
    this.wall?.start?.();
  }
  toggle(handle, source = "wall") {
    const palette = this.palette();
    const colour = this.wall?.byHandle?.get(handle);
    if (!colour) return;
    const name = UxColourCard.label(colour);
    /* nothing saved yet (#455): the first colour makes "My palette", which the page then opens */
    if (!palette) {
      const id = UxPalette.createPalette({ colours: [{ h: colour.h, n: colour.n, c: colour.c }] });
      if (!id) return;
      this.page?.open?.(id);
      const made = UxPalette.palette(id);
      UxPaletteUi.notify(this.t("added", { colour: name, name: made.name }), { undo: () => UxPalette.deletePalette(id) });
      UxPaletteTrack("palette_colour_added", { handle, source, palette: id });
      return;
    }
    if (palette.colours.some((saved) => saved.h === handle)) {
      const undo = UxPalette.removeFrom(palette.id, handle);
      if (!undo) return;
      UxPaletteUi.notify(this.t("removed", { colour: name, name: palette.name }), { undo: () => UxPalette.restore(undo) });
      UxPaletteTrack("palette_colour_removed", { handle, source, palette: palette.id });
    } else if (palette.colours.length >= UxPalette.LIMITS.colours) {
      UxPaletteUi.notify(UxPaletteUi.text("full", { name: palette.name }));
    } else if (UxPalette.addTo(palette.id, { h: colour.h, n: colour.n, c: colour.c })) {
      UxPaletteUi.notify(this.t("added", { colour: name, name: palette.name }), { undo: () => UxPalette.removeFrom(palette.id, handle) });
      UxPaletteTrack("palette_colour_added", { handle, source, palette: palette.id });
    }
  }

  /* mobile: the full-screen sheet */
  openSheet(from) {
    const sheet = this.sheet;
    if (!sheet) return;
    if (!sheet.dataset.cardWired) {
      sheet.dataset.cardWired = "true";
      UxPaletteUi.wire(sheet);
      sheet.addEventListener("close", () => this.closeSheet());
    }
    sheet.querySelector("[data-ux-card-sheet-body]").append(this);
    /* the Added notice lives under the page; a modal sheet sits above it, so it comes in too */
    const notice = document.querySelector("[data-ux-palette-notice]");
    if (notice) {
      this.noticeHome = notice.parentElement;
      sheet.append(notice);
    }
    this.sheetOpener = from;
    UxPaletteUi.open(sheet, from);
    sheet.querySelector("[data-ux-card-sheet-body]").scrollTop = 0;
    this.wall?.start?.().then(() => {
      this.wall.showDiscover?.();
      this.wall.reveal();
    });
  }
  closeSheet() {
    this.home?.append(this);
    const notice = this.sheet?.querySelector("[data-ux-palette-notice]");
    if (notice && this.noticeHome) this.noticeHome.append(notice);
    if (this.sheetOpener?.isConnected) this.sheetOpener.focus({ preventScroll: true });
    this.syncSplit();
    this.wall?.showDiscover?.();
    this.wall?.layout();
  }
};
if (!window.customElements.get("ux-colour-card")) {
  window.customElements.define("ux-colour-card", UxColourCard);
}

/* ---------------------------------------------------------------------------
   THE PALETTE VIEW (#449; Figma 875:12676, 03): the Adding here palette large. Its 12 slots: a colour each with its
   role (a select; setRole) and Remove (with Undo); drag a slot onto another, or Alt + Left/Right, to reorder
   (reorderColours). The sidebar: the name (Rename in place), its project (a select; movePalette), Duplicate (opens the
   copy), Delete (asks, then Undo), Order samples (Samples with it ticked), the share link with Copy, and the advice
   links. On desktop it replaces the palette drawer: a second click on Adding here opens this view.
   --------------------------------------------------------------------------- */
var UxPaletteView = class {
  constructor(wall) {
    this.wall = wall;
    this.stage = wall.querySelector('[data-ux-app-stage="palette"]');
    this.side = wall.querySelector('[data-ux-app-side="palette"]');
    if (!this.stage?.querySelector("[data-ux-pview]") || !this.side) return;
    this.ready = true;
    this.slots = this.stage.querySelector("[data-ux-pview-slots]");
    /* the slot templates, kept: they live in the open palette's view, which moves into its place in the list */
    this.templates = {
      roles: this.stage.querySelector("template[data-ux-pview-roles]"),
      steps: this.stage.querySelector("template[data-ux-pview-move]"),
      plus: this.stage.querySelector("template[data-ux-pview-plus]"),
      hint: this.stage.querySelector("[data-ux-pview-hint]")
    };
    /* one palette at a time (Matt, 22 Oct, after the Plan review): the sidebar's list, or the switcher at the top
       under 1000, picks which; New palette in both. A tap on a colour slides its pairings in, a drawer of their own */
    this.drawer = wall.querySelector("[data-ux-pair-drawer]");
    this.switcher = this.stage.querySelector("[data-ux-plan-switch]");
    this.tabsList = this.stage.querySelector("[data-ux-plan-tabs]");
    this.sideList = this.side.querySelector("[data-ux-pside-palettes]");
    [this.stage, this.side].forEach((box) => box.querySelectorAll("[data-ux-plan-new]").forEach((button) => button.addEventListener("click", (event) => UxPaletteUi.newPalette({ from: event.currentTarget, onCreate: (id) => this.open(id) }))));
    [this.sideList, this.tabsList].forEach((list) => list?.addEventListener("click", (event) => {
      const open = event.target.closest("[data-ux-pside-open]");
      if (open) this.open(open.dataset.uxPsideOpen);
    }));
    this.stage.querySelector("[data-ux-plan-samples]")?.addEventListener("click", () => this.id && this.wall.toSamples(this.id));
    this.drawer?.addEventListener("click", (event) => {
      if (event.target.closest("[data-ux-pair-close]")) this.closePairs({ focus: true });
      else this.onPairs(event);
    });
    /* Escape: a Swap with… list first, then the drawer */
    this.drawer?.addEventListener("keydown", (event) => {
      if (event.key !== "Escape") return;
      event.stopPropagation();
      if (!this.swapOpen && !this.addOpen) return this.closePairs({ focus: true });
      const button = this.swapOpen ? `[data-ux-pairs-swap="${CSS.escape(this.swapOpen)}"]` : `[data-ux-pairs-more="${CSS.escape(this.addOpen)}"]`;
      this.swapOpen = null;
      this.addOpen = null;
      this.renderPairs();
      this.drawer.querySelector(button)?.focus();
    });
    document.addEventListener("click", (event) => {
      if ((this.swapOpen || this.addOpen) && !event.target.closest?.(".ux-pairs__card")) {
        this.swapOpen = null;
        this.addOpen = null;
        this.renderPairs();
      }
    });
    /* every palette's colours (the open one's, and the others' in full, Matt 21 Oct) take the same edits; a list says
       which palette it is (data-palette) */
    this.stage.addEventListener("change", (event) => {
      const at = this.listOf(event);
      const slot = event.target.closest("[data-handle]");
      if (!at || !slot || !event.target.matches("select")) return;
      if (UxPalette.setRole(at.id, slot.dataset.handle, event.target.value || null)) UxPaletteTrack("palette_role_set", { palette: at.id, handle: slot.dataset.handle, role: event.target.value || null, source: "palette-view" });
    });
    this.stage.addEventListener("click", (event) => {
      const at = this.listOf(event);
      if (!at) return;
      /* Add a colour: Discover's wall, where a tap adds to this palette (Adding here); focus on the wall */
      if (event.target.closest("[data-ux-pview-add]")) {
        if (at.id !== this.id) this.wall.choose(at.id);
        this.wall.setDiscover(this.wall.discover === "palettes" ? "all" : this.wall.discover);
        requestAnimationFrame(() => (this.wall.grid?.querySelector('.ux-wtile[tabindex="0"]') || this.wall.tabs.find((tab) => tab.dataset.uxAppTab === "colours"))?.focus({ preventScroll: true }));
        return;
      }
      /* move earlier / later (#466): the buttons on each slot, for touch and keyboard; focus stays on the button */
      const step = event.target.closest("[data-ux-pview-move]");
      if (step) {
        const handle = step.closest("[data-handle]").dataset.handle;
        const way = step.dataset.uxPviewMove;
        const handles = this.handles(at.id);
        const index = handles.indexOf(handle) + (way === "later" ? 1 : -1);
        if (index >= 0 && index < handles.length) this.move(handle, index, { focus: `[data-ux-pview-move="${way}"]`, id: at.id });
        return;
      }
      const remove = event.target.closest("[data-ux-pview-remove]");
      if (!remove) {
        /* a tap on a colour slides its pairings in; on the same colour again, out */
        const slot = event.target.closest("li[data-handle]");
        if (slot && !event.target.closest("select, button")) this.pick(slot.dataset.handle);
        return;
      }
      const palette = UxPalette.palette(at.id);
      const colour = palette?.colours.find((saved) => saved.h === remove.closest("[data-handle]").dataset.handle);
      const undo = colour && UxPalette.removeFrom(at.id, colour.h);
      if (!undo) return;
      UxPaletteUi.notify(this.t(this.stage, "removed", { colour: UxColourCard.label(colour), name: palette.name }), { undo: () => UxPalette.restore(undo) });
      UxPaletteTrack("palette_colour_removed", { handle: colour.h, source: "palette-view", palette: at.id });
    });
    /* reorder and move between palettes: drag a colour (the others make room as it goes, Matt 21 Oct), the move
       buttons, Alt + Left/Right (earlier, later) or Alt + Up/Down (the palette above or below) */
    /* a mouse drags at once; touch holds first (moving before the hold is a scroll, Matt 20 Oct) */
    this.stage.addEventListener("pointerdown", (event) => this.listOf(event) && this.press(event));
    /* the click that ends a drag isn't a tap on the colour */
    this.stage.addEventListener("click", (event) => {
      if (!this.suppressClick) return;
      this.suppressClick = false;
      event.stopImmediatePropagation();
      event.preventDefault();
    }, true);
    this.stage.addEventListener("keydown", (event) => {
      const at = this.listOf(event);
      const slot = event.target.closest("li[data-handle]");
      if (!at || !slot || event.target !== slot) return;
      if (!event.altKey && (event.key === "Enter" || event.key === " ")) {
        event.preventDefault();
        this.pick(slot.dataset.handle);
        return;
      }
      if (!event.altKey || !["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(event.key)) return;
      event.preventDefault();
      const handles = this.handles(at.id);
      const index = handles.indexOf(slot.dataset.handle) + (["ArrowDown", "ArrowRight"].includes(event.key) ? 1 : -1);
      if (index < 0 || index >= handles.length) return;
      this.move(slot.dataset.handle, index, { focus: true, id: at.id });
    });
    this.side.addEventListener("click", (event) => {
      const button = event.target.closest("[data-ux-pside-act]");
      if (button) this.act(button.dataset.uxPsideAct, button);
    });
    /* the story image is drawn before the Instagram tap lands: phones only share inside the gesture (#190, #474) */
    ["pointerdown", "focusin"].forEach((name) => this.side.addEventListener(name, (event) => event.target.closest?.('[data-ux-pside-act="story"]') && this.drawStory()));
    this.side.querySelector("[data-ux-pside-project]").addEventListener("change", (event) => {
      if (UxPalette.movePalette(this.id, event.target.value || null)) UxPaletteTrack("palette_moved", { palette: this.id, source: "palette-view" });
    });
    const rename = this.side.querySelector("[data-ux-pside-rename]");
    rename.addEventListener("submit", (event) => {
      event.preventDefault();
      const name = rename.elements.name.value.trim();
      this.renaming(false); /* first, so the redraw the rename sets off shows the new name */
      if (name && UxPalette.renamePalette(this.id, name)) UxPaletteTrack("palette_renamed", { palette: this.id, source: "palette-view" });
      this.side.querySelector("[data-ux-pside-act='rename']").focus();
    });
    rename.querySelector("[data-ux-pside-cancel]").addEventListener("click", () => this.renaming(false));
    rename.addEventListener("keydown", (event) => event.key === "Escape" && (event.stopPropagation(), this.renaming(false)));
  }
  t(element, key, values = {}) {
    return Object.entries(values).reduce((string, [name, value]) => string.split(`[${name}]`).join(value), element.dataset[key] || "");
  }
  handles(id = this.id) {
    return (UxPalette.palette(id)?.colours || []).map((colour) => colour.h);
  }
  /* which palette's colours an event is in: the open one's list, or another's in full */
  listOf(event) {
    const list = event.target.closest?.("[data-ux-pview-slots], [data-ux-plan-slots]");
    return list ? { list, id: list.dataset.palette || this.id } : null;
  }
  /* open a palette in Plan: it becomes the one in use and takes the stage; focus stays where it was chosen */
  open(id) {
    if (!UxPalette.palette(id)) return;
    if (id === this.id) this.render(id);
    else this.wall.choose(id);
  }
  static still() {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  }
  /* another palette takes the stage: it settles in (a short fade and rise), from the top; nothing morphs between
     palettes (the review: the chips growing into the editor stretched the type and moved everything at once) */
  settle() {
    const box = [this.stage, this.wall].find((element) => element.scrollHeight > element.clientHeight + 1 && /auto|scroll/.test(getComputedStyle(element).overflowY));
    if (box === this.stage) box.scrollTop = 0;
    if (UxPaletteView.still()) return;
    this.stage.querySelector("[data-ux-pview]").animate([{ opacity: 0, transform: "translateY(8px)" }, { opacity: 1, transform: "none" }], { duration: 200, easing: "cubic-bezier(0.2, 0, 0, 1)" });
  }
  /* drag (Matt, 21 Oct: a preview of the places shifting, and between palettes): a mouse takes the colour after a few
     pixels, touch and pen after a 400 ms hold (any move before that is the page scrolling, Matt 20 Oct). A copy follows
     the pointer; the colour's own place travels through the lists as a dashed space and the others slide aside
     (FLIP, not with reduced motion), so where it lands is where it shows. It drops into its palette's order or another
     palette (not a full one, or one that holds it). Escape puts it back. The stage scrolls near its edges. */
  press(event) {
    if (!event.isPrimary || (event.pointerType === "mouse" && event.button !== 0)) return;
    const slot = event.target.closest("li[data-handle]");
    if (!slot || event.target.closest("select, button")) return;
    const { id: from } = this.listOf(event);
    const touch = event.pointerType !== "mouse";
    const start = { x: event.clientX, y: event.clientY };
    let ghost = null;
    let cancelled = false;
    let timer = null;
    const begin = () => {
      this.closePairs();
      const rect = slot.getBoundingClientRect();
      ghost = slot.cloneNode(true);
      ghost.classList.remove("is-picked");
      ghost.classList.add("ux-pview__ghost");
      ghost.setAttribute("aria-hidden", "true");
      Object.assign(ghost.style, { left: `${rect.left}px`, top: `${rect.top}px`, width: `${rect.width}px` });
      document.body.append(ghost);
      /* the colour itself stays put, hidden (moving the element a finger is on cancels the touch); a dashed copy is
         the place that travels */
      const place = slot.cloneNode(true);
      place.removeAttribute("data-handle");
      place.removeAttribute("tabindex");
      place.classList.remove("is-picked");
      place.classList.add("is-placeholder");
      place.setAttribute("aria-hidden", "true");
      slot.before(place);
      this.dragState = { handle: slot.dataset.handle, from, slot, place };
      slot.classList.add("is-source", "is-lifted");
      document.documentElement.classList.add("ux-pview-dragging");
      navigator.vibrate?.(10);
    };
    if (touch) timer = setTimeout(begin, 400);
    const onMove = (move) => {
      if (move.pointerId !== event.pointerId) return;
      if (!ghost) {
        const distance = Math.hypot(move.clientX - start.x, move.clientY - start.y);
        if (touch) {
          if (distance > 8) end();
          return;
        }
        if (distance < 6) return;
        begin();
      }
      ghost.style.transform = `translate(${move.clientX - start.x}px, ${move.clientY - start.y}px)`;
      this.dropOnPalette(move.clientX, move.clientY);
      this.dragOver(move.clientX, move.clientY);
      this.autoScroll(move.clientY);
    };
    const noScroll = (scroll) => ghost && scroll.cancelable && scroll.preventDefault();
    const noMenu = (menu) => (ghost || touch) && menu.preventDefault();
    const onKey = (key) => {
      if (key.key !== "Escape" || !ghost) return;
      key.preventDefault();
      cancelled = true;
      end();
    };
    const end = (up) => {
      clearTimeout(timer);
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", end);
      window.removeEventListener("pointercancel", end);
      window.removeEventListener("touchmove", noScroll);
      window.removeEventListener("keydown", onKey, true);
      slot.removeEventListener("contextmenu", noMenu);
      if (!ghost) return;
      ghost.remove();
      const { place, target } = this.dragState;
      this.dropOnPalette();
      const list = place.closest("[data-ux-pview-slots], [data-ux-plan-slots]");
      /* where it lands: the colours before the dashed place (not counting itself) */
      let index = 0;
      for (const child of list?.children || []) {
        if (child === place) break;
        if (child.dataset.handle && child !== slot) index += 1;
      }
      place.remove();
      slot.classList.remove("is-source", "is-lifted");
      document.documentElement.classList.remove("ux-pview-dragging");
      this.dragState = null;
      this.suppressClick = up?.type === "pointerup";
      setTimeout(() => (this.suppressClick = false), 400);
      /* onto another palette in the list (Matt, 22 Oct): it goes to the end of that one */
      if (!cancelled && up?.type === "pointerup" && target) return this.moveBetween(from, target, slot.dataset.handle, UxPalette.palette(target).colours.length);
      if (cancelled || up?.type !== "pointerup" || !list) return this.render(this.id);
      const to = list.dataset.palette || this.id;
      if (to !== from) return this.moveBetween(from, to, slot.dataset.handle, index);
      if (index !== this.handles(from).indexOf(slot.dataset.handle)) this.move(slot.dataset.handle, index, { id: from });
      else this.render(this.id);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", end);
    window.addEventListener("pointercancel", end);
    window.addEventListener("touchmove", noScroll, { passive: false });
    window.addEventListener("keydown", onKey, true);
    slot.addEventListener("contextmenu", noMenu);
  }
  /* the dragged colour's place moves to where the pointer is; the others slide (FLIP) */
  dragOver(x, y) {
    const state = this.dragState;
    if (!state) return;
    const list = document.elementFromPoint(x, y)?.closest?.("[data-ux-pview-slots], [data-ux-plan-slots]");
    if (!list || !this.stage.contains(list)) return;
    const { slot, place, handle } = state;
    const to = list.dataset.palette || this.id;
    if (to !== state.from && place.parentElement !== list) {
      const target = UxPalette.palette(to);
      if (!target || target.colours.length >= UxPalette.LIMITS.colours || target.colours.some((colour) => colour.h === handle)) return;
    }
    /* over a colour, the place takes its spot (after it when coming from before it, else before it); over the +
       space or past the colours, the end */
    const over = document.elementFromPoint(x, y)?.closest?.("li");
    /* once per colour the pointer reaches, so the place doesn't flip back and forth over one */
    if (over === state.lastOver) return;
    state.lastOver = over;
    let before;
    if (over && list.contains(over) && over.dataset.handle && over !== slot) {
      const children = [...list.children];
      before = place.parentElement === list && children.indexOf(place) < children.indexOf(over) ? over.nextElementSibling : over;
    } else if (over && list.contains(over) && over.classList.contains("ux-pview__slot--empty")) before = over;
    else if (place.parentElement !== list) before = list.querySelector(".ux-pview__slot--empty");
    else return;
    if (before === slot) before = slot.nextElementSibling;
    /* the hidden source doesn't count as a neighbour */
    let next = place.nextElementSibling;
    if (next === slot) next = next.nextElementSibling;
    if (place.parentElement === list && (before ? next === before : !next)) return;
    const moving = [...new Set([list, place.parentElement])].flatMap((one) => [...one.children]).filter((item) => item !== place && item !== slot);
    const first = new Map(moving.map((item) => [item, item.getBoundingClientRect()]));
    if (before) list.insertBefore(place, before);
    else list.append(place);
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    moving.forEach((item) => {
      const was = first.get(item);
      const now = item.getBoundingClientRect();
      const dx = was.left - now.left;
      const dy = was.top - now.top;
      if (dx || dy) item.animate([{ transform: `translate(${dx}px, ${dy}px)` }, { transform: "none" }], { duration: 180, easing: "cubic-bezier(0.22, 0.8, 0.24, 1)" });
    });
  }
  /* a colour dragged over another palette in the sidebar's list (or the switcher): it's marked as where it will go,
     unless it's full or holds the colour already. No point: the mark clears */
  dropOnPalette(x, y) {
    const state = this.dragState;
    const button = x === undefined ? null : document.elementFromPoint(x, y)?.closest?.("[data-ux-pside-open]");
    const id = button?.dataset.uxPsideOpen;
    const target = id && state && id !== state.from && UxPalette.palette(id);
    const fits = target && target.colours.length < UxPalette.LIMITS.colours && !target.colours.some((colour) => colour.h === state.handle);
    if (state) state.target = fits ? id : null;
    document.querySelectorAll(".ux-pside__open.is-drop").forEach((one) => one !== button && one.classList.remove("is-drop"));
    button?.classList.toggle("is-drop", Boolean(fits));
  }
  autoScroll(y) {
    const box = [this.stage, this.wall].find((element) => element.scrollHeight > element.clientHeight + 1 && /auto|scroll/.test(getComputedStyle(element).overflowY));
    if (!box) return;
    const edge = box.getBoundingClientRect();
    if (y < edge.top + 60) box.scrollTop -= 14;
    else if (y > edge.bottom - 60) box.scrollTop += 14;
  }
  /* a colour into another palette, at a place: Undo; a full palette, or one holding it, says so */
  moveBetween(from, to, handle, index, { focus = false } = {}) {
    const colour = UxPalette.palette(from)?.colours.find((saved) => saved.h === handle);
    const target = UxPalette.palette(to);
    const undo = colour && target && UxPalette.moveColour(from, to, handle, index);
    if (!undo) {
      if (target && target.colours.length >= UxPalette.LIMITS.colours) UxPaletteUi.notify(UxPaletteUi.text("full", { name: target.name }));
      this.render(this.id);
      return;
    }
    UxPaletteUi.notify(this.t(this.stage, "movedTo", { colour: UxColourCard.label(colour), name: target.name }), { undo: () => UxPalette.restore(undo) });
    this.say(this.t(this.stage, "movedTo", { colour: UxColourCard.label(colour), name: target.name }));
    UxPaletteTrack("palette_colour_moved", { handle, from, to, source: "palette-view" });
    if (focus) requestAnimationFrame(() => this.stage.querySelector(`:is([data-ux-pview-slots], [data-ux-plan-slots])[data-palette="${CSS.escape(to)}"] li[data-handle="${CSS.escape(handle)}"]`)?.focus());
  }
  /* focus: true for the slot, or a selector for a control inside it (the move button pressed) */
  move(handle, at, { focus = false, id = this.id } = {}) {
    const handles = this.handles(id).filter((other) => other !== handle);
    handles.splice(at, 0, handle);
    if (!UxPalette.reorderColours(id, handles)) return;
    const colour = UxPalette.palette(id).colours.find((saved) => saved.h === handle);
    this.say(this.t(this.stage, "moved", { colour: UxColourCard.label(colour), at: at + 1 }));
    UxPaletteTrack("palette_reordered", { palette: id, source: "palette-view" });
    if (focus) requestAnimationFrame(() => {
      const slot = this.stage.querySelector(`:is([data-ux-pview-slots], [data-ux-plan-slots])[data-palette="${CSS.escape(id)}"] li[data-handle="${CSS.escape(handle)}"]`);
      (typeof focus === "string" ? slot?.querySelector(focus) : slot)?.focus();
    });
  }
  /* the Instagram story (#474): UxPalettePage draws it (Share your palette's, #190), from this palette, kept till it changes */
  drawStory() {
    const page = this.wall.page;
    if (typeof page?.drawStory !== "function" || !this.storyData?.url) return null;
    return (this.story ??= page.drawStory(this.side.querySelector("[data-ux-pside-share]"), this.storyData));
  }
  say(text) {
    const status = this.side.querySelector("[data-ux-pside-status]");
    status.textContent = "";
    requestAnimationFrame(() => (status.textContent = text));
  }
  render(id) {
    if (!this.ready) return;
    const palette = id && UxPalette.palette(id);
    const switched = (palette?.id || null) !== this.id;
    if (switched) {
      this.picked = null;
      this.swapOpen = false;
    }
    this.id = palette?.id || null;
    this.palette = palette || null;
    this.stage.querySelector("[data-ux-pview]").hidden = !palette;
    this.side.hidden = !palette;
    if (this.switcher) this.switcher.hidden = !palette;
    this.renderSwitch();
    if (!palette) return;
    if (this.picked && !palette.colours.some((colour) => colour.h === this.picked)) this.picked = null;
    const meta = this.stage.querySelector("[data-ux-pview-meta]");
    if (meta) {
      meta.textContent = this.metaText(palette);
      meta.hidden = !meta.textContent;
    }
    const project = palette.project ? UxPalette.project(palette.project)?.name : "";
    this.stage.querySelector("[data-ux-pview-project]").textContent = project || (UxPalette.projectsShown ? UxPaletteUi.text("loose") : "");
    this.stage.querySelector("[data-ux-pview-name]").textContent = palette.name;
    this.slots.dataset.palette = palette.id;
    this.slots.replaceChildren(...this.slotsFor(palette));
    this.renderPairs();
    if (switched) this.settle();
    /* the next step, Try, once it has colours */
    const next = this.stage.querySelector("[data-ux-plan-next]");
    if (next) next.hidden = !palette.colours.length;
    /* the sidebar */
    const side = this.side;
    if (!side.querySelector("[data-ux-pside-rename]").hidden && this.renamingId === palette.id) return;
    this.renaming(false);
    side.querySelector("[data-ux-pside-name]").textContent = palette.name;
    const select = side.querySelector("[data-ux-pside-project]");
    select.replaceChildren(new Option(UxPaletteUi.text("loose"), ""), ...UxPalette.projects().map((one) => new Option(one.name, one.id)));
    select.value = palette.project || "";
    this.confirming(false);
    const url = palette.colours.length ? UxPalette.shareUrl(palette.colours.map((colour) => colour.h)) : "";
    side.querySelector("[data-ux-pside-link]").value = url;
    /* what WhatsApp, Facebook and More send (Share your palette's wording, #190) */
    const labels = palette.colours.map((colour) => UxPaletteUi.shortName(colour.n || colour.h));
    const list = labels.length > 1 ? `${labels.slice(0, -1).join(", ")} ${side.dataset.and} ${labels[labels.length - 1]}` : labels.join("");
    const named = palette.name && palette.name !== UxPaletteUi.text("defaultName");
    this.share = { url, title: side.dataset.shareTitle, text: this.t(side, named ? "shareTextNamed" : "shareText", { name: palette.name, colours: list }) };
    side.querySelector('[data-ux-pside-to="whatsapp"]').href = `https://wa.me/?text=${encodeURIComponent(`${this.share.text} ${url}`)}`;
    side.querySelector('[data-ux-pside-to="facebook"]').href = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
    side.querySelector("[data-ux-pside-more]").hidden = typeof navigator.share !== "function";
    const storyKey = JSON.stringify([palette.name, palette.colours.map((colour) => [colour.h, colour.c])]);
    if (this.storyKey !== storyKey) this.story = null;
    this.storyKey = storyKey;
    this.storyData = { url, text: this.share.text, name: palette.name, colours: palette.colours };
    side.querySelector("[data-ux-pside-story]").hidden = !url || typeof this.wall.page?.shareStory !== "function";
  }
  /* a palette's colours across, portrait swatch cards (Matt, 22 Oct: better than rows): the name and number inside
     the swatch (a tap shows its pairings), the surface under it, Remove on the corner; Add a colour after them while
     there's room */
  slotsFor(palette) {
    const { roles, steps, hint, plus } = this.templates;
    const el = (tag, className, props = {}) => Object.assign(document.createElement(tag), { className, ...props });
    const rows = palette.colours.map((colour, index) => {
      const label = UxColourCard.label(colour);
      const slot = el("li", "ux-pview__slot");
      slot.dataset.handle = colour.h;
      const picked = colour.h === this.picked;
      slot.classList.toggle("is-picked", picked);
      slot.tabIndex = 0;
      slot.setAttribute("aria-label", `${index + 1}. ${UxColourCard.fullName(colour)}. ${this.t(this.stage, picked ? "picked" : "pick", { colour: label })}`);
      if (hint?.id) slot.setAttribute("aria-describedby", hint.id);
      const chip = el("span", "ux-pview__chip");
      chip.style.background = colour.c || "var(--ux-grey)";
      chip.classList.toggle("ux-pview__chip--dark", UxSampleOrder.dark(colour.c));
      const number = this.wall.byHandle?.get(colour.h)?.no || (/No\.\s*(\S+)/.exec(colour.n || "") || [])[1];
      chip.append(el("span", "ux-pview__colour", { textContent: UxPaletteUi.shortName(colour.n || colour.h) }));
      if (number) chip.append(el("span", "ux-pview__no", { textContent: `No.${number}` }));
      const role = document.createElement("select");
      role.className = "ux-pview__role";
      role.append(roles.content.cloneNode(true));
      UxPaletteUi.surfaceOptions(role, palette);
      role.value = UxPaletteUi.surfaceValue(role, colour.r);
      role.setAttribute("aria-label", this.t(this.stage, "roleLabel", { colour: label }));
      role.classList.toggle("ux-pview__role--none", !colour.r);
      slot.append(chip, role);
      if (steps) {
        const move = steps.content.firstElementChild.cloneNode(true);
        move.querySelectorAll("[data-ux-pview-move]").forEach((button) => {
          const way = button.dataset.uxPviewMove;
          button.setAttribute("aria-label", this.t(this.stage, way, { colour: label }));
          /* aria-disabled, not disabled, so the button keeps focus once its colour reaches the end */
          button.setAttribute("aria-disabled", String(way === "earlier" ? index === 0 : index === palette.colours.length - 1));
        });
        slot.append(move);
      }
      const remove = el("button", "ux-pview__remove", { type: "button" });
      remove.dataset.uxPviewRemove = "";
      remove.setAttribute("aria-label", this.t(this.stage, "remove", { colour: label }));
      slot.append(remove);
      return slot;
    });
    if (palette.colours.length < UxPalette.LIMITS.colours) {
      const slot = el("li", "ux-pview__slot ux-pview__slot--empty");
      const add = el("button", "ux-pview__add", { type: "button" });
      add.dataset.uxPviewAdd = "";
      add.setAttribute("aria-label", this.t(this.stage, "add", { name: palette.name }));
      add.append(plus?.content.cloneNode(true) || "+", el("span", "ux-pview__add-text", { textContent: this.stage.dataset.addText }));
      slot.append(add);
      rows.push(slot);
    }
    return rows;
  }
  /* --- Plan as palettes (#481) --- */
  /* which way it faces: optional, this browser only until Odoo has a field for it (#484) */
  static aspects() {
    try {
      return JSON.parse(window.localStorage.getItem("mylands-palette-aspect") || "{}") || {};
    } catch (error) {
      return {};
    }
  }
  static aspect(id) {
    const way = UxPaletteView.aspects()[id];
    return ["n", "e", "s", "w"].includes(way) ? way : "";
  }
  static setAspect(id, way) {
    const all = UxPaletteView.aspects();
    if (["n", "e", "s", "w"].includes(way)) all[id] = way;
    else delete all[id];
    try {
      window.localStorage.setItem("mylands-palette-aspect", JSON.stringify(all));
    } catch (error) {
      /* storage blocked: this page view only */
    }
  }
  /* inside or outside follows the surfaces (no setting); "" when there are none, or both */
  static place(palette) {
    const groups = new Set((palette?.colours || []).map((colour) => colour.r && UxPaletteUi.surface(colour.r)?.group).filter(Boolean));
    return groups.size === 1 ? [...groups][0] : "";
  }
  metaText(palette) {
    const place = UxPaletteView.place(palette);
    /* which way it faces left Plan (Matt, 22 Oct): inside or outside only */
    const text = place ? this.stage.dataset[place === "outside" ? "placeOutside" : "placeInside"] : "";
    return text ? text[0].toUpperCase() + text.slice(1) : "";
  }
  /* every palette, newest first, in the sidebar's list (from 1000) and the switcher at the top (under it); the one on
     the stage marked; a tap puts another on the stage */
  renderSwitch() {
    const el = (tag, className, props = {}) => Object.assign(document.createElement(tag), { className, ...props });
    const palettes = UxPalette.palettes().reverse();
    [this.sideList, this.tabsList].forEach((list) => list?.replaceChildren(...palettes.map((palette) => {
      const item = el("li", "ux-pside__item");
      const button = el("button", "ux-pside__open", { type: "button" });
      button.dataset.uxPsideOpen = palette.id;
      if (palette.id === this.id) button.setAttribute("aria-current", "true");
      const count = palette.colours.length;
      button.append(el("span", "ux-pside__open-name", { textContent: palette.name }), el("span", "ux-pside__open-count", { textContent: count === 1 ? this.stage.dataset.countOne : this.t(this.stage, "count", { count }) }));
      const chips = el("span", "ux-pside__open-chips");
      chips.setAttribute("aria-hidden", "true");
      chips.append(...palette.colours.map((colour) => {
        const chip = el("span", "ux-pside__open-chip");
        chip.style.background = colour.c || "var(--ux-grey)";
        return chip;
      }));
      button.append(chips);
      item.append(button);
      return item;
    })));
    /* the switcher keeps the one on the stage in view */
    this.tabsList?.querySelector('[aria-current="true"]')?.scrollIntoView?.({ block: "nearest", inline: "nearest" });
  }
  /* a tap on a colour: its pairings slide in (another colour's change in place); again, they slide away */
  pick(handle) {
    if (!this.palette?.colours.some((colour) => colour.h === handle)) return;
    const closing = handle === this.picked;
    this.picked = closing ? null : handle;
    this.pairOpen = 0;
    this.swapOpen = null;
    this.addOpen = null;
    this.marks();
    const colour = this.palette.colours.find((saved) => saved.h === handle);
    if (!closing) this.say(this.t(this.stage, "picked", { colour: UxColourCard.label(colour) }));
    this.renderPairs({ animate: true });
  }
  /* Close, Escape, a drag starting: the drawer slides away; focus back on its colour when asked */
  closePairs({ focus = false } = {}) {
    if (!this.picked) return;
    const handle = this.picked;
    this.picked = null;
    this.swapOpen = null;
    this.marks();
    this.renderPairs({ animate: true });
    if (focus) this.slots.querySelector(`li[data-handle="${CSS.escape(handle)}"]`)?.focus({ preventScroll: true });
  }
  marks() {
    this.slots.querySelectorAll("li[data-handle]").forEach((slot) => {
      const on = slot.dataset.handle === this.picked;
      slot.classList.toggle("is-picked", on);
      const colour = this.palette?.colours.find((saved) => saved.h === slot.dataset.handle);
      if (colour) slot.setAttribute("aria-label", `${this.handles().indexOf(colour.h) + 1}. ${UxColourCard.fullName(colour)}. ${this.t(this.stage, on ? "picked" : "pick", { colour: UxColourCard.label(colour) })}`);
    });
  }
  /* the colours that pair with one, not in the palette already (custom.colour_pairings, #70) */
  pairsOf(handle) {
    const inPalette = new Set(this.handles());
    return (this.wall.byHandle?.get(handle)?.p || []).map((one) => this.wall.byHandle.get(one)).filter((one) => one && !inPalette.has(one.h));
  }
  pairList() {
    const colour = this.palette?.colours.find((saved) => saved.h === this.picked);
    return { colour, pairs: colour ? this.pairsOf(colour.h) : [] };
  }
  /* the drawer (Matt, 22 Oct): the product page's Colours, sliced (Figma 816:5640), kept horizontal at the drawer's
     width (Matt: the original was nicer than rows) on the tapped colour's band: the first pairing open as a card with
     Add and Swap with…, the rest as tall slices, named; a slice opens where it stands. A note when it has none. It
     slides in from the side and away again; another colour's content fades in place */
  renderPairs({ animate = false } = {}) {
    const drawer = this.drawer;
    if (!drawer) return;
    const { colour, pairs } = this.pairList();
    const motion = animate && !UxPaletteView.still();
    if (!colour) {
      if (drawer.hidden) return;
      if (!motion) {
        drawer.hidden = true;
        return;
      }
      drawer.animate([{ transform: "none" }, { transform: "translateX(100%)" }], { duration: 220, easing: "cubic-bezier(0.4, 0, 1, 1)" }).finished.then(() => {
        if (!this.picked) drawer.hidden = true;
      }, () => {});
      return;
    }
    const el = (tag, className, props = {}) => Object.assign(document.createElement(tag), { className, ...props });
    const opening = drawer.hidden;
    const full = this.palette.colours.length >= UxPalette.LIMITS.colours;
    const box = el("div", "ux-pairs ux-pairs--drawer");
    box.style.setProperty("--ux-pairs-band", colour.c || "var(--ux-grey)");
    box.classList.toggle("ux-pairs--dark", UxSampleOrder.dark(colour.c));
    const close = el("button", "ux-pd__close", { type: "button" });
    close.dataset.uxPairClose = "";
    close.setAttribute("aria-label", drawer.dataset.close);
    const text = el("div", "ux-pairs__text");
    const title = el("h2", "ux-pairs__title", { textContent: this.t(this.stage, "pairsTitle", { colour: UxColourCard.label(colour) }), tabIndex: -1 });
    text.append(el("p", "ux-pairs__eyebrow", { textContent: this.stage.dataset.pairsEyebrow }), title);
    box.append(close, text);
    if (!pairs.length) box.append(el("p", "ux-pairs__none", { textContent: drawer.dataset.none }));
    else {
      const open = Math.min(this.pairOpen || 0, pairs.length - 1);
      const row = el("div", "ux-pairs__row");
      pairs.forEach((pair, index) => {
        const short = UxPaletteUi.shortName(pair.n);
        if (index !== open) {
          const slice = el("button", "ux-pairs__slice", { type: "button" });
          slice.dataset.uxPairsShow = String(index);
          slice.style.setProperty("--ux-pair", pair.c || "var(--ux-grey)");
          slice.classList.toggle("ux-pairs__slice--dark", UxSampleOrder.dark(pair.c));
          slice.setAttribute("aria-label", this.t(this.stage, "pairsShow", { colour: short }));
          slice.append(el("span", "ux-pairs__slice-name", { textContent: short }));
          row.append(slice);
          return;
        }
        const card = el("div", "ux-pairs__open");
        card.style.setProperty("--ux-pair", pair.c || "var(--ux-grey)");
        card.classList.toggle("ux-pairs__open--dark", UxSampleOrder.dark(pair.c));
        if (pair.no) card.append(UxSampleOrder.diamond(pair.no));
        const info = el("div", "ux-pairs__card");
        info.append(el("p", "ux-pairs__name", { textContent: pair.n }));
        if (pair.d) info.append(el("p", "ux-pairs__descriptor", { textContent: pair.d }));
        const actions = el("div", "ux-pairs__actions");
        /* Add, to this palette; its chevron adds to another (Matt, 22 Oct) */
        const others = UxPalette.palettes().reverse().filter((one) => one.id !== this.id);
        const split = el("div", "ux-pairs__split");
        if (full) actions.append(el("p", "ux-pairs__full", { textContent: this.stage.dataset.pairsFull }));
        else {
          const add = el("button", "button button--sm ux-pairs__add", { type: "button", textContent: this.t(this.stage, "pairsAdd", { name: this.palette.name }) });
          add.dataset.uxPairsAdd = pair.h;
          split.append(add);
        }
        if (others.length) {
          const more = el("button", `button button--sm ux-pairs__more${full ? " button--secondary" : ""}`, { type: "button" });
          more.dataset.uxPairsMore = pair.h;
          more.setAttribute("aria-label", drawer.dataset.other);
          more.setAttribute("aria-expanded", String(this.addOpen === pair.h));
          if (full) more.append(drawer.dataset.other);
          more.append(this.stage.querySelector("template[data-ux-pairs-chevron]")?.content.cloneNode(true) || "");
          split.append(more);
        }
        if (split.children.length) actions.append(split);
        const swap = el("button", "ux-pairs__swap", { type: "button", textContent: this.stage.dataset.pairsSwap });
        swap.dataset.uxPairsSwap = pair.h;
        swap.setAttribute("aria-expanded", String(this.swapOpen === pair.h));
        swap.append(this.stage.querySelector("template[data-ux-pairs-chevron]")?.content.cloneNode(true) || "");
        actions.append(swap);
        info.append(actions);
        if (this.addOpen === pair.h && others.length) {
          const menu = el("div", "ux-pairs__menu");
          menu.append(el("p", "ux-pairs__menu-title", { textContent: this.t(drawer, "addTo", { colour: short }) }));
          const list = el("ul", "ux-pairs__menu-list unstyled-list");
          others.forEach((one) => {
            const holds = one.colours.some((saved) => saved.h === pair.h);
            const fullOne = one.colours.length >= UxPalette.LIMITS.colours;
            const choice = el("button", "ux-pairs__for", { type: "button" });
            choice.dataset.uxPairsTo = one.id;
            choice.dataset.pair = pair.h;
            if (holds || fullOne) choice.setAttribute("aria-disabled", "true");
            const chips = el("span", "ux-pairs__to-chips");
            chips.setAttribute("aria-hidden", "true");
            chips.append(...one.colours.slice(0, 6).map((saved) => {
              const chip = el("span", "ux-pairs__to-chip");
              chip.style.background = saved.c || "var(--ux-grey)";
              return chip;
            }));
            const words = el("span", "ux-pairs__for-text");
            words.append(el("span", "ux-pairs__for-name", { textContent: one.name }));
            if (holds || fullOne) words.append(el("span", "ux-pairs__for-surface", { textContent: holds ? drawer.dataset.holds : drawer.dataset.full }));
            choice.append(words, chips);
            const item = el("li");
            item.append(choice);
            list.append(item);
          });
          menu.append(list);
          info.append(menu);
        }
        if (this.swapOpen === pair.h) {
          const menu = el("div", "ux-pairs__menu");
          menu.append(el("p", "ux-pairs__menu-title", { textContent: this.t(this.stage, "pairsSwapFor", { colour: short }) }));
          const list = el("ul", "ux-pairs__menu-list unstyled-list");
          /* the colour it pairs with first, then the rest of the palette */
          [colour, ...this.palette.colours.filter((saved) => saved.h !== colour.h)].forEach((saved) => {
            const item = el("li");
            const choice = el("button", "ux-pairs__for", { type: "button" });
            choice.dataset.uxPairsFor = saved.h;
            choice.dataset.pair = pair.h;
            const chip = el("span", "ux-pairs__for-chip");
            chip.style.background = saved.c || "var(--ux-grey)";
            const words = el("span", "ux-pairs__for-text");
            words.append(el("span", "ux-pairs__for-name", { textContent: UxColourCard.label(saved) }));
            if (saved.r) words.append(el("span", "ux-pairs__for-surface", { textContent: UxPaletteUi.surface(saved.r).title }));
            choice.append(chip, words);
            item.append(choice);
            list.append(item);
          });
          menu.append(list);
          info.append(menu);
        }
        card.append(info);
        row.append(card);
      });
      box.append(row);
    }
    drawer.replaceChildren(box);
    drawer.hidden = false;
    if (motion && opening) drawer.animate([{ transform: "translateX(100%)" }, { transform: "none" }], { duration: 300, easing: "cubic-bezier(0.2, 0, 0, 1)" });
    else if (motion) drawer.animate([{ opacity: 0.5 }, { opacity: 1 }], { duration: 160, easing: "ease-out" });
    if (animate) title.focus({ preventScroll: true });
  }
  onPairs(event) {
    const { pairs } = this.pairList();
    /* a band: that pairing opens where it stands, growing from the band as the open one folds into a band (an
       accordion: heights only, so nothing stretches); its card fades in once there's room */
    const show = event.target.closest("[data-ux-pairs-show]");
    if (show) {
      const was = Math.min(this.pairOpen || 0, pairs.length - 1);
      const from = show.getBoundingClientRect().height;
      const fold = this.drawer.querySelector(".ux-pairs__open")?.getBoundingClientRect().height;
      this.pairOpen = Number(show.dataset.uxPairsShow) || 0;
      this.swapOpen = null;
      this.addOpen = null;
      this.renderPairs();
      if (!UxPaletteView.still()) {
        const ease = "cubic-bezier(0.2, 0, 0, 1)";
        const card = this.drawer.querySelector(".ux-pairs__open");
        const band = this.drawer.querySelector(`[data-ux-pairs-show="${was}"]`);
        if (card) {
          card.style.overflow = "hidden";
          card.animate([{ height: `${from}px`, minHeight: `${from}px` }, { height: `${card.getBoundingClientRect().height}px`, minHeight: `${card.getBoundingClientRect().height}px` }], { duration: 320, easing: ease }).finished.then(() => (card.style.overflow = ""), () => {});
          card.querySelectorAll(".ux-pairs__card, .ux-diamond").forEach((one) => one.animate([{ opacity: 0 }, { opacity: 0, offset: 0.45 }, { opacity: 1 }], { duration: 320, easing: "ease-out" }));
        }
        if (band && fold) {
          band.animate([{ flexBasis: `${fold}px` }, { flexBasis: `${band.getBoundingClientRect().height}px` }], { duration: 320, easing: ease });
          band.querySelector(".ux-pairs__slice-name")?.animate([{ opacity: 0 }, { opacity: 0, offset: 0.5 }, { opacity: 1 }], { duration: 320 });
        }
      }
      this.drawer.querySelector(".ux-pairs__add, .ux-pairs__swap")?.focus({ preventScroll: true });
      return;
    }
    const add = event.target.closest("[data-ux-pairs-add]");
    if (add) {
      const pair = pairs.find((one) => one.h === add.dataset.uxPairsAdd);
      if (!pair || !UxPalette.addTo(this.id, { h: pair.h, n: pair.n, c: pair.c })) return;
      const id = this.id;
      UxPaletteUi.notify(this.t(this.stage, "pairsAdded", { colour: UxPaletteUi.shortName(pair.n), name: this.palette.name }), { undo: () => UxPalette.removeFrom(id, pair.h) });
      UxPaletteTrack("palette_colour_added", { handle: pair.h, source: "plan-pairings", palette: id });
      this.pairsFocus();
      return;
    }
    const more = event.target.closest("[data-ux-pairs-more]");
    if (more) {
      const pair = more.dataset.uxPairsMore;
      this.addOpen = this.addOpen === pair ? null : pair;
      this.swapOpen = null;
      this.renderPairs();
      (this.addOpen ? this.drawer.querySelector('[data-ux-pairs-to]:not([aria-disabled="true"])') : this.drawer.querySelector(`[data-ux-pairs-more="${CSS.escape(pair)}"]`))?.focus();
      return;
    }
    const to = event.target.closest("[data-ux-pairs-to]");
    if (to) {
      if (to.getAttribute("aria-disabled") === "true") return;
      const pair = pairs.find((one) => one.h === to.dataset.pair);
      const target = UxPalette.palette(to.dataset.uxPairsTo);
      this.addOpen = null;
      if (!pair || !target || !UxPalette.addTo(target.id, { h: pair.h, n: pair.n, c: pair.c })) return this.renderPairs();
      UxPaletteUi.notify(this.t(this.stage, "pairsAdded", { colour: UxPaletteUi.shortName(pair.n), name: target.name }), { undo: () => UxPalette.removeFrom(target.id, pair.h) });
      UxPaletteTrack("palette_colour_added", { handle: pair.h, source: "plan-pairings-other", palette: target.id });
      this.renderPairs();
      this.drawer.querySelector(`[data-ux-pairs-more="${CSS.escape(pair.h)}"]`)?.focus();
      return;
    }
    const swap = event.target.closest("[data-ux-pairs-swap]");
    if (swap) {
      const pair = swap.dataset.uxPairsSwap;
      this.addOpen = null;
      this.swapOpen = this.swapOpen === pair ? null : pair;
      this.renderPairs();
      (this.swapOpen ? this.drawer.querySelector("[data-ux-pairs-for]") : this.drawer.querySelector(`[data-ux-pairs-swap="${CSS.escape(pair)}"]`))?.focus();
      return;
    }
    const choice = event.target.closest("[data-ux-pairs-for]");
    if (choice) {
      const pair = pairs.find((one) => one.h === choice.dataset.pair);
      const old = this.palette.colours.find((saved) => saved.h === choice.dataset.uxPairsFor);
      if (!pair || !old) return;
      this.swapOpen = null;
      const id = this.id;
      /* swapped in for the colour the drawer is about: the drawer follows the new one */
      const picked = this.picked;
      if (old.h === picked) this.picked = pair.h;
      const undo = UxPalette.swapColour(id, old.h, { h: pair.h, n: pair.n, c: pair.c });
      if (!undo) {
        this.picked = picked;
        return;
      }
      UxPaletteUi.notify(this.t(this.stage, "pairsSwapped", { colour: UxPaletteUi.shortName(pair.n), old: UxColourCard.label(old) }), { undo: () => UxPalette.restore(undo) });
      UxPaletteTrack("palette_colour_swapped", { handle: pair.h, replaced: old.h, source: "plan-pairings", palette: id });
      this.pairsFocus();
    }
  }
  /* Add and Swap change the palette, and the drawer redraws without the button that was pressed: focus goes to the
     open pairing's Add (or Swap), else the drawer's title, so it stays in the drawer and Escape still closes it */
  pairsFocus() {
    requestAnimationFrame(() => {
      if (!this.drawer || this.drawer.hidden || this.drawer.contains(document.activeElement)) return;
      (this.drawer.querySelector(".ux-pairs__open .ux-pairs__add, .ux-pairs__open .ux-pairs__swap") || this.drawer.querySelector(".ux-pairs__title"))?.focus({ preventScroll: true });
    });
  }
  renaming(on) {
    const form = this.side.querySelector("[data-ux-pside-rename]");
    const name = this.side.querySelector("[data-ux-pside-name]");
    form.hidden = !on;
    name.hidden = on;
    this.renamingId = on ? this.id : null;
    if (on) {
      form.elements.name.value = this.palette?.name || "";
      form.elements.name.focus();
      form.elements.name.select();
    }
  }
  confirming(on) {
    const box = this.side.querySelector("[data-ux-pside-confirm]");
    box.hidden = !on;
    this.side.querySelector("[data-ux-pside-act='delete']").setAttribute("aria-expanded", String(on));
    if (on) {
      box.querySelector("[data-ux-pside-confirm-text]").textContent = this.t(this.side, "confirm", { name: this.palette.name });
      box.querySelector("[data-ux-pside-act='delete-no']").focus();
    }
  }
  async act(act, button) {
    if (!this.palette) return;
    if (act === "rename") this.renaming(true);
    else if (act === "delete") this.confirming(true);
    else if (act === "delete-no") {
      this.confirming(false);
      this.side.querySelector("[data-ux-pside-act='delete']").focus();
    } else if (act === "delete-yes") {
      const { id, name } = this.palette;
      const undo = UxPalette.deletePalette(id);
      if (!undo) return;
      UxPaletteTrack("palette_deleted", { palette: id, source: "palette-view" });
      UxPaletteUi.notify(this.t(this.side, "deleted", { name }), { undo: () => {
        UxPalette.restore(undo);
        this.wall.choose(id);
        this.render(id);
      } });
      const next = UxPalette.active() || UxPalette.palettes()[0];
      if (next) {
        this.wall.choose(next.id);
        this.render(next.id); /* the store may have made it the one in use already, so no change event comes */
      } else this.wall.setView("colours");
      /* Delete is hidden now: focus goes to the next palette's name, or to Discover (#465) */
      requestAnimationFrame(() => this.wall.focusStage());
    } else if (act === "duplicate") {
      const copy = UxPalette.duplicatePalette(this.id);
      if (!copy) return;
      UxPaletteTrack("palette_duplicated", { palette: this.id, copy, source: "palette-view" });
      this.wall.choose(copy);
      UxPaletteUi.notify(this.t(this.side, "duplicated", { name: UxPalette.palette(copy)?.name || "" }));
    } else if (act === "samples") {
      this.wall.toSamples(this.id);
    } else if (act === "story") {
      if (this.storyData?.url) this.wall.page?.shareStory(button, { block: this.side.querySelector("[data-ux-pside-share]"), data: this.storyData, cache: this });
    } else if (act === "share") {
      try {
        await navigator.share(this.share);
        UxPaletteTrack("palette_shared", { palette: this.id, to: "more", source: "palette-view" });
      } catch (error) {
        /* closed without sharing */
      }
    } else if (act === "copy") {
      const input = this.side.querySelector("[data-ux-pside-link]");
      try {
        await navigator.clipboard.writeText(input.value);
      } catch (error) {
        input.select();
        document.execCommand?.("copy");
      }
      this.say(this.side.dataset.copied);
      UxPaletteTrack("palette_link_copied", { palette: this.id, source: "palette-view" });
    }
  }
};

/* ---------------------------------------------------------------------------
   SWATCH DEALS (#482): the free ones and the packs (snippets/ux-swatch-ladder.liquid's data), what a number of Colour
   Swatches costs (the cheapest of the free ones, then each, or a pack and each above it) and the ladder drawn into the
   box. Shared by Try and the Order samples drawer (#506), so the two can't price an order differently.
   --------------------------------------------------------------------------- */
var UxSwatchDeal = {
  packs(box) {
    return String(box?.dataset.packs || "")
      .split("|")
      .map((one) => one.split("~"))
      .map(([size, price, free]) => ({ size: Number(size), price: Number(price), free: free === "free" }))
      .filter((pack) => pack.size > 0 && pack.price >= 0)
      .sort((a, b) => a.size - b.size);
  },
  price(box, count, unit) {
    const free = Math.max(0, Number(box?.dataset.free) || 0);
    let best = { price: Math.max(0, count - free) * unit, post: false };
    UxSwatchDeal.packs(box).forEach((pack) => {
      if (count < pack.size) return;
      const price = pack.price + (count - pack.size) * unit;
      if (price < best.price || (price === best.price && pack.free)) best = { price, post: pack.free };
    });
    return best;
  },
  text(box, key, values = {}) {
    return Object.entries(values).reduce((string, [name, value]) => string.split(`[${name}]`).join(value), box.dataset[key] || "");
  },
  render(box, count, unit) {
    if (!box) return;
    box.hidden = !count || !unit;
    if (box.hidden) return box.replaceChildren();
    const el = (tag, className, props = {}) => Object.assign(document.createElement(tag), { className, ...props });
    const money = UxPaletteSamples.money;
    const free = Math.max(0, Number(box.dataset.free) || 0);
    const packs = UxSwatchDeal.packs(box);
    const end = Math.max(free, count, ...packs.map((pack) => pack.size));
    const now = UxSwatchDeal.price(box, count, unit);
    const track = el("div", "ux-ladder__track");
    track.setAttribute("aria-hidden", "true");
    const fill = el("span", "ux-ladder__fill");
    fill.style.width = `${Math.min(100, (count / end) * 100)}%`;
    track.append(fill);
    [...(free ? [{ size: free, label: UxSwatchDeal.text(box, "freeStop", { count: free }) }] : []), ...packs.map((pack) => ({ size: pack.size, label: String(pack.size) }))].forEach((stop) => {
      const mark = el("span", `ux-ladder__stop${count >= stop.size ? " is-reached" : ""}`);
      mark.style.left = `${(stop.size / end) * 100}%`;
      mark.append(el("span", "ux-ladder__stop-label", { textContent: stop.label }));
      track.append(mark);
    });
    const line = el("p", "ux-ladder__line", { textContent: UxSwatchDeal.text(box, now.post ? "lineFree" : "line", { count, price: money(now.price) }) });
    let next = "";
    if (count < free) next = UxSwatchDeal.text(box, "freeLeft", { more: free - count });
    else {
      const pack = packs.find((one) => one.size > count);
      if (pack) next = UxSwatchDeal.text(box, pack.free ? "nextFree" : "next", { more: pack.size - count, size: pack.size, price: money(pack.price) });
    }
    box.replaceChildren(el("p", "ux-ladder__label", { textContent: box.dataset.label }), track, line, ...(next ? [el("p", "ux-ladder__next", { textContent: next })] : []));
  }
};
window.UxSwatchDeal = UxSwatchDeal;

/* ---------------------------------------------------------------------------
   TRY (#448; Matt, 22 Oct: "very simply"): choose whole palettes or single colours from them, as Colour Swatches or
   Sample Pots, and order. The format is one choice for the order (the swatch while it's live, else the pot; a colour
   out of it falls back to the other). A palette a group, ticked whole or a colour at a time; every colour once, in the
   book's order (one already counted shows "Counted under Kitchen", one in the basket "In your basket"). The sidebar is
   the order: the count, a line per palette, the total, the pot offer or the swatch ladder, one Add to basket, which
   re-checks stock first and tags each sample with its own palette (#179).
   --------------------------------------------------------------------------- */
var UxSampleOrder = class {
  constructor(wall) {
    this.wall = wall;
    this.stage = wall.querySelector('[data-ux-app-stage="samples"]');
    this.side = wall.querySelector('[data-ux-app-side="samples"]');
    this.live = this.stage?.dataset.swatchesLive === "true";
    this.format = this.live ? "peel" : "sample";
    this.choices = new Map();
    this.left = new Set();
    this.extra = new Set();
    this.ids = [];
    /* the variants already in the basket (#462): a colour whose sample is there shows "In your basket" and isn't
       ordered again; read once, then from every cart:change */
    this.basket = new Set();
    if (!this.stage || !this.side) return;
    this.ready = true;
    this.basketRead = this.readBasket();
    document.addEventListener("cart:change", (event) => {
      const items = event.detail?.cart?.items;
      if (items) this.basket = new Set(items.map((item) => item.variant_id));
      else this.basketRead = this.readBasket();
      if (!this.stage.hidden) this.render(this.ids);
    });
    this.stage.addEventListener("change", (event) => {
      const input = event.target;
      /* all as: every colour to that format */
      if (input.closest("[data-ux-order-format]")) {
        this.format = input.value;
        this.choices.clear();
        this.render(this.ids);
        return;
      }
      /* a colour's own Swatch | Pot: what arrives changes in place, then the order */
      if (input.closest("[data-ux-line-format]")) {
        const item = input.closest("[data-handle]");
        this.choices.set(this.key(item.closest("[data-palette]").dataset.palette, item.dataset.handle), input.value);
        item.dataset.format = input.value;
        this.total();
        this.allAs();
        return;
      }
      const id = input.closest("[data-palette]")?.dataset.palette;
      if (!id) return;
      /* a whole palette: ticked, all its colours go in; unticked, none */
      if (input.matches("[data-ux-order-all]")) {
        const keys = (this.plan || []).find((group) => group.palette.id === id)?.rows.filter((row) => !row.under).map((row) => this.key(id, row.colour.h)) || [];
        keys.forEach((key) => (input.checked ? this.left.delete(key) : this.left.add(key)));
        this.render(this.ids).then(() => this.stage.querySelector(`[data-palette="${CSS.escape(id)}"] [data-ux-order-all]`)?.focus());
        return;
      }
      /* a colour */
      if (input.matches("[data-ux-order-pick]")) {
        const handle = input.closest("[data-handle]").dataset.handle;
        const key = this.key(id, handle);
        if (input.closest(".is-also")) this.extra[input.checked ? "add" : "delete"](key);
        else if (input.checked) this.left.delete(key);
        else this.left.add(key);
        this.render(this.ids).then(() => this.stage.querySelector(`[data-palette="${CSS.escape(id)}"] [data-handle="${CSS.escape(handle)}"] [data-ux-order-pick]`)?.focus());
      }
    });
    this.stage.addEventListener("click", (event) => {
      /* in another palette's order already: one more, here, too */
      const extra = event.target.closest("[data-ux-order-extra]");
      if (extra) {
        const tick = extra.closest("[data-handle]").querySelector("[data-ux-order-pick]");
        tick.checked = true;
        tick.dispatchEvent(new Event("change", { bubbles: true }));
        return;
      }
      const notify = event.target.closest("[data-ux-try-notify]");
      if (notify && window.showBackInStockPopup) window.showBackInStockPopup(Number(notify.dataset.variantId));
    });
    this.side.querySelector("[data-ux-order-add]").addEventListener("click", (event) => this.add(event.currentTarget));
    /* the swatch ladder (#482): the price as Colour Swatches are added, the deals as stops */
    this.ladderBox = this.side.querySelector("[data-ux-ladder]");
  }
  t(element, key, values = {}) {
    return Object.entries(values).reduce((string, [name, value]) => string.split(`[${name}]`).join(value), element.dataset[key] || "");
  }
  money(cents) {
    const currency = window.Shopify?.currency?.active || "GBP";
    return new Intl.NumberFormat(document.documentElement.lang || "en-GB", { style: "currency", currency }).format(cents / 100);
  }
  async readBasket() {
    try {
      const cart = await (await fetch(`${window.Shopify?.routes?.root || "/"}cart.js`)).json();
      this.basket = new Set((cart.items || []).map((item) => item.variant_id));
    } catch (error) {
      /* can't read the basket: nothing counts as in it */
    }
  }
  /* which of them is in the basket already ("peel" or "sample"), or null */
  held(data) {
    return ["peel", "sample"].find((format) => data?.[format] && this.basket.has(data[format].id)) || null;
  }
  /* Leave out is per palette (#458): a colour left out of Kitchen is counted under the next palette that holds it */
  key(paletteId, handle) {
    return `${paletteId}:${handle}`;
  }
  /* a dark colour takes paper type, a light one ink (relative luminance, as WCAG): 0.204, where paper (white) and
     the ink (#1c1c1c) contrast equally (#471; 0.3 put paper type on mid colours at 3 to 4.5:1) */
  /* the tin shot without its white: flood the paper in from the edges, so the tin's own highlights stay; one per
     photo, kept. Null when the photo can't be read, and the tin then sits on the colour as it is */
  static cutout(url) {
    UxSampleOrder.cutouts ||= new Map();
    if (!UxSampleOrder.cutouts.has(url)) UxSampleOrder.cutouts.set(url, new Promise((resolve) => {
      const image = new Image();
      image.crossOrigin = "anonymous";
      image.onerror = () => resolve(null);
      image.onload = () => {
        try {
          const { naturalWidth: w, naturalHeight: h } = image;
          const canvas = Object.assign(document.createElement("canvas"), { width: w, height: h });
          const context = canvas.getContext("2d", { willReadFrequently: true });
          context.drawImage(image, 0, 0);
          const pixels = context.getImageData(0, 0, w, h);
          const d = pixels.data;
          const paper = (at) => Math.min(d[at], d[at + 1], d[at + 2]) >= 232 && Math.max(d[at], d[at + 1], d[at + 2]) - Math.min(d[at], d[at + 1], d[at + 2]) <= 14;
          const seen = new Uint8Array(w * h);
          const stack = [];
          for (let x = 0; x < w; x++) stack.push(x, (h - 1) * w + x);
          for (let y = 0; y < h; y++) stack.push(y * w, y * w + w - 1);
          while (stack.length) {
            const at = stack.pop();
            if (seen[at] || !paper(at * 4)) continue;
            seen[at] = 1;
            const x = at % w;
            if (x > 0) stack.push(at - 1);
            if (x < w - 1) stack.push(at + 1);
            if (at >= w) stack.push(at - w);
            if (at < w * (h - 1)) stack.push(at + w);
          }
          /* clear the paper; the pixels along its edge go part way, by how white they are, so the tin's edge is soft */
          for (let at = 0; at < w * h; at++) {
            if (seen[at]) { d[at * 4 + 3] = 0; continue; }
            const x = at % w;
            const edge = (x > 0 && seen[at - 1]) || (x < w - 1 && seen[at + 1]) || (at >= w && seen[at - w]) || (at < w * (h - 1) && seen[at + w]);
            if (edge) d[at * 4 + 3] = Math.round(255 * Math.min(1, (255 - Math.min(d[at * 4], d[at * 4 + 1], d[at * 4 + 2])) / 40));
          }
          context.putImageData(pixels, 0, 0);
          resolve(canvas.toDataURL("image/png"));
        } catch {
          resolve(null);
        }
      };
      image.src = url;
    }));
    return UxSampleOrder.cutouts.get(url);
  }
  static dark(hex) {
    const match = /^#?([0-9a-f]{6})$/i.exec(String(hex || "").trim());
    if (!match) return false;
    const [r, g, b] = [0, 2, 4].map((at) => {
      const value = parseInt(match[1].slice(at, at + 2), 16) / 255;
      return value <= 0.03928 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
    });
    return 0.2126 * r + 0.7152 * g + 0.0722 * b < 0.204;
  }
  /* the colour-number diamond (snippets/ux-colour-diamond.liquid, the bare lockup), drawn in currentColor */
  static diamond(number) {
    const diamond = Object.assign(document.createElement("span"), { className: "ux-diamond" });
    diamond.innerHTML = '<svg class="ux-diamond__lozenge" viewBox="0 0 104.083 67.75" width="104" height="68" aria-hidden="true" focusable="false"><path fill="none" stroke="currentColor" stroke-miterlimit="10" d="M.906 34.213L51.163.598l51.993 33.615-51.993 32.942z"/></svg>';
    diamond.append(Object.assign(document.createElement("span"), { className: "ux-diamond__number", textContent: `N° ${number}` }));
    return diamond;
  }
  /* the format for a colour: its own choice, else the order's, if it's in stock; else the other (the swatch only
     while it's live) */
  can(data, format) {
    return Boolean(data?.[format]?.available) && (format !== "peel" || this.live);
  }
  choice(line, data) {
    const wanted = this.choices.get(line) || this.format;
    if (this.can(data, wanted)) return wanted;
    const other = wanted === "peel" ? "sample" : "peel";
    return this.can(data, other) ? other : null;
  }
  /* Order them all as: the format every counted colour has, or none while they differ */
  allAs() {
    const formats = new Set(this.items().map((item) => item.choice));
    const one = formats.size === 1 ? [...formats][0] : null;
    this.stage.querySelectorAll("[data-ux-order-format] input").forEach((input) => (input.checked = input.value === one));
  }
  async render(ids) {
    if (!this.ready) return;
    this.ids = ids;
    const token = (this.token = Symbol("order"));
    const palettes = ids.map((id) => UxPalette.palette(id)).filter(Boolean);
    const seen = new Map();
    const plan = palettes.map((palette) => ({ palette, rows: palette.colours.map((colour) => {
      const left = this.left.has(this.key(palette.id, colour.h));
      const under = left ? null : seen.get(colour.h) || null;
      if (!left && !under) seen.set(colour.h, palette);
      /* in another palette's order: not again, unless they ask for another */
      return { colour, under, left, extra: Boolean(under) && this.extra.has(this.key(palette.id, colour.h)) };
    }) }));
    const handles = new Set(palettes.flatMap((palette) => palette.colours.map((colour) => colour.h)));
    const [data] = await Promise.all([
      Promise.all([...handles].map(async (handle) => [handle, await UxPalette.colour(handle, { card: false })])).then((entries) => new Map(entries)),
      this.basketRead
    ]);
    if (token !== this.token) return;
    this.data = data;
    this.plan = plan;
    const box = this.stage.querySelector("[data-ux-order]");
    box.hidden = !plan.some((group) => group.rows.length);
    if (!box.hidden) {
      box.querySelector("[data-ux-order-title]").textContent = palettes.length === 1 ? this.t(this.stage, "titleOne", { name: palettes[0].name }) : this.t(this.stage, "titleOther", { count: palettes.length });
      box.querySelector("[data-ux-order-lead]").textContent = this.stage.dataset.lead;
      box.querySelector("[data-ux-order-groups]").replaceChildren(...plan.filter((group) => group.rows.length).map((group) => this.group(group)));
    }
    this.total();
    this.allAs();
    this.reveal(this.revealing);
  }
  /* Order samples from a palette: its group, once drawn */
  reveal(id) {
    this.revealing = id || null;
    const group = id && this.stage.querySelector(`.ux-sel__group[data-palette="${CSS.escape(id)}"]`);
    if (!group) return;
    this.revealing = null;
    /* the first group stays under the title */
    this.stage.scrollTop = group.previousElementSibling ? group.offsetTop - this.stage.offsetTop - 24 : 0;
  }
  /* a palette: its name with a tick for all of it (a dash while part of it is ticked), what it comes to, its colours
     as a strip under; then a tile a colour */
  group({ palette, rows }) {
    const el = (tag, className, props = {}) => Object.assign(document.createElement(tag), { className, ...props });
    const group = el("section", "ux-sel__group");
    group.dataset.palette = palette.id;
    const head = el("div", "ux-sel__head");
    const counted = rows.filter((row) => !row.under && !this.held(this.data.get(row.colour.h)) && this.choice(this.key(palette.id, row.colour.h), this.data.get(row.colour.h)));
    const label = el("label", "ux-sel__all");
    const all = el("input", "ux-sel__check", { type: "checkbox" });
    all.dataset.uxOrderAll = "";
    all.checked = counted.length > 0 && counted.every((row) => !row.left);
    all.indeterminate = !all.checked && counted.some((row) => !row.left);
    all.disabled = !counted.length;
    all.setAttribute("aria-label", this.t(this.stage, "all", { name: palette.name }));
    const project = palette.project ? UxPalette.project(palette.project)?.name : "";
    label.append(all, el("span", "ux-sel__name", { textContent: [palette.name, project].filter(Boolean).join(" · ") }));
    const meta = el("p", "ux-sel__meta");
    meta.dataset.uxOrderMeta = "";
    const strip = el("span", "ux-sel__strip");
    strip.setAttribute("aria-hidden", "true");
    strip.append(...palette.colours.map((colour) => {
      const one = el("span");
      one.style.background = colour.c || "var(--ux-grey)";
      return one;
    }));
    head.append(label, meta, strip);
    const list = el("ul", "ux-sel__rows unstyled-list");
    list.setAttribute("role", "list");
    list.append(...rows.map((row) => this.row(row, palette)));
    group.append(head, list);
    return group;
  }
  /* a colour, as what arrives (Matt, 22 Oct: a line at a time, and not bland): a painted Colour Swatch card or its
     Sample Pot (the tin shot, the lid in the colour), whichever it comes as, changing in place; a tick on it; its name;
     its own Swatch | Pot with their prices. Counted elsewhere, in the basket or out of stock: says so instead */
  row({ colour, under, left, extra }, palette) {
    const el = (tag, className, props = {}) => Object.assign(document.createElement(tag), { className, ...props });
    const data = this.data.get(colour.h);
    const item = el("li", "ux-sel__item");
    item.dataset.handle = colour.h;
    const paint = data?.colour || colour.c || "var(--ux-grey)";
    item.style.setProperty("--ux-paint", paint);
    const name = data?.name || colour.n;
    const held = this.held(data);
    const choice = !held && data ? this.choice(this.key(palette.id, colour.h), data) : null;
    /* a note that names another palette: its name as it heads that palette, so it reads as drawn from it */
    const named = (key, name) => this.t(this.stage, key).split("[name]").flatMap((part, at) => (at ? [el("span", "ux-sel__ref", { textContent: name }), part] : [part]));
    /* also in another palette's order: unticked, its tick and Order another for a second */
    if (under) {
      item.classList.add("is-also");
      left = !extra;
    }
    item.dataset.format = held || choice || (this.can(data, "sample") || !this.live ? "sample" : "peel");
    /* what arrives */
    const pick = el("label", "ux-sel__pick");
    const tick = el("input", "ux-sel__check", { type: "checkbox" });
    tick.dataset.uxOrderPick = "";
    tick.setAttribute("aria-label", this.t(this.stage, "formatLabel", { colour: name }));
    /* the colour itself, a square of it; what it comes as sits on it */
    const show = el("span", "ux-sel__show");
    show.setAttribute("aria-hidden", "true");
    const card = el("span", "ux-sel__card");
    const foot = el("span", "ux-sel__card-foot");
    const number = data?.no || (/No\.\s*(\S+)/.exec(name || "") || [])[1];
    foot.append(el("span", "ux-sel__card-name", { textContent: UxPaletteUi.shortName(name || colour.h) }));
    if (number) foot.append(el("span", "ux-sel__card-no", { textContent: `No.${number}` }));
    card.append(el("span", "ux-sel__card-paint"), foot);
    show.append(card);
    if (data?.sample?.image) {
      const pot = el("img", "ux-sel__pot", { alt: "", decoding: "async" });
      UxSampleOrder.cutout(data.sample.image).then((src) => {
        pot.src = src || data.sample.image;
        pot.classList.toggle("is-cut", !!src);
      });
      show.append(pot);
    } else {
      const tin = el("span", "ux-sel__pot ux-sel__pot--drawn");
      tin.append(el("span", "ux-sel__pot-lid"));
      show.append(tin);
    }
    pick.append(tick, show);
    const words = el("p", "ux-sel__words");
    words.append(el("span", "ux-sel__colour", { textContent: name }));
    const note = el("span", "ux-sel__note");
    words.append(note);
    item.append(pick, words);
    /* in the basket already: shown as it's coming, the basket with a tick on it where the tick would be */
    if (held) {
      item.classList.add("is-held");
      tick.remove();
      const badge = el("span", "ux-sel__held");
      badge.append(this.stage.querySelector("[data-ux-order-basket]").content.cloneNode(true));
      pick.append(badge);
      note.textContent = this.stage.dataset.held;
      return item;
    }
    if (!choice) {
      item.classList.add("is-out");
      tick.disabled = true;
      note.textContent = this.stage.dataset.out;
      if (data?.sample && !data.sample.available) {
        const notify = el("button", "button button--tertiary button--sm ux-sel__notify", { type: "button", textContent: this.stage.dataset.notify || "" });
        notify.dataset.uxTryNotify = "";
        notify.dataset.variantId = data.sample.id;
        item.append(notify);
      }
      return item;
    }
    tick.checked = !left;
    item.classList.toggle("is-left", left);
    if (under) note.replaceChildren(...named(extra ? "another" : "counted", under.name));
    else note.textContent = colour.r ? UxPaletteUi.surface(colour.r).title : "";
    /* its own Swatch | Pot */
    const formats = el("fieldset", "ux-sel__line");
    formats.dataset.uxLineFormat = "";
    formats.disabled = left;
    formats.append(el("legend", "sr-only", { textContent: this.t(this.stage, "formatLabel", { colour: name }) }));
    [["peel", this.stage.dataset.swatch], ["sample", this.stage.dataset.pot]].forEach(([format, label]) => {
      const option = el("label", "ux-sel__opt");
      const input = el("input", "", { type: "radio", name: `ux-line-${palette.id}-${colour.h}`, value: format });
      input.checked = format === choice;
      input.disabled = !this.can(data, format);
      /* a word or two, so every switch is one line high: its price, Coming soon, Out of stock, or Not made (no such variant) */
      const price = this.can(data, format) ? this.money(data[format].price) : format === "peel" && !this.live ? this.stage.dataset.soon : data[format] ? this.stage.dataset.lineOut : this.stage.dataset.lineNone;
      option.append(input, el("span", "ux-sel__opt-name", { textContent: label }), el("span", "ux-sel__opt-price", { textContent: price }));
      formats.append(option);
    });
    if (under && !extra) {
      const more = el("button", "button button--tertiary button--sm ux-sel__extra", { type: "button", textContent: this.stage.dataset.extra || "" });
      more.dataset.uxOrderExtra = "";
      item.append(more);
    } else item.append(formats);
    return item;
  }
  /* what goes in the basket: counted, not left out, its format in stock */
  items() {
    const items = [];
    (this.plan || []).forEach(({ palette, rows }) => rows.forEach(({ colour, under, left, extra }) => {
      const data = this.data.get(colour.h);
      if ((under && !extra) || left || this.held(data)) return;
      const choice = data && this.choice(this.key(palette.id, colour.h), data);
      const format = choice && data[choice];
      if (format?.available) items.push({ handle: colour.h, choice, id: format.id, price: format.price, palette: palette.id });
    }));
    return items;
  }
  total() {
    const items = this.items();
    const swatches = items.filter((item) => item.choice === "peel").length;
    const deal = this.ladderBox && swatches && this.swatchUnit() ? this.swatchPrice(swatches) : null;
    /* with the deals, swatches are priced together (the ladder), pots each */
    const sum = (list) => list.reduce((total, item) => total + (deal && item.choice === "peel" ? 0 : item.price), 0) + (deal && list.some((item) => item.choice === "peel") ? Math.round(deal.price * list.filter((item) => item.choice === "peel").length / swatches) : 0);
    const side = this.side;
    side.querySelector("[data-ux-order-count]").textContent = items.length === 1 ? side.dataset.countOne : this.t(side, "countOther", { count: items.length });
    side.querySelector("[data-ux-order-lines]").replaceChildren(...(this.plan || []).flatMap(({ palette }) => {
      const mine = items.filter((item) => item.palette === palette.id);
      if (!mine.length) return [];
      const line = Object.assign(document.createElement("li"), { className: "ux-order-side__line" });
      line.append(Object.assign(document.createElement("span"), { textContent: palette.name }), Object.assign(document.createElement("span"), { textContent: `${mine.length} · ${this.money(sum(mine))}` }));
      return [line];
    }));
    side.querySelector("[data-ux-order-total]").textContent = this.money(sum(items));
    this.stage.querySelectorAll(".ux-sel__group").forEach((group) => {
      const mine = items.filter((item) => item.palette === group.dataset.palette);
      group.querySelector("[data-ux-order-meta]").textContent = this.t(this.stage, "groupMeta", { count: mine.length, price: this.money(sum(mine)) });
    });
    side.querySelector("ux-offer-progress")?.render?.(items.filter((item) => item.choice === "sample").length);
    this.renderLadder(swatches);
    const add = side.querySelector("[data-ux-order-add]");
    const held = (this.plan || []).some(({ rows }) => rows.some(({ colour, under }) => !under && this.held(this.data.get(colour.h))));
    add.textContent = !items.length ? (held ? side.dataset.addHeld : side.dataset.addNone) : items.length === 1 ? side.dataset.addOne : this.t(side, "addOther", { count: items.length });
    add.disabled = !items.length;
  }
  packs() {
    return UxSwatchDeal.packs(this.ladderBox);
  }
  /* one swatch's price: the colour's /CS variant (none until swatches are live, and then the ladder hides) */
  swatchUnit() {
    return [...(this.data?.values() || [])].map((data) => data?.peel?.price).find((price) => price > 0) || 0;
  }
  swatchPrice(count, unit = this.swatchUnit()) {
    return UxSwatchDeal.price(this.ladderBox, count, unit);
  }
  renderLadder(count) {
    UxSwatchDeal.render(this.ladderBox, count, this.swatchUnit());
  }

  async add(button) {
    if (button.getAttribute("aria-busy") === "true") return;
    const notice = this.side.querySelector("[data-ux-order-notice]");
    notice.hidden = true;
    button.setAttribute("aria-busy", "true");
    const before = this.items();
    /* stock first, and the basket as it is now (another tab): if that changes anything, redraw and say so */
    before.forEach((item) => UxPalette.forget(item.handle));
    this.basketRead = this.readBasket();
    await this.render(this.ids);
    const after = this.items();
    if (!after.length || after.length !== before.length || after.some((item, index) => item.id !== before[index]?.id)) {
      notice.textContent = this.side.dataset.updated;
      notice.hidden = false;
      button.removeAttribute("aria-busy");
      return;
    }
    try {
      const byPalette = new Map();
      after.forEach((item) => byPalette.set(item.palette, [...(byPalette.get(item.palette) || []), item]));
      const items = (await Promise.all([...byPalette].map(([palette, list]) => UxPaletteSamples.items(list, palette)))).flat();
      await UxPaletteSamples.post(items);
      UxPaletteTrack("palette_samples_added", { count: after.length, pots: after.filter((item) => item.choice === "sample").length, swatches: after.filter((item) => item.choice === "peel").length, palettes: byPalette.size, tagged: items.filter((item) => item.properties).length, source: "samples-view" });
    } catch (error) {
      after.forEach((item) => UxPalette.forget(item.handle));
      await this.render(this.ids);
      notice.textContent = this.side.dataset.failed;
      notice.hidden = false;
    } finally {
      button.removeAttribute("aria-busy");
    }
  }
};
window.UxSampleOrder = UxSampleOrder;

/* ---------------------------------------------------------------------------
   ORDER SAMPLES DRAWER (#506; Figma 931:14239; snippets/ux-samples-drawer.liquid): "Order a sample" on a colour card
   saves the colour as the card's + does (to the palette in use; left where it is if a palette holds it already; a
   full palette starts the next "My palette" as basket adds do, #461) and opens Try for that palette over the page.
   The new colour comes first; every colour starts ticked, as on Try (Matt, 25 Oct), each as the Colour Swatch while
   they're live (else the pot). Priced as Try prices them (UxSwatchDeal); added as Try adds them, tagged with the palette, and the basket
   drawer opens. Switching "Saving to" makes that palette the one in use. */
var UxSamplesDrawer = class extends HTMLElement {
  connectedCallback() {
    if (this._wired) return; /* the drawer moves itself to <body> and back: wire once */
    this._wired = true;
    this.drawer = this.closest("ux-drawer");
    this.select = this.querySelector("[data-ux-sdrawer-palette]");
    this.list = this.querySelector("[data-ux-sdrawer-rows]");
    this.ladder = this.querySelector("[data-ux-ladder]");
    this.foot = this.drawer.querySelector("[data-ux-sdrawer-footer]");
    this.basket = new Set();
    this.select.addEventListener("change", () => {
      this.paletteId = this.select.value;
      this.ticked = null; /* another palette: all its colours ticked, as on Try */
      UxPalette.setActive(this.paletteId);
      this.render();
    });
    this.list.addEventListener("change", (event) => {
      const row = event.target.closest("[data-handle]");
      if (!row) return;
      const handle = row.dataset.handle;
      if (event.target.matches("[data-ux-sdrawer-tick]")) {
        if (event.target.checked) this.ticked.add(handle);
        else this.ticked.delete(handle);
        row.classList.toggle("is-off", !event.target.checked);
      } else if (event.target.matches("[data-ux-sdrawer-format]")) this.choices.set(handle, event.target.value);
      this.total();
    });
    this.foot.querySelector("[data-ux-sdrawer-add]").addEventListener("click", (event) => this.add(event.currentTarget));
    document.addEventListener("cart:change", (event) => {
      const items = event.detail?.cart?.items;
      if (items) this.basket = new Set(items.map((item) => item.variant_id));
    });
  }
  t(key, values = {}) {
    return UxSwatchDeal.text(this, key, values);
  }
  static open(handle) {
    return document.querySelector("ux-samples-drawer")?.start(handle);
  }
  async readBasket() {
    try {
      const cart = await (await fetch(`${window.Shopify?.routes?.root || "/"}cart.js`)).json();
      this.basket = new Set((cart.items || []).map((item) => item.variant_id));
    } catch (error) {
      /* can't read the basket: nothing counts as in it */
    }
  }
  /* save the colour (as the card's +) and say where it went */
  save(handle, data) {
    const colour = { h: handle, n: data?.name || handle, c: data?.colour || "" };
    const name = colour.n;
    const holding = UxPalette.palettesWith(handle);
    if (holding.length) {
      const active = UxPalette.active()?.id;
      const id = holding.includes(active) ? active : holding[0];
      return { id, key: "already", name };
    }
    if (UxPalette.add(colour)) {
      const id = UxPalette.active()?.id;
      UxPaletteTrack("palette_colour_added", { handle, source: "order-sample", palette: id });
      return { id, key: "added", name };
    }
    const full = UxPalette.active()?.name || "";
    const id = UxPalette.createPalette({ name: window.uxPaletteDefaultName || "My palette", project: null, colours: [colour], activate: true });
    if (id) {
      UxPaletteTrack("palette_colour_added", { handle, source: "order-sample", palette: id });
      return { id, key: "created", name, full };
    }
    return { id: UxPalette.active()?.id || null, key: "full", name, extra: colour };
  }
  async start(handle) {
    this.drawer.show();
    this.list.replaceChildren(Object.assign(document.createElement("li"), { className: "ux-sdrawer__loading", textContent: this.dataset.loading }));
    const [data] = await Promise.all([UxPalette.colour(handle, { card: false }), this.readBasket()]);
    const saved = this.save(handle, data);
    this.newHandle = handle;
    this.extra = saved.extra || null;
    this.paletteId = saved.id;
    this.ticked = null; /* every colour, once the palette's are known (render) */
    this.choices = new Map();
    const notice = this.querySelector("[data-ux-sdrawer-notice]");
    const palette = saved.id ? UxPalette.palette(saved.id) : null;
    notice.querySelector("[data-ux-sdrawer-notice-text]").textContent = this.t(saved.key, { colour: saved.name, palette: palette?.name || "", full: saved.full || "" });
    notice.hidden = false;
    await this.render();
    this.list.querySelector(`[data-handle="${CSS.escape(handle)}"] [data-ux-sdrawer-tick]`)?.focus({ preventScroll: true });
  }
  async render() {
    const palettes = UxPalette.palettes();
    this.select.replaceChildren(...palettes.map((one) => Object.assign(document.createElement("option"), { value: one.id, textContent: one.name, selected: one.id === this.paletteId })));
    this.select.closest(".ux-sdrawer__saving").hidden = !palettes.length;
    const palette = this.paletteId ? UxPalette.palette(this.paletteId) : null;
    const colours = [...(palette?.colours || [])];
    if (this.extra && !colours.some((colour) => colour.h === this.extra.h)) colours.unshift(this.extra);
    colours.sort((a, b) => (b.h === this.newHandle) - (a.h === this.newHandle));
    this.data = new Map(await Promise.all(colours.map(async (colour) => [colour.h, await UxPalette.colour(colour.h, { card: false })])));
    this.rows = colours;
    this.ticked ||= new Set(colours.map((colour) => colour.h));
    this.list.replaceChildren(...colours.map((colour) => this.row(colour, palette)));
    this.total();
  }
  held(data) {
    return ["peel", "sample"].find((format) => data?.[format] && this.basket.has(data[format].id)) || null;
  }
  choice(handle, data) {
    const chosen = this.choices.get(handle);
    if (chosen && data?.[chosen]?.available) return chosen;
    return UxPaletteSamples.pick(data);
  }
  row(colour, palette) {
    const el = (tag, className, props = {}) => Object.assign(document.createElement(tag), { className, ...props });
    const data = this.data.get(colour.h);
    const held = this.held(data);
    const choice = this.choice(colour.h, data);
    const ticked = this.ticked.has(colour.h) && !held && Boolean(choice);
    const name = data?.name || colour.n || colour.h;
    const item = el("li", `ux-sdrow${colour.h === this.newHandle ? " is-new" : ""}${ticked ? "" : " is-off"}`);
    item.dataset.handle = colour.h;
    const tickLabel = el("label", "ux-sdrow__tick");
    const tick = el("input", "sr-only", { type: "checkbox", checked: ticked, disabled: Boolean(held) || !choice });
    tick.dataset.uxSdrawerTick = "";
    tick.setAttribute("aria-label", this.t("tick", { colour: name }));
    tickLabel.append(tick, el("span", "ux-sdrow__box", { ariaHidden: "true" }));
    const chip = el("span", "ux-sdrow__chip");
    chip.style.setProperty("--ux-paint", data?.colour || colour.c || "var(--ux-grey)");
    chip.setAttribute("aria-hidden", "true");
    const text = el("div", "ux-sdrow__text");
    const caption = held ? this.dataset.inBasket : !choice ? this.dataset.soldOut : colour.h === this.newHandle ? this.dataset.justAdded : this.t("inPalette", { palette: palette?.name || "" });
    text.append(el("p", "ux-sdrow__name", { textContent: name }), el("p", "ux-sdrow__caption", { textContent: caption }));
    if (!held) {
      const formats = el("fieldset", "ux-sdrow__formats");
      formats.append(el("legend", "sr-only", { textContent: this.t("format", { colour: name }) }));
      [["peel", this.dataset.swatch], ["sample", this.dataset.pot]].forEach(([format, label]) => {
        const variant = data?.[format];
        const live = format !== "peel" || this.dataset.live === "true";
        const option = el("label", "ux-sdrow__format");
        const radio = el("input", "sr-only", { type: "radio", name: `ux-sdrawer-format-${colour.h}`, value: format, checked: choice === format, disabled: !live || !variant?.available });
        radio.dataset.uxSdrawerFormat = "";
        const price = !live ? this.dataset.soon : !variant ? "–" : variant.available ? UxPaletteSamples.money(variant.price) : this.dataset.soldOut;
        option.append(radio, el("span", "ux-sdrow__format-name", { textContent: label }), el("span", "ux-sdrow__format-price", { textContent: price }));
        formats.append(option);
      });
      text.append(formats);
    }
    item.append(tickLabel, chip, text);
    return item;
  }
  items() {
    return (this.rows || []).flatMap((colour) => {
      if (!this.ticked.has(colour.h)) return [];
      const data = this.data.get(colour.h);
      if (this.held(data)) return [];
      const choice = this.choice(colour.h, data);
      const format = choice && data[choice];
      return format?.available ? [{ handle: colour.h, choice, id: format.id, price: format.price }] : [];
    });
  }
  total() {
    const items = this.items();
    const swatches = items.filter((item) => item.choice === "peel");
    const unit = swatches[0]?.price || 0;
    const deal = this.ladder && swatches.length && unit ? UxSwatchDeal.price(this.ladder, swatches.length, unit) : null;
    const sum = items.reduce((total, item) => total + (deal && item.choice === "peel" ? 0 : item.price), 0) + (deal ? deal.price : 0);
    UxSwatchDeal.render(this.ladder, swatches.length, unit);
    this.foot.querySelector("[data-ux-sdrawer-count]").textContent = items.length === 1 ? this.dataset.countOne : this.t("countOther", { count: items.length });
    this.foot.querySelector("[data-ux-sdrawer-total]").textContent = items.length ? this.t(deal?.post ? "postFree" : "post", { price: UxPaletteSamples.money(sum) }) : "";
    const add = this.foot.querySelector("[data-ux-sdrawer-add]");
    add.textContent = !items.length ? this.dataset.addNone : items.length === 1 ? this.dataset.addOne : this.t("addOther", { count: items.length });
    add.disabled = !items.length;
    const url = new URL(this.dataset.paletteUrl || window.uxPaletteUrl || "/", window.location.origin);
    url.searchParams.set("tab", "samples");
    if (this.paletteId) url.searchParams.set("palette", this.paletteId);
    this.foot.querySelector("[data-ux-sdrawer-try]").setAttribute("href", url.pathname + url.search);
  }
  async add(button) {
    const items = this.items();
    if (!items.length || button.getAttribute("aria-busy") === "true") return;
    const error = this.foot.querySelector("[data-ux-sdrawer-error]");
    error.hidden = true;
    button.setAttribute("aria-busy", "true");
    try {
      const lines = await UxPaletteSamples.items(items, this.paletteId);
      /* this drawer closes first, so the basket drawer that the add opens isn't under it */
      await new Promise((resolve) => {
        this.drawer.addEventListener("dialog:after-hide", resolve, { once: true });
        this.drawer.hide();
        setTimeout(resolve, 600);
      });
      await UxPaletteSamples.post(lines);
      UxPaletteTrack("palette_samples_added", { count: items.length, pots: items.filter((item) => item.choice === "sample").length, swatches: items.filter((item) => item.choice === "peel").length, palettes: 1, tagged: lines.filter((line) => line.properties).length, source: "order-sample" });
    } catch (failure) {
      error.textContent = this.dataset.failed;
      error.hidden = false;
      this.drawer.show();
    } finally {
      button.removeAttribute("aria-busy");
    }
  }
};
if (!window.customElements.get("ux-samples-drawer")) {
  window.customElements.define("ux-samples-drawer", UxSamplesDrawer);
}
window.UxSamplesDrawer = UxSamplesDrawer;
/* a colour card's "Order a sample" (snippets/product-card.liquid) opens it; without script the card's form still adds
   the pot. Caught before the theme's product form sees the submit. */
document.addEventListener("submit", (event) => {
  const button = event.submitter?.closest?.("[data-ux-order-sample]");
  if (!button || !document.querySelector("ux-samples-drawer")) return;
  event.preventDefault();
  event.stopPropagation();
  UxSamplesDrawer.open(button.dataset.uxOrderSample);
}, true);
/* the homepage hero's and Colour of the Year's "Order a sample" (#271) are links to the colour: with script, the drawer */
document.addEventListener("click", (event) => {
  const link = event.target.closest?.("a[data-ux-order-sample]");
  if (!link || event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0 || !document.querySelector("ux-samples-drawer")) return;
  event.preventDefault();
  UxSamplesDrawer.open(link.dataset.uxOrderSample);
});

/* ---------------------------------------------------------------------------
   BUY (#483; Figma: Website, Palette builder, Buy 908:14353; Matt, 21 Oct 2026), built from the product page and the
   basket's parts (Matt: "use the basket and product pages"): a card a colour, its name first and what it's for; the
   product page's Finish row opening its finish cards (the surface's recommended ones marked); its size rows (coverage,
   price, a stepper) filled with the paint calculator's suggestion (the room's size for walls, ceilings, woodwork and
   floors; how many doors or radiators; roughly how big for the rest), changed freely, Use suggestion to go back. Primer
   is the product page's Recommended primer on its tint, for the colours it sits under (each colour's own primer:
   Wall Primer under plaster, Multi-Surface Primer under wood and metal; Stabilising Primer under masonry), asked, not
   assumed: Add the suggested primer. Tools: the Added to basket drawer's You may also need, with steppers. The data:
   /products/<handle>?view=buy-data. The sidebar is the order: a line each, the total, Add all to basket.
   --------------------------------------------------------------------------- */
var UxBuyView = class {
  constructor(wall) {
    this.wall = wall;
    this.stage = wall.querySelector('[data-ux-app-stage="buy"]');
    this.side = wall.querySelector('[data-ux-app-side="buy"]');
    if (!this.stage?.querySelector("[data-ux-buy]") || !this.side) return;
    try {
      this.data = JSON.parse(this.stage.querySelector("[data-ux-buy-data]")?.textContent || "{}");
    } catch (error) {
      this.data = {};
    }
    this.ready = true;
    this.products = new Map(); /* handle → its buy data (templates/product.buy-data.liquid) */
    this.finish = new Map(); /* colour handle → the finish chosen */
    this.qty = new Map(); /* owner|variant id → quantity, once someone has set it */
    this.touched = new Set(); /* owners whose amounts were set by hand (the rest follow the suggestion) */
    this.amounts = new Map(); /* colour handle → doors, radiators or m² for surfaces the room doesn't size */
    this.cards = new Map(); /* colour handle → what its card was drawn from, for the drawers */
    this.room = this.stage.querySelector("[data-ux-buy-room]");
    this.stage.querySelector("[data-ux-buy-palette]").addEventListener("change", (event) => {
      this.paletteId = event.target.value;
      this.render();
    });
    this.room.addEventListener("input", () => this.draw());
    /* the product page's Finish and Size drawers (Matt, 22 Oct), filled for the colour whose row was pressed. The theme
       moves a drawer to <body> while it's open, so each listens for itself */
    this.finishDrawer = document.getElementById("ux-buy-finish-drawer");
    this.sizeDrawer = document.getElementById("ux-buy-size-drawer");
    this.drawerFor = null;
    const onQty = (event) => {
      const row = event.target.closest("[data-owner]");
      if (!row) return;
      const owner = row.dataset.owner;
      /* the first hand-set amount fixes the suggestion's others as they were */
      if (!this.touched.has(owner)) this.lines.filter((line) => line.owner === owner).forEach((line) => this.qty.set(`${owner}|${line.id}`, line.quantity));
      this.touched.add(owner);
      this.qty.set(`${owner}|${event.detail.variantId}`, event.detail.quantity);
      this.draw({ focus: { owner, variant: event.detail.variantId, step: event.target.closest("ux-qty-stepper") && document.activeElement?.dataset.step } });
    };
    this.stage.addEventListener("ux-qty:change", onQty);
    this.sizeDrawer?.addEventListener("ux-qty:change", onQty);
    this.sizeDrawer?.addEventListener("click", (event) => {
      if (this.suggestClick(event)) return;
      if (event.target.closest("[data-ux-buy-change-finish]")) this.swap(this.sizeDrawer, () => this.openFinishes(this.drawerFor, { back: true }));
      else if (event.target.closest("[data-ux-buy-size-done]")) this.sizeDrawer.hide();
    });
    this.finishDrawer?.addEventListener("change", () => this.confirmLabel());
    this.finishDrawer?.addEventListener("click", (event) => {
      if (!event.target.closest("[data-ux-buy-finish-confirm]")) return;
      const picked = this.finishDrawer.querySelector("[data-ux-buy-finish]:checked")?.value;
      const owner = this.drawerFor;
      if (picked && picked !== this.finish.get(owner)) {
        this.finish.set(owner, picked);
        this.touched.delete(owner);
      }
      /* from the Size drawer's Change: back to the sizes, for the new finish */
      if (this.back) this.draw().then(() => this.swap(this.finishDrawer, () => this.openSizes(owner)));
      else {
        this.finishDrawer.hide();
        this.draw();
      }
    });
    /* the card under a drawer is drawn again as amounts change, so the row that opened it is a new one: back to it */
    [this.finishDrawer, this.sizeDrawer].forEach((drawer) => drawer?.addEventListener("dialog:after-hide", () => {
      const active = document.activeElement;
      if (document.querySelector("ux-drawer[open]") || (active && active !== document.body && !active.closest("ux-drawer"))) return;
      this.stage.querySelector(`[data-ux-buy-open="${this.from}"][data-owner-for="${CSS.escape(this.drawerFor || "")}"]`)?.focus({ preventScroll: true });
    }));
    [this.finishDrawer, this.sizeDrawer].forEach((drawer) => drawer?.addEventListener("toggle", () => {
      this.stage.querySelector(`[data-ux-buy-open="${drawer === this.finishDrawer ? "finish" : "size"}"][data-owner-for="${CSS.escape(this.drawerFor || "")}"]`)?.setAttribute("aria-expanded", String(drawer.open));
    }));
    this.stage.addEventListener("input", (event) => {
      const amount = event.target.closest("[data-ux-buy-amount]");
      if (!amount) return;
      this.amounts.set(amount.dataset.uxBuyAmount, Math.max(0, parseFloat(amount.value) || 0));
      this.touched.delete(amount.dataset.uxBuyAmount);
      clearTimeout(this.typing);
      this.typing = setTimeout(() => this.draw({ focus: { amount: amount.dataset.uxBuyAmount } }), 400);
    });
    this.stage.addEventListener("click", (event) => {
      if (event.target.closest("[data-ux-buy-plan]")) return this.wall.setView("palette");
      const open = event.target.closest("[data-ux-buy-open]");
      if (open) {
        this.from = open.dataset.uxBuyOpen;
        if (open.dataset.uxBuyOpen === "finish") this.openFinishes(open.dataset.ownerFor);
        else this.openSizes(open.dataset.ownerFor);
        return;
      }
      this.suggestClick(event);
    });
    this.side.querySelector("[data-ux-buy-add]").addEventListener("click", (event) => this.add(event.currentTarget));
  }
  t(element, key, values = {}) {
    return Object.entries(values).reduce((string, [name, value]) => string.split(`[${name}]`).join(value), element.dataset[key] || "");
  }
  money(cents) {
    const currency = window.Shopify?.currency?.active || "GBP";
    return new Intl.NumberFormat(document.documentElement.lang || "en-GB", { style: "currency", currency }).format(cents / 100);
  }
  icon(name) {
    return this.stage.querySelector(`template[data-ux-buy-${name}]`)?.content.cloneNode(true) || document.createTextNode("");
  }
  async product(handle) {
    if (!handle) return null;
    if (!this.products.has(handle)) {
      this.products.set(handle, fetch(`${window.Shopify?.routes?.root || "/"}products/${encodeURIComponent(handle)}?view=buy-data`).then((response) => (response.ok ? response.text() : null)).then((text) => {
        try {
          return text ? JSON.parse(text) : null;
        } catch (error) {
          return null;
        }
      }).catch(() => null));
    }
    return this.products.get(handle);
  }
  palette() {
    return UxPalette.palette(this.paletteId) || UxPalette.active() || UxPalette.palettes()[0] || null;
  }
  roomValue(name) {
    return Math.max(0, parseFloat(this.room.querySelector(`[name="${name}"]`)?.value) || 0);
  }
  /* how a surface is sized: by the room, by a count, or roughly by area (fair starting m², editable) */
  static SIZING = {
    "walls-ceilings": "room", "kitchens-bathrooms": "room", "woodwork-panelling": "room", floors: "room",
    "front-doors": { count: "doors", each: 2 }, "radiators-metalwork": { count: "radiators", each: 1.5 },
    "windows-woodwork": { area: 3 }, "railings-fences": { area: 10 }, "sheds-garden": { area: 6 }, "furniture-cabinetry": { area: 4 }, "walls-brick-render": { area: 30 }
  };
  /* what primes what: bare plaster takes Wall Primer, bare wood, metal and MDF Multi-Surface Primer, masonry its own */
  static PRIMING = {
    "walls-ceilings": "Wall Primer", "kitchens-bathrooms": "Wall Primer",
    "woodwork-panelling": "Multi-Surface Primer", "furniture-cabinetry": "Multi-Surface Primer", "front-doors": "Multi-Surface Primer", "windows-woodwork": "Multi-Surface Primer", "sheds-garden": "Multi-Surface Primer", "radiators-metalwork": "Multi-Surface Primer", "railings-fences": "Multi-Surface Primer",
    "walls-brick-render": "masonry"
  };
  isCeiling(colour, palette) {
    if (colour.r === "ceiling") return true;
    const onWalls = palette.colours.filter((one) => ["walls-ceilings", "kitchens-bathrooms"].includes(UxPaletteUi.surface(one.r)?.slug) && one.r !== "ceiling");
    return onWalls.length > 1 && onWalls.indexOf(colour) === 1 && !palette.colours.some((one) => one.r === "ceiling");
  }
  /* the area a colour covers: walls (perimeter × height, less doors and windows), a ceiling, woodwork (doors both
     sides and the skirting), a floor; a count or rough area for the rest */
  area(colour, palette) {
    const surface = colour.r ? UxPaletteUi.surface(colour.r) : null;
    const sizing = UxBuyView.SIZING[surface?.slug];
    if (!sizing) return 0;
    if (sizing !== "room") {
      const value = this.amounts.has(colour.h) ? this.amounts.get(colour.h) : sizing.count ? 1 : sizing.area;
      return sizing.count ? value * sizing.each : value;
    }
    const [length, width, height, doors, windows] = ["length", "width", "height", "doors", "windows"].map((name) => this.roomValue(name));
    if (surface.slug === "floors") return length * width;
    if (surface.slug === "woodwork-panelling") return doors * 2 * 1.8 + 2 * (length + width) * 0.15;
    if (this.isCeiling(colour, palette)) return length * width;
    return Math.max(0, 2 * (length + width) * height - doors * 1.8 - windows * 1.5);
  }
  /* a finish's tins (no sample pots), with litres and m² per coat; its coverage per litre from finish-data, else the
     sizes' own coverage */
  sizes(finish) {
    return (finish?.sizes || []).filter((size) => !size.sample).map((size) => ({ ...size, litres: uxLitres(size.size || "") })).filter((size) => size.litres > 0);
  }
  perLitre(finish) {
    if (finish?.coverage > 0) return finish.coverage;
    const sized = this.sizes(finish).find((size) => size.area > 0);
    return sized ? sized.area / sized.litres : 12;
  }
  suggest(finish, litres) {
    const packs = this.sizes(finish).filter((size) => size.available).map((size) => ({ id: size.id, size: size.size, litres: size.litres, price: size.price, max: Infinity }));
    if (!packs.length || litres <= 0) return [];
    return uxCheapestCover(litres, packs) || [];
  }
  /* the amounts for an owner: as set by hand, else the suggestion */
  chosen(owner, finish, suggestion) {
    if (!this.touched.has(owner)) return suggestion.map((pick) => ({ id: pick.id, quantity: pick.quantity }));
    return this.sizes(finish).map((size) => ({ id: size.id, quantity: this.qty.get(`${owner}|${size.id}`) || 0 })).filter((pick) => pick.quantity > 0);
  }
  async render() {
    if (!this.ready) return;
    const token = (this.token = Symbol("buy"));
    const palette = this.palette();
    const box = this.stage.querySelector("[data-ux-buy]");
    const empty = this.stage.querySelector("[data-ux-app-empty]");
    box.hidden = !palette;
    empty.hidden = Boolean(palette);
    this.side.hidden = !palette;
    if (!palette) return;
    this.paletteId = palette.id;
    const select = this.stage.querySelector("[data-ux-buy-palette]");
    select.replaceChildren(...UxPalette.palettes().map((one) => new Option(one.name, one.id)));
    select.value = palette.id;
    await Promise.all(palette.colours.map((colour) => this.product(colour.h)));
    if (token !== this.token) return;
    this.palette_ = palette;
    this.draw();
  }
  async draw({ focus = null } = {}) {
    const palette = this.palette_;
    if (!palette) return;
    const token = (this.drawToken = Symbol("draw"));
    const el = (tag, className, props = {}) => Object.assign(document.createElement(tag), { className, ...props });
    const coats = Math.max(1, this.roomValue("coats") || 1);
    const lines = [];
    this.suggested = new Map();
    const primers = new Map(); /* primer handle → { finishes: Map(finish → area), colours } */
    let roomNeeded = false;
    const cards = [];
    for (const colour of palette.colours) {
      const product = await this.product(colour.h);
      if (token !== this.drawToken) return;
      if (!product) continue;
      const surface = colour.r ? UxPaletteUi.surface(colour.r) : null;
      const recommended = (surface?.finish && this.data.finishes?.[surface.finish]) || null;
      const finishes = (product.finishes || []).filter((finish) => this.sizes(finish).length);
      if (!finishes.length) continue;
      let finishValue = this.finish.get(colour.h);
      if (!finishes.some((finish) => finish.value === finishValue)) {
        const preferred = recommended?.default || recommended?.options?.[0]?.value;
        finishValue = (finishes.find((finish) => finish.value === preferred) || finishes.find((finish) => recommended?.options?.some((option) => option.value === finish.value)) || finishes[0]).value;
      }
      this.finish.set(colour.h, finishValue);
      const finish = finishes.find((one) => one.value === finishValue);
      const sizing = UxBuyView.SIZING[surface?.slug];
      if (sizing === "room") roomNeeded = true;
      const area = surface ? Math.round(this.area(colour, palette) * 10) / 10 : 0;
      const suggestion = area ? this.suggest(finish, (area * coats) / this.perLitre(finish)) : [];
      const picks = this.chosen(colour.h, finish, suggestion);
      const priming = UxBuyView.PRIMING[surface?.slug];
      if (priming) {
        const handle = priming === "masonry" ? this.data.masonryPrimer : this.wall.byHandle?.get(colour.h)?.pr;
        if (handle) {
          const entry = primers.get(handle) || { finishes: new Map(), colours: [] };
          const key = priming === "masonry" ? "" : priming;
          entry.finishes.set(key, (entry.finishes.get(key) || 0) + area);
          if (!entry.colours.includes(UxPaletteUi.shortName(colour.n))) entry.colours.push(UxPaletteUi.shortName(colour.n));
          primers.set(handle, entry);
        }
      }
      /* the card: the colour first, what it's for, its finish (the product page's Finish row and cards), its sizes */
      const card = el("article", "ux-buyc");
      card.dataset.owner = colour.h;
      const swatch = el("span", "ux-buyc__swatch");
      swatch.style.background = colour.c || product.colour || "var(--ux-grey)";
      swatch.classList.toggle("ux-buyc__swatch--dark", UxSampleOrder.dark(colour.c));
      const number = this.wall.byHandle?.get(colour.h)?.no;
      if (number) swatch.append(UxSampleOrder.diamond(number));
      const body = el("div", "ux-buyc__body");
      const head = el("div", "ux-buyc__head");
      const words = el("div", "ux-buyc__words");
      words.append(el("h3", "ux-buyc__name", { textContent: product.title || UxColourCard.fullName(colour) }));
      if (surface) words.append(el("p", "ux-buyc__for", { textContent: this.t(this.stage, "for", { surface: this.isCeiling(colour, palette) ? `${surface.title} (${this.stage.dataset.ceiling.toLowerCase()})` : surface.title }) }));
      else {
        const note = el("p", "ux-buyc__for");
        note.append(this.stage.dataset.noSurface, " ");
        const go = el("button", "ux-buyc__plan", { type: "button", textContent: this.stage.dataset.plan });
        go.dataset.uxBuyPlan = "";
        note.append(go);
        words.append(note);
      }
      const subtotal = this.subtotal(finish, picks);
      head.append(words, el("p", "ux-buyc__total", { textContent: subtotal ? this.money(subtotal) : "" }));
      body.append(head);
      /* the product page's Finish and Size rows; each opens its drawer */
      const short = UxPaletteUi.shortName(colour.n);
      const finishText = [finish.value, finish.sheen].filter(Boolean).join(" · ");
      const rows = el("div", "ux-buyc__rows");
      const chosen = this.summary(finish, picks);
      const sizeText = chosen ? `${chosen} · ${this.money(subtotal)}` : this.t(this.stage, "sizesPrompt", { price: this.money(Math.min(...this.sizes(finish).map((size) => size.price))) });
      rows.append(
        this.selectorRow("finish", colour.h, this.stage.dataset.finish, finishText, `${this.t(this.stage, "finishLabel", { colour: short })}: ${finishText}`),
        this.selectorRow("size", colour.h, this.stage.dataset.size, sizeText, `${this.stage.dataset.size}, ${short}: ${sizeText}`)
      );
      body.append(rows);
      /* how big, for surfaces the room doesn't size */
      if (sizing && sizing !== "room") {
        const label = el("label", "ux-buyc__amount");
        label.append(el("span", "", { textContent: this.stage.dataset[sizing.count ? sizing.count : "area"] }));
        const input = el("input", "ux-field__input", { type: "number", min: "0", step: sizing.count ? "1" : "0.5", inputMode: "decimal", value: String(this.amounts.has(colour.h) ? this.amounts.get(colour.h) : sizing.count ? 1 : sizing.area) });
        input.dataset.uxBuyAmount = colour.h;
        label.append(input);
        body.append(label);
      }
      this.cards.set(colour.h, { colour, product, finishes, finish, surface, recommended, picks, suggestion, line: area ? this.t(this.stage, "suggest", { sizes: suggestion.map((pick) => `${pick.quantity} × ${pick.size}`).join(" + "), area, coats }) : "" });
      card.append(swatch, body);
      cards.push(card);
      picks.forEach((pick) => {
        const size = this.sizes(finish).find((one) => one.id === pick.id);
        if (size) lines.push({ owner: colour.h, id: pick.id, quantity: pick.quantity, price: size.price, label: `${UxPaletteUi.shortName(colour.n)} · ${finish.value.replace(/ Paint$/, "")}`, size: size.size });
      });
    }
    this.room.hidden = !roomNeeded;
    /* primer: the product page's Recommended primer, on its tint, for the colours it sits under */
    const primerBlocks = [];
    for (const [handle, entry] of primers) {
      const product = await this.product(handle);
      if (token !== this.drawToken) return;
      if (!product) continue;
      const owner = `primer:${handle}`;
      const block = el("section", "ux-buyp");
      block.dataset.owner = owner;
      if (product.band) block.style.setProperty("--ux-band", product.band);
      block.classList.toggle("ux-buyp--dark", Boolean(product.band) && UxSampleOrder.dark(product.band));
      if (product.image) {
        const image = el("img", "ux-buyp__image", { src: product.image, alt: "", loading: "lazy", width: 300, height: 300 });
        block.append(image);
      }
      const inner = el("div", "ux-buyp__inner");
      inner.append(el("p", "ux-buyp__eyebrow", { textContent: this.stage.dataset.primerEyebrow }), el("h3", "ux-buyp__title", { textContent: this.stage.dataset.primerTitle }));
      const card = el("div", "ux-buyp__card");
      card.append(el("p", "ux-buyp__name", { textContent: product.title }));
      const colours = entry.colours.length > 1 ? `${entry.colours.slice(0, -1).join(", ")} ${this.stage.dataset.and} ${entry.colours[entry.colours.length - 1]}` : entry.colours[0];
      card.append(el("p", "ux-buyp__intro", { textContent: entry.finishes.has("") ? this.stage.dataset.primerMasonry : this.t(this.stage, "primerIntro", { colour: colours }) }));
      card.append(el("p", "ux-buyp__why", { textContent: this.stage.dataset.why }));
      const why = el("ul", "ux-buyp__points unstyled-list");
      ["whySeals", "whyBinds", "whySaves"].forEach((key) => {
        const item = el("li", "");
        item.append(this.icon("tick"), el("span", "", { textContent: this.stage.dataset[key] }));
        why.append(item);
      });
      card.append(why);
      const suggestion = [];
      for (const [key, area] of entry.finishes) {
        const finish = (product.finishes || []).find((one) => one.value === key) || (key === "" ? product.finishes?.[0] : null);
        if (!finish) continue;
        const picks = this.suggest(finish, Math.max(area, 1) / this.perLitre(finish));
        suggestion.push(...picks);
        const chosen = this.chosen(owner, finish, []);
        const group = el("div", "ux-buyp__group");
        if (key) group.append(el("p", "ux-buyp__finish", { textContent: this.stage.dataset[key === "Wall Primer" ? "primerWall" : "primerMs"] }));
        group.append(this.sizeRows(owner, finish, chosen, picks, this.t(this.stage, "primerSuggest", { area: Math.round(area * 10) / 10, sizes: picks.map((pick) => `${pick.quantity} × ${pick.size}`).join(" + ") }), { primer: true }));
        card.append(group);
        chosen.forEach((pick) => {
          const size = this.sizes(finish).find((one) => one.id === pick.id);
          if (size) lines.push({ owner, id: pick.id, quantity: pick.quantity, price: size.price, label: `${product.title}${key ? ` · ${key}` : ""}`, size: size.size });
        });
      }
      this.suggested.set(owner, suggestion);
      if (!this.touched.has(owner)) {
        const add = el("button", "button button--secondary ux-buyp__add", { type: "button", textContent: this.stage.dataset.primerAdd });
        add.dataset.uxBuySuggest = owner;
        card.append(add);
      }
      inner.append(card);
      block.append(inner);
      primerBlocks.push(block);
    }
    /* tools: the Added to basket drawer's You may also need, as cards with a stepper */
    const tools = (this.data.tools || []).filter((tool) => tool.available);
    let toolBox = null;
    if (tools.length) {
      toolBox = el("section", "ux-buyt");
      toolBox.append(el("h3", "ux-buyt__title", { textContent: this.stage.dataset.toolsTitle }));
      const grid = el("ul", "ux-buyt__grid unstyled-list");
      tools.forEach((tool) => {
        const owner = `tool:${tool.handle}`;
        const quantity = this.qty.get(`${owner}|${tool.id}`) || 0;
        const item = el("li", `ux-buyt__tool${quantity ? " is-selected" : ""}`);
        item.dataset.owner = owner;
        const image = el("a", "ux-buyt__image", { href: tool.url, tabIndex: -1 });
        image.setAttribute("aria-hidden", "true");
        if (tool.image) image.append(el("img", "", { src: tool.image, alt: "", loading: "lazy", width: 150, height: 150 }));
        item.append(image, el("a", "ux-buyt__name", { href: tool.url, textContent: tool.title }), el("p", "ux-buyt__price", { textContent: this.money(tool.price) }), this.stepper(tool.id, quantity, tool.title));
        grid.append(item);
        if (quantity) lines.push({ owner, id: tool.id, quantity, price: tool.price, label: tool.title, size: "" });
      });
      toolBox.append(grid);
    }
    const rows = this.stage.querySelector("[data-ux-buy-rows]");
    rows.replaceChildren(el("h3", "ux-buy__section", { textContent: this.stage.dataset.paint }), ...cards);
    const also = this.stage.querySelector("[data-ux-buy-also]");
    also.replaceChildren(...primerBlocks, ...(toolBox ? [toolBox] : []));
    this.lines = lines;
    this.drawOrder(palette);
    if (this.sizeDrawer?.open) this.fillSizes();
    if (focus) this.refocus(focus);
  }
  /* Use the suggestion (a paint) or Add the suggested primer: the amounts go back to, or are set to, the suggestion */
  suggestClick(event) {
    const suggest = event.target.closest("[data-ux-buy-suggest]");
    if (!suggest) return false;
    const owner = suggest.dataset.uxBuySuggest;
    [...this.qty.keys()].filter((key) => key.startsWith(`${owner}|`)).forEach((key) => this.qty.delete(key));
    if (owner.startsWith("primer:")) {
      /* primer is asked, not assumed: the button sets the suggested amounts */
      (this.suggested.get(owner) || []).forEach((pick) => this.qty.set(`${owner}|${pick.id}`, pick.quantity));
      this.touched.add(owner);
    } else this.touched.delete(owner);
    this.draw();
    return true;
  }
  /* open one drawer once the other has finished closing, so two never stack (as the product page) */
  swap(from, then) {
    if (from?.open) {
      from.addEventListener("dialog:after-hide", then, { once: true });
      from.hide();
    } else then();
  }
  /* the Finish drawer for a colour: the product page's finish cards, its finish ticked, the recommended marked */
  openFinishes(owner, { back = false } = {}) {
    const card = this.cards.get(owner);
    if (!card || !this.finishDrawer) return;
    this.drawerFor = owner;
    this.back = back;
    const el = (tag, className, props = {}) => Object.assign(document.createElement(tag), { className, ...props });
    const { colour, finishes, finish, surface, recommended } = card;
    const name = UxPaletteUi.shortName(colour.n);
    this.finishDrawer.querySelector("[data-ux-buy-finish-intro]").textContent = this.t(this.stage, "finishIntro", { colour: name, count: finishes.length });
    this.finishDrawer.querySelector("[data-ux-buy-finish-list]").replaceChildren(...finishes.map((one) => {
      const option = el("label", "ux-finish-option");
      const input = el("input", "ux-finish-option__input sr-only", { type: "radio", name: "ux-buy-finish", value: one.value, checked: one.value === finish.value });
      input.dataset.uxBuyFinish = "";
      const header = el("span", "ux-finish-option__header");
      const radio = el("span", "ux-radio");
      radio.setAttribute("aria-hidden", "true");
      radio.append(this.icon("check"));
      const title = el("span", "ux-finish-option__name");
      title.append(el("span", "ux-finish-option__title", { textContent: one.value }));
      if (one.sub) title.append(el("span", "ux-finish-option__sub", { textContent: one.sub }));
      if (surface && recommended?.options?.some((option) => option.value === one.value)) title.append(el("span", "ux-buyc__recommended", { textContent: this.t(this.stage, "recommended", { surface: surface.title.toLowerCase() }) }));
      const meta = el("span", "ux-finish-option__meta");
      if (one.sheen) meta.append(el("span", "ux-finish-option__sheen", { textContent: one.sheen }));
      meta.append(el("span", "ux-finish-option__price", { textContent: this.t(this.stage, "from", { price: this.money(Math.min(...this.sizes(one).map((size) => size.price))) }) }));
      header.append(radio, title, meta);
      option.append(input, header);
      const details = el("span", "ux-finish-option__details");
      if (one.desc) details.append(el("span", "ux-finish-option__desc", { textContent: one.desc }));
      if (one.suitable) {
        const suitable = el("span", "ux-finish-option__suitable");
        suitable.append(el("span", "ux-finish-option__suitable-label", { textContent: this.stage.dataset.suitable }), el("span", "", { textContent: one.suitable }));
        details.append(suitable);
      }
      if (details.childElementCount) option.append(details);
      return option;
    }));
    this.confirmLabel();
    this.finishDrawer.show();
  }
  confirmLabel() {
    const button = this.finishDrawer.querySelector("[data-ux-buy-finish-confirm]");
    const picked = this.finishDrawer.querySelector("[data-ux-buy-finish]:checked")?.value;
    button.disabled = !picked;
    if (picked) button.textContent = this.t(this.stage, "chooseFinish", { finish: picked });
  }
  /* the Size drawer for a colour: the colour and finish (Change), the suggestion, the product page's size rows, and
     what's chosen with its subtotal */
  openSizes(owner) {
    if (!this.cards.has(owner) || !this.sizeDrawer) return;
    this.drawerFor = owner;
    this.fillSizes();
    this.sizeDrawer.show();
  }
  fillSizes() {
    const card = this.cards.get(this.drawerFor);
    if (!card || !this.sizeDrawer) return;
    const { colour, product, finish, picks, suggestion, line } = card;
    this.sizeDrawer.querySelector("[data-ux-buy-size-chip]").style.background = colour.c || product.colour || "var(--ux-grey)";
    this.sizeDrawer.querySelector("[data-ux-buy-size-context]").textContent = `${product.title || UxColourCard.fullName(colour)}  ·  ${finish.value}`;
    this.sizeDrawer.querySelector("[data-ux-buy-size-rows]").replaceChildren(this.sizeRows(colour.h, finish, picks, suggestion, line));
    const items = this.sizeDrawer.querySelector("[data-ux-buy-size-items]");
    items.textContent = this.summary(finish, picks) || items.dataset.empty;
    this.sizeDrawer.querySelector("[data-ux-buy-size-total]").textContent = this.money(this.subtotal(finish, picks));
  }
  summary(finish, picks) {
    return picks.map((pick) => `${pick.quantity} × ${this.sizes(finish).find((size) => size.id === pick.id)?.size || ""}`).join(", ");
  }
  subtotal(finish, picks) {
    return picks.reduce((total, pick) => total + (this.sizes(finish).find((size) => size.id === pick.id)?.price || 0) * pick.quantity, 0);
  }
  /* a selector row, as the product page's: its label over its value, a chevron; it opens its drawer */
  selectorRow(kind, owner, label, value, aria) {
    const el = (tag, className, props = {}) => Object.assign(document.createElement(tag), { className, ...props });
    const row = el("button", "ux-selector-row", { type: "button" });
    row.dataset.uxBuyOpen = kind;
    row.dataset.ownerFor = owner;
    row.setAttribute("aria-haspopup", "dialog");
    row.setAttribute("aria-expanded", "false");
    row.setAttribute("aria-label", aria);
    const text = el("span", "ux-selector-row__text");
    text.append(el("span", "ux-selector-row__label", { textContent: label }), el("span", "ux-selector-row__value", { textContent: value }));
    row.append(text, this.icon("chevron"));
    return row;
  }
  stepper(variantId, quantity, title) {
    const stepper = document.createElement("ux-qty-stepper");
    stepper.className = "ux-qty-stepper";
    stepper.dataset.variantId = variantId;
    const button = (step) => {
      const one = Object.assign(document.createElement("button"), { type: "button", className: "ux-qty-stepper__button" });
      one.dataset.step = String(step);
      one.setAttribute("aria-label", this.t(this.stage, step < 0 ? "less" : "more", { title }));
      if (step < 0 && quantity <= 0) one.setAttribute("aria-disabled", "true");
      one.append(this.icon(step < 0 ? "minus" : "plus"));
      return one;
    };
    const count = Object.assign(document.createElement("span"), { className: "ux-qty-stepper__count" });
    count.setAttribute("aria-live", "polite");
    count.append(Object.assign(document.createElement("span"), { className: "sr-only", textContent: this.t(this.stage, "quantity", { title }) }), Object.assign(document.createElement("span"), { textContent: String(quantity) }));
    count.lastChild.dataset.count = "";
    stepper.append(button(-1), count, button(1));
    return stepper;
  }
  /* the product page's size rows: size over coverage, the price, a stepper; the suggestion over them */
  sizeRows(owner, finish, picks, suggestion, line, { primer = false } = {}) {
    const el = (tag, className, props = {}) => Object.assign(document.createElement(tag), { className, ...props });
    const box = el("div", "ux-buyc__sizes");
    const differs = suggestion.length && (suggestion.length !== picks.length || suggestion.some((pick) => picks.find((one) => one.id === pick.id)?.quantity !== pick.quantity));
    if (line && suggestion.length) {
      const say = el("p", "ux-buyc__suggest");
      say.append(line);
      if (differs && !primer) {
        const use = el("button", "ux-buyc__use", { type: "button", textContent: this.stage.dataset.useSuggestion });
        use.dataset.uxBuySuggest = owner;
        say.append(" ", use);
      }
      box.append(say);
    }
    this.sizes(finish).forEach((size) => {
      const quantity = picks.find((pick) => pick.id === size.id)?.quantity || 0;
      const row = el("div", `ux-size-row${quantity ? " is-selected" : ""}`);
      row.dataset.owner = owner;
      row.dataset.variantId = size.id;
      const info = el("div", "ux-size-row__info");
      info.append(el("p", "ux-size-row__size", { textContent: size.size }));
      const area = size.area > 0 ? size.area : size.litres * this.perLitre(finish);
      if (area) {
        const covers = el("p", "ux-size-row__covers");
        const value = { area: Math.max(1, Math.ceil(area - 0.5)) };
        covers.append(el("span", "ux-size-row__covers-long", { textContent: this.t(this.stage, "covers", value) }), el("span", "ux-size-row__covers-short", { textContent: this.t(this.stage, "coversShort", value) }));
        info.append(covers);
      }
      row.append(info, el("p", "ux-size-row__price", { textContent: this.money(size.price) }));
      if (size.available) row.append(this.stepper(size.id, quantity, `${finish.value || ""} ${size.size}`.trim()));
      else row.append(el("p", "ux-size-row__sold-out", { textContent: this.stage.dataset.none }));
      box.append(row);
    });
    return box;
  }
  refocus(focus) {
    requestAnimationFrame(() => {
      let target = null;
      /* a stepper's row is in the Size drawer while it's open, else on the stage */
      const root = this.sizeDrawer?.open ? this.sizeDrawer : this.stage;
      if (focus.amount) target = this.stage.querySelector(`[data-ux-buy-amount="${CSS.escape(focus.amount)}"]`);
      else if (focus.owner) target = root.querySelector(`[data-owner="${CSS.escape(focus.owner)}"] ux-qty-stepper[data-variant-id="${focus.variant}"] [data-step="${focus.step || "1"}"]`) || root.querySelector(`[data-owner="${CSS.escape(focus.owner)}"] ux-qty-stepper[data-variant-id="${focus.variant}"] [data-step="1"]`);
      target?.focus({ preventScroll: true });
    });
  }
  drawOrder(palette) {
    const side = this.side;
    side.querySelector("[data-ux-buy-name]").textContent = palette.name;
    side.querySelector("[data-ux-buy-lines]").replaceChildren(...this.lines.map((line) => {
      const item = Object.assign(document.createElement("li"), { className: "ux-order-side__line" });
      item.append(Object.assign(document.createElement("span"), { textContent: line.size ? `${line.label} · ${line.quantity} × ${line.size}` : `${line.label}${line.quantity > 1 ? ` × ${line.quantity}` : ""}` }), Object.assign(document.createElement("span"), { textContent: this.money(line.price * line.quantity) }));
      return item;
    }));
    side.querySelector("[data-ux-buy-total]").textContent = this.money(this.lines.reduce((total, line) => total + line.price * line.quantity, 0));
    const add = side.querySelector("[data-ux-buy-add]");
    const count = this.lines.reduce((total, line) => total + line.quantity, 0);
    add.textContent = count ? this.t(side, "add", { count }) : side.dataset.addNone;
    add.disabled = !count;
  }
  async add(button) {
    if (button.getAttribute("aria-busy") === "true" || !this.lines?.length) return;
    const notice = this.side.querySelector("[data-ux-buy-notice]");
    notice.hidden = true;
    button.setAttribute("aria-busy", "true");
    const merged = new Map();
    this.lines.forEach((line) => merged.set(line.id, (merged.get(line.id) || 0) + line.quantity));
    try {
      await UxPaletteSamples.post([...merged].map(([id, quantity]) => ({ id, quantity })));
      const count = [...merged.values()].reduce((total, quantity) => total + quantity, 0);
      notice.textContent = this.t(this.side, "added", { count });
      notice.hidden = false;
      UxPaletteTrack("palette_paint_added", { palette: this.paletteId, lines: merged.size, count, source: "buy" });
    } catch (error) {
      notice.textContent = this.side.dataset.failed;
      notice.hidden = false;
    } finally {
      button.removeAttribute("aria-busy");
    }
  }
};
window.UxBuyView = UxBuyView;

/* ---------------------------------------------------------------------------
   The wall of colour and its dock (Palettes v2, GitHub #183; sections/ux-palette.liquid <ux-colour-wall>, inside the
   colour card). Every colour as a square in UxColours.wallColumns order: on desktop the columns run light to dark and
   the rows are chosen so the squares come out as big as the space allows (24 x 9 at 1440 x 900); on mobile, rows of 6
   that scroll. Find a colour and Spotlight a family dim the rest to 15% (the wall never reflows). A tap adds to the
   dock's "Adding here" palette or takes it out (the card's own toggle: notice, Undo, a full palette says so); a tile
   drags onto any card ("Drop to add"); a slot drags to another card to copy it (Alt/Option moves it); a filled slot
   removes on click, with Undo. Tick = in Adding here; dot = in another palette on show. Keyboard: the tiles are an
   ARIA grid (arrow keys, Home/End, Enter adds or takes out, Shift+Enter Save to…); the dock is a radio group. Mobile:
   the dock swipes, and the card it settles on becomes Adding here. On desktop (MP3-8, GitHub #415) a click on Adding
   here (so a double-click on any card), or Enter on it, opens that palette in the palette drawer: roles, order,
   rename, samples.
   --------------------------------------------------------------------------- */
var UxColourWall = class extends HTMLElement {
  connectedCallback() {
    if (this._wired) return;
    this._wired = true;
    this.card = this.closest("ux-colour-card");
    this.page = this.closest("ux-palette-page");
    this.area = this.querySelector("[data-ux-wall-area]");
    this.grid = this.querySelector("[data-ux-wall-grid]");
    this.tip = this.querySelector("[data-ux-wall-tip]");
    this.cards = this.querySelector("[data-ux-dock-cards]");
    this.showing = this.querySelector("[data-ux-dock-showing]");
    this.find = this.card.querySelector("[data-ux-wall-find]");
    this.spot = this.card.querySelector("[data-ux-wall-spot]");
    this.wide = window.matchMedia("(min-width: 768px)");
    /* the colour panel (#184): beside the wall from 1000 px (was 1200, #451); narrower, press and hold a tile for the
       colour sheet */
    this.panel = this.querySelector("ux-colour-panel");
    this.roomy = window.matchMedia("(min-width: 1000px)");
    this.colourSheet = this.page?.querySelector("[data-ux-colour-sheet]");
    this.grid.addEventListener("pointerdown", (event) => this.holdStart(event));
    this.grid.addEventListener("contextmenu", (event) => event.target.closest(".ux-wtile") && event.preventDefault());
    this.addEventListener("keydown", (event) => {
      if (event.key === "Escape" && this.panel?.pinned && this.roomy.matches && !this.closest("dialog")) {
        event.preventDefault();
        this.panel.unpin();
      }
    });
    this.roomy.addEventListener("change", (event) => event.matches && this.colourSheet?.open && this.colourSheet.close());
    /* from 1000 px Search and Colour sit at the top of the colour panel (Matt, 19 Oct); narrower, in the bar */
    this.tools = this.card.querySelector("[data-ux-wall-tools]");
    this.toolsHome = this.tools?.parentElement;
    const placeTools = () => {
      const slot = this.panel?.querySelector("[data-ux-cpanel-tools]");
      if (!this.tools || !slot) return;
      (this.roomy.matches ? slot : this.toolsHome).append(this.tools);
    };
    placeTools();
    this.roomy.addEventListener("change", placeTools);
    this.grid.addEventListener("click", (event) => this.onTileClick(event));
    this.grid.addEventListener("keydown", (event) => this.onTileKey(event));
    this.grid.addEventListener("pointerdown", (event) => this.dragStart(event, "tile"));
    /* the panel previews a tile (and fetches its photos) only once the pointer has rested on it, 300 ms without
       moving (#472): a sweep across the wall fetches nothing, and leaving a tile first calls it off */
    const rest = (tile) => {
      clearTimeout(this.previewTimer);
      if (tile && this.panelShown()) this.previewTimer = setTimeout(() => this.panel.show(tile.dataset.handle), 300);
    };
    this.grid.addEventListener("pointerover", (event) => {
      if (event.pointerType !== "mouse") return;
      const tile = event.target.closest(".ux-wtile");
      this.showTip(tile);
      this.markHolders(tile?.dataset.handle);
      rest(tile);
    });
    this.grid.addEventListener("pointermove", (event) => {
      if (event.pointerType !== "mouse") return;
      const tile = event.target.closest(".ux-wtile");
      if (tile) rest(tile);
    });
    this.grid.addEventListener("pointerout", (event) => {
      if (event.pointerType !== "mouse" || event.relatedTarget?.closest?.(".ux-wtile")) return;
      this.hideTip();
      this.markHolders(null);
      clearTimeout(this.previewTimer);
    });
    this.grid.addEventListener("focusin", (event) => {
      const tile = event.target.closest(".ux-wtile");
      if (!tile?.matches(":focus-visible")) return;
      this.showTip(tile);
      this.markHolders(tile.dataset.handle);
      if (this.panelShown()) this.panel.show(tile.dataset.handle);
    });
    this.grid.addEventListener("focusout", () => {
      this.hideTip();
      this.markHolders(null);
    });
    this.cards.addEventListener("click", (event) => this.onCardClick(event));
    this.cards.addEventListener("keydown", (event) => this.onCardKey(event));
    this.cards.addEventListener("pointerdown", (event) => this.dragStart(event, "slot"));
    let settle = 0;
    this.cards.addEventListener("scroll", () => {
      if (this.wide.matches) return;
      clearTimeout(settle);
      settle = setTimeout(() => this.settle(), 140);
    }, { passive: true });
    this.showing.addEventListener("change", () => this.update());
    /* phones: the dock is one line (the palette you're adding to, and how full it is); it opens to swipe between them */
    this.dockToggle = this.querySelector("[data-ux-dock-toggle]");
    this.dockToggle?.addEventListener("click", () => this.openDock(!this.dockOpen()));
    /* open, it closes on Escape (focus back to the bar from inside it) or a press outside it (#471) */
    if (this.dockToggle) {
      const dock = this.dockToggle.closest(".ux-dock");
      document.addEventListener("keydown", (event) => {
        if (event.key !== "Escape" || !this.dockOpen()) return;
        const inside = dock.contains(document.activeElement);
        this.openDock(false);
        if (inside) {
          event.stopPropagation();
          this.dockToggle.focus();
        }
      });
      document.addEventListener("pointerdown", (event) => this.dockOpen() && !dock.contains(event.target) && this.openDock(false));
    }
    this.querySelector("[data-ux-dock-new]").addEventListener("click", (event) => UxPaletteUi.newPalette({ from: event.currentTarget, onCreate: (id) => {
      this.choose(id);
      this.openDock(false);
    } }));
    this.querySelector("[data-ux-dock-plan]")?.addEventListener("click", () => {
      this.openDock(false);
      this.setView("palette");
      requestAnimationFrame(() => this.querySelector('[data-ux-app-stage="palette"] [data-ux-stage-heading]')?.focus({ preventScroll: true }));
    });
    this.querySelector("[data-ux-dock-samples]")?.addEventListener("click", (event) => {
      if (this.appActive()) {
        event.preventDefault();
        this.toSamples();
        return;
      }
      const target = document.getElementById("ux-palette-try");
      if (!target || target.hidden) return;
      event.preventDefault();
      target.scrollIntoView({ behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth", block: "start" });
    });
    /* THE PALETTE APP (#447): from 1000 px the tabs switch the stage between Colours, Samples and Palette, the view in
       the URL (?tab=), Back included. Until #448/#449 Samples holds Try your palette at home and Palette the pairs and
       Share and advice, moved in from under the wall (and back below 1000, where the page still scrolls). */
    this.tabs = [...this.querySelectorAll("[data-ux-app-tab]")];
    /* Samples (#448): every palette (Matt, 19 Oct), the order draws them; Palette (#449): the Adding here palette */
    this.order = new UxSampleOrder(this);
    this.paletteView = new UxPaletteView(this);
    this.buyView = new UxBuyView(this);
    document.addEventListener("ux-palette:change", () => this.view === "palette" && this.paletteView.render(this.adding()?.id));
    document.addEventListener("ux-palette:change", () => this.view === "buy" && this.buyView.render());
    document.addEventListener("ux-palette:change", () => this.view === "samples" && this.order.render(this.ticks()));
    this.stages = { colours: this.area, samples: this.querySelector('[data-ux-app-stage="samples"]'), palette: this.querySelector('[data-ux-app-stage="palette"]'), buy: this.querySelector('[data-ux-app-stage="buy"]') };
    this.tabs.forEach((tab) => {
      tab.addEventListener("click", () => this.setView(tab.dataset.uxAppTab));
      tab.addEventListener("keydown", (event) => this.onTabKey(event));
    });
    /* Back/Forward (#464): the entry's palette, way into Discover and view, in that order */
    window.addEventListener("popstate", () => {
      const id = new URLSearchParams(window.location.search).get("palette");
      if (id && id !== this.adding()?.id && UxPalette.palette(id)) {
        if (this.page?.open) this.page.open(id);
        else this.card.open(id);
      }
      this.discover = this.urlDiscover();
      this.setView(this.urlView(), { push: false });
    });
    /* Discover's ways in (Matt, 19 Oct): All colours, Colour cards (the shelf; a card lays the wall out as printed),
       Palettes (ready-made); ?discover=cards|palettes */
    this.shelf = this.querySelector("[data-ux-shelf]");
    this.readyStage = this.querySelector('[data-ux-app-stage="ready"]');
    this.discover = this.urlDiscover();
    this.querySelectorAll("[data-ux-discover-way]").forEach((button) => button.addEventListener("click", () => this.setDiscover(button.dataset.uxDiscoverWay)));
    this.shelf?.addEventListener("click", (event) => {
      const card = event.target.closest("[data-ux-shelf-card]");
      if (!card || card.dataset.uxShelfCard === this.cardKey) return;
      this.cardKey = card.dataset.uxShelfCard;
      this.buildShelf();
      this.shape = "";
      this.layout();
      this.dim();
    });
    this.wide.addEventListener("change", () => {
      this.shape = "";
      this.layout();
    });
    this.placeSections();
    this.setView(this.urlView(), { push: false });
    this.find.addEventListener("input", () => this.dim());
    this.find.addEventListener("keydown", (event) => event.key === "Enter" && event.preventDefault());
    this.spot.addEventListener("change", () => this.dim());
    /* Colour opens the filter drawer; a family or shade picked there filters the wall behind it at once */
    this.filter = this.card.querySelector(".ux-cfilter");
    this.spotOpen = this.card.querySelector("[data-ux-wall-spot-open]");
    this.spotOpen?.addEventListener("click", () => this.filter?.show());
    this.filter?.addEventListener("toggle", () => this.spotOpen?.setAttribute("aria-expanded", String(this.filter.open)));
    this.filter?.addEventListener("change", (event) => {
      if (!event.target.matches("[data-ux-cfilter]")) return;
      this.spot.value = event.target.value;
      this.spot.dispatchEvent(new Event("change"));
      /* a family chosen: its shades, below, into view */
      const shades = this.filter.querySelector("[data-ux-cfilter-shades-box]");
      if (event.target.name === "ux-cfilter-family" && !shades.hidden) shades.scrollIntoView({ block: "nearest", behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth" });
    });
    this.filter?.addEventListener("click", (event) => {
      if (event.target.closest("[data-ux-cfilter-clear]")) {
        this.spot.value = "";
        this.spot.dispatchEvent(new Event("change"));
      } else if (event.target.closest("[data-ux-cfilter-done]")) this.filter.hide();
    });
    document.addEventListener("ux-palette:change", () => !this.hidden && this.update());
    new ResizeObserver(() => !this.hidden && this.layout()).observe(this.area);
    this.wide.addEventListener("change", () => !this.hidden && this.layout());
    /* a page that opens on the wall (the remembered view) */
    if (!this.hidden && this.page && !this.page.shared) this.start().then(() => this.reveal());
  }
  t(key, values = {}) {
    return Object.entries(values).reduce((string, [name, value]) => string.split(`[${name}]`).join(value), this.dataset[key] || "");
  }
  adding() {
    return this.card.palette();
  }

  /* the first time the wall shows: one tile per colour, then its layout (till then the grid is busy, and the Mylands
     loader shows, #429); if the colours can't load, it says so */
  start() {
    const error = this.querySelector("[data-ux-wall-error]");
    return UxColours.load().then((data) => {
      if (error) error.hidden = true;
      if (!this.data) this.build(data);
      if (!this.matrix) this.layout();
      if (this.discover === "cards") this.showDiscover(); /* the shelf needs the data's cards */
      this.update();
      /* Plan's Pairings buttons and numbers come from the colours' data */
      if (this.view === "palette") this.paletteView.render(this.adding()?.id);
    }).catch(() => {
      this.grid.removeAttribute("aria-busy");
      if (error) error.hidden = false;
    });
  }
  build(data) {
    this.data = data;
    this.byHandle = new Map(data.colours.map((colour) => [colour.h, colour]));
    this.tiles = new Map(data.colours.map((colour) => {
      const tile = document.createElement("div");
      tile.className = "ux-wtile";
      tile.setAttribute("role", "gridcell");
      tile.tabIndex = -1;
      tile.dataset.handle = colour.h;
      tile.style.backgroundColor = colour.c || "";
      if (colour.i) {
        tile.classList.add("ux-wtile--metal");
        tile.style.backgroundImage = `url("${colour.i}")`;
      }
      return [colour.h, tile];
    }));
    this.tiles.values().next().value.tabIndex = 0;
    /* Colour (#445; Figma 808:9538, 05): All colours, then each family as a group, "All greens · 33" and its shade
       groups (Theme settings › Palette › Shade groups, under the family their name ends in), "Sage Green · 5" */
    const onWall = new Set(data.colours.map((colour) => colour.h));
    this.shades = new Map((data.shades || []).map((shade) => [shade.h, new Set(shade.c)]));
    UxColours.byColour(data).forEach((family) => {
      const group = document.createElement("optgroup");
      group.label = family.label;
      group.append(new Option(this.t("family", { family: family.label.toLowerCase(), count: family.colours.length }), `family:${family.slug}`));
      (data.shades || []).filter((shade) => shade.f === family.slug).forEach((shade) => {
        const count = shade.c.filter((handle) => onWall.has(handle)).length;
        if (count) group.append(new Option(this.t("shade", { shade: shade.l, count }), `shade:${shade.h}`));
      });
      this.spot.append(group);
    });
    this.families = UxColours.byColour(data);
    this.drawFilter();
  }
  /* the filter drawer's families: a square of a middling colour from each, its name and count */
  drawFilter() {
    const box = this.filter?.querySelector("[data-ux-cfilter-families]");
    if (!box) return;
    const el = (tag, className, props = {}) => Object.assign(document.createElement(tag), { className, ...props });
    box.replaceChildren(...this.families.map((family) => {
      const option = el("label", "ux-cfilter__family");
      const input = el("input", "sr-only", { type: "radio", name: "ux-cfilter-family", value: `family:${family.slug}` });
      input.dataset.uxCfilter = "";
      const swatch = el("span", "ux-cfilter__swatch");
      swatch.style.background = family.colours[Math.floor(family.colours.length / 2)]?.c || "var(--ux-grey)";
      option.append(input, swatch, el("span", "ux-cfilter__name", { textContent: family.label }), el("span", "ux-cfilter__count", { textContent: String(family.colours.length) }));
      return option;
    }));
    this.syncFilter();
  }
  /* the drawer follows the choice: its family ticked, that family's shades listed (All, then each), the count */
  syncFilter(count = this.matching ? this.matching.size : this.tiles?.size || 0) {
    if (!this.filter || !this.families) return;
    const el = (tag, className, props = {}) => Object.assign(document.createElement(tag), { className, ...props });
    const [kind, key] = this.spot.value.split(":");
    const slug = kind === "family" ? key : kind === "shade" ? (this.data.shades || []).find((shade) => shade.h === key)?.f : "";
    this.filter.querySelectorAll('[name="ux-cfilter-family"]').forEach((input) => (input.checked = input.value === `family:${slug}`));
    const shadesBox = this.filter.querySelector("[data-ux-cfilter-shades-box]");
    const group = slug ? [...this.spot.querySelectorAll("optgroup")].find((one) => one.querySelector(`option[value="family:${CSS.escape(slug)}"]`)) : null;
    shadesBox.hidden = !group || group.children.length < 2;
    if (this.filterFamily !== slug) {
      this.filterFamily = slug;
      this.filter.querySelector("[data-ux-cfilter-shades]").replaceChildren(...[...(group?.children || [])].map((option) => {
        const label = el("label", "ux-cfilter__shade");
        const input = el("input", "", { type: "radio", name: "ux-cfilter-shade", value: option.value });
        input.dataset.uxCfilter = "";
        label.append(input, el("span", "", { textContent: option.text }));
        return label;
      }));
    }
    this.filter.querySelectorAll('[name="ux-cfilter-shade"]').forEach((input) => (input.checked = input.value === this.spot.value));
    const done = this.filter.querySelector("[data-ux-cfilter-done]");
    done.textContent = done.dataset.label.replace("[count]", count);
    this.filter.querySelector("[data-ux-cfilter-clear]").hidden = !kind;
  }
  /* Colour cards (#431's data): the open card's printed columns, left to right across its sections (a narrow gap
     between sections, each headed where the card heads it), each column top to bottom with blanks where the card has
     them; squares as big as the space beside the shelf allows. Colours not on the card stay off the wall. */
  cardLayout() {
    const cards = this.data.cards;
    const card = cards.shelf.find((one) => one.key === this.cardKey) || cards.shelf[0];
    const style = getComputedStyle(this.area);
    /* the shelf beside the wall from 1200, a row of covers above it under (tablets) */
    const above = style.flexDirection === "column";
    const width = this.area.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight) - (above ? 0 : (this.shelf?.offsetWidth || 0) + (parseFloat(style.columnGap) || 24));
    const fullHeight = this.area.clientHeight - parseFloat(style.paddingTop) - parseFloat(style.paddingBottom) - (above ? (this.shelf?.offsetHeight || 0) + (parseFloat(style.rowGap) || 16) : 0);
    if (width <= 0 || fullHeight <= 0) return;
    const columns = [];
    const heads = [];
    card.sections.forEach((section, index) => {
      if (index) columns.push("gap");
      const start = columns.length;
      const printed = cards.systems[section.system]?.columns || [];
      printed.forEach((column) => columns.push(column));
      if (section.heading) heads.push({ text: section.heading, column: start, span: printed.length });
      (section.columnHeadings || []).forEach((text, at) => text && heads.push({ text, column: start + at, span: 1, small: true }));
    });
    const rows = Math.max(...columns.filter((column) => column !== "gap").map((column) => column.length));
    const headRow = heads.length ? 48 : 0; /* two lines, for a heading over narrow columns ("Archive Collection") */
    const height = fullHeight - headRow;
    const gaps = columns.filter((column) => column === "gap").length;
    const size = Math.floor(Math.min((width - (columns.length - 1) * 8) / (columns.length - gaps * 0.5), (height - (rows - 1) * 8) / rows));
    if (size <= 0) return;
    const key = `cards:${card.key}:${size}`;
    if (this.shape === key) return;
    this.shape = key;
    this.grid.style.gridTemplateColumns = columns.map((column) => (column === "gap" ? `${Math.round(size / 2)}px` : `${size}px`)).join(" ");
    this.grid.style.gridTemplateRows = `${headRow ? `${headRow - 8}px ` : ""}repeat(${rows}, ${size}px)`;
    /* the shelf one height whichever card is open (the room there is), never too short for its longest spine */
    if (!above) this.shelf?.style.setProperty("--ux-shelf-height", `${Math.max(320, Math.floor(fullHeight))}px`);
    const offset = headRow ? 1 : 0;
    this.matrix = Array.from({ length: rows }, (_, row) => columns.map((column) => (column === "gap" ? null : this.tiles.get(column[row]) || null)));
    const focused = this.grid.contains(document.activeElement) ? document.activeElement : null;
    const lines = this.matrix.map((cells, row) => {
      const line = Object.assign(document.createElement("div"), { className: "ux-wall__row" });
      line.setAttribute("role", "row");
      cells.forEach((tile, column) => {
        if (!tile) return;
        tile.style.gridArea = `${row + 1 + offset} / ${column + 1}`;
        tile.dataset.row = row;
        tile.dataset.column = column;
        line.append(tile);
      });
      return line;
    });
    const headings = heads.map((head) => {
      const label = Object.assign(document.createElement("span"), { className: `ux-wall__head${head.small ? " ux-wall__head--column" : ""}`, textContent: head.text });
      label.setAttribute("aria-hidden", "true");
      label.style.gridArea = `1 / ${head.column + 1} / 2 / span ${head.span}`;
      return label;
    });
    this.grid.replaceChildren(...headings, ...lines);
    this.grid.removeAttribute("aria-busy");
    focused?.isConnected && focused.focus({ preventScroll: true });
    this.rove();
  }
  /* Colour cards in the mobile sheet: the open card's printed columns as rows of the six-across wall, in the card's
     order (no card's column runs past six), blanks where the card has them; a section's heading and a column's each
     take a row of their own above it */
  cardRows() {
    const cards = this.data.cards;
    const card = cards.shelf.find((one) => one.key === this.cardKey) || cards.shelf[0];
    const key = `cards:${card.key}:rows`;
    if (this.shape === key) return;
    this.shape = key;
    this.grid.style.gridTemplateColumns = "";
    this.grid.style.gridTemplateRows = "";
    const items = [];
    card.sections.forEach((section) => {
      if (section.heading) items.push({ head: section.heading });
      (cards.systems[section.system]?.columns || []).forEach((column, at) => {
        const text = section.columnHeadings?.[at];
        if (text) items.push({ head: text, small: true });
        for (let start = 0; start < column.length; start += 6) items.push({ cells: column.slice(start, start + 6).map((handle) => (handle && this.tiles.get(handle)) || null) });
      });
    });
    this.matrix = [];
    const headings = [];
    const lines = [];
    items.forEach((item, at) => {
      const line = at + 1;
      if (item.head) {
        const label = Object.assign(document.createElement("span"), { className: `ux-wall__head${item.small ? " ux-wall__head--column" : ""}`, textContent: item.head });
        label.setAttribute("aria-hidden", "true");
        label.style.gridArea = `${line} / 1 / ${line + 1} / -1`;
        headings.push(label);
        return;
      }
      const row = this.matrix.length;
      this.matrix.push(item.cells);
      const element = Object.assign(document.createElement("div"), { className: "ux-wall__row" });
      element.setAttribute("role", "row");
      item.cells.forEach((tile, column) => {
        if (!tile) return;
        tile.style.gridArea = `${line} / ${column + 1}`;
        tile.dataset.row = row;
        tile.dataset.column = column;
        element.append(tile);
      });
      lines.push(element);
    });
    const focused = this.grid.contains(document.activeElement) ? document.activeElement : null;
    this.grid.replaceChildren(...headings, ...lines);
    this.grid.removeAttribute("aria-busy");
    focused?.isConnected && focused.focus({ preventScroll: true });
    this.rove();
  }
  /* desktop: columns of `rows`, light to dark, squares as big as the area allows; mobile: rows of 6. Searching or
     choosing a Colour, only the matches, laid out the same way (Matt, 22 Oct: not the rest faded) */
  layout() {
    if (!this.data) return;
    let chunks;
    let size = 0;
    if (this.area.classList.contains("ux-wall__area--cards")) {
      if (this.wide.matches) this.cardLayout();
      else this.cardRows();
      return;
    }
    const data = this.matching ? { ...this.data, colours: this.data.colours.filter((colour) => this.matching.has(colour.h)) } : this.data;
    this.area.querySelector("[data-ux-wall-none]").hidden = data.colours.length > 0;
    if (!data.colours.length) {
      this.shape = "none";
      this.matrix = [];
      this.grid.replaceChildren();
      this.grid.removeAttribute("aria-busy");
      return;
    }
    if (this.wide.matches) {
      const style = getComputedStyle(this.area);
      const width = this.area.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight);
      const height = this.area.clientHeight - parseFloat(style.paddingTop) - parseFloat(style.paddingBottom);
      if (width <= 0 || height <= 0) return;
      for (let rows = 1; rows <= 16; rows += 1) {
        const columns = UxColours.wallColumns(data, rows);
        /* a few matches don't grow into posters */
        const fit = Math.min(120, Math.floor(Math.min((width - (columns.length - 1) * 8) / columns.length, (height - (rows - 1) * 8) / rows)));
        if (fit > size) {
          size = fit;
          chunks = columns;
        }
      }
      if (!chunks) return;
      const rows = Math.max(...chunks.map((column) => column.length));
      if (this.shape === `${rows}x${size}`) return;
      this.shape = `${rows}x${size}`;
      this.grid.style.gridTemplateColumns = `repeat(${chunks.length}, ${size}px)`;
      this.grid.style.gridTemplateRows = `repeat(${rows}, ${size}px)`;
      this.matrix = Array.from({ length: rows }, (_, row) => chunks.map((column) => column[row] ? this.tiles.get(column[row].h) : null));
    } else {
      if (this.shape === "6") return;
      this.shape = "6";
      chunks = UxColours.wallColumns(data, 6);
      this.grid.style.gridTemplateColumns = "";
      this.grid.style.gridTemplateRows = "";
      this.matrix = chunks.map((row) => row.map((colour) => this.tiles.get(colour.h)));
    }
    /* ARIA rows in reading order; the squares sit where the grid puts them (moving them keeps focus where it was) */
    const focused = this.grid.contains(document.activeElement) ? document.activeElement : null;
    this.grid.replaceChildren(...this.matrix.map((cells, row) => {
      const line = document.createElement("div");
      line.setAttribute("role", "row");
      line.className = "ux-wall__row";
      cells.forEach((tile, column) => {
        if (!tile) return;
        tile.style.gridArea = `${row + 1} / ${column + 1}`;
        tile.dataset.row = row;
        tile.dataset.column = column;
        line.append(tile);
      });
      return line;
    }));
    this.grid.removeAttribute("aria-busy");
    focused?.focus({ preventScroll: true });
    this.rove();
  }

  /* ticks, dots, names; the dock */
  shownPalettes() {
    const showing = this.showing.value;
    return UxPalette.palettes().filter((palette) => !showing || (showing === "none" ? !palette.project : palette.project === showing));
  }
  update() {
    if (!this.data) return;
    const adding = this.adding();
    const shown = this.shownPalettes();
    if (adding && !shown.some((palette) => palette.id === adding.id)) shown.unshift(adding);
    const holders = new Map();
    shown.forEach((palette) => palette.colours.forEach((colour) => {
      if (!holders.has(colour.h)) holders.set(colour.h, []);
      holders.get(colour.h).push(palette);
    }));
    this.tiles.forEach((tile, handle) => {
      const held = holders.get(handle) || [];
      const inAdding = Boolean(adding && held.some((palette) => palette.id === adding.id));
      tile.classList.toggle("ux-wtile--in", inAdding);
      tile.classList.toggle("ux-wtile--other", held.some((palette) => palette.id !== adding?.id));
      tile.setAttribute("aria-selected", String(inAdding));
      const names = held.map((palette) => palette.name).join(", ");
      tile.setAttribute("aria-label", [UxColourCard.fullName(this.byHandle.get(handle)), names && this.t("in", { names })].filter(Boolean).join(", "));
    });
    this.renderDock(shown, adding);
  }
  renderDock(shown, adding) {
    /* Showing: All projects, each project, Not in a project */
    const value = this.showing.value;
    const projects = UxPalette.projects();
    const options = [["", this.dataset.all], ...projects.map((project) => [project.id, project.name])];
    if (UxPalette.palettes().some((palette) => !palette.project)) options.push(["none", UxPaletteUi.text("loose")]);
    if (this.showing.options.length !== options.length || options.some(([id, name], index) => this.showing.options[index]?.value !== id || this.showing.options[index]?.text !== name)) {
      this.showing.replaceChildren(...options.map(([id, name]) => new Option(name, id)));
      this.showing.value = options.some(([id]) => id === value) ? value : "";
    }
    const key = shown.map((palette) => palette.id).join(",");
    if (this.cards.dataset.key !== key) {
      this.cards.dataset.key = key;
      this.cards.replaceChildren(...shown.map((palette) => this.dockCard(palette)));
    }
    shown.forEach((palette) => {
      const card = this.cards.querySelector(`[data-palette="${CSS.escape(palette.id)}"]`);
      if (!card) return;
      const on = palette.id === adding?.id;
      const project = palette.project ? UxPalette.project(palette.project)?.name || "" : UxPalette.projectsShown ? UxPaletteUi.text("loose") : "";
      card.classList.toggle("ux-dcard--active", on);
      card.setAttribute("aria-checked", String(on));
      card.tabIndex = on ? 0 : -1;
      /* without a project (projects are for consultancies only) its part goes, not "Kitchen, , 3 of 12" (#471) */
      card.setAttribute("aria-label", this.t("cardLabel", { name: palette.name, project, count: palette.colours.length }).split(", ").filter((part) => part.trim()).join(", "));
      card.querySelector("[data-ux-dcard-project]").textContent = project;
      card.querySelector("[data-ux-dcard-name]").textContent = palette.name;
      /* not saved to the account: it had 30 when this was made (#476) */
      const held = UxPalette.isHeld(palette.id);
      const heldLine = card.querySelector("[data-ux-dcard-held]");
      heldLine.hidden = !held;
      heldLine.textContent = held ? this.t("cardHeld") : "";
      card.classList.toggle("ux-dcard--held", held);
      if (held) card.setAttribute("aria-label", `${card.getAttribute("aria-label")}, ${this.t("cardHeld")}`);
      card.querySelectorAll(".ux-dcard__slot").forEach((slot, index) => {
        const colour = palette.colours[index];
        slot.classList.toggle("ux-dcard__slot--filled", Boolean(colour));
        slot.style.background = colour ? colour.c || "var(--ux-grey)" : "";
        slot.dataset.handle = colour?.h || "";
        slot.title = colour ? this.t("slotRemove", { colour: UxColourCard.label(colour) }) : "";
      });
    });
    if (!this.cards.querySelector('[tabindex="0"]')) this.cards.querySelector(".ux-dcard")?.setAttribute("tabindex", "0");
    this.markHolders(this.holding);
    /* the Adding here palette's samples are under the wall, out of sight: once it has colours, the dock points to them
       (#446). Not in the mobile sheet, which covers the page. */
    if (this.dockToggle) {
      /* no palette yet: "Your palettes", not a bare chevron (#471) */
      this.dockToggle.querySelector("[data-ux-dock-toggle-text]").textContent = adding ? this.dockToggle.dataset.label.replace("[name]", adding.name).replace("[count]", adding.colours.length).replace("[limit]", UxPalette.LIMITS.colours) : this.dockToggle.dataset.empty || "";
    }
    const samples = this.querySelector("[data-ux-dock-samples]");
    if (samples) {
      const count = adding?.colours.length || 0;
      /* in the app, Order samples is the side panel's (Matt, 22 Oct): the dock's link is for the page without it */
      samples.hidden = !count || Boolean(this.closest("dialog")) || this.view === "samples" || this.appActive();
      samples.querySelector("[data-ux-dock-samples-text]").textContent = samples.dataset.label.replace("[count]", count);
    }
    /* View in Plan (Matt, 22 Oct): the palette in use, in Plan */
    const plan = this.querySelector("[data-ux-dock-plan]");
    if (plan) plan.hidden = !adding || !this.appActive();
  }
  dockOpen() {
    return this.dockToggle?.getAttribute("aria-expanded") === "true";
  }
  openDock(open) {
    if (!this.dockToggle) return;
    this.dockToggle.setAttribute("aria-expanded", String(open));
    this.dockToggle.closest(".ux-dock").classList.toggle("ux-dock--open", open);
  }
  /* the colour under the pointer (or keyboard focus) lights up the dock's palettes that hold it, and its slot on each
     (#441); kept across a re-render, so a click that adds it lights its palette at once */
  markHolders(handle) {
    this.holding = handle || null;
    this.cards.querySelectorAll(".ux-dcard").forEach((card) => {
      let holds = false;
      card.querySelectorAll(".ux-dcard__slot").forEach((slot) => {
        const held = Boolean(this.holding) && slot.dataset.handle === this.holding;
        slot.classList.toggle("ux-dcard__slot--held", held);
        holds ||= held;
      });
      card.classList.toggle("ux-dcard--holds", holds);
    });
  }
  appActive() {
    return !this.closest("dialog");
  }
  urlDiscover() {
    const way = new URLSearchParams(window.location.search).get("discover");
    return ["cards", "palettes"].includes(way) ? way : "all";
  }
  setDiscover(way, { push = true } = {}) {
    this.discover = ["cards", "palettes"].includes(way) ? way : "all";
    if (this.view !== "colours" && this.appActive()) this.setView("colours", { push });
    else this.showDiscover({ push });
  }
  /* the mobile sheet (Add colours): All colours or Colour cards; ready-made palettes stay on the page there */
  inSheet() {
    return !this.wide.matches && Boolean(this.closest("dialog"));
  }
  /* which way into Discover is on show: the wall (with the shelf for Colour cards) or the ready-made palettes */
  showDiscover({ push = false, write = true } = {}) {
    const sheet = this.inSheet();
    const way = this.view === "colours" && this.appActive() ? this.discover : sheet && this.discover === "cards" ? "cards" : "all";
    this.querySelectorAll("[data-ux-discover-way]").forEach((button) => button.setAttribute("aria-pressed", String(button.dataset.uxDiscoverWay === way)));
    /* the Discover tab controls the panel on show: the wall, or Palettes' ready-made ones (#471) */
    const discoverTab = this.tabs?.find((tab) => tab.dataset.uxAppTab === "colours");
    if (discoverTab && this.readyStage?.id) discoverTab.setAttribute("aria-controls", way === "palettes" ? this.readyStage.id : this.area.id);
    if (this.view === "colours") {
      this.area.hidden = way === "palettes";
      if (this.readyStage) this.readyStage.hidden = way !== "palettes";
    } else if (this.readyStage) this.readyStage.hidden = true;
    const cards = way === "cards" && Boolean(this.data?.cards?.shelf?.length);
    this.area.classList.toggle("ux-wall__area--cards", cards);
    if (this.shelf) this.shelf.hidden = !cards;
    if (cards) this.buildShelf();
    this.shape = "";
    /* the wall shows only the matches, a card fades the rest: each way, as it opens */
    if (this.view === "colours" && way !== "palettes") requestAnimationFrame(() => {
      this.layout();
      this.dim();
    });
    /* setView writes the URL itself (write: false), once, so the entry it leaves keeps its ?discover (#464) */
    if (write && (this.appActive() || sheet)) {
      const url = new URL(window.location.href);
      if (way === "all" || this.view !== "colours") url.searchParams.delete("discover");
      else url.searchParams.set("discover", way);
      if (url.href !== window.location.href) window.history[push ? "pushState" : "replaceState"](window.history.state, "", url);
    }
  }
  /* the shelf: every card a spine in its cover colour, the open one its front cover. Built once; opening another only
     moves the state, and CSS does the motion, as the product page's slice gallery does (#374): the open card widens to
     its cover while the one it replaces narrows to a spine, and a spine widens a little on hover. */
  buildShelf() {
    const shelf = this.data?.cards?.shelf || [];
    if (!this.shelf || !shelf.length) return;
    if (!shelf.some((card) => card.key === this.cardKey)) this.cardKey = shelf[0].key;
    if (this.shelf.childElementCount !== shelf.length) {
      this.shelf.style.setProperty("--ux-shelf-n", shelf.length);
      this.shelf.replaceChildren(...shelf.map((card) => {
        const button = Object.assign(document.createElement("button"), { type: "button", className: `ux-shelf__card${card.ink === "dark" ? " ux-shelf__card--light" : ""}${card.border ? " ux-shelf__card--border" : ""}` });
        button.style.setProperty("--ux-cover", card.cover);
        button.dataset.uxShelfCard = card.key;
        button.setAttribute("aria-label", card.name);
        const cover = Object.assign(document.createElement("span"), { className: "ux-shelf__cover" });
        cover.setAttribute("aria-hidden", "true");
        cover.append(Object.assign(document.createElement("span"), { className: "ux-shelf__mylands", textContent: this.shelf.dataset.mylands }), Object.assign(document.createElement("span"), { className: "ux-shelf__name", textContent: card.name }));
        const spine = Object.assign(document.createElement("span"), { className: "ux-shelf__spine", textContent: card.spine });
        spine.setAttribute("aria-hidden", "true");
        button.append(spine, cover);
        return button;
      }));
    }
    this.shelf.querySelectorAll("[data-ux-shelf-card]").forEach((button) => {
      const open = button.dataset.uxShelfCard === this.cardKey;
      button.classList.toggle("is-open", open);
      button.setAttribute("aria-pressed", String(open));
    });
  }
  urlView() {
    /* ?tab=, not ?view=: Shopify's view picks the page's template (the dev route is /pages/contact?view=palette) */
    const view = new URLSearchParams(window.location.search).get("tab");
    return this.stages?.[view] ? view : "colours";
  }
  /* push: true adds a history entry, false replaces the current one */
  setView(view, { push = true } = {}) {
    if (!this.stages?.[view] || !this.appActive()) view = "colours";
    const changed = view !== this.view;
    this.view = view;
    this.tabs.forEach((tab) => {
      const on = tab.dataset.uxAppTab === view;
      tab.setAttribute("aria-selected", String(on));
      tab.tabIndex = on ? 0 : -1;
    });
    Object.entries(this.stages).forEach(([name, stage]) => stage && (stage.hidden = name !== view));
    this.dataset.view = view;
    if (changed) this.openDock(false);
    this.showDiscover({ write: !this.appActive() });
    if (changed && this.stages[view] && view !== "colours") this.stages[view].scrollTop = 0;
    if (view === "samples") this.ordering = this.order.render(this.ticks());
    if (view === "palette") this.paletteView.render(this.adding()?.id);
    if (view === "buy") {
      if (changed) this.buyView.paletteId = this.adding()?.id || this.buyView.paletteId;
      this.buyView.render();
    }
    if (changed) this.update();
    if (this.appActive()) {
      const url = new URL(window.location.href);
      if (view === "colours") url.searchParams.delete("tab");
      else url.searchParams.set("tab", view);
      if (view === "colours" && this.discover !== "all") url.searchParams.set("discover", this.discover);
      else url.searchParams.delete("discover");
      url.searchParams.delete("palettes");
      if (url.href !== window.location.href) window.history[push ? "pushState" : "replaceState"](window.history.state, "", url);
    }
    if (view === "colours") requestAnimationFrame(() => this.layout());
  }
  /* Samples holds every palette, in the book's order (Matt, 19 Oct) */
  ticks() {
    return UxPalette.palettes().map((palette) => palette.id);
  }
  /* Order samples (dock, colour panel, the Palette view): Samples, at that palette's group (else Adding here's) */
  toSamples(id) {
    this.setView("samples");
    this.order.reveal(id || this.adding()?.id);
    /* the button pressed is hidden now: focus goes to Try's heading once the order is drawn (#465) */
    Promise.resolve(this.ordering).then(() => this.view === "samples" && this.focusStage("samples"));
  }
  /* after a button switches the view, focus goes to the new stage's heading (#465); Discover has none, so its tab */
  focusStage(view = this.view) {
    const headings = view === "colours" ? [] : [...(this.stages?.[view]?.querySelectorAll("[data-ux-stage-heading]") || [])];
    const target = headings.find((heading) => !heading.closest("[hidden]")) || this.tabs.find((tab) => tab.dataset.uxAppTab === view);
    target?.focus({ preventScroll: true });
  }

  /* tabs: Left and Right (Home, End) move along and show the view, as a tablist does */
  onTabKey(event) {
    const at = this.tabs.indexOf(event.currentTarget);
    const to = { ArrowRight: at + 1, ArrowLeft: at - 1, Home: 0, End: this.tabs.length - 1 }[event.key];
    if (to === undefined) return;
    event.preventDefault();
    const tab = this.tabs[(to + this.tabs.length) % this.tabs.length];
    tab.focus();
    /* arrowing along the tabs replaces the entry rather than adding one per key (#464) */
    this.setView(tab.dataset.uxAppTab, { push: false });
  }
  /* #450: Colours that pair and Start from a palette go into the Palette stage from 1000 px, and back under the wall
     below it; Try it first and Share and advice give way to the Samples view and the Palette sidebar (CSS) */
  placeSections() {
    /* Colours that pair no longer goes into Plan: a colour's Pairings there do its job (Matt, 22 Oct) */
    const moves = { ready: ["[data-ux-palette-ideas]"] };
    this.homes = this.homes || new Map();
    Object.entries(moves).forEach(([view, selectors]) => selectors.forEach((selector) => {
      const section = this.page?.querySelector(selector);
      const stage = view === "ready" ? this.readyStage : this.stages[view];
      if (!section || !stage) return;
      if (!this.homes.has(section)) {
        const home = document.createComment(`ux-app home: ${selector}`);
        section.before(home);
        this.homes.set(section, home);
      }
      if (this.appActive()) stage.append(section);
      else this.homes.get(section).after(section);
    }));
  }
  dockCard(palette) {
    const card = document.createElement("div");
    card.className = "ux-dcard";
    card.setAttribute("role", "radio");
    card.dataset.palette = palette.id;
    const project = Object.assign(document.createElement("span"), { className: "ux-dcard__project" });
    project.dataset.uxDcardProject = "";
    const name = Object.assign(document.createElement("span"), { className: "ux-dcard__name" });
    name.dataset.uxDcardName = "";
    const held = Object.assign(document.createElement("span"), { className: "ux-dcard__held", hidden: true });
    held.dataset.uxDcardHeld = "";
    const slots = document.createElement("span");
    slots.className = "ux-dcard__slots";
    slots.setAttribute("aria-hidden", "true");
    for (let index = 0; index < UxPalette.LIMITS.colours; index += 1) slots.append(Object.assign(document.createElement("span"), { className: "ux-dcard__slot" }));
    card.append(project, name, held, slots);
    return card;
  }

  /* a tap: the card's own add-or-take-out, on the Adding here palette */
  onTileClick(event) {
    const tile = event.target.closest(".ux-wtile");
    if (!tile || this.dragged || this.held) return;
    this.focusTile(tile, false);
    this.card.toggle(tile.dataset.handle, "wall");
    if (this.panelShown()) this.panel.show(tile.dataset.handle, { pin: true });
  }
  panelShown() {
    return Boolean(this.panel && !this.hidden && this.roomy.matches && !this.panel.closest("dialog") && !this.closest("dialog"));
  }
  markPinned(handle) {
    this.tiles?.forEach((tile, key) => tile.classList.toggle("ux-wtile--pinned", key === handle));
  }
  /* desktop: Adding here opens in the palette drawer, where its roles, order, name and samples are (MP3-8) */
  opens() {
    return this.wide.matches && !this.closest("dialog");
  }
  /* press and hold a tile (narrower than 1200 px): the colour sheet */
  holdStart(event) {
    const tile = event.target.closest(".ux-wtile");
    if (!tile || this.panelShown() || event.button > 0) return;
    const x0 = event.clientX;
    const y0 = event.clientY;
    this.held = false;
    const timer = setTimeout(() => {
      this.held = true;
      stop();
      this.openColourSheet(tile);
    }, 500);
    const move = (moveEvent) => Math.hypot(moveEvent.clientX - x0, moveEvent.clientY - y0) > 8 && stop();
    const stop = () => {
      clearTimeout(timer);
      document.removeEventListener("pointermove", move);
      document.removeEventListener("pointerup", stop);
      document.removeEventListener("pointercancel", stop);
      if (this.held) setTimeout(() => (this.held = false), 400);
    };
    document.addEventListener("pointermove", move);
    document.addEventListener("pointerup", stop);
    document.addEventListener("pointercancel", stop);
  }
  openColourSheet(tile) {
    const sheet = this.colourSheet;
    if (!sheet || !this.panel) return;
    if (!sheet.dataset.panelWired) {
      sheet.dataset.panelWired = "true";
      UxPaletteUi.wire(sheet);
      sheet.addEventListener("close", () => {
        this.panel.unpin();
        sheet.style.transform = "";
        this.append(this.panel);
        const notice = sheet.querySelector("[data-ux-palette-notice]");
        if (notice && this.noticeHome) this.noticeHome.append(notice);
        if (this.sheetFrom?.isConnected) this.sheetFrom.focus({ preventScroll: true });
      });
      this.dragToClose(sheet);
    }
    this.sheetFrom = tile;
    sheet.querySelector("[data-ux-colour-sheet-body]").append(this.panel);
    const notice = document.querySelector("[data-ux-palette-notice]");
    if (notice) {
      this.noticeHome = notice.parentElement;
      sheet.append(notice);
    }
    this.panel.show(tile.dataset.handle, { pin: true });
    UxPaletteUi.open(sheet, tile);
    sheet.querySelector("[data-ux-colour-sheet-body]").scrollTop = 0;
  }
  /* drag the sheet's bar down to close it */
  dragToClose(sheet) {
    const grab = sheet.querySelector("[data-ux-colour-sheet-grab]");
    grab.addEventListener("pointerdown", (event) => {
      if (event.target.closest("button")) return;
      const y0 = event.clientY;
      let dy = 0;
      grab.setPointerCapture(event.pointerId);
      const move = (moveEvent) => {
        dy = Math.max(0, moveEvent.clientY - y0);
        sheet.style.transform = `translateY(${dy}px)`;
      };
      const end = () => {
        grab.removeEventListener("pointermove", move);
        grab.removeEventListener("pointerup", end);
        grab.removeEventListener("pointercancel", end);
        if (dy > 100) sheet.close();
        else sheet.style.transform = "";
      };
      grab.addEventListener("pointermove", move);
      grab.addEventListener("pointerup", end);
      grab.addEventListener("pointercancel", end);
    });
  }
  /* after a layout, exactly one tile in the grid takes Tab (#463): the focused one, else the one that had it, else the
     first (a Colour card can leave out the tile that had it) */
  rove() {
    const roving = [...this.grid.querySelectorAll('.ux-wtile[tabindex="0"]')];
    const keep = roving.find((tile) => tile === document.activeElement) || roving[0] || this.matrix?.flat().find(Boolean);
    if (keep) this.focusTile(keep, false);
  }
  focusTile(tile, focus = true) {
    this.grid.querySelectorAll('.ux-wtile[tabindex="0"]').forEach((other) => other !== tile && (other.tabIndex = -1));
    tile.tabIndex = 0;
    if (focus) tile.focus();
  }
  onTileKey(event) {
    const tile = event.target.closest(".ux-wtile");
    if (!tile || !this.matrix) return;
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      const colour = this.byHandle.get(tile.dataset.handle);
      if (event.shiftKey && event.key === "Enter") UxPaletteUi.saveTo({ h: colour.h, n: colour.n, c: colour.c }, tile);
      else this.card.toggle(tile.dataset.handle, "wall");
      return;
    }
    let row = +tile.dataset.row;
    let column = +tile.dataset.column;
    const rowCells = (at) => this.matrix[at] || [];
    const moves = {
      ArrowRight: () => (column += 1),
      ArrowLeft: () => (column -= 1),
      ArrowDown: () => (row += 1),
      ArrowUp: () => (row -= 1),
      Home: () => (event.ctrlKey ? ((row = 0), (column = 0)) : (column = 0)),
      End: () => {
        if (event.ctrlKey) row = this.matrix.length - 1;
        column = rowCells(row).length - 1;
        while (column > 0 && !rowCells(row)[column]) column -= 1;
      }
    };
    if (!moves[event.key]) return;
    event.preventDefault();
    moves[event.key]();
    /* the arrows step over gaps (between a card's sections, its printed blanks) to the next tile that way (#463) */
    const step = { ArrowRight: [0, 1], ArrowLeft: [0, -1], ArrowDown: [1, 0], ArrowUp: [-1, 0] }[event.key];
    if (step) {
      /* up and down also pass rows that end before this column (the phone's card rows) */
      while (row >= 0 && row < this.matrix.length && column >= 0 && (step[0] || column < rowCells(row).length) && !rowCells(row)[column]) {
        row += step[0];
        column += step[1];
      }
    }
    const next = rowCells(row)[column];
    if (next) this.focusTile(next);
  }

  /* Find a colour and Spotlight a family: everything else dims to 15% */
  dim() {
    if (!this.data) return;
    const query = this.find.value.replace(/™/g, "").trim().toLowerCase();
    /* Colour: "family:greens" fades everything outside the family, "shade:sage-green-paints" outside the group; the
       box shows the choice (ink border), or "Colour" */
    const [kind, key] = this.spot.value.split(":");
    const shade = kind === "shade" ? this.shades?.get(key) : null;
    this.card.querySelector("[data-ux-wall-spot-text]").textContent = kind ? this.spot.selectedOptions[0]?.text || "" : this.t("colour");
    this.spot.closest(".ux-ccard__spot").classList.toggle("ux-ccard__spot--on", Boolean(kind));
    const matching = new Set();
    this.tiles.forEach((tile, handle) => {
      const colour = this.byHandle.get(handle);
      const inColour = !kind || (kind === "family" ? colour.f === key : Boolean(shade?.has(handle)));
      if (inColour && (!query || `${UxColourCard.fullName(colour)} ${colour.no || ""}`.toLowerCase().includes(query))) matching.add(handle);
    });
    /* the wall shows only the matches; a printed card keeps its layout, so there the rest fade */
    const cards = this.area.classList.contains("ux-wall__area--cards");
    this.tiles.forEach((tile, handle) => tile.classList.toggle("ux-wtile--dim", cards && !matching.has(handle)));
    const next = query || kind ? matching : null;
    this.syncFilter(next ? next.size : this.tiles.size);
    /* a filter with no matches keys as "=" so it never reads as no filter (""): emptied in one go, the wall comes back */
    const matchKey = next ? `=${[...next].join(",")}` : "";
    if (matchKey === this.matchKey) return;
    this.matchKey = matchKey;
    this.matching = next;
    if (!cards) {
      this.shape = "";
      this.layout();
    }
  }

  /* the name over the tile under the pointer or focus: "Market Green No.38 · in Kitchen, Hall" */
  showTip(tile) {
    if (!tile) return;
    const label = tile.getAttribute("aria-label") || "";
    const name = UxColourCard.fullName(this.byHandle.get(tile.dataset.handle));
    this.tip.textContent = label.length > name.length ? `${name} · ${label.slice(name.length + 2)}` : name;
    this.tip.hidden = false;
    const box = this.area.getBoundingClientRect();
    const at = tile.getBoundingClientRect();
    const width = this.tip.offsetWidth;
    const left = Math.min(Math.max(0, at.left - box.left + at.width / 2 - width / 2), box.width - width);
    this.tip.style.left = `${left}px`;
    const above = at.top - box.top - this.tip.offsetHeight - 6;
    this.tip.style.top = `${above >= 0 ? above : at.bottom - box.top + 6}px`;
  }
  hideTip() {
    this.tip.hidden = true;
  }

  /* the dock: a card makes it Adding here; a filled slot's click removes it, with Undo */
  choose(id) {
    if (!UxPalette.palette(id)) return;
    if (this.page?.open) this.page.open(id);
    else this.card.open(id);
    UxPaletteTrack("palette_activated", { palette: id, source: "dock" });
  }
  onCardClick(event) {
    const card = event.target.closest(".ux-dcard");
    if (!card || this.dragged) return;
    const slot = event.target.closest(".ux-dcard__slot--filled");
    if (slot && this.wide.matches) {
      const palette = UxPalette.palette(card.dataset.palette);
      const colour = palette?.colours.find((saved) => saved.h === slot.dataset.handle);
      const undo = colour && UxPalette.removeFrom(palette.id, colour.h);
      if (undo) {
        UxPaletteUi.notify(this.t("removed", { colour: UxColourCard.label(colour), name: palette.name }), { undo: () => UxPalette.restore(undo) });
        UxPaletteTrack("palette_colour_removed", { handle: colour.h, source: "dock", palette: palette.id });
      }
      return;
    }
    if (card.dataset.palette !== this.adding()?.id) {
      this.choose(card.dataset.palette);
      this.openDock(false); /* the phone's dock, open, closes once a palette is chosen (#471) */
    } else if (this.appActive()) this.setView("palette");
    else if (this.opens()) UxPaletteDrawer.open(card, { at: card.dataset.palette });
  }
  onCardKey(event) {
    const cards = [...this.cards.querySelectorAll(".ux-dcard")];
    const at = cards.indexOf(event.target.closest(".ux-dcard"));
    if (at < 0) return;
    if (event.key === "Enter" && cards[at].dataset.palette === this.adding()?.id && this.opens()) {
      event.preventDefault();
      if (this.appActive()) this.setView("palette");
      else UxPaletteDrawer.open(cards[at], { at: cards[at].dataset.palette });
      return;
    }
    let next = null;
    if (event.key === "ArrowRight" || event.key === "ArrowDown") next = cards[(at + 1) % cards.length];
    if (event.key === "ArrowLeft" || event.key === "ArrowUp") next = cards[(at - 1 + cards.length) % cards.length];
    if (event.key === " " || event.key === "Enter") next = cards[at];
    if (!next) return;
    event.preventDefault();
    this.choose(next.dataset.palette);
    /* the phone's open dock: Enter or Space chooses and closes it, focus back on its bar; the arrows only move (#471) */
    if (next === cards[at] && this.dockOpen()) {
      this.openDock(false);
      this.dockToggle.focus();
      return;
    }
    requestAnimationFrame(() => this.cards.querySelector(`[data-palette="${CSS.escape(next.dataset.palette)}"]`)?.focus());
  }
  /* mobile: the card the dock settles on is Adding here */
  settle() {
    const start = this.cards.getBoundingClientRect().left + (parseFloat(getComputedStyle(this.cards).scrollPaddingLeft) || 0);
    let best = null;
    this.cards.querySelectorAll(".ux-dcard").forEach((card) => {
      const distance = Math.abs(card.getBoundingClientRect().left - start);
      if (!best || distance < best.distance) best = { card, distance };
    });
    if (best && best.card.dataset.palette !== this.adding()?.id) this.choose(best.card.dataset.palette);
  }
  /* bring Adding here into view in the dock (mobile: snapped to the start) */
  reveal() {
    const card = this.cards.querySelector(".ux-dcard--active");
    if (!card) return;
    const box = this.cards.getBoundingClientRect();
    const at = card.getBoundingClientRect();
    const pad = parseFloat(getComputedStyle(this.cards).scrollPaddingLeft) || 0;
    if (!this.wide.matches || at.left < box.left || at.right > box.right) this.cards.scrollLeft += at.left - box.left - pad;
  }

  /* drag a tile onto a card (adds there), or a slot to another card (copies; Alt/Option moves) */
  dragStart(event, kind) {
    if (event.pointerType !== "mouse" || event.button !== 0) return;
    const source = kind === "tile" ? event.target.closest(".ux-wtile") : event.target.closest(".ux-dcard__slot--filled");
    if (!source) return;
    const from = kind === "slot" ? source.closest(".ux-dcard").dataset.palette : null;
    const colour = kind === "tile" ? this.byHandle.get(source.dataset.handle) : UxPalette.palette(from)?.colours.find((saved) => saved.h === source.dataset.handle);
    if (!colour) return;
    const x0 = event.clientX;
    const y0 = event.clientY;
    let ghost = null;
    let target = null;
    this.dragged = false;
    const move = (moveEvent) => {
      if (!ghost) {
        if (Math.hypot(moveEvent.clientX - x0, moveEvent.clientY - y0) < 6) return;
        ghost = Object.assign(document.createElement("span"), { className: "ux-wall__ghost" });
        ghost.style.background = getComputedStyle(source).background;
        document.body.append(ghost);
        this.card.classList.add("ux-wall--dragging");
        this.hideTip();
      }
      moveEvent.preventDefault();
      ghost.style.transform = `translate(${moveEvent.clientX - 17}px, ${moveEvent.clientY - 17}px)`;
      const over = document.elementFromPoint(moveEvent.clientX, moveEvent.clientY)?.closest(".ux-dcard");
      const next = over && over.dataset.palette !== from ? over : null;
      if (next !== target) {
        target?.classList.remove("ux-dcard--drop");
        target = next;
        target?.classList.add("ux-dcard--drop");
      }
    };
    const end = (endEvent) => {
      document.removeEventListener("pointermove", move);
      document.removeEventListener("pointerup", end);
      document.removeEventListener("pointercancel", end);
      if (!ghost) return;
      ghost.remove();
      this.card.classList.remove("ux-wall--dragging");
      this.dragged = true;
      setTimeout(() => (this.dragged = false), 0);
      if (!target || endEvent.type === "pointercancel") return;
      target.classList.remove("ux-dcard--drop");
      this.drop(colour, target.dataset.palette, kind === "slot" && endEvent.altKey ? from : null);
    };
    document.addEventListener("pointermove", move);
    document.addEventListener("pointerup", end);
    document.addEventListener("pointercancel", end);
  }
  drop(colour, id, movingFrom) {
    const palette = UxPalette.palette(id);
    if (!palette) return;
    const name = UxColourCard.label(colour);
    if (palette.colours.some((saved) => saved.h === colour.h)) {
      UxPaletteUi.notify(this.t("already", { colour: name, name: palette.name }));
      return;
    }
    if (palette.colours.length >= UxPalette.LIMITS.colours) {
      UxPaletteUi.notify(UxPaletteUi.text("full", { name: palette.name }));
      return;
    }
    if (!UxPalette.addTo(id, { h: colour.h, n: colour.n, c: colour.c })) return;
    UxPaletteTrack("palette_colour_added", { handle: colour.h, source: movingFrom ? "dock-move" : "wall-drag", palette: id });
    if (movingFrom) {
      const undo = UxPalette.removeFrom(movingFrom, colour.h);
      UxPaletteUi.notify(this.t("moved", { colour: name, name: palette.name }), { undo: () => {
        UxPalette.removeFrom(id, colour.h);
        if (undo) UxPalette.restore(undo);
      } });
    } else {
      UxPaletteUi.notify(this.t("added", { colour: name, name: palette.name }), { undo: () => UxPalette.removeFrom(id, colour.h) });
    }
  }
};
if (!window.customElements.get("ux-colour-wall")) {
  window.customElements.define("ux-colour-wall", UxColourWall);
}

/* ---------------------------------------------------------------------------
   The colour panel (Palettes v2, GitHub #184; sections/ux-palette.liquid <ux-colour-panel>, in the wall of colour).
   show(handle) draws the colour at once from the all-colours JSON (name, descriptor and collection, chip, the
   colour page) and then its /products/<handle>?view=palette-panel data (room photos, short description, finishes,
   the sample pot), kept for the page, failures too. A hover previews once the pointer rests (300 ms still, #472); a click pins it (2 px outline on the
   tile; × or Esc lets go). Photo alt text in the #191 pattern ("Market Green No.38: arched doorway; Grey Ochre
   No.152: walls | Photograph: Beth Davis") gives the caption, the credit and Seen with; without it they hide.
   While a colour's data loads (MP3-9, #407) the panel keeps its shape: the photo box, four thumbnails, the sample
   button and "Available in" as still blocks (#438); the photo keeps its placeholder until it
   has loaded (each photo, as the arrows and thumbnails change it).
   --------------------------------------------------------------------------- */
var UxColourPanel = class extends HTMLElement {
  /* "A: where; B: where | Photograph: credit" → { parts: [{ colour, where }], credit } or null */
  static caption(alt) {
    const [body, credit] = String(alt || "").split("|").map((part) => part.trim());
    if (!body || !credit || !/^photograph:/i.test(credit)) return null;
    const parts = body.split(";").map((part) => {
      const at = part.indexOf(":");
      return at < 0 ? { colour: part.trim(), where: "" } : { colour: part.slice(0, at).trim(), where: part.slice(at + 1).trim() };
    }).filter((part) => part.colour);
    return parts.length ? { parts, credit: credit.replace(/^photograph:\s*/i, "") } : null;
  }
  /* "Marble Matt Emulsion · Multi-Surface Dead Matt, Eggshell, Satin and Gloss · Masonry Paint" */
  static finishes(list) {
    const finishes = (list || []).filter((finish) => /paint|emulsion|matt|eggshell|satin|gloss|masonry|floor|limewash|varnish/i.test(finish));
    const multi = finishes.filter((finish) => /^multi-surface /i.test(finish)).map((finish) => finish.replace(/^multi-surface /i, "").replace(/ paint$/i, ""));
    const out = [];
    finishes.forEach((finish) => {
      if (!/^multi-surface /i.test(finish)) out.push(finish);
      else if (!out.some((entry) => entry.startsWith("Multi-Surface"))) {
        out.push(`Multi-Surface ${multi.length > 1 ? `${multi.slice(0, -1).join(", ")} and ${multi[multi.length - 1]}` : multi[0]}`);
      }
    });
    return out.join(" · ");
  }
  /* the panel's one motion (#444; audit-fixes.css ux-photo-reveal is the same): from `from` to `to`, halfway easing
     out (0.4 s), a slow drift through the middle (0.2 s), then quickly on (0.2 s), 0.8 s (UxColourPanel.TIME);
     frame(value) gives each keyframe */
  static TIME = 800;
  static motion(frame, from, to) {
    const at = (share) => from + (to - from) * share;
    return [
      { offset: 0, ...frame(at(0)), easing: "cubic-bezier(0.2, 0.6, 0.35, 1)" },
      { offset: 0.5, ...frame(at(0.5)), easing: "linear" },
      { offset: 0.75, ...frame(at(0.54)), easing: "cubic-bezier(0.5, 0, 0.85, 0.45)" },
      { offset: 1, ...frame(at(1)) },
    ];
  }
  /* thumbnail placeholders wipe out to the left in place, all together (#439) */
  static wipe(thumbs) {
    const still = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    return Promise.all(thumbs.map((thumb) => {
      if (still) return thumb.remove();
      return thumb.animate(UxColourPanel.motion((value) => ({ clipPath: `inset(0 ${value}% 0 0)` }), 0, 100), { duration: UxColourPanel.TIME, fill: "forwards" })
        .finished.then(() => thumb.remove(), () => thumb.remove());
    }));
  }
  /* a photo that has just loaded is revealed from the right over its colour, in the panel's one motion (#444;
     audit-fixes.css, is-arriving) */
  static arrive(box) {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    box.classList.remove("is-arriving");
    void box.offsetWidth;
    box.classList.add("is-arriving");
    const end = (event) => {
      if (event.animationName !== "ux-photo-reveal") return;
      box.classList.remove("is-arriving");
      box.removeEventListener("animationend", end);
    };
    box.addEventListener("animationend", end);
  }
  connectedCallback() {
    if (this._wired) return;
    this._wired = true;
    this.wall = this.closest("ux-colour-wall");
    this.card = this.closest("ux-colour-card");
    this.home = this.parentElement;
    this.cache = new Map();
    this.photoIndex = 0;
    this.addEventListener("click", (event) => this.onClick(event));
    this.addEventListener("keydown", (event) => {
      if (event.key !== "Escape") return;
      /* Esc shuts the palettes list first, then lets go */
      if (!this.querySelector("[data-ux-cpanel-palettes]").hidden) {
        event.stopPropagation();
        this.palettes(false, { focus: true });
      } else if (this.pinned && !this.closest("dialog")) {
        event.stopPropagation();
        this.unpin();
      }
    });
    this.querySelector("[data-ux-cpanel-choices]").addEventListener("change", (event) => this.choose(event.target));
    document.addEventListener("pointerdown", (event) => {
      if (!this.querySelector("[data-ux-cpanel-adding]").contains(event.target)) this.palettes(false);
    });
    document.addEventListener("ux-palette:change", () => {
      if (!this.handle) return;
      /* an open Add to list is rebuilt when palettes come, go or are renamed elsewhere (#467) */
      if (!this.querySelector("[data-ux-cpanel-palettes]").hidden) this.refreshPalettes();
      this.renderState();
    });
  }
  t(key, values = {}) {
    return Object.entries(values).reduce((string, [name, value]) => string.split(`[${name}]`).join(value), this.dataset[key] || "");
  }
  colour(handle) {
    return this.wall?.byHandle?.get(handle) || null;
  }
  say(text) {
    const status = this.querySelector("[data-ux-cpanel-status]");
    status.textContent = "";
    requestAnimationFrame(() => (status.textContent = text));
  }

  /* hover previews don't move a pinned panel; a pin always does */
  show(handle, { pin = false } = {}) {
    const colour = this.colour(handle);
    if (!colour) return;
    if (this.pinned && !pin && handle !== this.pinned) return;
    if (pin) this.pinned = handle;
    this.wall?.markPinned(this.pinned);
    if (handle === this.handle) {
      this.renderState();
      return;
    }
    this.handle = handle;
    this.photoIndex = 0;
    this.palettes(false);
    this.querySelector("[data-ux-cpanel-empty]").hidden = true;
    this.querySelector("[data-ux-cpanel-colour]").hidden = false;
    this.querySelector("[data-ux-cpanel-name]").textContent = colour.n;
    this.style.setProperty("--ux-cpanel-tone", colour.c || "");
    const chip = this.querySelector("[data-ux-cpanel-chip]");
    chip.style.backgroundColor = colour.c || "";
    chip.style.backgroundImage = colour.i ? `url("${colour.i}")` : "";
    chip.classList.toggle("ux-cswatch__chip--metal", Boolean(colour.i));
    const collection = (this.wall.data.collections || []).find((entry) => (colour.k || []).includes(entry.handle));
    this.querySelector("[data-ux-cpanel-descriptor]").textContent = [colour.d, collection?.label].filter(Boolean).join(" · ");
    this.querySelector("[data-ux-cpanel-view]").href = colour.u || `${window.Shopify?.routes?.root || "/"}products/${encodeURIComponent(handle)}`;
    this.querySelector("[data-ux-cpanel-unpin]").hidden = this.pinned !== handle;
    this.loading();
    this.renderState();
    if (this.pinned === handle) this.say(this.t("showing", { colour: UxColourCard.fullName(colour) }));
    this.load(handle).then((data) => {
      if (handle !== this.handle) return;
      this.classList.remove("is-loading");
      this.renderDetails(data);
    });
  }
  /* a colour with no room photos (12 of 213 in Oct 2026, FTT-007 among them) keeps the 4:6 box, filled with the colour
     itself fading in over the placeholder (#443); a metallic shows its metal */
  colourField(on) {
    const box = this.querySelector("[data-ux-cpanel-photo]");
    const colour = this.colour(this.handle);
    box.classList.toggle("ux-cpanel__photo--colour", on);
    box.style.setProperty("--ux-cpanel-field", on ? (colour?.i ? `url("${colour.i}") center / cover` : colour?.c || "var(--ux-grey)") : "");
    if (on) box.classList.remove("ux-cpanel__photo--loading");
  }
  /* ...and its thumbnail placeholders wipe away, then their row eases shut, everything under it gliding up (the row
     holds its height through the wipe, or it would empty, and jump, the moment the placeholders go) */
  foldThumbs() {
    const handle = this.handle;
    const row = this.querySelector("[data-ux-cpanel-thumbs-row]");
    const height = row.offsetHeight;
    row.style.height = `${height}px`;
    UxColourPanel.wipe([...row.querySelectorAll(".ux-cpanel__thumb--skeleton")]).then(() => {
      if (this.handle !== handle || this.data?.photos?.length) return;
      const shut = () => {
        if (this.handle !== handle) return;
        row.hidden = true;
        row.style.height = "";
      };
      if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return shut();
      const gap = parseFloat(getComputedStyle(this).rowGap) || 0;
      row.animate([{ height: `${height}px`, marginBottom: "0px" }, { height: "0px", marginBottom: `${-gap}px` }], { duration: 500, easing: "cubic-bezier(0.65, 0, 0.35, 1)", fill: "forwards" })
        .finished.then(shut, shut);
    });
  }

  /* the shape of a loaded colour, held by placeholders, so nothing moves when the data comes */
  loading() {
    this.data = null;
    this.colourField(false);
    this.classList.add("is-loading");
    const photo = this.querySelector("[data-ux-cpanel-photo]");
    photo.hidden = false;
    photo.classList.add("ux-cpanel__photo--loading");
    const image = this.querySelector("[data-ux-cpanel-image]");
    image.removeAttribute("src");
    image.alt = "";
    this.querySelector("[data-ux-cpanel-count]").textContent = "";
    this.querySelectorAll("[data-ux-cpanel-step]").forEach((button) => (button.hidden = true));
    const row = this.querySelector("[data-ux-cpanel-thumbs-row]");
    row.hidden = false;
    row.style.height = "";
    row.getAnimations().forEach((animation) => animation.cancel());
    this.querySelector("[data-ux-cpanel-thumbs]").replaceChildren(...Array.from({ length: 4 }, () => Object.assign(document.createElement("span"), { className: "ux-cpanel__thumb ux-cpanel__thumb--skeleton" })));
    const caption = this.querySelector("[data-ux-cpanel-caption]");
    caption.textContent = "";
    caption.hidden = true;
    const short = this.querySelector("[data-ux-cpanel-short]");
    short.textContent = "";
    short.hidden = true;
    this.querySelector("[data-ux-cpanel-finishes]").textContent = "";
    this.querySelector("[data-ux-cpanel-finishes-box]").hidden = false;
    this.querySelector("[data-ux-cpanel-seen-box]").hidden = true;
  }
  unpin() {
    this.pinned = null;
    this.handle = null;
    this.wall?.markPinned(null);
    this.querySelector("[data-ux-cpanel-empty]").hidden = false;
    this.querySelector("[data-ux-cpanel-colour]").hidden = true;
  }
  load(handle) {
    if (!this.cache.has(handle)) {
      this.cache.set(handle, fetch(`${window.Shopify?.routes?.root || "/"}products/${encodeURIComponent(handle)}?view=palette-panel`)
        /* a failure is kept too, for the page (#472): hovering that tile again doesn't ask again */
        .then((response) => (response.ok ? response.json() : null))
        .catch(() => null));
    }
    return this.cache.get(handle);
  }

  /* the parts from the product: photos, caption, short description, sample pot, finishes, Seen with */
  renderDetails(data) {
    this.data = data;
    const photos = data?.photos || [];
    this.colourField(!photos.length);
    if (!photos.length) this.foldThumbs();
    this.renderPhoto();
    const short = this.querySelector("[data-ux-cpanel-short]");
    short.textContent = data?.short || "";
    short.hidden = !data?.short;
    const finishes = UxColourPanel.finishes(data?.finishes);
    this.querySelector("[data-ux-cpanel-finishes]").textContent = finishes;
    this.querySelector("[data-ux-cpanel-finishes-box]").hidden = !finishes;
    /* Seen with: the other colours named in the photo captions */
    const seen = [];
    photos.forEach((photo) => UxColourPanel.caption(photo.alt)?.parts.forEach((part) => {
      const match = this.findColour(part.colour);
      if (match && match.h !== this.handle && !seen.includes(match)) seen.push(match);
    }));
    const list = this.querySelector("[data-ux-cpanel-seen]");
    list.replaceChildren(...seen.slice(0, 4).map((colour) => {
      const item = document.createElement("li");
      item.className = "ux-cpanel__pair";
      item.dataset.handle = colour.h;
      const chip = Object.assign(document.createElement("span"), { className: "ux-cpanel__pair-chip" });
      chip.style.background = colour.c || "";
      const name = Object.assign(document.createElement("span"), { className: "ux-cpanel__pair-name", textContent: colour.n });
      const add = Object.assign(document.createElement("button"), { type: "button", className: "ux-cpanel__pair-add" });
      add.dataset.uxCpanelPair = colour.h;
      item.append(chip, name, add);
      return item;
    }));
    this.querySelector("[data-ux-cpanel-seen-box]").hidden = !seen.length;
    this.renderState();
  }
  findColour(name) {
    const key = String(name || "").replace(/™/g, "").replace(/\s+/g, " ").trim().toLowerCase();
    if (!key) return null;
    return [...this.wall.byHandle.values()].find((colour) => UxColourCard.fullName(colour).toLowerCase() === key)
      || [...this.wall.byHandle.values()].find((colour) => UxColourCard.label(colour).toLowerCase() === key) || null;
  }
  renderPhoto({ slide = 0 } = {}) {
    const photos = this.data?.photos || [];
    if (!photos.length) return;
    const index = (this.photoIndex + photos.length) % photos.length;
    this.photoIndex = index;
    const photo = photos[index];
    const image = this.querySelector("[data-ux-cpanel-image]");
    /* the placeholder stays until this photo has loaded */
    const box = this.querySelector("[data-ux-cpanel-photo]");
    const done = () => {
      if (image.getAttribute("src") !== photo.src || !box.classList.contains("ux-cpanel__photo--loading")) return;
      box.classList.remove("ux-cpanel__photo--loading");
      UxColourPanel.arrive(box);
    };
    image.onload = done;
    image.onerror = done;
    if (image.getAttribute("src") !== photo.src) {
      if (slide && image.complete && image.naturalWidth && !window.matchMedia("(prefers-reduced-motion: reduce)").matches) this.slidePhoto(box, image, photo.src, slide);
      else {
        box.classList.add("ux-cpanel__photo--loading");
        image.src = photo.src;
      }
    }
    if (image.complete && image.naturalWidth) done();
    const caption = UxColourPanel.caption(photo.alt);
    image.alt = caption ? "" : photo.alt || "";
    this.querySelector("[data-ux-cpanel-count]").textContent = this.t("count", { index: index + 1, count: photos.length });
    this.querySelectorAll("[data-ux-cpanel-step]").forEach((button) => (button.hidden = photos.length < 2));
    /* up to four thumbnails; more: three and "+N" */
    const shown = photos.length > 4 ? 3 : photos.length;
    const thumbs = photos.slice(0, shown).map((entry, at) => {
      const button = Object.assign(document.createElement("button"), { type: "button", className: "ux-cpanel__thumb" });
      button.dataset.uxCpanelThumb = at;
      button.setAttribute("aria-label", this.t("photo", { index: at + 1, count: photos.length }));
      if (at === index) button.setAttribute("aria-current", "true");
      const thumb = Object.assign(document.createElement("img"), { src: entry.thumb, alt: "", loading: "lazy" });
      if (!thumb.complete) {
        thumb.classList.add("is-loading");
        thumb.onload = thumb.onerror = () => {
          thumb.classList.remove("is-loading");
          UxColourPanel.arrive(button);
        };
      }
      button.append(thumb);
      return button;
    });
    if (photos.length > 4) {
      const more = Object.assign(document.createElement("button"), { type: "button", className: "ux-cpanel__thumb ux-cpanel__thumb--more", textContent: `+${photos.length - 3}` });
      more.dataset.uxCpanelThumb = 3;
      more.setAttribute("aria-label", this.t("photo", { index: 4, count: photos.length }));
      if (index >= 3) more.setAttribute("aria-current", "true");
      thumbs.push(more);
    }
    /* placeholders beyond the photos there are wipe out to the left within their own place (#439), together, not
       vanish */
    const holder = this.querySelector("[data-ux-cpanel-thumbs]");
    const spare = [...holder.querySelectorAll(".ux-cpanel__thumb--skeleton")].slice(thumbs.length);
    holder.replaceChildren(...thumbs, ...spare);
    UxColourPanel.wipe(spare);
    const text = caption ? `${caption.parts.map((part, at) => {
      const name = UxColourCard.label(this.findColour(part.colour) || { n: part.colour });
      if (!part.where) return name;
      const where = at === 0 ? part.where[0].toUpperCase() + part.where.slice(1) : part.where;
      return this.t("inPhoto", { where, colour: name });
    }).join("; ")}. ${this.t("credit", { credit: caption.credit })}` : "";
    const captionBox = this.querySelector("[data-ux-cpanel-caption]");
    captionBox.textContent = text;
    captionBox.hidden = !text;
  }
  /* the arrows and thumbnails slide the photos like a carousel (#442): once the next one has loaded (the current one
     holds till then) it comes in from its side as the current one leaves the other way; quick clicks slide to the last */
  slidePhoto(box, image, src, direction) {
    const next = new Image();
    next.src = src;
    (next.decode ? next.decode() : Promise.resolve()).catch(() => {}).then(() => {
      if (this.data?.photos?.[this.photoIndex]?.src !== src || !box.isConnected) return;
      box.querySelectorAll(".ux-cpanel__image--leaving").forEach((old) => old.remove());
      box.classList.remove("is-arriving");
      image.getAnimations().forEach((animation) => animation.cancel());
      const leaving = image.cloneNode();
      leaving.removeAttribute("data-ux-cpanel-image");
      leaving.classList.add("ux-cpanel__image--leaving");
      leaving.alt = "";
      image.before(leaving);
      image.src = src;
      /* a plain ease-in-out, not the panel's reveal curve: its drift through the middle left two half photos (Matt) */
      const timing = { duration: 500, easing: "cubic-bezier(0.65, 0, 0.35, 1)" };
      leaving.animate([{ transform: "translateX(0)" }, { transform: `translateX(${-100 * direction}%)` }], timing)
        .finished.then(() => leaving.remove(), () => leaving.remove());
      image.animate([{ transform: `translateX(${100 * direction}%)` }, { transform: "translateX(0)" }], timing);
    });
  }
  /* what follows the palettes: Add to / Remove from [Adding here], Already in…, Seen with's + */
  renderState() {
    const colour = this.colour(this.handle);
    if (!colour) return;
    const adding = this.card?.palette();
    const on = Boolean(adding?.colours.some((saved) => saved.h === colour.h));
    const add = this.querySelector("[data-ux-cpanel-add]");
    add.textContent = adding ? this.t(on ? "removeFrom" : "addTo", { name: adding.name }) : this.t("addToPalette");
    add.classList.toggle("button--secondary", on);
    this.querySelector("[data-ux-cpanel-more]").classList.toggle("button--secondary", on);
    /* an open list follows changes made elsewhere (Add to, the dock), ticks kept in place so focus stays */
    this.querySelectorAll("[data-ux-cpanel-choices] input").forEach((input) => {
      input.checked = Boolean(UxPalette.palette(input.value)?.colours.some((saved) => saved.h === colour.h));
    });
    const holders = UxPalette.palettes().filter((palette) => palette.colours.some((saved) => saved.h === colour.h));
    const already = this.querySelector("[data-ux-cpanel-in]");
    already.textContent = holders.length ? this.t("already", { names: holders.map((palette) => palette.name).join(", ") }) : "";
    already.classList.toggle("ux-cpanel__in-text--on", Boolean(holders.length));
    /* Order samples: the Adding here palette's, under the wall, once it has colours. Once this colour is in that
       palette, Add to turns to an outlined Remove from and Order samples is the primary (one primary, Matt 19 Oct) */
    const samples = this.querySelector("[data-ux-cpanel-samples]");
    samples.hidden = !adding?.colours.length;
    samples.classList.toggle("button--secondary", !on);
    this.querySelectorAll("[data-ux-cpanel-pair]").forEach((button) => {
      const pair = this.colour(button.dataset.uxCpanelPair);
      const has = Boolean(adding?.colours.some((saved) => saved.h === pair?.h));
      button.classList.toggle("ux-cpanel__pair-add--in", has);
      button.setAttribute("aria-label", this.t(has ? "removeLabel" : "addLabel", { colour: UxColourCard.fullName(pair), name: adding?.name || "" }));
    });
  }

  onClick(event) {
    const target = event.target.closest("button, a");
    if (!target || !this.contains(target)) return;
    if (target.matches("[data-ux-cpanel-show]")) this.show(target.dataset.uxCpanelShow, { pin: true });
    else if (target.matches("[data-ux-cpanel-unpin]")) {
      if (this.closest("dialog")) this.closest("dialog").close();
      else this.unpin();
    } else if (target.matches("[data-ux-cpanel-step]")) {
      this.photoIndex += Number(target.dataset.uxCpanelStep);
      this.renderPhoto({ slide: Math.sign(Number(target.dataset.uxCpanelStep)) });
    } else if (target.matches("[data-ux-cpanel-thumb]")) {
      const from = this.photoIndex;
      this.photoIndex = Number(target.dataset.uxCpanelThumb);
      this.renderPhoto({ slide: Math.sign(this.photoIndex - from) });
    } else if (target.matches("[data-ux-cpanel-add]")) {
      /* no palette to add to yet: the button opens the list (New palette) */
      if (this.card?.palette()) this.card.toggle(this.handle, "panel");
      else this.palettes(true);
    } else if (target.matches("[data-ux-cpanel-more]")) this.palettes(target.getAttribute("aria-expanded") !== "true");
    else if (target.matches("[data-ux-cpanel-new]")) {
      const colour = this.colour(this.handle);
      this.palettes(false);
      UxPaletteUi.newPalette({ colour: { h: colour.h, n: colour.n, c: colour.c }, from: this.querySelector("[data-ux-cpanel-more]") });
    } else if (target.matches("[data-ux-cpanel-pair]")) this.card?.toggle(target.dataset.uxCpanelPair, "seen-with");
    else if (target.matches("[data-ux-cpanel-samples]")) this.toSamples(event);
  }

  /* the split button's list (#440): every palette by project, ticked where this colour is; a tick adds, an untick
     takes it out, at once (as Save to did); New palette at the foot */
  palettes(open, { focus = false } = {}) {
    const list = this.querySelector("[data-ux-cpanel-palettes]");
    const more = this.querySelector("[data-ux-cpanel-more]");
    if (list.hidden === !open) return;
    list.hidden = !open;
    more.setAttribute("aria-expanded", String(open));
    if (open) {
      this.renderPalettes();
      list.querySelector("input")?.focus({ preventScroll: true });
    } else if (focus) more.focus();
  }
  renderPalettes() {
    const handle = this.handle;
    UxPaletteUi.choices(this.querySelector("[data-ux-cpanel-choices]"), "checkbox", (palette) => palette.colours.some((saved) => saved.h === handle));
    this.error("");
  }
  refreshPalettes() {
    const handle = this.handle;
    UxPaletteUi.refresh(this.querySelector("[data-ux-cpanel-choices]"), "checkbox", (palette) => palette.colours.some((saved) => saved.h === handle));
  }
  error(message) {
    const box = this.querySelector("[data-ux-cpanel-error]");
    box.textContent = message;
    box.hidden = !message;
  }
  choose(input) {
    const colour = this.colour(this.handle);
    if (!colour || input.type !== "checkbox") return;
    this.error("");
    if (input.checked) {
      if (UxPalette.addTo(input.value, { h: colour.h, n: colour.n, c: colour.c })) {
        UxPaletteTrack("palette_colour_added", { handle: colour.h, source: "panel-list", palette: input.value });
      } else {
        input.checked = false;
        this.error(UxPaletteUi.refused(input));
        this.refreshPalettes();
      }
    } else if (UxPalette.removeFrom(input.value, colour.h)) {
      UxPaletteTrack("palette_colour_removed", { handle: colour.h, source: "panel-list", palette: input.value });
    }
  }
  /* down to Try your palette at home; from the mobile sheets, which cover the page, they close first */
  toSamples(event) {
    if (this.wall?.appActive?.()) {
      event.preventDefault();
      this.wall.toSamples();
      return;
    }
    const target = document.getElementById("ux-palette-try");
    if (!target || target.hidden) return;
    event.preventDefault();
    document.querySelectorAll("dialog.ux-sheet[open]").forEach((dialog) => dialog.close());
    requestAnimationFrame(() => target.scrollIntoView({ behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth", block: "start" }));
  }

};
if (!window.customElements.get("ux-colour-panel")) {
  window.customElements.define("ux-colour-panel", UxColourPanel);
}

/* ---------------------------------------------------------------------------
   Full-height Paint menu (experiment, GitHub #284; snippets/mega-menu-full.liquid <ux-full-menu>). While its <details>
   is open the panel fills the window below the header (--ux-fmenu-top, measured on opening and on resize) and the page
   behind doesn't scroll. Shop by project: Inside or Outside slides its list in (the cards' pane goes inert, focus moves
   to the back row) and the back row slides it home. Your palette: the palette in use, its first four colours, the
   count and names, Order N samples (the 100 ml pots, tagged _palette as on the palette page) and View your palette;
   nothing saved, the how-to and How it works.
   --------------------------------------------------------------------------- */
var UxFullMenu = class extends HTMLElement {
  connectedCallback() {
    if (this._wired) return;
    this._wired = true;
    this.root = this.closest(".mega-menu");
    this.details = this.closest("details");
    this.details?.addEventListener("toggle", () => this.toggled());
    window.addEventListener("resize", () => this.details?.open && this.size());
    this.addEventListener("click", (event) => this.onClick(event));
    this.addEventListener("keydown", (event) => {
      if (event.key === "Escape" && this.projects?.classList.contains("ux-fmenu__col--list")) {
        event.stopPropagation();
        this.closeList();
      }
    });
    this.projects = this.querySelector("[data-ux-fmenu-projects]");
    this.listPane = this.projects?.querySelector(".ux-fmenu__pane--list");
    if (this.listPane) this.listPane.inert = true;
    document.addEventListener("ux-palette:change", () => this.renderPalette());
    this.renderPalette();
  }
  t(key, values = {}) {
    return Object.entries(values).reduce((string, [name, value]) => string.split(`[${name}]`).join(value), this.dataset[key] || "");
  }
  /* the panel runs from the header's bottom edge to the bottom of the window */
  size() {
    const header = this.root.offsetParent || this.closest("x-header, .header");
    const top = Math.max(0, Math.round((header?.getBoundingClientRect().bottom ?? 0) / uxPageZoom()));
    this.root.style.setProperty("--ux-fmenu-top", `${top}px`);
  }
  toggled() {
    const open = this.details.open;
    if (open) {
      /* the sticky header may still be sliding back in (sections/header.liquid): follow it until it settles */
      const until = performance.now() + 700;
      const follow = () => {
        this.size();
        if (this.details.open && performance.now() < until) requestAnimationFrame(follow);
      };
      follow();
    }
    document.documentElement.classList.toggle("ux-fmenu-open", open);
    if (!open) this.closeList(false);
  }

  /* Shop by project */
  onClick(event) {
    const card = event.target.closest("[data-ux-fmenu-open]");
    if (card) {
      this.openList(card.dataset.uxFmenuOpen, card);
      return;
    }
    if (event.target.closest("[data-ux-fmenu-back]")) {
      this.closeList();
      return;
    }
    if (event.target.closest("[data-ux-fmenu-palette-order]")) this.orderSamples(event.target.closest("[data-ux-fmenu-palette-order]"));
  }
  openList(place, card) {
    if (!this.projects) return;
    this.projects.querySelectorAll("[data-ux-fmenu-list]").forEach((list) => (list.hidden = list.dataset.uxFmenuList !== place));
    this.projects.querySelectorAll("[data-ux-fmenu-open]").forEach((button) => button.setAttribute("aria-expanded", String(button === card)));
    this.opener = card;
    this.projects.querySelector("[data-ux-fmenu-cards]").inert = true;
    this.listPane.inert = false;
    /* the rows cascade in after the slide has started */
    this.projects.classList.remove("ux-fmenu__col--list");
    void this.projects.offsetWidth;
    this.projects.classList.add("ux-fmenu__col--list");
    this.projects.querySelector(`[data-ux-fmenu-list="${place}"] [data-ux-fmenu-back]`)?.focus({ preventScroll: true });
  }
  closeList(focus = true) {
    if (!this.projects?.classList.contains("ux-fmenu__col--list")) return;
    this.projects.classList.remove("ux-fmenu__col--list");
    this.projects.querySelector("[data-ux-fmenu-cards]").inert = false;
    this.listPane.inert = true;
    this.projects.querySelectorAll("[data-ux-fmenu-open]").forEach((button) => button.setAttribute("aria-expanded", "false"));
    if (focus) this.opener?.focus({ preventScroll: true });
  }

  /* Your palette: the palette in use */
  renderPalette() {
    const card = this.querySelector("[data-ux-fmenu-palette]");
    if (!card || !window.UxPalette) return;
    const palette = UxPalette.active();
    const colours = palette?.colours || [];
    const count = colours.length;
    card.classList.toggle("ux-fmenu__palette--empty", !count);
    card.querySelectorAll("[data-ux-fmenu-palette-colours] span").forEach((slot, index) => {
      slot.style.background = colours[index] ? colours[index].c || "var(--ux-grey)" : "";
      slot.classList.toggle("is-filled", Boolean(colours[index]));
    });
    const title = card.querySelector("[data-ux-fmenu-palette-title]");
    const line = card.querySelector("[data-ux-fmenu-palette-line]");
    title.dataset.start ??= title.textContent;
    line.dataset.start ??= line.textContent;
    const order = card.querySelector("[data-ux-fmenu-palette-order]");
    const view = card.querySelector("[data-ux-fmenu-palette-view]");
    if (!count) {
      title.textContent = title.dataset.start;
      line.textContent = line.dataset.start;
      order.hidden = true;
      view.textContent = view.dataset.start;
      view.href = UxPalette.pageUrl().toString();
      return;
    }
    const names = colours.map((colour) => UxPaletteUi.shortName(colour.n || colour.h));
    title.textContent = this.t(count === 1 ? "savedOne" : "savedOther", { count });
    line.textContent = names.length > 1 ? `${names.slice(0, -1).join(", ")} ${this.dataset.and} ${names[names.length - 1]}` : names[0];
    order.hidden = false;
    order.textContent = this.t(count === 1 ? "orderOne" : "orderOther", { count });
    view.textContent = view.dataset.view;
    const url = UxPalette.pageUrl();
    url.searchParams.set("palette", palette.id);
    view.href = `${url.pathname}${url.search}`;
  }
  async orderSamples(button) {
    const palette = UxPalette.active();
    if (!palette?.colours.length || button.getAttribute("aria-busy") === "true") return;
    button.setAttribute("aria-busy", "true");
    const root = window.Shopify?.routes?.root || "/";
    try {
      const chosen = await UxPaletteSamples.choose(palette.colours);
      if (!chosen.length) throw new Error("none available");
      /* the same _palette tagging as Try your palette at home (UxPalettePage) */
      const items = await UxPalettePage.prototype.paletteItems.call({ shared: null }, chosen);
      const sections = [];
      document.documentElement.dispatchEvent(new CustomEvent("cart:prepare-bundled-sections", { bubbles: true, detail: { sections } }));
      const response = await fetch(`${root}cart/add.js`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items, sections: sections.join(",") })
      });
      const added = await response.json();
      if (!response.ok) throw new Error(added.description || added.message || response.statusText);
      const cart = await (await fetch(`${root}cart.js`)).json();
      cart.sections = added.sections;
      UxPaletteTrack("palette_samples_added", { count: chosen.length, pots: chosen.filter((item) => item.choice === "sample").length, swatches: chosen.filter((item) => item.choice === "peel").length, source: "menu" });
      /* the basket drawer opens in the menu's place */
      if (typeof window.animateMenuClose === "function") window.animateMenuClose(this.details);
      else this.details.removeAttribute("open");
      document.documentElement.dispatchEvent(new CustomEvent("cart:change", { bubbles: true, detail: { baseEvent: "variant:add", cart } }));
    } catch (error) {
      UxPaletteUi.notify(this.dataset.failed);
    } finally {
      button.removeAttribute("aria-busy");
    }
  }
};
if (!window.customElements.get("ux-full-menu")) {
  window.customElements.define("ux-full-menu", UxFullMenu);
}

/* ---------------------------------------------------------------------------
   <ux-acct-carry>: "Your palettes come with you" on Create account (milestone 9, GitHub #295; Figma: Account
   285:3454). The palettes in this browser's book: how many, a strip of their colours (up to 8, in book order) and
   their names. Hidden while the book is empty; follows changes made in another tab.
   --------------------------------------------------------------------------- */
var UxAcctCarry = class extends HTMLElement {
  connectedCallback() {
    this.render = this.render.bind(this);
    this.render();
    document.addEventListener("ux-palette:change", this.render);
  }
  disconnectedCallback() {
    document.removeEventListener("ux-palette:change", this.render);
  }
  render() {
    const palettes = (window.UxPalette?.palettes() || []).filter((palette) => palette.colours.length);
    this.hidden = !palettes.length;
    if (!palettes.length) return;
    const line = this.querySelector("[data-ux-acct-carry-line]");
    line.textContent = palettes.length === 1 ? line.dataset.one : line.dataset.other.replace("[count]", palettes.length);
    const strip = this.querySelector("[data-ux-acct-carry-strip]");
    strip.replaceChildren(...palettes.flatMap((palette) => palette.colours).slice(0, 8).map((colour) => {
      const swatch = document.createElement("span");
      swatch.style.background = colour.c;
      return swatch;
    }));
    this.querySelector("[data-ux-acct-carry-names]").textContent = palettes.map((palette) => palette.name).join(" · ");
  }
};
if (!window.customElements.get("ux-acct-carry")) {
  window.customElements.define("ux-acct-carry", UxAcctCarry);
}

/* ---------------------------------------------------------------------------
   Account shell and overview (milestone 9, GitHub #287 / #288; Figma: Account 279:300, 283:2701, 283:3187).
   <ux-acct-nav>: the mobile "Account · <page>" control opens the nav in a .ux-sheet (UxPaletteUi's open and wire,
   so Esc, the backdrop and focus return behave as the palette sheets do).
   <ux-acct-palettes>: R1's Your palettes. The count line and the first three palettes of the book as cards (the
   #185 card, name and "project · N colours"), each opening the palette page; the empty state when there are none.
   <ux-acct-unsampled>: "Kitchen has 4 colours you haven't sampled". The palette in use against the sample pots on
   past orders (data-sampled, product handles); Order samples adds those pots tagged _palette, as the palette page
   and the menu do, and the basket drawer opens.
   --------------------------------------------------------------------------- */
var UxAcctNav = class extends HTMLElement {
  connectedCallback() {
    if (this._wired) return;
    this._wired = true;
    const sheet = this.querySelector("[data-ux-acct-sheet]");
    const button = this.querySelector("[data-ux-acct-menu]");
    if (!sheet || !button) return;
    UxPaletteUi.wire(sheet);
    button.addEventListener("click", () => UxPaletteUi.open(sheet, button));
    /* back on desktop, a sheet left open would sit over the sidenav */
    window.matchMedia("(min-width: 1000px)").addEventListener("change", (event) => event.matches && sheet.open && sheet.close());
  }
};
if (!window.customElements.get("ux-acct-nav")) {
  window.customElements.define("ux-acct-nav", UxAcctNav);
}

var uxAcctPlural = (element, key, count) => (count === 1 ? element.dataset[`${key}One`] : (element.dataset[`${key}Other`] || "").replace("[count]", count));

var UxAcctPalettes = class extends HTMLElement {
  connectedCallback() {
    this.render = this.render.bind(this);
    this.render();
    document.addEventListener("ux-palette:change", this.render);
  }
  disconnectedCallback() {
    document.removeEventListener("ux-palette:change", this.render);
  }
  render() {
    if (!window.UxPalette) return;
    const palettes = UxPalette.palettes();
    const projects = UxPalette.projects();
    const count = this.querySelector("[data-ux-acct-palettes-count]");
    const cards = this.querySelector("[data-ux-acct-palettes-cards]");
    this.querySelector("[data-ux-acct-palettes-empty]").hidden = palettes.length > 0;
    this.querySelector("[data-ux-acct-palettes-all]").hidden = !palettes.length;
    cards.hidden = !palettes.length;
    const used = projects.filter((project) => palettes.some((palette) => palette.project === project.id)).length;
    count.textContent = palettes.length ? [uxAcctPlural(this, "palettes", palettes.length), used ? uxAcctPlural(this, "projects", used) : ""].filter(Boolean).join(" ") : "";
    cards.replaceChildren(...palettes.slice(0, 3).map((palette) => {
      const card = document.createElement("li");
      card.className = "ux-pcard";
      const colours = document.createElement("span");
      colours.className = `ux-pcard__colours${palette.colours.length ? "" : " ux-pcard__colours--empty"}`;
      colours.setAttribute("aria-hidden", "true");
      colours.append(...palette.colours.slice(0, UxPalette.LIMITS.colours).map((colour) => {
        const block = document.createElement("span");
        block.style.background = colour.c || "var(--ux-grey)";
        return block;
      }));
      const url = UxPalette.pageUrl();
      url.searchParams.set("palette", palette.id);
      const project = projects.find((entry) => entry.id === palette.project);
      card.innerHTML = '<div class="ux-pcard__meta"><div class="ux-pcard__text"><h3 class="ux-pcard__name"><a class="ux-pcard__link"></a></h3><p class="ux-pcard__detail"></p></div></div>';
      const link = card.querySelector(".ux-pcard__link");
      link.href = `${url.pathname}${url.search}`;
      link.textContent = palette.name;
      card.querySelector(".ux-pcard__detail").textContent = [project?.name, uxAcctPlural(this, "colours", palette.colours.length)].filter(Boolean).join(" · ");
      card.prepend(colours);
      return card;
    }));
  }
};
if (!window.customElements.get("ux-acct-palettes")) {
  window.customElements.define("ux-acct-palettes", UxAcctPalettes);
}

var UxAcctUnsampled = class extends HTMLElement {
  connectedCallback() {
    if (this._wired) return;
    this._wired = true;
    this.sampled = new Set((this.dataset.sampled || "").split(",").filter(Boolean));
    this.render = this.render.bind(this);
    this.render();
    document.addEventListener("ux-palette:change", this.render);
    this.querySelector("[data-ux-acct-unsampled-order]").addEventListener("click", (event) => this.order(event.currentTarget));
  }
  missing() {
    const palette = window.UxPalette?.active();
    return { palette, colours: (palette?.colours || []).filter((colour) => !this.sampled.has(colour.h)) };
  }
  render() {
    const { palette, colours } = this.missing();
    this.hidden = !colours.length;
    if (!colours.length) return;
    const title = colours.length === 1 ? this.dataset.titleOne : this.dataset.titleOther.replace("[count]", colours.length);
    this.querySelector("[data-ux-acct-unsampled-title]").textContent = title.replace("[name]", palette.name);
  }
  async order(button) {
    const { colours } = this.missing();
    if (!colours.length || button.getAttribute("aria-busy") === "true") return;
    button.setAttribute("aria-busy", "true");
    const root = window.Shopify?.routes?.root || "/";
    try {
      const chosen = await UxPaletteSamples.choose(colours);
      if (!chosen.length) throw new Error("none available");
      const items = await UxPalettePage.prototype.paletteItems.call({ shared: null }, chosen);
      const sections = [];
      document.documentElement.dispatchEvent(new CustomEvent("cart:prepare-bundled-sections", { bubbles: true, detail: { sections } }));
      const response = await fetch(`${root}cart/add.js`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items, sections: sections.join(",") })
      });
      const added = await response.json();
      if (!response.ok) throw new Error(added.description || added.message || response.statusText);
      const cart = await (await fetch(`${root}cart.js`)).json();
      cart.sections = added.sections;
      UxPaletteTrack("palette_samples_added", { count: chosen.length, pots: chosen.filter((item) => item.choice === "sample").length, swatches: chosen.filter((item) => item.choice === "peel").length, source: "account" });
      document.documentElement.dispatchEvent(new CustomEvent("cart:change", { bubbles: true, detail: { baseEvent: "variant:add", cart } }));
    } catch (error) {
      UxPaletteUi.notify(this.dataset.failed);
    } finally {
      button.removeAttribute("aria-busy");
    }
  }
};
if (!window.customElements.get("ux-acct-unsampled")) {
  window.customElements.define("ux-acct-unsampled", UxAcctUnsampled);
}

/* <ux-acct-orders>: R3's tabs (milestone 9, GitHub #290). All orders / Open / Dispatched filter the rows on this
   page by their data-status; "No orders here on this page." when a tab empties the table. */
var UxAcctOrders = class extends HTMLElement {
  connectedCallback() {
    if (this._wired) return;
    this._wired = true;
    this.tabs = [...this.querySelectorAll("[data-ux-acct-orders-tab]")];
    this.tabs.forEach((tab) => tab.addEventListener("click", () => this.show(tab.dataset.uxAcctOrdersTab)));
  }
  show(group) {
    this.tabs.forEach((tab) => tab.setAttribute("aria-pressed", String(tab.dataset.uxAcctOrdersTab === group)));
    const rows = [...this.querySelectorAll("tbody tr")];
    rows.forEach((row) => (row.hidden = group !== "all" && row.dataset.status !== group));
    this.querySelector("[data-ux-acct-orders-none]").hidden = rows.some((row) => !row.hidden);
  }
};
if (!window.customElements.get("ux-acct-orders")) {
  window.customElements.define("ux-acct-orders", UxAcctOrders);
}

/* <ux-acct-addresses>: R5's sheets (milestone 9, GitHub #292). A [data-ux-acct-sheet] button opens the address
   sheet it names (UxPaletteUi's open and wire: Esc, backdrop, focus back on the button); a form Shopify sent back
   with errors opens its sheet again on load. */
var UxAcctAddresses = class extends HTMLElement {
  connectedCallback() {
    if (this._wired) return;
    this._wired = true;
    this.addEventListener("click", (event) => {
      const button = event.target.closest("[data-ux-acct-sheet]");
      if (!button) return;
      const sheet = document.getElementById(button.dataset.uxAcctSheet);
      if (!sheet) return;
      UxPaletteUi.wire(sheet);
      UxPaletteUi.open(sheet, button);
    });
    const failed = this.querySelector("dialog [data-ux-acct-sheet-open-now]")?.closest("dialog");
    if (failed) {
      UxPaletteUi.wire(failed);
      UxPaletteUi.open(failed, this.querySelector(`[data-ux-acct-sheet="${failed.id}"]`));
    }
  }
};
if (!window.customElements.get("ux-acct-addresses")) {
  window.customElements.define("ux-acct-addresses", UxAcctAddresses);
}

/* <ux-acct-sync>: "Not saved yet" above Your palettes in the account (milestone 9, GitHub #297; Figma: States
   286:3439). Shown while palette changes are failing to reach the account (UxPaletteSync.failing, ux-palette:sync);
   Try again sends them now. Only while something is waiting to be saved (the store's pending palettes, projects or
   deletes), so a proxy that's down with nothing to send stays quiet. It clears itself on the next successful save.
   Never red: nothing is lost. */
var UxAcctSync = class extends HTMLElement {
  connectedCallback() {
    if (this._wired) return;
    this._wired = true;
    this.render = this.render.bind(this);
    document.addEventListener("ux-palette:sync", this.render);
    document.addEventListener("ux-palette:change", this.render);
    this.querySelector("[data-ux-acct-sync-retry]")?.addEventListener("click", () => window.UxPaletteSync?.retryNow());
    this.render();
  }
  render() {
    const store = window.UxPalette?.store?.();
    const unsaved = Boolean(store && (store.pending.projects.length || store.pending.palettes.length || store.deleted.length));
    this.hidden = !(window.UxPaletteSync?.failing && unsaved);
  }
};
if (!window.customElements.get("ux-acct-sync")) {
  window.customElements.define("ux-acct-sync", UxAcctSync);
}

/* ---------------------------------------------------------------------------
   Consultations in the account (milestone 9, GitHub #291 / #288 / #298; Figma: Account R4 280:1243, R1 279:484).
   The customer metafield mylands.consultancy (schema v1 from md_colour_consultancy, docs/palette-accounts.md §14.5),
   printed as #ux-consultancy. sessions[]: every upcoming booking (soonest first), then the latest past or cancelled.
   Times are shown in Europe/London; a missing URL removes its action; v 1 or later is read (v 2 adds to projects[],
   §14.9.2), anything else counts as nothing.
   <ux-acct-consultations> (R4): Upcoming cards and Past rows, the empty state when there are neither; Share another
   lists the book's palettes not yet shared and posts {proxy}/consultancy/share with the booking's full list (the route
   replaces what's shared, it doesn't add).
   <ux-acct-next-consultation> (R1): the soonest upcoming booking, hidden without one.
   Milestone 11 (#333; Figma Build · 09, 462:5973): the first of projects[] fills Your colour project (the stages, the
   current one shaded: v2's timeline[].current, else the first not done), Your specification (each room's state:
   approved, changed since approved with an ink square, or to approve, with Review and approve), Orders from this
   consultation (orders, then one Ready to order row for approved rooms not ordered yet, then the rooms still to
   approve: 524:7476) and Your consultancy discount (Copy code; gone once expired).
   --------------------------------------------------------------------------- */
var UxConsultancy = (() => {
  const read = () => {
    try {
      const data = JSON.parse(document.getElementById("ux-consultancy")?.textContent || "null");
      return data && Number(data.v) >= 1 && Array.isArray(data.sessions) ? data : null;
    } catch (error) {
      return null;
    }
  };
  const locale = () => (/^en\b/i.test(document.documentElement.lang || "en") ? "en-GB" : document.documentElement.lang);
  const format = (iso, options, timeZone) => {
    const date = new Date(iso);
    return Number.isNaN(date.getTime()) ? "" : new Intl.DateTimeFormat(locale(), { timeZone, ...options }).format(date);
  };
  /* a date with no time ("2026-10-08") is read at noon UTC, so it's the same day in London */
  const day = (value, options, timeZone) => (/^\d{4}-\d{2}-\d{2}$/.test(value || "") ? format(`${value}T12:00:00Z`, options, timeZone) : "");
  const today = (timeZone) => new Intl.DateTimeFormat("en-CA", { timeZone }).format(new Date());
  const money = (value) => (value === null || value === undefined || value === "" || !Number.isFinite(Number(value)) ? "" : new Intl.NumberFormat(locale(), { style: "currency", currency: "GBP" }).format(Number(value)));
  const plural = (element, key, count) => (count === 1 ? element.dataset[`${key}One`] : (element.dataset[`${key}Other`] || "").replace("[count]", count));
  const fill = (template, values) => Object.entries(values).reduce((text, [key, value]) => text.replaceAll(`[${key}]`, value ?? ""), template || "");
  /* room names hold commas ("Hall, stairs and landing"), so the last one joins with ", and" */
  const list = (names, and) => (names.length < 2 ? names[0] || "" : `${names.slice(0, -1).join(", ")}${and}${names[names.length - 1]}`);
  const upper = (text) => (text ? text.charAt(0).toUpperCase() + text.slice(1) : "");
  /* the session's links: href when there's one, otherwise the action goes */
  const links = (root, urls = {}) => {
    root.querySelectorAll("[data-ux-consult-url]").forEach((link) => {
      const url = urls[link.dataset.uxConsultUrl];
      if (url) link.href = url;
      else link.remove();
    });
  };
  const tags = (root, palettes = []) => {
    const box = root.querySelector("[data-ux-consult-tags]");
    if (!box) return;
    box.replaceChildren(...palettes.map((palette) => {
      const tag = document.createElement("span");
      tag.className = "ux-acct-tag";
      tag.textContent = palette.name;
      return tag;
    }));
  };
  /* orders placed from a project's basket that Odoo hasn't imported yet (#348; its import takes 10–25 minutes): the
     customer's own orders whose lines carry the project's _consultation ("CC-2026-0042/v1/Kitchen"; a room name may
     hold "/"), from #ux-consultancy-shop-orders, as order rows like Odoo's. A cancelled order doesn't count, and one
     Odoo already lists (its order_name in known) is Odoo's to show. */
  const placed = (ref, known) => {
    let all = [];
    try {
      all = JSON.parse(document.getElementById("ux-consultancy-shop-orders")?.textContent || "[]");
    } catch (error) {
      return [];
    }
    if (!ref || !Array.isArray(all)) return [];
    return all.filter((order) => order && order.name && !order.cancelled && !known.has(order.name)).map((order) => {
      const lines = (Array.isArray(order.lines) ? order.lines : []).map((line) => String(line).split("/")).filter((parts) => parts[0].trim() === ref);
      if (!lines.length) return null;
      const total = Number(order.total);
      return {
        order_name: order.name,
        rooms: [...new Set(lines.map((parts) => parts.slice(2).join("/").trim()).filter(Boolean))],
        source: "spec",
        date: order.date,
        total: Number.isFinite(total) ? total / 100 : null,
        status_label: order.status,
        url: order.url,
      };
    }).filter(Boolean);
  };
  return { read, format, day, today, money, plural, fill, list, upper, links, tags, placed };
})();

var UxAcctConsultations = class extends HTMLElement {
  connectedCallback() {
    if (this._wired) return;
    this._wired = true;
    this.data = UxConsultancy.read();
    this.render();
    this.addEventListener("click", (event) => {
      const share = event.target.closest("[data-ux-consult-share]");
      if (share) this.openShare(share);
    });
  }
  render() {
    const tz = this.dataset.tz;
    const sessions = this.data?.sessions || [];
    const upcoming = sessions.filter((session) => session.state === "upcoming");
    const past = sessions.filter((session) => session.state === "past" || session.state === "cancelled");
    this.querySelector("[data-ux-consults-upcoming]").hidden = !upcoming.length;
    this.querySelector("[data-ux-consults-past]").hidden = !past.length;
    const project = (this.data?.projects || []).find((entry) => entry && typeof entry === "object") || null;
    this.renderProject(project);
    this.querySelector("[data-ux-consults-empty]").hidden = Boolean(upcoming.length || past.length || project);
    const card = this.querySelector("template[data-ux-consult-upcoming]");
    this.querySelector("[data-ux-consults-upcoming-list]").replaceChildren(...upcoming.map((session) => {
      const item = card.content.firstElementChild.cloneNode(true);
      item.dataset.session = session.id;
      item.querySelector("[data-ux-consult-weekday]").textContent = UxConsultancy.format(session.start, { weekday: "short" }, tz);
      item.querySelector("[data-ux-consult-day]").textContent = UxConsultancy.format(session.start, { day: "2-digit" }, tz);
      item.querySelector("[data-ux-consult-month]").textContent = UxConsultancy.format(session.start, { month: "short" }, tz);
      item.querySelector("[data-ux-consult-type]").textContent = session.type_label || "";
      const day = UxConsultancy.format(session.start, { weekday: "long", day: "numeric", month: "long" }, tz);
      const from = UxConsultancy.format(session.start, { hour: "2-digit", minute: "2-digit", hourCycle: "h23" }, tz);
      const to = UxConsultancy.format(session.end, { hour: "2-digit", minute: "2-digit", hourCycle: "h23" }, tz);
      item.querySelector("[data-ux-consult-when]").textContent = to ? `${day}, ${from} to ${to}` : `${day}, ${from}`;
      const detail = item.querySelector("[data-ux-consult-detail]");
      if (session.detail) detail.textContent = session.detail;
      else detail.remove();
      UxConsultancy.tags(item, session.palettes);
      /* Share another needs a booking to share with and palettes in the book */
      if (!session.id || !window.UxPalette) item.querySelector("[data-ux-consult-share]").remove();
      /* nothing shared yet: Share another on its own, without the label */
      if (!(session.palettes || []).length) {
        if (item.querySelector("[data-ux-consult-share]")) item.querySelectorAll(".ux-acct-consult__shared-label, [data-ux-consult-tags]").forEach((part) => part.remove());
        else item.querySelector("[data-ux-consult-shared]").remove();
      }
      UxConsultancy.links(item, session.urls);
      return item;
    }));
    const row = this.querySelector("template[data-ux-consult-past]");
    this.querySelector("[data-ux-consults-past-list]").replaceChildren(...past.map((session) => {
      const item = row.content.firstElementChild.cloneNode(true);
      item.querySelector("[data-ux-consult-type]").textContent = session.type_label || "";
      const cancelled = item.querySelector("[data-ux-consult-cancelled]");
      if (session.state === "cancelled") {
        cancelled.textContent = this.dataset.cancelled;
        cancelled.hidden = false;
      } else cancelled.remove();
      const day = UxConsultancy.format(session.start, { day: "numeric", month: "long", year: "numeric" }, tz);
      const minutes = Number(session.minutes) || 0;
      const length = !minutes ? "" : minutes % 60 === 0 ? UxConsultancy.plural(this, "hours", minutes / 60) : UxConsultancy.plural(this, "minutes", minutes);
      const consultant = session.consultant ? UxConsultancy.fill(this.dataset.with, { name: session.consultant }) : "";
      item.querySelector("[data-ux-consult-when]").textContent = [day, length, consultant].filter(Boolean).join(" · ");
      /* past: the notes; cancelled: Odoo's line ("Cancelled on 1 October.") and no links (its URLs are all null) */
      const summary = item.querySelector("[data-ux-consult-summary]");
      const text = session.state === "cancelled" ? session.detail : session.summary;
      if (text) summary.textContent = text;
      else summary.remove();
      UxConsultancy.links(item, session.urls);
      return item;
    }));
  }
  /* 09b: the specification as it is (metafield v3) */
  renderSpecV3(block, spec, rooms, { long, short, tz, clone, link }) {
    const { day, fill } = UxConsultancy;
    const money = (value) => UxConsultancy.money(value);
    const litres = (tin) => `${tin.quantity} × ${tin.size}`;
    const count = UxConsultancy.plural(this, "rooms", rooms.length);
    const signed = spec.signed_on ? fill(this.dataset.specSigned, { date: day(spec.signed_on, long, tz) }) : "";
    const signedShort = spec.signed_on ? fill(this.dataset.specSigned, { date: day(spec.signed_on, short, tz) }) : "";
    const minutes = spec.updated_at ? Math.max(0, Math.round((Date.now() - new Date(spec.updated_at).getTime()) / 60000)) : null;
    const ago = minutes === null ? "" : minutes < 2 ? this.dataset.agoNow : minutes < 120 ? fill(this.dataset.agoMinutes, { count: minutes }) : fill(this.dataset.agoHours, { count: Math.round(minutes / 60) });
    const asof = ago ? fill(this.dataset.specAsof, { ago }) : "";
    block.querySelector("[data-ux-spec-meta]").textContent = [signed, count, asof].filter(Boolean).join(" · ");
    block.querySelector("[data-ux-spec-meta-short]").textContent = [signedShort, count, asof].filter(Boolean).join(" · ");
    block.querySelectorAll("[data-ux-spec-pdf]").forEach((pdf) => link(pdf, spec.pdf));
    const change = block.querySelector("[data-ux-spec-change]");
    if (spec.url) {
      change.href = spec.url;
      change.hidden = false;
    }
    const swatch = (hex) => {
      const el = document.createElement("span");
      el.style.background = hex;
      return el;
    };
    block.querySelector("[data-ux-spec-rooms]").replaceChildren(...rooms.map((room, index) => {
      const item = clone("room3");
      const colours = (Array.isArray(room.colours) ? room.colours : []).filter((colour) => /^#[0-9a-f]{3,8}$/i.test(colour?.hex || ""));
      const swatches = item.querySelector("[data-ux-room-swatches]");
      swatches.replaceChildren(...colours.slice(0, 4).map((colour) => swatch(colour.hex)));
      if (!colours.length) swatches.remove();
      item.querySelector("[data-ux-room-name]").textContent = room.name;
      item.querySelector("[data-ux-room-total]").textContent = money(room.total);
      const order = room.ordered?.order_name || "";
      item.querySelector("[data-ux-room-meta-short]").textContent = [money(room.total), order ? fill(this.dataset.roomOrderedShort, { order }) : this.dataset.roomNotOrdered].filter(Boolean).join(" · ");
      const tag = item.querySelector("[data-ux-room-ordered]");
      if (order) tag.textContent = fill(this.dataset.roomOrdered, { order });
      else tag.remove();
      item.querySelectorAll("[data-ux-room-add]").forEach((add) => {
        if (!order && room.add_url) add.href = room.add_url;
        else add.remove();
      });
      /* the first room open, as 09b draws it */
      if (index === 0) item.querySelector("[data-ux-room3]").open = true;
      item.querySelector("[data-ux-room-surfaces]").replaceChildren(...(room.surfaces || []).map((surface) => {
        const row = clone("surface");
        const coats = surface.coats ? fill(this.dataset.surfaceCoats, { count: surface.coats }) : "";
        const area = surface.area ? `${Number(surface.area)} m²` : "";
        row.querySelector("[data-ux-surface-kind]").textContent = surface.label || "";
        row.querySelector("[data-ux-surface-basis]").textContent = area ? [area, coats].filter(Boolean).join(" · ") : this.dataset.surfaceNoSize;
        const hex = surface.colour?.hex;
        const box = row.querySelector("[data-ux-surface-swatch]");
        if (/^#[0-9a-f]{3,8}$/i.test(hex || "")) box.style.background = hex;
        else box.classList.add("is-empty");
        const items = row.querySelector("[data-ux-surface-items]");
        const line = (name, detail, tins, price) => {
          const el = document.createElement("span");
          el.className = "ux-acct-surface__item";
          el.innerHTML = '<span class="ux-acct-surface__name"><span></span><span class="ux-acct-row__meta"></span></span><span class="ux-acct-surface__tins"></span><span class="ux-acct-surface__price"></span>';
          el.children[0].children[0].textContent = name;
          el.children[0].children[1].textContent = detail;
          el.children[1].textContent = tins;
          el.children[2].textContent = price;
          return el;
        };
        /* the phone's first line: "WALLS · 35.7 M²" (09b mobile) */
        const head = document.createElement("span");
        head.className = "ux-acct-surface__head ux-acct-mobile";
        head.textContent = [surface.label, area].filter(Boolean).join(" · ");
        items.append(head);
        const paintTins = (surface.tins || []).map(litres).join(" + ");
        const paintPrice = (surface.tins || []).reduce((sum, tin) => sum + Number(tin.price || 0), 0);
        items.append(line(surface.colour?.name || "", surface.paint || "", paintTins, paintTins ? money(paintPrice) : ""));
        if (surface.primer) {
          const primerTins = (surface.primer.tins || []).map(litres).join(" + ");
          const primerPrice = (surface.primer.tins || []).reduce((sum, tin) => sum + Number(tin.price || 0), 0);
          items.append(line(surface.primer.name || "", [surface.primer.finish, this.dataset.primerCoat].filter(Boolean).join(" · "), primerTins, primerTins ? money(primerPrice) : ""));
        }
        return row;
      }));
      return item;
    }));
    block.hidden = false;
  }
  /* --- a consultation over weeks (#333): the first project's four blocks --- */
  renderProject(project) {
    const tz = this.dataset.tz;
    const long = { day: "numeric", month: "long", year: "numeric" };
    const short = { day: "numeric", month: "long" };
    const { day, fill } = UxConsultancy;
    const blocks = Object.fromEntries(["project", "spec", "orders", "discount"].map((name) => [name, this.querySelector(`[data-ux-consult-${name}]`)]));
    Object.values(blocks).forEach((block) => { block.hidden = true; });
    if (!project) return;
    const consultant = typeof project.consultant === "string" ? project.consultant : "";
    const text = (root, selector, value) => {
      const element = root.querySelector(selector);
      if (value) element.textContent = value;
      else element.remove();
    };
    const link = (element, url) => {
      if (url) element.href = url;
      else element.remove();
    };
    const clone = (name) => this.querySelector(`template[data-ux-consult-${name}]`).content.firstElementChild.cloneNode(true);
    /* an order from this project's basket that Odoo hasn't imported yet already counts as ordered (#348) */
    const odooOrders = (Array.isArray(project.orders) ? project.orders : []).filter((order) => order && (order.order_name || order.name));
    const placed = UxConsultancy.placed(project.ref, new Set(odooOrders.map((order) => order.order_name || order.name)));
    const closed = project.stage?.key === "closed";

    /* Your colour project: name, consultant and reference, the stage tag, the stages */
    const head = blocks.project;
    head.querySelector("[data-ux-project-name]").textContent = project.name || project.ref || "";
    const since = day(project.since, long, tz);
    const meta = consultant && since ? { name: consultant, date: since, ref: project.ref || "" } : null;
    text(head, "[data-ux-project-meta]", meta ? fill(this.dataset.projectMeta, meta) : project.ref);
    text(head, "[data-ux-project-meta-short]", meta ? fill(this.dataset.projectMetaShort, meta) : project.ref);
    text(head, "[data-ux-project-stage]", placed.length && !closed ? this.dataset.stageOrdered || project.stage?.label : project.stage?.label);
    let steps = (Array.isArray(project.timeline) ? project.timeline : []).filter((step) => step && step.label);
    /* with such an order the Orders step is done, and the current step moves on as Odoo's does: the one after the
       last done, else the first open of Approval and Orders */
    const ordersAt = steps.findIndex((step) => step.key === "orders");
    if (placed.length && !closed && ordersAt > -1 && !steps[ordersAt].done) {
      const first = placed.map((order) => order.date).filter(Boolean).sort()[0] || null;
      steps = steps.map((step, index) => (index === ordersAt ? { ...step, done: true, date: step.date || first } : step));
      if (steps.some((step) => "current" in step)) {
        const lastDone = steps.reduce((last, step, index) => (step.done ? index : last), -1);
        const open = lastDone + 1 < steps.length ? lastDone + 1 : steps.findIndex((step) => ["approval", "signoff", "orders"].includes(step.key) && !step.done);
        steps = steps.map((step, index) => ({ ...step, current: index === open }));
      }
    }
    /* v2 says which step is current (none once everything's done); v1 doesn't, so the first not done */
    const current = steps.some((step) => "current" in step) ? steps.findIndex((step) => step.current === true) : steps.findIndex((step) => !step.done);
    head.querySelector("[data-ux-project-stages]").replaceChildren(...steps.map((step, index) => {
      const item = clone("stage");
      item.dataset.state = index === current ? "current" : step.done ? "done" : "todo";
      if (index === current) item.setAttribute("aria-current", "step");
      item.querySelector("[data-ux-stage-label]").textContent = step.label;
      text(item, "[data-ux-stage-note]", [step.note, day(step.date, short, tz)].filter(Boolean).join(" "));
      return item;
    }));
    link(head.querySelector("[data-ux-project-url]"), project.url);
    head.hidden = false;

    /* Your specification: one row per room with its colours and state */
    const spec = project.spec && typeof project.spec === "object" ? project.spec : null;
    const rooms = (Array.isArray(spec?.rooms) ? spec.rooms : []).filter((room) => room && room.name);
    const stateOf = (room) => (["approved", "partly", "changed", "to_approve"].includes(room.state) ? room.state : room.approved ? "approved" : "to_approve");
    /* 07d: "3 of 4 lines approved" (lines_approved / lines_total; approved_lines / lines in the first draft) */
    const count = (value) => (Number.isInteger(value) && value >= 0 ? value : null);
    const partly = (room) => {
      const done = count(room.lines_approved ?? room.approved_lines);
      const total = count(room.lines_total ?? room.lines);
      return done !== null && total ? fill(this.dataset.roomPartly, { approved: done, lines: total }) : this.dataset.roomWaiting;
    };
    /* 09b (11 Oct 2026, metafield v3): the specification by rooms and surfaces, read-only; each room ordered or "Add to
       my basket" (the live page, which asks for the sign-off first); "Change your specification" opens it */
    const v3 = Boolean(spec) && rooms.some((room) => Array.isArray(room.surfaces));
    if (v3) this.renderSpecV3(blocks.spec, spec, rooms, { long, short, tz, clone, link });
    if (spec && rooms.length && !v3) {
      const block = blocks.spec;
      const issued = { version: spec.version, rooms: UxConsultancy.plural(this, "rooms", rooms.length) };
      const shared = rooms.some((room) => room.palette);
      const wide = [fill(this.dataset.specMeta, { ...issued, date: day(spec.issued, long, tz) })];
      const narrow = [fill(this.dataset.specMeta, { ...issued, date: day(spec.issued, short, tz) })];
      if (shared) {
        wide.push(consultant ? fill(this.dataset.specPalettes, { name: consultant }) : this.dataset.specPalettesShort);
        narrow.push(this.dataset.specPalettesShort);
      }
      block.querySelector("[data-ux-spec-meta]").textContent = wide.join(" · ");
      block.querySelector("[data-ux-spec-meta-short]").textContent = narrow.join(" · ");
      block.querySelectorAll("[data-ux-spec-pdf]").forEach((pdf) => link(pdf, spec.pdf));
      block.querySelector("[data-ux-spec-rooms]").replaceChildren(...rooms.map((room) => {
        const item = clone("room");
        const state = stateOf(room);
        item.dataset.state = state;
        const colours = (Array.isArray(room.colours) ? room.colours : []).filter((colour) => colour && colour.name);
        /* a swatch only for a colour Odoo knows the hex of (a colour missing from its catalogue has none) */
        const painted = colours.filter((colour) => /^#[0-9a-f]{3,8}$/i.test(colour.hex || ""));
        item.querySelector("[data-ux-room-swatches]").replaceChildren(...painted.map((colour) => {
          const swatch = document.createElement("span");
          swatch.style.background = colour.hex;
          return swatch;
        }));
        if (!painted.length) item.querySelector("[data-ux-room-swatches]").remove();
        item.querySelector("[data-ux-room-name]").textContent = room.name;
        const status = state === "approved"
          ? (room.approved_on ? fill(this.dataset.roomApproved, { date: day(room.approved_on, short, tz) }) : room.approved_version ? fill(this.dataset.roomApprovedVersion, { version: room.approved_version }) : "")
          : state === "changed" ? this.dataset.roomChanged : state === "partly" ? partly(room) : this.dataset.roomWaiting;
        /* changed since approved needs attention: a small ink square before it (§4.9, §4.11) */
        const flagged = (value) => {
          const parts = [];
          if (state === "changed") {
            const square = document.createElement("span");
            square.className = "ux-acct-attention";
            square.setAttribute("aria-hidden", "true");
            parts.push(square);
          }
          parts.push(value);
          return parts;
        };
        const names = colours.map((colour) => colour.name).join(", ");
        const wideMeta = item.querySelector("[data-ux-room-meta]");
        if (names) wideMeta.append(status ? `${names} · ` : names);
        if (status) wideMeta.append(...flagged(status));
        item.querySelector("[data-ux-room-meta-short]").append(...flagged(UxConsultancy.upper(status)));
        if (state !== "approved") item.querySelector("[data-ux-room-approved]").remove();
        const review = state === "approved" ? null : room.approve_url || spec.review_url;
        link(item.querySelector("[data-ux-room-review]"), review);
        return item;
      }));
      block.hidden = false;
    }

    /* Orders from this consultation: each order (the ones Odoo hasn't imported yet first), then the rooms still to approve */
    const orders = [...placed, ...odooOrders];
    const waiting = spec && !v3 ? rooms.filter((room) => stateOf(room) !== "approved") : [];
    /* approved, not ordered yet (524:7476): one row for every such room, Fill my basket; no basket link, no row */
    const ordered = new Set(orders.flatMap((order) => (Array.isArray(order.rooms) ? order.rooms : [order.room])));
    const inBasket = Array.isArray(project.basket?.rooms) ? project.basket.rooms.map(String) : null;
    /* a room with some lines approved (07d) is ready for what's approved, and still waits for the rest */
    const ready = project.basket?.url && !v3 ? rooms.filter((room) => ["approved", "partly"].includes(stateOf(room)) && !ordered.has(room.name) && (!inBasket || inBasket.includes(String(room.id)))) : [];
    if (orders.length || ready.length || waiting.length) {
      const block = blocks.orders;
      let mine = {};
      try {
        mine = JSON.parse(document.getElementById("ux-consultancy-orders")?.textContent || "{}") || {};
      } catch (error) {
        /* View order falls back to Odoo's copy */
      }
      const percent = project.discount?.percent ?? spec?.discount_percent;
      const rows = orders.map((order) => {
        const item = clone("order");
        const name = order.order_name || order.name;
        const names = (Array.isArray(order.rooms) && order.rooms.length ? order.rooms : order.room ? [order.room] : []).filter((room) => typeof room === "string");
        item.querySelector("[data-ux-order-title]").textContent = [UxConsultancy.list(names, this.dataset.and), name].filter(Boolean).join(" · ");
        const source = order.source || (names.length ? "spec" : "");
        const how = { spec: this.dataset.orderSpec, code: this.dataset.orderCode }[source] || this.dataset.orderOther;
        const total = UxConsultancy.money(order.total);
        const amount = total && Number(order.saved) > 0 && percent ? fill(this.dataset.orderSaved, { total, percent }) : total;
        text(item, "[data-ux-order-meta]", [order.date ? fill(how, { date: day(order.date, long, tz) }) : "", amount].filter(Boolean).join(" · "));
        text(item, "[data-ux-order-meta-short]", [day(order.date, short, tz), amount].filter(Boolean).join(" · "));
        text(item, "[data-ux-order-status]", order.status_label || UxConsultancy.upper(String(order.status || "")));
        link(item.querySelector("[data-ux-order-url]"), (typeof mine[name] === "string" && mine[name]) || order.url);
        return item;
      });
      if (ready.length) {
        const item = clone("order");
        item.dataset.state = "ready";
        item.querySelector("[data-ux-order-title]").textContent = UxConsultancy.list(ready.map((room) => room.name), this.dataset.and);
        const latest = ready.map((room) => room.approved_on).filter(Boolean).sort().pop();
        const caption = (date) => (percent ? fill(this.dataset.readyMeta, { date, percent }) : fill(this.dataset.readyMetaPlain, { date }));
        text(item, "[data-ux-order-meta]", latest ? caption(day(latest, long, tz)) : "");
        text(item, "[data-ux-order-meta-short]", latest ? caption(day(latest, short, tz)) : "");
        item.querySelector("[data-ux-order-status]").textContent = this.dataset.ready;
        const fillBasket = item.querySelector("[data-ux-order-url]");
        fillBasket.textContent = this.dataset.readyFill;
        fillBasket.href = project.basket.url;
        rows.push(item);
      }
      /* not approved yet: "Approve the spare room …" (the room name in the sentence, its first letter lowered) */
      const waits = waiting.map((room) => {
        const item = clone("waiting");
        item.querySelector("[data-ux-waiting-name]").textContent = room.name;
        const sentence = stateOf(room) === "partly" ? this.dataset.waitingPartly : this.dataset.waiting;
        item.querySelector("[data-ux-waiting-text]").textContent = fill(sentence, { room: room.name.charAt(0).toLowerCase() + room.name.slice(1) });
        return item;
      });
      block.querySelector("[data-ux-orders-list]").replaceChildren(...rows, ...waits);
      block.querySelector("[data-ux-orders-waiting]").replaceChildren(...waiting.map((room) => {
        const line = document.createElement("p");
        line.className = "ux-acct-row__meta";
        line.textContent = fill(stateOf(room) === "partly" ? this.dataset.waitingLinePartly : this.dataset.waitingLine, { room: room.name });
        return line;
      }));
      block.hidden = false;
    }

    /* Your consultancy discount: until it expires */
    const discount = project.discount;
    if (discount?.code && !(discount.expires && discount.expires < UxConsultancy.today(tz))) {
      const block = blocks.discount;
      text(block, "[data-ux-discount-title]", discount.percent ? fill(this.dataset.discountTitle, { percent: discount.percent }) : "");
      block.querySelector("[data-ux-discount-body]").textContent = fill(this.dataset.discountBody, { applies_to: discount.applies_to || "", date: day(discount.expires, long, tz), code: discount.code });
      const copy = block.querySelector("[data-ux-discount-copy]");
      copy.onclick = async () => {
        try {
          await navigator.clipboard.writeText(discount.code);
          UxPaletteUi.notify(this.dataset.discountCopied);
        } catch (error) {
          UxPaletteUi.notify(fill(this.dataset.discountCopyFailed, { code: discount.code }));
        }
      };
      block.hidden = false;
    }
  }
  openShare(button) {
    const session = (this.data?.sessions || []).find((entry) => String(entry.id) === button.closest("[data-session]")?.dataset.session);
    const dialog = this.querySelector("[data-ux-consult-share-sheet]");
    if (!session || !dialog) return;
    const shared = new Set((session.palettes || []).map((palette) => palette.id));
    const left = UxPalette.palettes().filter((palette) => !shared.has(palette.id));
    const choice = this.querySelector("template[data-ux-consult-choice]");
    dialog.querySelector("[data-ux-consult-share-choices]").replaceChildren(...left.map((palette) => {
      const label = choice.content.firstElementChild.cloneNode(true);
      label.querySelector("input").value = palette.id;
      label.querySelector(".ux-choice__label").textContent = palette.name;
      return label;
    }));
    dialog.querySelector("[data-ux-consult-share-lead]").textContent = left.length ? dialog.querySelector("[data-ux-consult-share-lead]").textContent : this.dataset.shareNone;
    dialog.querySelector("[data-ux-consult-share-submit]").disabled = !left.length;
    const error = dialog.querySelector("[data-ux-sheet-error]");
    error.hidden = true;
    dialog.querySelector("form").onsubmit = (event) => {
      event.preventDefault();
      const chosen = [...dialog.querySelectorAll('input[name="palette"]:checked')].map((input) => input.value);
      if (chosen.length) this.share(session, dialog, [...(session.palettes || []).map((palette) => palette.id), ...chosen]);
    };
    UxPaletteUi.wire(dialog);
    UxPaletteUi.open(dialog, button);
  }
  /* ids: everything that should be shared with the booking afterwards (the ones already shared and the new ones) */
  async share(session, dialog, ids) {
    const submit = dialog.querySelector("[data-ux-consult-share-submit]");
    const error = dialog.querySelector("[data-ux-sheet-error]");
    if (!ids.length || submit.getAttribute("aria-busy") === "true") return;
    submit.setAttribute("aria-busy", "true");
    try {
      const proxy = uxAppProxy();
      const response = await fetch(`${proxy}/consultancy/share`, {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ session: session.id, palettes: ids })
      });
      const data = await response.json().catch(() => null);
      if (!response.ok || !data?.ok) throw Object.assign(new Error(data?.message || response.statusText), { code: data?.error });
      session.palettes = data.palettes || session.palettes;
      UxConsultancy.tags(this.querySelector(`[data-session="${CSS.escape(String(session.id))}"]`), session.palettes);
      dialog.close();
      UxPaletteUi.notify(this.dataset.shared);
    } catch (failure) {
      /* not_found: the booking has passed or been cancelled; invalid: a palette changed since the page loaded;
         login_required: signed out in another tab. Anything else (signature, 503, offline) is worth another try. */
      const messages = { not_found: this.dataset.shareGone, invalid: this.dataset.shareInvalid, login_required: this.dataset.shareLogin };
      error.textContent = messages[failure.code] || this.dataset.shareFailed;
      error.hidden = false;
    } finally {
      submit.removeAttribute("aria-busy");
    }
  }
};
if (!window.customElements.get("ux-acct-consultations")) {
  window.customElements.define("ux-acct-consultations", UxAcctConsultations);
}

var UxAcctNextConsultation = class extends HTMLElement {
  connectedCallback() {
    if (this._wired) return;
    this._wired = true;
    const session = (UxConsultancy.read()?.sessions || []).find((entry) => entry.state === "upcoming");
    this.hidden = !session;
    if (!session) return;
    const tz = this.dataset.tz;
    const day = UxConsultancy.format(session.start, { weekday: "long", day: "numeric", month: "long" }, tz);
    const from = UxConsultancy.format(session.start, { hour: "2-digit", minute: "2-digit", hourCycle: "h23" }, tz);
    this.querySelector("[data-ux-consult-when]").textContent = `${day}, ${from}`;
    this.querySelector("[data-ux-consult-line]").textContent = [session.type_label, session.minutes ? UxConsultancy.plural(this, "minutes", session.minutes) : ""].filter(Boolean).join(" · ");
    if ((session.palettes || []).length) UxConsultancy.tags(this, session.palettes);
    else this.querySelector("[data-ux-consult-shared]").remove();
    UxConsultancy.links(this, session.urls);
  }
};
if (!window.customElements.get("ux-acct-next-consultation")) {
  window.customElements.define("ux-acct-next-consultation", UxAcctNextConsultation);
}

/* ---------------------------------------------------------------------------
   Mylands Professionals overview (milestone 10, GitHub #302; Figma: Account P1 281:1294, P1m 283:2986).
   <ux-pro-code>: the client code's Copy (the code) and Share with a client / Share client code (a short message
   with the code: the phone's share sheet on touch screens, otherwise copied to paste).
   <ux-pro-projects>: Projects from the palette book. The first three projects as cards: name, "For <client>" when
   the project has one (#303), up to three palettes as a colour strip and name each ("+N more" beyond), and Share with
   client (each palette's share link in one message, shared or copied as above); on mobile one strip of the
   project's first five colours and "· N palettes". The mobile Projects figure (count, palettes). With no projects:
   the palettes empty state.
   --------------------------------------------------------------------------- */
var uxProSend = async (title, text, copied) => {
  if (navigator.share && window.matchMedia("(pointer: coarse)").matches) {
    try {
      await navigator.share({ title, text });
      return;
    } catch (error) {
      if (error?.name === "AbortError") return;
    }
  }
  try {
    await navigator.clipboard.writeText(text);
    UxPaletteUi.notify(copied);
  } catch (error) {
    window.prompt(title, text);
  }
};

/* P4 (#305) adds a "Copied" label on Copy code for 2 s (data-copied-label) and More, shown where there's a share sheet */
var UxProCode = class extends HTMLElement {
  connectedCallback() {
    if (this._wired) return;
    this._wired = true;
    if (navigator.share) this.querySelectorAll("[data-ux-pro-code-more]").forEach((item) => (item.hidden = false));
    this.addEventListener("click", async (event) => {
      const copy = event.target.closest("[data-ux-pro-code-copy]");
      if (copy) {
        try {
          await navigator.clipboard.writeText(this.dataset.code);
          UxPaletteUi.notify(this.dataset.copied);
          if (this.dataset.copiedLabel) {
            copy._label ??= copy.innerHTML;
            copy.textContent = this.dataset.copiedLabel;
            clearTimeout(copy._copiedTimer);
            copy._copiedTimer = setTimeout(() => (copy.innerHTML = copy._label), 2000);
          }
        } catch (error) {
          UxPaletteUi.notify(this.dataset.copyFailed);
        }
      } else if (event.target.closest("[data-ux-pro-code-share]")) {
        uxProSend(this.dataset.shareTitle, this.dataset.shareText, this.dataset.shared);
      } else if (event.target.closest("[data-ux-pro-code-sheet]")) {
        try {
          await navigator.share({ title: this.dataset.shareTitle, text: this.dataset.shareText });
        } catch (error) {
          /* closed without sharing */
        }
      }
    });
  }
};
if (!window.customElements.get("ux-pro-code")) {
  window.customElements.define("ux-pro-code", UxProCode);
}

/* <ux-pro-client-orders> (P4, #305): orders placed with the designer's client code, from Odoo through the app proxy
   (GET {proxy}/professionals/client-orders: {ok, code, orders: [{date, name, client, items, total, currency,
   status}], totals: {count, value}}; agreed with odoo-73, 25 Oct; 403 not_designer for anyone else). Shows only when
   there are orders; any error leaves it hidden. data-fixture stands in for the response on the dev template. */
var UxProClientOrders = class extends HTMLElement {
  async connectedCallback() {
    if (this._wired) return;
    this._wired = true;
    let data = null;
    try {
      if (this.dataset.fixture) data = JSON.parse(this.dataset.fixture);
      else {
        const response = await fetch(`${uxAppProxy()}/professionals/client-orders`, { credentials: "same-origin", headers: { Accept: "application/json" } });
        data = response.ok ? await response.json() : null;
      }
    } catch (error) {
      data = null;
    }
    const orders = data?.ok && Array.isArray(data.orders) ? data.orders : [];
    if (!orders.length) return;
    const fill = (template, values) => Object.entries(values).reduce((text, [key, value]) => text.replaceAll(`[${key}]`, value), template);
    const plural = (key, count, values = {}) => fill(this.dataset[`${key}${count === 1 ? "One" : "Other"}`] || "", { count, ...values });
    /* "20 Oct", as the account's own order rows (the storefront's lang is plain "en") */
    const date = (value) => {
      const day = new Date(`${value}T12:00:00`);
      return Number.isNaN(day.getTime()) ? "" : day.toLocaleDateString(/^en(-GB)?$/i.test(document.documentElement.lang || "en") ? "en-GB" : document.documentElement.lang, { day: "numeric", month: "short" });
    };
    /* totals leave out cancelled and refunded orders; value is null when the orders are in more than one currency */
    const count = Number(data.totals?.count ?? orders.length);
    const value = data.totals?.value;
    this.querySelector("[data-ux-pro-client-orders-meta]").textContent = value ? plural("count", count, { value }) : plural("plain", count);
    const list = this.querySelector("[data-ux-pro-client-orders-list]");
    list.replaceChildren(
      ...orders.map((order) => {
        const row = document.createElement("li");
        row.className = "ux-pro-orders__row";
        const text = document.createElement("div");
        text.className = "ux-pro-orders__text";
        const name = document.createElement("p");
        name.className = "ux-pro-orders__name";
        name.textContent = [order.name, order.client].filter(Boolean).join(" · ");
        const meta = document.createElement("p");
        meta.className = "ux-pro-orders__meta";
        const status = this.dataset[`status${String(order.status || "").replace(/^./, (letter) => letter.toUpperCase())}`];
        meta.textContent = [date(order.date), order.items ? plural("items", Number(order.items)) : "", status].filter(Boolean).join(" · ");
        const total = document.createElement("p");
        total.className = "ux-pro-orders__total";
        total.textContent = order.total || "";
        text.append(name, meta);
        row.append(text, total);
        return row;
      })
    );
    this.hidden = false;
  }
};
if (!window.customElements.get("ux-pro-client-orders")) {
  window.customElements.define("ux-pro-client-orders", UxProClientOrders);
}

var UxProProjects = class extends HTMLElement {
  connectedCallback() {
    this.render = this.render.bind(this);
    this.render();
    document.addEventListener("ux-palette:change", this.render);
    if (this._wired) return;
    this._wired = true;
    this.addEventListener("click", (event) => {
      const button = event.target.closest("[data-ux-pro-project-share]");
      if (button) this.share(button.dataset.uxProProjectShare);
    });
  }
  disconnectedCallback() {
    document.removeEventListener("ux-palette:change", this.render);
  }
  strip(colours, className) {
    const strip = document.createElement("span");
    strip.className = `ux-pro-strip ${className}${colours.length ? "" : " ux-pro-strip--empty"}`;
    strip.setAttribute("aria-hidden", "true");
    strip.append(...colours.map((colour) => {
      const block = document.createElement("span");
      block.style.background = colour.c || "var(--ux-grey)";
      return block;
    }));
    return strip;
  }
  render() {
    if (!window.UxPalette) return;
    const projects = UxPalette.projects();
    const palettes = UxPalette.palettes();
    /* the store announces changes it hasn't made (a sync with nothing new); redrawing then would swap a card's
       button out between press and release */
    const drawn = JSON.stringify([projects, palettes.map((palette) => [palette.id, palette.name, palette.project, palette.colours])]);
    if (drawn === this.drawn) return;
    this.drawn = drawn;
    const cards = this.querySelector("[data-ux-pro-projects-cards]");
    const inProject = (project) => palettes.filter((palette) => palette.project === project.id);
    /* no projects: palettes made outside a project (say, before the account became Interior Designer) are named and
       linked, not called "no palettes" (found testing 25 Oct) */
    this.querySelector("[data-ux-pro-projects-empty]").hidden = projects.length > 0 || palettes.length > 0;
    const loose = this.querySelector("[data-ux-pro-projects-loose]");
    if (loose) {
      loose.hidden = projects.length > 0 || !palettes.length;
      loose.querySelector("[data-ux-pro-loose-title]").textContent = uxAcctPlural(this, "loose", palettes.length);
    }
    this.querySelector("[data-ux-pro-projects-all]").hidden = !projects.length;
    this.querySelector("[data-ux-pro-projects-meta]").textContent = projects.length ? uxAcctPlural(this, "projects", projects.length) : "";
    cards.hidden = !projects.length;

    const figure = document.querySelector("[data-ux-pro-projects-figure]");
    if (figure) {
      figure.hidden = false;
      figure.querySelector("[data-ux-pro-projects-count]").textContent = projects.length;
      figure.querySelector("[data-ux-pro-palettes-count]").textContent = uxAcctPlural(this, "palettes", palettes.length);
    }

    cards.replaceChildren(...projects.slice(0, 3).map((project) => {
      const own = inProject(project);
      const card = document.createElement("li");
      card.className = "ux-pro-pcard";
      card.innerHTML = '<div class="ux-pro-pcard__text"><h3 class="ux-pro-pcard__name"><a></a></h3><p class="ux-pro-pcard__client"></p></div><ul class="ux-pro-pcard__palettes unstyled-list" role="list"></ul><div class="ux-pro-pcard__links"></div>';
      const link = card.querySelector(".ux-pro-pcard__name a");
      const all = this.querySelector("[data-ux-pro-projects-all]");
      link.href = `${all.getAttribute("href")}#project-${project.id}`;
      link.textContent = project.name;
      const client = card.querySelector(".ux-pro-pcard__client");
      const count = document.createElement("span");
      count.className = "ux-pro-mobile-inline";
      count.textContent = `${project.client ? " · " : ""}${uxAcctPlural(this, "palettes", own.length)}`;
      if (project.client) client.textContent = this.dataset.client.replace("[client]", project.client);
      client.append(count);
      card.querySelector(".ux-pro-pcard__text").after(this.strip(own.flatMap((palette) => palette.colours).slice(0, 5), "ux-pro-strip--project"));
      card.querySelector(".ux-pro-pcard__palettes").replaceChildren(...own.slice(0, 3).map((palette) => {
        const row = document.createElement("li");
        row.className = "ux-pro-pcard__palette";
        const name = document.createElement("span");
        name.textContent = palette.name;
        row.append(this.strip(palette.colours.slice(0, 6), "ux-pro-strip--palette"), name);
        return row;
      }));
      if (own.length > 3) {
        const more = document.createElement("li");
        more.className = "ux-pro-pcard__more";
        more.textContent = this.dataset.more.replace("[count]", own.length - 3);
        card.querySelector(".ux-pro-pcard__palettes").append(more);
      }
      if (own.some((palette) => palette.colours.length)) {
        const share = document.createElement("button");
        share.type = "button";
        share.className = "button button--tertiary button--sm";
        share.dataset.uxProProjectShare = project.id;
        share.textContent = this.dataset.share;
        card.querySelector(".ux-pro-pcard__links").append(share);
      }
      return card;
    }));
  }
  share(id) {
    const project = UxPalette.projects().find((entry) => entry.id === id);
    if (!project) return;
    const own = UxPalette.palettes().filter((palette) => palette.project === id && palette.colours.length);
    const title = this.dataset.shareText.replace("[name]", project.name);
    const lines = own.map((palette) => `${palette.name}: ${UxPalette.shareUrl(palette.colours.map((colour) => colour.h))}`);
    uxProSend(title, [title, ...lines].join("\n"), this.dataset.copied);
  }
};
if (!window.customElements.get("ux-pro-projects")) {
  window.customElements.define("ux-pro-projects", UxProProjects);
}

/* ---------------------------------------------------------------------------
   Trade application (milestone 10, GitHub #309; Figma: Account › Proposed · Trade, S3 943:3639).
   <ux-trade-apply>: one form for both tiers. Odoo sets the tags, never the theme (contract with odoo-73, on #309):
   - signed in (data-state apply | upgrade): POST { tier, business_name, website } to the app proxy's
     /professionals/apply, then show the box for Odoo's status, or the error by its code;
   - signed out (register): the application waits in localStorage while Shopify's create_customer makes the account;
     once signed in, any page brings the customer back here (below) and it is sent then.
   The website field shows only for Interior Designer.
   --------------------------------------------------------------------------- */
const UX_TRADE_APPLY_KEY = "ux-trade-apply";
const UX_TRADE_APPLY_DAYS = 7;

const uxTradeApplyPending = () => {
  try {
    const pending = JSON.parse(localStorage.getItem(UX_TRADE_APPLY_KEY) || "null");
    if (pending && pending.tier && Date.now() - pending.at < UX_TRADE_APPLY_DAYS * 864e5) return pending;
    localStorage.removeItem(UX_TRADE_APPLY_KEY);
  } catch (error) {
    /* storage blocked: the applicant fills the form again once signed in */
  }
  return null;
};

class UxTradeApply extends HTMLElement {
  connectedCallback() {
    if (this.wired) return;
    this.wired = true;
    this.form = this.querySelector("form");
    if (!this.form) {
      try { localStorage.removeItem(UX_TRADE_APPLY_KEY); } catch (error) { /* nothing to clear */ }
      return;
    }
    this.error = this.querySelector("[data-ux-apply-error]");
    this.submit = this.form.querySelector('[type="submit"]');
    this.form.addEventListener("change", (event) => event.target.name === "tier" && this.syncTier());
    this.form.addEventListener("submit", (event) => this.onSubmit(event));
    this.syncTier();

    const pending = this.dataset.state === "register" ? null : uxTradeApplyPending();
    if (pending) {
      this.fill(pending);
      this.send(pending);
      return;
    }
    /* the landing page's Apply as Trade / Apply as an Interior Designer (?tier=trade | designer) pick that box */
    const asked = new URLSearchParams(window.location.search).get("tier");
    const box = asked && this.form.querySelector(`[name="tier"][value="${asked === "designer" ? "designer" : "trade"}"]`);
    if (box) {
      box.checked = true;
      this.syncTier();
    }
  }

  get payload() {
    const data = new FormData(this.form);
    const value = (name) => String(data.get(name) || "").trim();
    const tier = value("tier");
    return { tier, business_name: value("business_name"), website: tier === "designer" ? value("website") : "" };
  }

  syncTier() {
    const designer = this.form.querySelector('[name="tier"][value="designer"]');
    this.querySelectorAll("[data-ux-apply-designer]").forEach((field) => {
      field.hidden = !(designer && designer.checked);
    });
  }

  fill(pending) {
    const tier = this.form.querySelector(`[name="tier"][value="${pending.tier}"]`);
    if (tier) tier.checked = true;
    const business = this.form.querySelector('[name="business_name"]');
    if (business && pending.business_name) business.value = pending.business_name;
    const website = this.form.querySelector('[name="website"]');
    if (website && pending.website) website.value = pending.website;
    this.syncTier();
  }

  showError(html) {
    this.error.innerHTML = `<p>${html}</p>`;
    this.error.hidden = false;
  }

  onSubmit(event) {
    this.error.hidden = true;
    /* spaces alone aren't a business name (Odoo would answer missing_business_name) */
    const business = this.form.querySelector('[name="business_name"]');
    if (business && !business.value.trim()) business.value = "";
    if (!this.form.reportValidity()) {
      event.preventDefault();
      return;
    }
    const payload = this.payload;
    if (this.dataset.state === "register") {
      /* Shopify makes the account; the application goes once the customer is signed in */
      try {
        localStorage.setItem(UX_TRADE_APPLY_KEY, JSON.stringify({ ...payload, at: Date.now() }));
      } catch (error) {
        /* storage blocked: they'll fill the form again when signed in */
      }
      return;
    }
    event.preventDefault();
    this.send(payload);
  }

  async send(payload) {
    this.error.hidden = true;
    this.submit.disabled = true;
    this.submit.setAttribute("aria-busy", "true");
    const label = this.submit.textContent;
    this.submit.textContent = this.dataset.sending;
    let reply = null;
    let status = 0;
    try {
      const response = await fetch(this.dataset.endpoint, {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ tier: payload.tier, business_name: payload.business_name, website: payload.website || "" }),
      });
      status = response.status;
      reply = await response.json().catch(() => null);
    } catch (error) {
      reply = null;
    }
    this.submit.disabled = false;
    this.submit.removeAttribute("aria-busy");
    this.submit.textContent = label;

    if (reply && reply.ok) {
      try { localStorage.removeItem(UX_TRADE_APPLY_KEY); } catch (error) { /* already gone */ }
      const box = this.querySelector(`[data-ux-apply-result="${reply.status}"]`) || this.querySelector('[data-ux-apply-result="received"]');
      this.querySelector("[data-ux-apply-body]").hidden = true;
      box.hidden = false;
      box.focus();
      return;
    }

    const code = reply && reply.error;
    /* a definite no from Odoo: keep nothing to resend; a hiccup (5xx, 429, network): keep it for the next visit */
    if (status >= 400 && status < 500 && status !== 429) {
      try { localStorage.removeItem(UX_TRADE_APPLY_KEY); } catch (error) { /* already gone */ }
    }
    if (code === "designer_rejected") this.showError(this.dataset.errorRejected);
    else if (code === "missing_business_name") this.showError(this.dataset.errorBusiness);
    else if (code === "login_required") this.showError(this.dataset.errorLogin);
    else this.showError(this.dataset.errorGeneric);
  }
}
if (!window.customElements.get("ux-trade-apply")) {
  window.customElements.define("ux-trade-apply", UxTradeApply);
}

/* A new applicant has just been signed in (create_customer, or a later sign-in after confirming their email): bring
   them back to the application so it's sent. */
if (window.uxCustomer && window.uxTradeApplyUrl && !document.querySelector("ux-trade-apply") && uxTradeApplyPending()) {
  window.location.assign(window.uxTradeApplyUrl);
}

/* ---------------------------------------------------------------------------
   Project reference (milestone 10, GitHub #308; Figma: Account › States 286:3433). A Professional's optional basket
   field, saved as the cart attribute "Project reference" 600ms after typing stops (and on leaving the field), so it
   reaches the order, P3 and the invoice. It doesn't announce a cart change: nothing else on the page depends on it,
   and the summary that holds it re-renders from the cart, which keeps the saved value.
   --------------------------------------------------------------------------- */
var UxProjectRef = class extends HTMLElement {
  connectedCallback() {
    if (this.wired) return;
    this.wired = true;
    this.input = this.querySelector("[data-ux-project-ref]");
    if (!this.input) return;
    this.saved = this.input.value;
    this.input.addEventListener("input", () => {
      clearTimeout(this.timer);
      this.timer = setTimeout(() => this.save(), 600);
    });
    this.input.addEventListener("change", () => this.save());
  }
  disconnectedCallback() {
    if (this.input && this.input.value !== this.saved) this.save();
  }
  async save() {
    clearTimeout(this.timer);
    const value = this.input.value.trim().slice(0, 100);
    if (value === this.saved) return;
    this.saved = value;
    try {
      await fetch(`${window.Shopify?.routes?.root || "/"}cart/update.js`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ attributes: { "Project reference": value } }),
      });
    } catch (error) {
      this.saved = null; /* try again on the next change */
    }
  }
};
if (!window.customElements.get("ux-project-ref")) {
  window.customElements.define("ux-project-ref", UxProjectRef);
}

// js/components/ux-upcycled.js
// Upcycled sale (sections/ux-upcycled.liquid). Add puts one 2.5L tin in the basket and the row (and the colour drawer,
// when it shows that colour) swaps to the basket stepper, which sets the line's quantity through /cart/change.js
// (debounced, so quick taps send one request; 0 takes the line out and brings Add back). The swatch and name fill the
// shared drawer from the colour's <template> before the theme opens it. Every cart:change re-syncs the steppers and the
// pinned basket bar, so the cart drawer, header count and this page agree whichever of them changed the basket. The
// bar sits at the foot of the screen, on top of the palette tray when there is one (html.ux-upc-bar-open). The drawer's
// gallery (after the colour wall's panel) is a scroll-snap track: the arrows and thumbnails scroll it, and its scroll
// sets the count, the current thumbnail and the arrows.
var UxUpcycled = class extends HTMLElement {
  connectedCallback() {
    this.strings = JSON.parse(this.querySelector("[data-ux-upc-strings]").textContent);
    this.drawer = document.getElementById("ux-upc-qv");
    this.timers = new Map();
    this.controller = new AbortController();
    const signal = this.controller.signal;
    // capture: the drawer is filled before the theme's aria-controls handler opens it
    this.addEventListener("click", (event) => {
      const opener = event.target.closest("[data-ux-upc-open]");
      if (opener) this._fill(opener.dataset.uxUpcOpen);
    }, { capture: true, signal });
    // document: the drawer moves itself to <body> when it opens
    document.addEventListener("click", (event) => {
      const add = event.target.closest("[data-ux-upc-add]");
      if (add && this._owns(add)) this._add(add);
      const duo = event.target.closest("[data-ux-upc-duo-add]");
      if (duo && this.contains(duo)) this._addDuo(duo);
      // a Works with colour that's in the sale too: the drawer shows it in place, from the top, its name focused
      const swap = event.target.closest("[data-ux-upc-swap]");
      if (swap && this.drawer?.contains(swap)) {
        this._fill(swap.dataset.uxUpcSwap);
        this.drawer.querySelector(".ux-upc-qv__name")?.focus({ preventScroll: true });
        this.drawer.querySelector(".ux-upc-qv")?.scrollIntoView({ block: "start" });
      }
      const band = event.target.closest("[data-ux-upc-pair-show]");
      if (band && this.drawer?.contains(band)) this._openPair(band);
      const step = event.target.closest("[data-ux-upc-step], [data-ux-upc-thumb]");
      if (step && this._owns(step)) {
        const gallery = step.closest("[data-ux-upc-gallery]");
        const at = step.matches("[data-ux-upc-step]") ? this._slide(gallery) + Number(step.dataset.uxUpcStep) : Number(step.dataset.uxUpcThumb);
        this._goTo(gallery, at);
      }
    }, { signal });
    // scroll doesn't bubble: caught on the way down
    document.addEventListener("scroll", (event) => {
      if (event.target.matches?.("[data-ux-upc-track]") && this._owns(event.target)) this._galleryState(event.target.closest("[data-ux-upc-gallery]"));
    }, { capture: true, passive: true, signal });
    document.addEventListener("ux-qty:change", (event) => {
      if (!this._owns(event.target)) return;
      this._queue(event.detail.variantId, event.detail.quantity, event.target);
    }, { signal });
    document.addEventListener("cart:change", (event) => this._onCartChange(event), { signal });
    this._barState();
  }
  disconnectedCallback() {
    this.controller.abort();
    document.documentElement.classList.remove("ux-upc-bar-open");
  }
  // the basket bar sits at the foot of the screen, on top of the palette tray when there is one (ux-upcycled.css)
  _barState() {
    const bar = this.querySelector("[data-ux-upc-bar]");
    const open = Boolean(bar && !bar.hidden);
    document.documentElement.classList.toggle("ux-upc-bar-open", open);
    if (open) document.documentElement.style.setProperty("--ux-upc-bar-height", `${bar.offsetHeight}px`);
  }
  _owns(element) {
    return this.contains(element) || Boolean(this.drawer?.contains(element));
  }
  _fill(productId) {
    const template = document.getElementById(`ux-upc-qv-${productId}`);
    if (!template || !this.drawer) return;
    const content = template.content.cloneNode(true);
    this.drawer.querySelector("[data-ux-upc-qv-body]").replaceChildren(content.querySelector(".ux-upc-qv"));
    this.drawer.querySelector("[data-ux-upc-qv-foot]").replaceChildren(content.querySelector(".ux-upc-qv__foot"));
    // the foot and any pairing in the sale start as their rows stand
    this.drawer.querySelectorAll(":is(.ux-upc-qv__foot, .ux-pairs__open)[data-variant-id]").forEach((target) => {
      const row = this.querySelector(`.ux-upc-item[data-variant-id="${target.dataset.variantId}"]`);
      this._show(target, row?.querySelector("ux-qty-stepper")?.value || 0);
    });
    // a photo that has to load comes in over its colour (theme.css ux-photo-reveal); a cached one is simply there
    const still = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    this.drawer.querySelectorAll(".ux-upc-gallery__image").forEach((image) => {
      if (image.complete) return;
      image.classList.add("is-loading");
      image.addEventListener("load", () => {
        image.classList.remove("is-loading");
        if (!still) image.classList.add("is-arriving");
      }, { once: true });
      image.addEventListener("error", () => image.classList.remove("is-loading"), { once: true });
    });
    const gallery = this.drawer.querySelector("[data-ux-upc-gallery]");
    if (gallery) this._galleryState(gallery);
  }
  // Works with, as Plan's pairings drawer (UxPaletteView.onPairs): a band opens where it stands, growing from the band as
  // the open card folds into one (heights only, so nothing stretches); its card fades in once there's room
  _openPair(band) {
    const section = band.closest("[data-ux-upc-pairs]");
    const index = band.dataset.uxUpcPairShow;
    const card = section.querySelector(`[data-ux-upc-pair="${index}"]`);
    const open = section.querySelector("[data-ux-upc-pair]:not([hidden])");
    if (!card || card === open) return;
    const from = band.getBoundingClientRect().height;
    const fold = open?.getBoundingClientRect().height;
    const was = open?.dataset.uxUpcPair;
    if (open) open.hidden = true;
    const folded = was != null ? section.querySelector(`[data-ux-upc-pair-show="${was}"]`) : null;
    if (folded) folded.hidden = false;
    band.hidden = true;
    card.hidden = false;
    (card.querySelector("[data-ux-upc-add]:not([hidden])") || card.querySelector(".ux-pairs__swap"))?.focus({ preventScroll: true });
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const ease = "cubic-bezier(0.2, 0, 0, 1)";
    const to = card.getBoundingClientRect().height;
    card.style.overflow = "hidden";
    card.animate([{ height: `${from}px`, minHeight: `${from}px` }, { height: `${to}px`, minHeight: `${to}px` }], { duration: 320, easing: ease })
      .finished.then(() => (card.style.overflow = ""), () => {});
    card.querySelectorAll(".ux-pairs__card, .ux-diamond").forEach((part) => part.animate([{ opacity: 0 }, { opacity: 0, offset: 0.45 }, { opacity: 1 }], { duration: 320, easing: "ease-out" }));
    if (folded && fold) {
      folded.animate([{ flexBasis: `${fold}px` }, { flexBasis: `${folded.getBoundingClientRect().height}px` }], { duration: 320, easing: ease });
      folded.querySelectorAll("span").forEach((part) => part.animate([{ opacity: 0 }, { opacity: 0, offset: 0.5 }, { opacity: 1 }], { duration: 320 }));
    }
  }
  _slide(gallery) {
    const track = gallery.querySelector("[data-ux-upc-track]");
    return Math.round(track.scrollLeft / (track.clientWidth || 1));
  }
  _goTo(gallery, at) {
    const track = gallery.querySelector("[data-ux-upc-track]");
    // the arrows go round, as the colour wall's do
    const index = (at + track.children.length) % track.children.length;
    const still = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    track.scrollTo({ left: index * track.clientWidth, behavior: still ? "auto" : "smooth" });
  }
  _galleryState(gallery) {
    const track = gallery.querySelector("[data-ux-upc-track]");
    const count = track.children.length;
    const index = this._slide(gallery);
    const counter = gallery.querySelector("[data-ux-upc-count]");
    if (counter) counter.textContent = `${index + 1} / ${count}`;
    // the last thumbnail stands for every slide from its own on ("+N")
    const thumbs = [...gallery.querySelectorAll("[data-ux-upc-thumb]")];
    const current = thumbs.filter((thumb) => Number(thumb.dataset.uxUpcThumb) <= index).pop();
    thumbs.forEach((thumb) => thumb.toggleAttribute("aria-current", thumb === current));
    current?.setAttribute("aria-current", "true");
  }
  _targets(variantId) {
    const selector = `[data-variant-id="${variantId}"]:is(.ux-upc-item, .ux-upc-qv__foot, .ux-pairs__open)`;
    return [...this.querySelectorAll(selector), ...(this.drawer ? this.drawer.querySelectorAll(selector) : [])];
  }
  _show(target, quantity) {
    target.classList.toggle("is-in-basket", quantity > 0);
    target.querySelector("ux-qty-stepper")?.sync(quantity);
  }
  _sections() {
    const sections = [];
    document.documentElement.dispatchEvent(new CustomEvent("cart:prepare-bundled-sections", { bubbles: true, detail: { sections } }));
    return sections.join(",");
  }
  _error(target, show) {
    const holder = target.closest(".ux-upc-item, .ux-upc-qv__foot, .ux-pairs__card, .ux-upc-duo");
    if (!holder) return;
    let message = holder.querySelector(".ux-upc-error");
    if (!show) {
      message?.remove();
      return;
    }
    if (!message) {
      message = document.createElement("p");
      message.className = "ux-upc-error";
      message.setAttribute("role", "alert");
      holder.append(message);
    }
    message.textContent = typeof show === "string" ? show : this.strings.error;
  }
  async _add(button) {
    if (button.getAttribute("aria-busy") === "true") return;
    const variantId = Number(button.closest("[data-variant-id]").dataset.variantId);
    const focusAfter = button.closest(".ux-upc-buy");
    button.setAttribute("aria-busy", "true");
    this._error(button, false);
    const root = window.Shopify.routes.root;
    try {
      const response = await fetch(`${root}cart/add.js`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }], sections: this._sections() })
      });
      const added = await response.json();
      if (!response.ok) throw new Error(added.description || added.message || response.statusText);
      const cart = await (await fetch(`${root}cart.js`)).json();
      cart.sections = added.sections;
      document.documentElement.dispatchEvent(new CustomEvent("cart:change", { bubbles: true, detail: { baseEvent: "ux-upcycled:add", cart } }));
      // Add has gone; keep the keyboard on this colour's stepper (a pairing's card: on View)
      focusAfter?.querySelector('ux-qty-stepper [data-step="1"]')?.focus({ preventScroll: true });
      button.closest(".ux-pairs__card")?.querySelector("[data-ux-upc-swap]")?.focus({ preventScroll: true });
    } catch (error) {
      this._error(button, error.message || true);
    } finally {
      button.removeAttribute("aria-busy");
    }
  }
  // Paired in the sale: a tin of each that isn't in the basket yet, in one request; the card says so once both are
  async _addDuo(button) {
    if (button.getAttribute("aria-busy") === "true") return;
    const card = button.closest("[data-ux-upc-duo]");
    const root = window.Shopify.routes.root;
    button.setAttribute("aria-busy", "true");
    this._error(button, false);
    try {
      const current = await (await fetch(`${root}cart.js`)).json();
      const held = new Set(current.items.map((item) => item.variant_id));
      const items = card.dataset.uxUpcDuo.split(",").map(Number).filter((id) => !held.has(id)).map((id) => ({ id, quantity: 1 }));
      let sections;
      if (items.length) {
        const response = await fetch(`${root}cart/add.js`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ items, sections: this._sections() })
        });
        const added = await response.json();
        if (!response.ok) throw new Error(added.description || added.message || response.statusText);
        sections = added.sections;
      }
      const cart = await (await fetch(`${root}cart.js`)).json();
      cart.sections = sections;
      document.documentElement.dispatchEvent(new CustomEvent("cart:change", { bubbles: true, detail: { baseEvent: "ux-upcycled:add-duo", cart } }));
      // Add both has gone: the keyboard stays on the card, on its first colour
      card.querySelector("[data-ux-upc-open]")?.focus({ preventScroll: true });
    } catch (error) {
      this._error(button, error.message || true);
    } finally {
      button.removeAttribute("aria-busy");
    }
  }
  _queue(variantId, quantity, source) {
    this._targets(variantId).forEach((target) => target.querySelector("ux-qty-stepper") !== source && target.querySelector("ux-qty-stepper")?.sync(quantity));
    clearTimeout(this.timers.get(variantId));
    this.timers.set(variantId, setTimeout(() => this._change(variantId, quantity, source), 350));
  }
  async _change(variantId, quantity, source) {
    this.timers.delete(variantId);
    this._error(source, false);
    const root = window.Shopify.routes.root;
    try {
      const response = await fetch(`${root}cart/change.js`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: String(variantId), quantity, sections: this._sections() })
      });
      const cart = await response.json();
      if (!response.ok) throw new Error(cart.description || cart.message || response.statusText);
      document.documentElement.dispatchEvent(new CustomEvent("cart:change", { bubbles: true, detail: { baseEvent: "ux-upcycled:change", cart } }));
      if (quantity === 0) source.closest(".ux-upc-buy")?.querySelector("[data-ux-upc-add]")?.focus({ preventScroll: true });
    } catch (error) {
      this._error(source, error.message || true);
      this._sync(await (await fetch(`${root}cart.js`)).json());
    }
  }
  async _onCartChange(event) {
    let cart = event.detail?.cart;
    if (!cart || !Array.isArray(cart.items)) cart = await (await fetch(`${window.Shopify.routes.root}cart.js`)).json();
    this._sync(cart);
  }
  _sync(cart) {
    const quantities = new Map();
    cart.items.forEach((item) => quantities.set(item.variant_id, (quantities.get(item.variant_id) || 0) + item.quantity));
    const targets = [...this.querySelectorAll(".ux-upc-item[data-variant-id]"), ...(this.drawer ? this.drawer.querySelectorAll(":is(.ux-upc-qv__foot, .ux-pairs__open)[data-variant-id]") : [])];
    targets.forEach((target) => {
      const variantId = Number(target.dataset.variantId);
      if (this.timers.has(variantId)) return; // a change of ours is still to be sent
      this._show(target, quantities.get(variantId) || 0);
    });
    this.querySelectorAll("[data-ux-upc-duo]").forEach((card) => {
      card.classList.toggle("is-in-basket", card.dataset.uxUpcDuo.split(",").every((id) => quantities.get(Number(id)) > 0));
    });
    const bar = this.querySelector("[data-ux-upc-bar]");
    if (!bar) return;
    bar.hidden = cart.item_count === 0;
    bar.querySelector("[data-ux-upc-bar-count]").textContent = cart.item_count === 1 ? this.strings.countOne : this.strings.count.replace("[count]", cart.item_count);
    const locale = document.documentElement.lang || "en-GB";
    bar.querySelector("[data-ux-upc-bar-total]").textContent = new Intl.NumberFormat(locale, { style: "currency", currency: cart.currency }).format(cart.total_price / 100);
    this._barState();
  }
};
if (!window.customElements.get("ux-upcycled")) {
  window.customElements.define("ux-upcycled", UxUpcycled);
}
